// SPDX-License-Identifier: AGPL-3.0-or-later

package one.wabbit.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.dom.svg.Line
import com.varabyte.kobweb.compose.dom.svg.Path
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.core.Page
import kairos.Kairos
import kairos.KairosAdminSnapshot
import kairos.KairosAdminUserRow
import kairos.KairosAgentSubaccount
import kairos.KairosAgentWorkspaceGrantRequest
import kairos.KairosDailyPlan
import kairos.KairosKpiEvaluation
import kairos.KairosKpiValuePoint
import kairos.KairosMarkdownBlock
import kairos.KairosMarkdownInline
import kairos.KairosObjectivePressure
import kairos.KairosSessionInfo
import kairos.KairosWorkspaceAccess
import kairos.KairosWorkspaceMember
import kairos.KairosWorkspaceShareTarget
import kairos.KairosWorkspaceSnapshot
import kairos.PatchKairosUserRequest
import kairos.PrimitiveTimeSpan
import kairos.TimeSpan
import kairos.TimeUnit
import kairos.ZonedDateTime
import kairos.buildKairosDailyPlan
import kairos.buildKairosExecutionFlow
import kairos.buildKairosKpiEvaluations
import kairos.formatKairosKpiFormula
import kairos.parseKairosKpiFormula
import kairos.parseKairosMarkdown
import kairos.parseKairosMarkdownInline
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.js.Date
import kotlin.js.unsafeCast
import kotlin.math.abs
import kotlin.math.max
import kotlin.math.roundToInt
import kotlin.time.Clock
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.builders.InputAttrsScope
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.KeyboardEvent
import org.w3c.files.File
import org.w3c.files.FileReader

private const val Charcoal = "#2e3035"
private const val Sidebar = "#292b30"
private const val Canvas = "#f6f7f8"
private const val Paper = "#ffffff"
private const val Ink = "#25272d"
private const val Muted = "#71757e"
private const val Border = "#e4e6ea"
private const val Coral = "#f26a57"
private const val Blue = "#4f83d8"
private const val Teal = "#56c6b8"
private const val Green = "#7fc36f"
private const val Yellow = "#f3c84f"
private const val Pink = "#e9589f"
private const val CurrentProjectStorageKey = "kairos.web.currentProjectId"
private const val AccountTimeZoneStorageKey = "kairos.web.accountTimeZoneId"
private const val AccountLocaleStorageKey = "kairos.web.accountLocaleTag"
private const val LegacyCurrentUserTimeZoneStorageKey = "kairos.web.currentUserTimeZoneId"
private const val LegacyCurrentUserLocaleStorageKey = "kairos.web.currentUserLocaleTag"
private const val DatePickerStyleElementId = "kairos-web-date-picker-style"
private const val DatePickerCssClass = "kairos-date-picker"
private const val ProfileAvatarInputId = "web-profile-avatar-file-input"
private const val ProfileAvatarMaxDimensionPx = 160
private const val ProfileAvatarMaxStoredCharacters = 220_000

private val AccentColors = listOf(Coral, Blue, Teal, Pink, Green, Yellow)

data class WebWorkspace(
    val workspace: Kairos.Workspace,
    val memberships: List<Kairos.WorkspaceMembership> = emptyList(),
    val sourceName: String,
    val users: List<Kairos.User>,
    val projects: List<Kairos.Project>,
    val tickets: List<Kairos.Ticket>,
    val ticketBlockers: List<Kairos.TicketBlocker> = emptyList(),
    val activities: List<Kairos.TicketActivity> = emptyList(),
    val objectives: List<Kairos.Objective> = emptyList(),
    val kpis: List<Kairos.Kpi> = emptyList(),
    val measurements: List<Kairos.KpiMeasurement> = emptyList(),
    val kpiEvaluations: List<KairosKpiEvaluation> = emptyList(),
    val timeboxes: List<Kairos.Timebox> = emptyList(),
    val routines: List<Kairos.Routine> = emptyList(),
    val events: List<Kairos.Event> = emptyList(),
)

private data class WebTask(
    val ticket: Kairos.Ticket,
    val project: Kairos.Project,
    val workspaceLabel: String,
    val owner: Kairos.User?,
    val status: String,
    val statusColor: String,
    val accent: String,
    val objectiveCount: Int,
    val blockerLabel: String?,
)

private enum class AuthScreenMode {
    SignIn,
    SignUp,
}

private enum class SignUpStep {
    Account,
    Workspace,
}

private enum class InitialWebLoadState {
    Loading,
    Ready,
    Unavailable,
}

private fun hasKairosSessionCookie(): Boolean =
    try {
        js("document.cookie").unsafeCast<String>().split(";").any { cookie ->
            cookie.trim().startsWith("kairos_session=")
        }
    } catch (_: Throwable) {
        false
    }

private sealed interface AuthSubmissionState {
    data object Idle : AuthSubmissionState

    data class Loading(val mode: AuthScreenMode) : AuthSubmissionState

    data class Error(val mode: AuthScreenMode, val message: String) : AuthSubmissionState
}

private enum class SessionModalTab {
    Profile,
    Agents,
}

private data class WebAgentWorkspaceDraft(
    val workspaceId: Kairos.WorkspaceId,
    val role: Kairos.WorkspaceMembershipRole,
)

private data class WebAgentTokenReveal(
    val displayName: String,
    val username: String,
    val token: String,
    val rotated: Boolean,
)

private data class WebAgentWorkspaceAccessMutation(
    val agentUserId: Kairos.UserId,
    val workspaceId: Kairos.WorkspaceId,
)

private fun List<WebAgentWorkspaceDraft>.roleFor(
    workspaceId: Kairos.WorkspaceId
): Kairos.WorkspaceMembershipRole? = firstOrNull { draft -> draft.workspaceId == workspaceId }?.role

private fun List<WebAgentWorkspaceDraft>.withRole(
    workspaceId: Kairos.WorkspaceId,
    role: Kairos.WorkspaceMembershipRole,
): List<WebAgentWorkspaceDraft> =
    filterNot { draft -> draft.workspaceId == workspaceId } +
        WebAgentWorkspaceDraft(workspaceId, role)

private fun List<WebAgentWorkspaceDraft>.without(
    workspaceId: Kairos.WorkspaceId
): List<WebAgentWorkspaceDraft> = filterNot { draft -> draft.workspaceId == workspaceId }

private data class WebColumn(val project: Kairos.Project, val tasks: List<WebTask>)

private data class WebStageColumn(val stage: Kairos.TicketStage, val tasks: List<WebTask>)

private data class WebExecutionSections(
    val now: List<WebTask>,
    val inFlight: List<WebTask>,
    val next: List<WebTask>,
    val later: List<WebTask>,
    val inbox: List<WebTask>,
)

private data class WebTimelineMonthSpan(val label: String, val dayCount: Int)

private data class WebTimelineSchedule(
    val days: List<LocalDate>,
    val monthSpans: List<WebTimelineMonthSpan>,
    val focusDayIndex: Int,
)

private data class WebTimelinePlacedEntry(
    val entry: WebScheduleEntry,
    val laneIndex: Int,
    val startDayIndex: Int,
    val endDayIndex: Int,
)

private data class WebTimelineRow(
    val project: Kairos.Project,
    val placedEntries: List<WebTimelinePlacedEntry>,
    val laneCount: Int,
)

private data class WebCalendarMonthGrid(
    val label: String,
    val startDate: LocalDate,
    val endDate: LocalDate,
    val weeks: List<List<LocalDate?>>,
)

private data class WebCalendarWeekRange(
    val label: String,
    val startDate: LocalDate,
    val endDate: LocalDate,
    val days: List<LocalDate>,
)

private enum class WebCalendarMode {
    Month,
    Week,
}

private enum class WebDateControlKind {
    Date,
    DateTime,
}

private enum class WebScheduleManagerTab {
    Events,
    Timeboxes,
    Routines,
}

private enum class WebScheduleEntryKind {
    Ticket,
    Event,
    Timebox,
}

private data class WebScheduleEntry(
    val key: String,
    val projectId: Kairos.ProjectId,
    val projectName: String,
    val title: String,
    val startDate: LocalDate,
    val endDate: LocalDate,
    val accent: String,
    val kind: WebScheduleEntryKind,
    val detail: String? = null,
    val blockerLabel: String? = null,
    val sortEpochMs: Long = 0L,
    val clickableTask: WebTask? = null,
    val completed: Boolean = false,
)

private data class WebScheduleSelection(val kind: WebScheduleEntryKind, val key: String)

private data class WebActivityEntry(
    val activityId: Kairos.TicketActivityId,
    val ticketId: Kairos.TicketId,
    val ticketTitle: String,
    val summary: String,
    val actorName: String,
    val createdAtLabel: String,
)

private data class WebUpcomingScheduleItem(
    val kind: String,
    val title: String,
    val detail: String,
    val accent: String,
    val sortEpochMs: Long,
)

private sealed interface WebWorkspaceShareLookupState {
    data object Idle : WebWorkspaceShareLookupState

    data object Loading : WebWorkspaceShareLookupState

    data class Resolved(val target: KairosWorkspaceShareTarget) : WebWorkspaceShareLookupState

    data class NotFound(val identifier: String) : WebWorkspaceShareLookupState

    data class Error(val message: String) : WebWorkspaceShareLookupState
}

private sealed interface WebWorkspaceMemberAction {
    val member: KairosWorkspaceMember

    data class Promote(override val member: KairosWorkspaceMember) : WebWorkspaceMemberAction

    data class Demote(override val member: KairosWorkspaceMember) : WebWorkspaceMemberAction

    data class Remove(override val member: KairosWorkspaceMember) : WebWorkspaceMemberAction
}

private data class WebRouteState(
    val area: WorkspaceArea,
    val projectId: String?,
    val view: String,
    val settingsTab: String?,
    val ticketId: String?,
    val detailsOpen: Boolean,
)

private enum class WorkspaceArea {
    Home,
    Inbox,
    Objectives,
    Kpis,
    Admin,
    Downloads,
    Project,
    WorkspaceSettings,
}

private enum class WebWorkspaceSettingsTab {
    General,
    Members,
}

private enum class WebSort {
    Created,
    Due,
    Title,
}

private enum class WebRouteCommitMode {
    Replace,
    Push,
}

private enum class WebKpiFilter {
    All,
    Measured,
    Derived,
    Attention,
}

private enum class WebKpiChartMode {
    Line,
    Scatter,
    Bars,
}

private enum class WebTaskQuickActionTone {
    Neutral,
    Primary,
    Review,
    Success,
}

private data class WebTaskQuickAction(
    val label: String,
    val stage: Kairos.TicketStage? = null,
    val markCompleted: Boolean = false,
    val tone: WebTaskQuickActionTone = WebTaskQuickActionTone.Neutral,
)

private enum class WebPanelTone {
    Primary,
    Secondary,
}

private val projectViews = listOf("Overview", "List", "Board", "Timeline", "Calendar")

private fun normalizedProjectView(view: String): String =
    if (view in projectViews) view else "Board"

private fun parseRouteState(search: String): WebRouteState {
    val params =
        search
            .removePrefix("?")
            .split("&")
            .mapNotNull { part ->
                val trimmed = part.trim()
                if (trimmed.isEmpty()) {
                    null
                } else {
                    val pieces = trimmed.split("=", limit = 2)
                    val key = pieces.firstOrNull().orEmpty()
                    val value = pieces.getOrNull(1).orEmpty()
                    key.takeIf { it.isNotEmpty() }?.let { it to value }
                }
            }
            .toMap()
    return WebRouteState(
        area = params["area"].toWorkspaceArea(),
        projectId = params["project"]?.takeIf { it.isNotBlank() },
        view = params["view"].toProjectView(),
        settingsTab = params["tab"]?.takeIf { it.isNotBlank() },
        ticketId = params["ticket"]?.takeIf { it.isNotBlank() },
        detailsOpen = params["details"] == "1" || params["details"] == "true",
    )
}

private fun String?.toWorkspaceArea(): WorkspaceArea =
    when (this?.lowercase()) {
        "today",
        "home",
        "my-tasks",
        "my-tickets" -> WorkspaceArea.Home
        "inbox" -> WorkspaceArea.Inbox
        "objectives" -> WorkspaceArea.Objectives
        "kpis" -> WorkspaceArea.Kpis
        "admin" -> WorkspaceArea.Admin
        "downloads" -> WorkspaceArea.Downloads
        "project" -> WorkspaceArea.Project
        "workspace-settings" -> WorkspaceArea.WorkspaceSettings
        else -> WorkspaceArea.Home
    }

private fun String?.toProjectView(): String =
    when (this?.lowercase()) {
        "overview" -> "Overview"
        "list" -> "List"
        "board" -> "Board"
        "timeline" -> "Timeline"
        "calendar" -> "Calendar"
        else -> normalizedProjectView(this.orEmpty())
    }

private fun WorkspaceArea.routeToken(): String =
    when (this) {
        WorkspaceArea.Home -> "today"
        WorkspaceArea.Inbox -> "inbox"
        WorkspaceArea.Objectives -> "objectives"
        WorkspaceArea.Kpis -> "kpis"
        WorkspaceArea.Admin -> "admin"
        WorkspaceArea.Downloads -> "downloads"
        WorkspaceArea.Project -> "project"
        WorkspaceArea.WorkspaceSettings -> "workspace-settings"
    }

private fun projectViewRouteToken(view: String): String =
    when (normalizedProjectView(view)) {
        "Overview" -> "overview"
        "List" -> "list"
        "Board" -> "board"
        "Timeline" -> "timeline"
        "Calendar" -> "calendar"
        else -> "board"
    }

private fun WebRouteState.toUrl(): String {
    val params = buildList {
        add("area=${area.routeToken()}")
        if (area == WorkspaceArea.Project) {
            projectId?.let { add("project=$it") }
            add("view=${projectViewRouteToken(view)}")
        }
        if (area == WorkspaceArea.WorkspaceSettings) {
            settingsTab?.let { add("tab=$it") }
        }
        if (detailsOpen && ticketId != null) {
            add("ticket=$ticketId")
            add("details=1")
        }
    }
    return if (params.isEmpty()) "/" else "/?${params.joinToString("&")}"
}

private fun routeProjectId(value: String): Kairos.ProjectId = Kairos.ProjectId(value)

private fun routeTicketId(value: String): Kairos.TicketId = Kairos.TicketId(value)

private fun String?.toWorkspaceSettingsTab(): WebWorkspaceSettingsTab =
    when (this?.lowercase()) {
        "general" -> WebWorkspaceSettingsTab.General
        "members" -> WebWorkspaceSettingsTab.Members
        else -> WebWorkspaceSettingsTab.Members
    }

private fun WebWorkspaceSettingsTab.routeToken(): String =
    when (this) {
        WebWorkspaceSettingsTab.General -> "general"
        WebWorkspaceSettingsTab.Members -> "members"
    }

private fun loadStoredCurrentProjectId(): Kairos.ProjectId? =
    try {
        window.localStorage
            .getItem(CurrentProjectStorageKey)
            ?.takeIf { it.isNotBlank() }
            ?.let(::routeProjectId)
    } catch (error: Throwable) {
        propagateUiThrowable(error)
        null
    }

private fun saveStoredCurrentWorkspaceId(workspaceId: Kairos.WorkspaceId?) {
    storeWorkspaceId(workspaceId?.value)
}

private fun saveStoredCurrentProjectId(projectId: Kairos.ProjectId?) {
    try {
        if (projectId == null) {
            window.localStorage.removeItem(CurrentProjectStorageKey)
        } else {
            window.localStorage.setItem(CurrentProjectStorageKey, projectId.value)
        }
    } catch (error: Throwable) {
        propagateUiThrowable(error)
        return
    }
}

private fun loadStoredCurrentTimeZoneId(): String? =
    try {
        window.localStorage.getItem(AccountTimeZoneStorageKey)?.trim()?.takeIf { it.isNotEmpty() }
            ?: window.localStorage.getItem(LegacyCurrentUserTimeZoneStorageKey)?.trim()?.takeIf {
                it.isNotEmpty()
            }
    } catch (error: Throwable) {
        propagateUiThrowable(error)
        null
    }

private fun loadStoredCurrentLocaleTag(): String? =
    try {
        window.localStorage.getItem(AccountLocaleStorageKey)?.trim()?.takeIf { it.isNotEmpty() }
            ?: window.localStorage.getItem(LegacyCurrentUserLocaleStorageKey)?.trim()?.takeIf {
                it.isNotEmpty()
            }
    } catch (error: Throwable) {
        propagateUiThrowable(error)
        null
    }

private fun saveStoredAccountPreferences(user: Kairos.User?) {
    try {
        if (user?.timeZoneId.isNullOrBlank()) {
            window.localStorage.removeItem(AccountTimeZoneStorageKey)
        } else {
            window.localStorage.setItem(AccountTimeZoneStorageKey, user.timeZoneId.orEmpty())
        }
        if (user?.localeTag.isNullOrBlank()) {
            window.localStorage.removeItem(AccountLocaleStorageKey)
        } else {
            window.localStorage.setItem(AccountLocaleStorageKey, user.localeTag.orEmpty())
        }
        window.localStorage.removeItem(LegacyCurrentUserTimeZoneStorageKey)
        window.localStorage.removeItem(LegacyCurrentUserLocaleStorageKey)
    } catch (error: Throwable) {
        propagateUiThrowable(error)
    }
}

private fun normalizeAvatarValue(value: String?): String? =
    value?.trim()?.takeIf { it.isNotEmpty() }

private suspend fun prepareAvatarUpload(file: File): String {
    val name = file.name.lowercase()
    val mimeType = file.type.lowercase()
    val prepared =
        when {
            mimeType == "image/png" || name.endsWith(".png") -> resizeAvatarPng(file)
            mimeType == "image/svg+xml" || name.endsWith(".svg") -> readFileAsDataUrl(file)
            else -> throw IllegalArgumentException("Choose a PNG or SVG avatar.")
        }
    if (prepared.length > ProfileAvatarMaxStoredCharacters) {
        throw IllegalArgumentException("Avatar is too large. Choose a simpler PNG or SVG.")
    }
    return prepared
}

private suspend fun resizeAvatarPng(file: File): String {
    val sourceDataUrl = readFileAsDataUrl(file)
    val image = loadImageElement(sourceDataUrl)
    val longestSide = max(image.naturalWidth, image.naturalHeight).coerceAtLeast(1)
    val scale =
        if (longestSide > ProfileAvatarMaxDimensionPx) {
            ProfileAvatarMaxDimensionPx.toDouble() / longestSide.toDouble()
        } else {
            1.0
        }
    val targetWidth = (image.naturalWidth * scale).roundToInt().coerceAtLeast(1)
    val targetHeight = (image.naturalHeight * scale).roundToInt().coerceAtLeast(1)
    val canvas = document.createElement("canvas") as HTMLCanvasElement
    canvas.width = targetWidth
    canvas.height = targetHeight
    val context =
        canvas.getContext("2d") as? CanvasRenderingContext2D
            ?: throw IllegalStateException("Could not prepare avatar canvas.")
    context.clearRect(0.0, 0.0, targetWidth.toDouble(), targetHeight.toDouble())
    context.drawImage(image, 0.0, 0.0, targetWidth.toDouble(), targetHeight.toDouble())
    return canvas.toDataURL("image/png")
}

private suspend fun readFileAsDataUrl(file: File): String =
    suspendCancellableCoroutine { continuation ->
        val reader = FileReader()
        reader.onload = {
            if (continuation.isCompleted) {
                null
            } else {
                val result = reader.result as? String
                if (result != null) {
                    continuation.resume(result)
                } else {
                    continuation.resumeWithException(
                        IllegalStateException("Could not read avatar file.")
                    )
                }
                null
            }
        }
        reader.onerror = {
            if (!continuation.isCompleted) {
                continuation.resumeWithException(
                    IllegalStateException("Could not read avatar file.")
                )
            }
            null
        }
        reader.readAsDataURL(file)
    }

private suspend fun loadImageElement(source: String): HTMLImageElement =
    suspendCancellableCoroutine { continuation ->
        val image = document.createElement("img") as HTMLImageElement
        image.addEventListener(
            "load",
            EventListener {
                if (!continuation.isCompleted) {
                    continuation.resume(image)
                }
            },
        )
        image.addEventListener(
            "error",
            EventListener {
                if (!continuation.isCompleted) {
                    continuation.resumeWithException(
                        IllegalArgumentException("Could not decode the PNG avatar.")
                    )
                }
            },
        )
        image.src = source
    }

@Composable
private fun EnsureDatePickerStyles() {
    DisposableEffect(Unit) {
        if (document.getElementById(DatePickerStyleElementId) == null) {
            val styleElement = document.createElement("style")
            styleElement.id = DatePickerStyleElementId
            styleElement.textContent =
                """
                .$DatePickerCssClass::-webkit-calendar-picker-indicator {
                    background: transparent;
                    color: transparent;
                    cursor: pointer;
                    height: 100%;
                    margin: 0;
                    opacity: 0;
                    width: 38px;
                }

                .$DatePickerCssClass::-webkit-clear-button,
                .$DatePickerCssClass::-webkit-inner-spin-button {
                    display: none;
                }

                .$DatePickerCssClass:focus {
                    border-color: $Blue !important;
                    box-shadow: 0 0 0 2px rgba(79, 131, 216, 0.18);
                }
                """
                    .trimIndent()
            document.head?.appendChild(styleElement)
        }
        onDispose {}
    }
}

@Page
@Composable
fun HomePage() {
    EnsureDatePickerStyles()

    var viewportWidth by remember { mutableStateOf(window.innerWidth) }
    val initialRoute = remember { parseRouteState(window.location.search) }
    var workspace by remember { mutableStateOf(createEmptyWorkspace()) }
    val initialProjectId: Kairos.ProjectId? =
        initialRoute.projectId?.let(::routeProjectId)
            ?: loadStoredCurrentProjectId()?.takeIf { id ->
                workspace.projects.any { project -> project.id == id }
            }
            ?: workspace.primaryProject()?.id
    val initialHasSessionCookie = hasKairosSessionCookie()
    val initialTicketId: Kairos.TicketId? = initialRoute.ticketId?.let(::routeTicketId)
    var selectedArea by remember { mutableStateOf(initialRoute.area) }
    var selectedProjectId by remember { mutableStateOf<Kairos.ProjectId?>(initialProjectId) }
    var selectedTicketId by remember { mutableStateOf<Kairos.TicketId?>(initialTicketId) }
    var view by remember { mutableStateOf(initialRoute.view) }
    var workspaceSettingsTab by remember {
        mutableStateOf(initialRoute.settingsTab.toWorkspaceSettingsTab())
    }
    var searchQuery by remember { mutableStateOf("") }
    var includeCompleted by remember { mutableStateOf(false) }
    var hideEmptyColumns by remember { mutableStateOf(false) }
    var showBoardInbox by remember { mutableStateOf(false) }
    var sort by remember { mutableStateOf(WebSort.Created) }
    var sidebarOpen by remember { mutableStateOf(viewportWidth >= 1100) }
    var detailsOpen by remember {
        mutableStateOf(initialRoute.detailsOpen && initialTicketId != null)
    }
    var selectedScheduleSelection by remember { mutableStateOf<WebScheduleSelection?>(null) }
    var scheduleToolsOpen by remember { mutableStateOf(false) }
    var notice by remember { mutableStateOf<String?>(null) }
    var apiConnected by remember { mutableStateOf(false) }
    var sessionInfo by remember { mutableStateOf<KairosSessionInfo?>(null) }
    var authModalOpen by remember { mutableStateOf(false) }
    var authRequired by remember { mutableStateOf(!initialHasSessionCookie) }
    var initialLoadState by remember {
        mutableStateOf(
            if (initialHasSessionCookie) {
                InitialWebLoadState.Loading
            } else {
                InitialWebLoadState.Ready
            }
        )
    }
    var initialLoadErrorMessage by remember { mutableStateOf<String?>(null) }
    var authSubmissionState by remember {
        mutableStateOf<AuthSubmissionState>(AuthSubmissionState.Idle)
    }
    var sessionModalTab by remember { mutableStateOf(SessionModalTab.Profile) }
    var workspaceMembers by remember { mutableStateOf<List<KairosWorkspaceMember>>(emptyList()) }
    var workspaceMembersLoading by remember { mutableStateOf(false) }
    var workspaceMembersNotice by remember { mutableStateOf<String?>(null) }
    var workspaceMembersError by remember { mutableStateOf<String?>(null) }
    var agentSubaccounts by remember { mutableStateOf<List<KairosAgentSubaccount>>(emptyList()) }
    var agentSubaccountsLoading by remember { mutableStateOf(false) }
    var agentSubaccountsNotice by remember { mutableStateOf<String?>(null) }
    var agentSubaccountsError by remember { mutableStateOf<String?>(null) }
    var agentNameSaving by remember { mutableStateOf<Kairos.UserId?>(null) }
    var agentNameSaved by remember { mutableStateOf<Kairos.UserId?>(null) }
    var agentWorkspaceAccessSaving by remember {
        mutableStateOf<WebAgentWorkspaceAccessMutation?>(null)
    }
    var agentWorkspaceAccessSaved by remember {
        mutableStateOf<WebAgentWorkspaceAccessMutation?>(null)
    }
    var agentCreateModalOpen by remember { mutableStateOf(false) }
    var agentTokenReveal by remember { mutableStateOf<WebAgentTokenReveal?>(null) }
    var agentRotateConfirm by remember { mutableStateOf<KairosAgentSubaccount?>(null) }
    var agentDeleteConfirm by remember { mutableStateOf<KairosAgentSubaccount?>(null) }
    var agentSettingsSelection by remember { mutableStateOf<Kairos.UserId?>(null) }
    var agentSettingsFromWorkspace by remember { mutableStateOf(false) }
    var workspaceShareModalOpen by remember { mutableStateOf(false) }
    var workspaceShareIdentifierDraft by remember { mutableStateOf("") }
    var workspaceShareLookupState by remember {
        mutableStateOf<WebWorkspaceShareLookupState>(WebWorkspaceShareLookupState.Idle)
    }
    var workspaceMemberAction by remember { mutableStateOf<WebWorkspaceMemberAction?>(null) }
    var downloadsManifest by remember { mutableStateOf<WebDownloadsManifest?>(null) }
    var downloadsLoading by remember { mutableStateOf(false) }
    var downloadsError by remember { mutableStateOf<String?>(null) }
    var adminSnapshot by remember { mutableStateOf<KairosAdminSnapshot?>(null) }
    var adminLoading by remember { mutableStateOf(false) }
    var adminError by remember { mutableStateOf<String?>(null) }
    var adminNotice by remember { mutableStateOf<String?>(null) }
    var adminRoleSavingUserId by remember { mutableStateOf<Kairos.UserId?>(null) }
    var createProjectModalOpen by remember { mutableStateOf(false) }
    var createProjectNameDraft by remember { mutableStateOf("") }
    var createTicketProjectModalOpen by remember { mutableStateOf(false) }
    var createTicketProjectDraft by remember { mutableStateOf("") }
    var routeCommitMode by remember { mutableStateOf(WebRouteCommitMode.Replace) }
    val scope = rememberCoroutineScope()
    val compactLayout = viewportWidth < 1100

    DisposableEffect(Unit) {
        val resizeListener = EventListener { viewportWidth = window.innerWidth }
        window.addEventListener("resize", resizeListener)
        onDispose { window.removeEventListener("resize", resizeListener) }
    }

    fun applyRouteState(route: WebRouteState, nextWorkspace: WebWorkspace = workspace) {
        val routeProjectId =
            route.projectId?.let(::routeProjectId)?.takeIf { id ->
                nextWorkspace.projects.any { project -> project.id == id }
            }
        val routeTicketId =
            route.ticketId?.let(::routeTicketId)?.takeIf { id ->
                nextWorkspace.tickets.any { ticket -> ticket.id == id }
            }
        selectedArea = route.area
        selectedProjectId =
            routeProjectId
                ?: loadStoredCurrentProjectId()?.takeIf { id ->
                    nextWorkspace.projects.any { project -> project.id == id }
                }
                ?: nextWorkspace.primaryProject()?.id
        selectedTicketId = routeTicketId
        view = route.view
        workspaceSettingsTab = route.settingsTab.toWorkspaceSettingsTab()
        detailsOpen = route.detailsOpen && routeTicketId != null
        selectedScheduleSelection = null
        scheduleToolsOpen = false
    }

    DisposableEffect(workspace) {
        val popstateListener = EventListener {
            routeCommitMode = WebRouteCommitMode.Replace
            applyRouteState(parseRouteState(window.location.search), workspace)
        }
        window.addEventListener("popstate", popstateListener)
        onDispose { window.removeEventListener("popstate", popstateListener) }
    }

    fun applyWorkspace(next: WebWorkspace, preferredTicketId: Kairos.TicketId? = selectedTicketId) {
        val route = parseRouteState(window.location.search)
        val routeProjectId =
            route.projectId?.let(::routeProjectId)?.takeIf { id ->
                next.projects.any { project -> project.id == id }
            }
        val routeTicketId =
            route.ticketId?.let(::routeTicketId)?.takeIf { id ->
                next.tickets.any { ticket -> ticket.id == id }
            }
        val nextSelectedTicketId =
            routeTicketId
                ?: preferredTicketId?.takeIf { id ->
                    next.tickets.any { ticket -> ticket.id == id }
                }
                ?: selectedTicketId?.takeIf { id -> next.tickets.any { ticket -> ticket.id == id } }
        val detailsRequested =
            if (selectedTicketId == null && preferredTicketId == null) route.detailsOpen
            else detailsOpen
        workspace = next
        selectedProjectId =
            routeProjectId
                ?: selectedProjectId?.takeIf { id -> next.projects.any { it.id == id } }
                ?: loadStoredCurrentProjectId()?.takeIf { id ->
                    next.projects.any { project -> project.id == id }
                }
                ?: next.primaryProject()?.id
        selectedTicketId = nextSelectedTicketId
        workspaceSettingsTab = route.settingsTab.toWorkspaceSettingsTab()
        detailsOpen = detailsRequested && nextSelectedTicketId != null
        saveStoredCurrentWorkspaceId(next.workspace.id)
    }

    fun closeRightPanel() {
        routeCommitMode = WebRouteCommitMode.Push
        detailsOpen = false
        selectedScheduleSelection = null
    }

    fun currentUserIsAdmin(): Boolean = sessionInfo?.user?.role == Kairos.UserRole.Admin

    suspend fun loadDownloads(force: Boolean = false) {
        if (downloadsLoading) return
        if (!force && downloadsManifest != null) return
        downloadsLoading = true
        downloadsError = null
        try {
            downloadsManifest = fetchKairosDownloadsManifest()
        } catch (error: Throwable) {
            downloadsError =
                error.message?.takeIf { message -> message.isNotBlank() }
                    ?: "Could not load downloads."
        } finally {
            downloadsLoading = false
        }
    }

    suspend fun loadAdminSnapshot(force: Boolean = false) {
        if (adminLoading) return
        if (!force && adminSnapshot != null) return
        adminLoading = true
        adminError = null
        try {
            adminSnapshot = fetchKairosAdminSnapshot()
        } catch (error: Throwable) {
            adminError =
                error.message?.takeIf { message -> message.isNotBlank() }
                    ?: "Could not load admin console."
        } finally {
            adminLoading = false
        }
    }

    DisposableEffect(
        detailsOpen,
        selectedScheduleSelection,
        scheduleToolsOpen,
        authModalOpen,
        agentCreateModalOpen,
        agentTokenReveal,
        agentRotateConfirm,
        agentDeleteConfirm,
        workspaceShareModalOpen,
        workspaceMemberAction,
        compactLayout,
        sidebarOpen,
    ) {
        val keyListener = EventListener { event ->
            val keyEvent = event as? KeyboardEvent ?: return@EventListener
            if (keyEvent.key != "Escape") return@EventListener

            when {
                agentTokenReveal != null -> {
                    keyEvent.preventDefault()
                    return@EventListener
                }
                agentDeleteConfirm != null -> agentDeleteConfirm = null
                agentRotateConfirm != null -> agentRotateConfirm = null
                agentCreateModalOpen -> agentCreateModalOpen = false
                workspaceMemberAction != null -> workspaceMemberAction = null
                workspaceShareModalOpen -> {
                    workspaceShareModalOpen = false
                    workspaceShareIdentifierDraft = ""
                    workspaceShareLookupState = WebWorkspaceShareLookupState.Idle
                }
                authModalOpen -> authModalOpen = false
                scheduleToolsOpen -> {
                    routeCommitMode = WebRouteCommitMode.Push
                    scheduleToolsOpen = false
                }
                detailsOpen || selectedScheduleSelection != null -> closeRightPanel()
                compactLayout && sidebarOpen -> sidebarOpen = false
                else -> return@EventListener
            }
            keyEvent.preventDefault()
        }
        window.addEventListener("keydown", keyListener)
        onDispose { window.removeEventListener("keydown", keyListener) }
    }

    LaunchedEffect(selectedProjectId?.value, workspace.projects) {
        saveStoredCurrentProjectId(
            selectedProjectId?.takeIf { id ->
                workspace.projects.any { project -> project.id == id }
            }
        )
    }

    LaunchedEffect(selectedArea) {
        if (selectedArea == WorkspaceArea.Downloads) {
            loadDownloads()
        }
        if (selectedArea == WorkspaceArea.Admin && currentUserIsAdmin()) {
            loadAdminSnapshot()
        }
    }

    LaunchedEffect(selectedArea, sessionInfo?.user?.role) {
        if (selectedArea == WorkspaceArea.Admin && sessionInfo != null && !currentUserIsAdmin()) {
            routeCommitMode = WebRouteCommitMode.Replace
            selectedArea = WorkspaceArea.Home
        }
    }

    LaunchedEffect(
        selectedArea,
        selectedProjectId?.value,
        selectedTicketId?.value,
        view,
        workspaceSettingsTab,
        detailsOpen,
    ) {
        val targetUrl =
            WebRouteState(
                    area = selectedArea,
                    projectId =
                        selectedProjectId?.value?.takeIf {
                            selectedArea == WorkspaceArea.Project && it.isNotBlank()
                        },
                    view = normalizedProjectView(view),
                    settingsTab =
                        workspaceSettingsTab.routeToken().takeIf {
                            selectedArea == WorkspaceArea.WorkspaceSettings
                        },
                    ticketId = selectedTicketId?.value?.takeIf { detailsOpen },
                    detailsOpen = detailsOpen && selectedTicketId != null,
                )
                .toUrl()
        val currentUrl = "${window.location.pathname}${window.location.search}"
        if (currentUrl != targetUrl) {
            when (routeCommitMode) {
                WebRouteCommitMode.Replace -> window.history.replaceState(null, "", targetUrl)
                WebRouteCommitMode.Push -> window.history.pushState(null, "", targetUrl)
            }
        }
        routeCommitMode = WebRouteCommitMode.Replace
    }

    fun recordApiFailure(error: Throwable, message: String) {
        if (error.isUnauthorizedKairosApiError()) {
            storeAuthToken(null)
            sessionInfo = KairosSessionInfo(authenticated = false)
            authRequired = true
            initialLoadState = InitialWebLoadState.Ready
            initialLoadErrorMessage = null
            authModalOpen = false
            sessionModalTab = SessionModalTab.Profile
            authSubmissionState = AuthSubmissionState.Idle
            agentSubaccounts = emptyList()
            agentSubaccountsNotice = null
            agentSubaccountsError = null
            agentNameSaving = null
            agentNameSaved = null
            agentWorkspaceAccessSaving = null
            agentWorkspaceAccessSaved = null
            adminSnapshot = null
            adminError = null
            adminNotice = null
            adminRoleSavingUserId = null
            apiConnected = true
            notice = null
            return
        }
        propagateUiThrowable(error)
        apiConnected = false
        notice = "$message ${error.message.orEmpty()}".trimEnd()
    }

    fun launchApiJob(message: String, block: suspend () -> Unit) {
        scope.launch {
            try {
                block()
            } catch (error: Throwable) {
                recordApiFailure(error, message)
            }
        }
    }

    fun launchApiMutationWithFallback(
        message: String,
        onDisconnectedFallback: () -> Unit,
        block: suspend () -> Unit,
    ) {
        scope.launch {
            if (apiConnected) {
                try {
                    block()
                } catch (error: Throwable) {
                    recordApiFailure(error, message)
                }
                return@launch
            }

            try {
                block()
                apiConnected = true
            } catch (error: Throwable) {
                recordApiFailure(error, message)
                if (!error.isUnauthorizedKairosApiError()) {
                    onDisconnectedFallback()
                }
            }
        }
    }

    suspend fun loadWorkspaceFromApi() {
        try {
            initialLoadState = InitialWebLoadState.Loading
            initialLoadErrorMessage = null
            val loadedSession = fetchKairosSession()
            sessionInfo = loadedSession
            loadedSession.currentWorkspace?.id?.let(::saveStoredCurrentWorkspaceId)
            if (!loadedSession.authenticated) {
                authRequired = true
                initialLoadState = InitialWebLoadState.Ready
                authModalOpen = false
                sessionModalTab = SessionModalTab.Profile
                authSubmissionState = AuthSubmissionState.Idle
                agentSubaccounts = emptyList()
                agentSubaccountsNotice = null
                agentSubaccountsError = null
                agentNameSaving = null
                agentNameSaved = null
                agentWorkspaceAccessSaving = null
                agentWorkspaceAccessSaved = null
                adminSnapshot = null
                adminError = null
                adminNotice = null
                adminRoleSavingUserId = null
                apiConnected = true
                notice = null
                return
            }
            val loaded = fetchKairosWorkspace()
            apiConnected = true
            authRequired = false
            initialLoadState = InitialWebLoadState.Ready
            applyWorkspace(loaded)
            notice = null
        } catch (error: Throwable) {
            if (error.isUnauthorizedKairosApiError()) {
                storeAuthToken(null)
                sessionInfo = KairosSessionInfo(authenticated = false)
                authRequired = true
                initialLoadState = InitialWebLoadState.Ready
                initialLoadErrorMessage = null
                authModalOpen = false
                sessionModalTab = SessionModalTab.Profile
                authSubmissionState = AuthSubmissionState.Idle
                agentSubaccounts = emptyList()
                agentSubaccountsNotice = null
                agentSubaccountsError = null
                agentNameSaving = null
                agentNameSaved = null
                agentWorkspaceAccessSaving = null
                agentWorkspaceAccessSaved = null
                adminSnapshot = null
                adminError = null
                adminNotice = null
                adminRoleSavingUserId = null
                apiConnected = true
                notice = null
                return
            }
            propagateUiThrowable(error)
            apiConnected = false
            if (hasKairosSessionCookie() || sessionInfo?.authenticated == true) {
                authRequired = false
                initialLoadState = InitialWebLoadState.Unavailable
                initialLoadErrorMessage = "Kairos is temporarily unavailable."
            } else {
                authRequired = true
                initialLoadState = InitialWebLoadState.Ready
                initialLoadErrorMessage = null
            }
            notice = null
        }
    }

    LaunchedEffect(Unit) {
        if (initialHasSessionCookie) {
            loadWorkspaceFromApi()
        } else {
            authRequired = true
            initialLoadState = InitialWebLoadState.Ready
            initialLoadErrorMessage = null
            apiConnected = true
        }
    }

    fun refreshSessionFromApi() {
        launchApiJob("Could not refresh session.") {
            val loadedSession = fetchKairosSession()
            sessionInfo = loadedSession
            loadedSession.currentWorkspace?.id?.let(::saveStoredCurrentWorkspaceId)
        }
    }

    fun switchWorkspace(workspaceId: Kairos.WorkspaceId) {
        if (workspace.workspace.id == workspaceId) {
            return
        }
        saveStoredCurrentWorkspaceId(workspaceId)
        launchApiJob("Could not switch workspace.") {
            val loadedSession = fetchKairosSession()
            loadedSession.currentWorkspace?.id?.let(::saveStoredCurrentWorkspaceId)
            val loadedWorkspace = fetchKairosWorkspace()
            sessionInfo = loadedSession
            routeCommitMode = WebRouteCommitMode.Replace
            applyWorkspace(loadedWorkspace)
            selectedTicketId = null
            detailsOpen = false
            selectedScheduleSelection = null
            scheduleToolsOpen = false
            workspaceMembers = emptyList()
            workspaceMembersError = null
            workspaceMembersNotice = null
            workspaceShareModalOpen = false
            workspaceShareIdentifierDraft = ""
            workspaceShareLookupState = WebWorkspaceShareLookupState.Idle
            workspaceMemberAction = null
            authModalOpen = false
            sessionModalTab = SessionModalTab.Profile
            agentSettingsSelection = null
            agentSettingsFromWorkspace = false
            agentCreateModalOpen = false
            agentTokenReveal = null
            agentRotateConfirm = null
            agentDeleteConfirm = null
            notice = "Switched to ${loadedWorkspace.workspace.name}."
        }
    }

    fun loadWorkspaceMembers(force: Boolean = false) {
        if (!apiConnected || authRequired) {
            return
        }
        if (workspaceMembersLoading) {
            return
        }
        if (!force && workspaceMembers.isNotEmpty()) {
            return
        }
        workspaceMembersLoading = true
        workspaceMembersError = null
        scope.launch {
            try {
                workspaceMembers = fetchKairosWorkspaceMembers()
            } catch (error: Throwable) {
                workspaceMembersError =
                    error.kairosApiMessageOrNull() ?: "Could not load workspace members."
                if (!error.isUnauthorizedKairosApiError()) {
                    propagateUiThrowable(error)
                }
            } finally {
                workspaceMembersLoading = false
            }
        }
    }

    suspend fun refreshWorkspaceMembershipViews() {
        applyWorkspace(fetchKairosWorkspace(), selectedTicketId)
        workspaceMembers = fetchKairosWorkspaceMembers()
    }

    fun mirrorAgentWorkspaceNotice(message: String) {
        if (
            selectedArea != WorkspaceArea.WorkspaceSettings ||
                workspaceSettingsTab != WebWorkspaceSettingsTab.Members
        ) {
            return
        }
        workspaceMembersError = null
        workspaceMembersNotice = message
        window.setTimeout(
            {
                if (workspaceMembersNotice == message) {
                    workspaceMembersNotice = null
                }
            },
            2600,
        )
    }

    fun loadAgentSubaccounts(force: Boolean = false) {
        if (!apiConnected || authRequired || sessionInfo?.user?.kind != Kairos.UserKind.Human) {
            return
        }
        if (agentSubaccountsLoading) {
            return
        }
        if (!force && agentSubaccounts.isNotEmpty()) {
            return
        }
        agentSubaccountsLoading = true
        agentSubaccountsError = null
        scope.launch {
            try {
                agentSubaccounts = fetchKairosAgentSubaccounts()
            } catch (error: Throwable) {
                agentSubaccountsError =
                    error.kairosApiMessageOrNull() ?: "Could not load agent subaccounts."
                if (!error.isUnauthorizedKairosApiError()) {
                    propagateUiThrowable(error)
                }
            } finally {
                agentSubaccountsLoading = false
            }
        }
    }

    fun createAgentSubaccount(
        displayName: String,
        username: String,
        workspaceAccess: List<WebAgentWorkspaceDraft>,
    ) {
        if (!apiConnected) return
        agentSubaccountsLoading = true
        agentSubaccountsError = null
        agentSubaccountsNotice = null
        scope.launch {
            try {
                val created =
                    createKairosAgentSubaccount(
                        displayName = displayName.trim(),
                        username = username.trim(),
                        workspaceAccess =
                            workspaceAccess.map { draft ->
                                KairosAgentWorkspaceGrantRequest(
                                    workspaceId = draft.workspaceId,
                                    role = draft.role,
                                )
                            },
                    )
                refreshWorkspaceMembershipViews()
                agentSubaccounts = fetchKairosAgentSubaccounts()
                agentCreateModalOpen = false
                agentTokenReveal =
                    WebAgentTokenReveal(
                        displayName = created.agent.user.displayName,
                        username = created.agent.username,
                        token = created.token,
                        rotated = false,
                    )
                val message = "Created ${created.agent.user.displayName}."
                agentSubaccountsNotice = message
                mirrorAgentWorkspaceNotice(message)
            } catch (error: Throwable) {
                agentSubaccountsError =
                    error.kairosApiMessageOrNull() ?: "Could not create agent subaccount."
                if (!error.isUnauthorizedKairosApiError()) {
                    propagateUiThrowable(error)
                }
            } finally {
                agentSubaccountsLoading = false
            }
        }
    }

    fun updateAgentSubaccountName(agentUserId: Kairos.UserId, displayName: String) {
        if (!apiConnected) return
        val trimmedName = displayName.trim()
        if (trimmedName.isEmpty()) return
        agentSubaccountsLoading = true
        agentSubaccountsError = null
        agentSubaccountsNotice = null
        agentNameSaving = agentUserId
        agentNameSaved = null
        scope.launch {
            try {
                val updated = updateKairosAgentSubaccount(agentUserId, trimmedName)
                refreshWorkspaceMembershipViews()
                agentSubaccounts =
                    agentSubaccounts.map { agent ->
                        if (agent.user.id == agentUserId) updated else agent
                    }
                val message = "Updated ${updated.user.displayName}."
                agentSubaccountsNotice = message
                mirrorAgentWorkspaceNotice(message)
                agentNameSaved = agentUserId
                window.setTimeout(
                    {
                        if (agentNameSaved == agentUserId) {
                            agentNameSaved = null
                        }
                    },
                    1600,
                )
            } catch (error: Throwable) {
                agentSubaccountsError =
                    error.kairosApiMessageOrNull() ?: "Could not update agent subaccount."
                agentNameSaved = null
                if (!error.isUnauthorizedKairosApiError()) {
                    propagateUiThrowable(error)
                }
            } finally {
                if (agentNameSaving == agentUserId) {
                    agentNameSaving = null
                }
                agentSubaccountsLoading = false
            }
        }
    }

    fun rotateAgentSubaccountToken(agentUserId: Kairos.UserId) {
        if (!apiConnected) return
        agentSubaccountsLoading = true
        agentSubaccountsError = null
        agentSubaccountsNotice = null
        scope.launch {
            try {
                val rotated = rotateKairosAgentSubaccountToken(agentUserId)
                agentSubaccounts =
                    agentSubaccounts.map { agent ->
                        if (agent.user.id == agentUserId) rotated.agent else agent
                    }
                agentTokenReveal =
                    WebAgentTokenReveal(
                        displayName = rotated.agent.user.displayName,
                        username = rotated.agent.username,
                        token = rotated.token,
                        rotated = true,
                    )
                agentSubaccountsNotice = "Rotated token for ${rotated.agent.user.displayName}."
            } catch (error: Throwable) {
                agentSubaccountsError =
                    error.kairosApiMessageOrNull() ?: "Could not rotate the agent token."
                if (!error.isUnauthorizedKairosApiError()) {
                    propagateUiThrowable(error)
                }
            } finally {
                agentSubaccountsLoading = false
            }
        }
    }

    fun removeAgentSubaccount(agentUserId: Kairos.UserId) {
        if (!apiConnected) return
        agentSubaccountsLoading = true
        agentSubaccountsError = null
        agentSubaccountsNotice = null
        scope.launch {
            try {
                val removedName =
                    agentSubaccounts.firstOrNull { it.user.id == agentUserId }?.user?.displayName
                        ?: "Agent"
                removeKairosAgentSubaccount(agentUserId)
                refreshWorkspaceMembershipViews()
                agentSubaccounts = agentSubaccounts.filterNot { it.user.id == agentUserId }
                val message = "Deleted $removedName."
                agentSubaccountsNotice = message
                mirrorAgentWorkspaceNotice(message)
            } catch (error: Throwable) {
                agentSubaccountsError =
                    error.kairosApiMessageOrNull() ?: "Could not delete the agent subaccount."
                if (!error.isUnauthorizedKairosApiError()) {
                    propagateUiThrowable(error)
                }
            } finally {
                agentSubaccountsLoading = false
            }
        }
    }

    fun updateAgentWorkspaceAccess(
        agentUserId: Kairos.UserId,
        workspaceId: Kairos.WorkspaceId,
        role: Kairos.WorkspaceMembershipRole?,
    ) {
        if (!apiConnected) return
        val mutation = WebAgentWorkspaceAccessMutation(agentUserId, workspaceId)
        agentSubaccountsLoading = true
        agentSubaccountsError = null
        agentSubaccountsNotice = null
        agentWorkspaceAccessSaving = mutation
        agentWorkspaceAccessSaved = null
        scope.launch {
            try {
                val updated =
                    if (role == null) {
                        removeKairosAgentWorkspaceAccess(agentUserId, workspaceId)
                    } else {
                        upsertKairosAgentWorkspaceAccess(agentUserId, workspaceId, role)
                    }
                refreshWorkspaceMembershipViews()
                agentSubaccounts =
                    agentSubaccounts.map { agent ->
                        if (agent.user.id == agentUserId) updated else agent
                    }
                val workspaceName =
                    sessionInfo
                        ?.availableWorkspaceAccess
                        ?.firstOrNull { it.workspace.id == workspaceId }
                        ?.workspace
                        ?.name ?: "workspace"
                val message =
                    if (role == null) {
                        "Removed access to $workspaceName."
                    } else {
                        "Updated access to $workspaceName."
                    }
                agentSubaccountsNotice = message
                mirrorAgentWorkspaceNotice(message)
                agentWorkspaceAccessSaved = mutation
                window.setTimeout(
                    {
                        if (agentWorkspaceAccessSaved == mutation) {
                            agentWorkspaceAccessSaved = null
                        }
                    },
                    1600,
                )
            } catch (error: Throwable) {
                agentSubaccountsError =
                    error.kairosApiMessageOrNull() ?: "Could not update agent workspace access."
                agentWorkspaceAccessSaved = null
                if (!error.isUnauthorizedKairosApiError()) {
                    propagateUiThrowable(error)
                }
            } finally {
                agentWorkspaceAccessSaving = null
                agentSubaccountsLoading = false
            }
        }
    }

    fun shareWorkspaceAccess(username: String) {
        val trimmedUsername = username.trim()
        if (trimmedUsername.isEmpty() || !apiConnected) {
            return
        }
        workspaceMembersError = null
        workspaceMembersNotice = null
        workspaceMembersLoading = true
        scope.launch {
            try {
                workspaceMembers = createKairosWorkspaceMember(trimmedUsername)
                applyWorkspace(fetchKairosWorkspace(), selectedTicketId)
                workspaceMembersNotice =
                    "$trimmedUsername now has access to ${workspace.workspace.name}."
            } catch (error: Throwable) {
                workspaceMembersError =
                    error.kairosApiMessageOrNull() ?: "Could not add workspace member."
                if (!error.isUnauthorizedKairosApiError()) {
                    propagateUiThrowable(error)
                }
            } finally {
                workspaceMembersLoading = false
            }
        }
    }

    fun openWorkspaceShareModal() {
        workspaceShareIdentifierDraft = ""
        workspaceShareLookupState = WebWorkspaceShareLookupState.Idle
        workspaceMembersError = null
        workspaceMembersNotice = null
        workspaceShareModalOpen = true
        if (selectedArea == WorkspaceArea.WorkspaceSettings) {
            loadWorkspaceMembers(force = true)
        }
    }

    fun dismissWorkspaceShareModal() {
        workspaceShareModalOpen = false
        workspaceShareIdentifierDraft = ""
        workspaceShareLookupState = WebWorkspaceShareLookupState.Idle
    }

    fun lookupWorkspaceShareTarget(identifier: String) {
        val trimmedIdentifier = identifier.trim()
        if (trimmedIdentifier.isEmpty() || !apiConnected) {
            return
        }
        workspaceMembersError = null
        workspaceShareLookupState = WebWorkspaceShareLookupState.Loading
        scope.launch {
            try {
                val target = resolveKairosWorkspaceShareTarget(trimmedIdentifier)
                workspaceShareLookupState =
                    if (target == null) {
                        WebWorkspaceShareLookupState.NotFound(trimmedIdentifier)
                    } else {
                        WebWorkspaceShareLookupState.Resolved(target)
                    }
            } catch (error: Throwable) {
                val message = error.kairosApiMessageOrNull() ?: "Could not look up that account."
                workspaceShareLookupState = WebWorkspaceShareLookupState.Error(message)
                if (!error.isUnauthorizedKairosApiError()) {
                    propagateUiThrowable(error)
                }
            }
        }
    }

    fun confirmWorkspaceShareTarget(target: KairosWorkspaceShareTarget) {
        if (!apiConnected || target.currentRole != null) {
            return
        }
        workspaceMembersError = null
        workspaceMembersNotice = null
        workspaceMembersLoading = true
        scope.launch {
            try {
                workspaceMembers = createKairosWorkspaceMember(target.username)
                applyWorkspace(fetchKairosWorkspace(), selectedTicketId)
                workspaceMembersNotice =
                    "${target.user.displayName} now has access to ${workspace.workspace.name}."
                dismissWorkspaceShareModal()
            } catch (error: Throwable) {
                workspaceShareLookupState =
                    WebWorkspaceShareLookupState.Error(
                        error.kairosApiMessageOrNull() ?: "Could not add that account."
                    )
                if (!error.isUnauthorizedKairosApiError()) {
                    propagateUiThrowable(error)
                }
            } finally {
                workspaceMembersLoading = false
            }
        }
    }

    fun copyWorkspaceOwnerContacts(ownerHandles: String) {
        val trimmed = ownerHandles.trim()
        if (trimmed.isEmpty()) return
        workspaceMembersError = null
        copyTextToClipboard(
            trimmed,
            onSuccess = {
                workspaceMembersNotice =
                    if (',' in trimmed) "Copied owner usernames." else "Copied $trimmed."
            },
            onFailure = { workspaceMembersError = "Could not copy owner username." },
        )
    }

    fun updateWorkspaceAccessRole(userId: Kairos.UserId, role: Kairos.WorkspaceMembershipRole) {
        if (!apiConnected) {
            return
        }
        workspaceMembersError = null
        workspaceMembersNotice = null
        workspaceMembersLoading = true
        scope.launch {
            try {
                val memberName =
                    workspaceMembers
                        .firstOrNull { member -> member.user.id == userId }
                        ?.user
                        ?.displayName ?: "Member"
                workspaceMembers = updateKairosWorkspaceMemberRole(userId, role)
                applyWorkspace(fetchKairosWorkspace(), selectedTicketId)
                val roleLabel =
                    if (role == Kairos.WorkspaceMembershipRole.Owner) "owner" else "member"
                workspaceMembersNotice =
                    "$memberName is now an $roleLabel in ${workspace.workspace.name}."
            } catch (error: Throwable) {
                workspaceMembersError =
                    error.kairosApiMessageOrNull() ?: "Could not update workspace access."
                if (!error.isUnauthorizedKairosApiError()) {
                    propagateUiThrowable(error)
                }
            } finally {
                workspaceMembersLoading = false
            }
        }
    }

    fun removeWorkspaceAccess(userId: Kairos.UserId) {
        if (!apiConnected) {
            return
        }
        workspaceMembersError = null
        workspaceMembersNotice = null
        workspaceMembersLoading = true
        scope.launch {
            try {
                val removedName =
                    workspaceMembers.firstOrNull { member -> member.user.id == userId }?.username
                        ?: "Member"
                workspaceMembers = removeKairosWorkspaceMember(userId)
                applyWorkspace(fetchKairosWorkspace(), selectedTicketId)
                workspaceMembersNotice =
                    "$removedName no longer has access to ${workspace.workspace.name}."
            } catch (error: Throwable) {
                workspaceMembersError =
                    error.kairosApiMessageOrNull() ?: "Could not remove workspace member."
                if (!error.isUnauthorizedKairosApiError()) {
                    propagateUiThrowable(error)
                }
            } finally {
                workspaceMembersLoading = false
            }
        }
    }

    LaunchedEffect(selectedArea, workspace.workspace.id.value, apiConnected, authRequired) {
        if (selectedArea == WorkspaceArea.WorkspaceSettings && apiConnected && !authRequired) {
            loadWorkspaceMembers(force = true)
        }
    }

    fun clearSessionToken() {
        scope.launch {
            try {
                clearKairosSession()
            } catch (error: Throwable) {
                if (!error.isUnauthorizedKairosApiError()) {
                    propagateUiThrowable(error)
                }
            }
        }
        sessionInfo = KairosSessionInfo(authenticated = false)
        authRequired = true
        initialLoadState = InitialWebLoadState.Ready
        initialLoadErrorMessage = null
        authModalOpen = false
        sessionModalTab = SessionModalTab.Profile
        authSubmissionState = AuthSubmissionState.Idle
        agentSubaccounts = emptyList()
        agentSubaccountsNotice = null
        agentSubaccountsError = null
        agentNameSaving = null
        agentNameSaved = null
        agentWorkspaceAccessSaving = null
        agentWorkspaceAccessSaved = null
        adminSnapshot = null
        adminError = null
        adminNotice = null
        adminRoleSavingUserId = null
        notice = "Signed out."
    }

    fun updateAdminRole(userId: Kairos.UserId, role: Kairos.UserRole) {
        val previousSnapshot = adminSnapshot ?: return
        adminRoleSavingUserId = userId
        adminError = null
        adminNotice = null
        adminSnapshot =
            previousSnapshot.copy(
                users =
                    previousSnapshot.users.map { row ->
                        if (row.user.id == userId) {
                            row.copy(user = row.user.copy(role = role))
                        } else {
                            row
                        }
                    }
            )
        scope.launch {
            try {
                val updated = updateKairosAdminUserRole(userId, role)
                adminSnapshot =
                    adminSnapshot?.copy(
                        users =
                            adminSnapshot
                                ?.users
                                .orEmpty()
                                .map { row -> if (row.user.id == userId) updated else row }
                                .sortedWith(
                                    compareByDescending<KairosAdminUserRow> { row ->
                                            row.user.role == Kairos.UserRole.Admin
                                        }
                                        .thenBy { row -> row.user.displayName.lowercase() }
                                )
                    )
                if (sessionInfo?.user?.id == userId) {
                    sessionInfo =
                        sessionInfo?.copy(
                            user = sessionInfo?.user?.copy(role = updated.user.role),
                            ownerUser =
                                sessionInfo?.ownerUser?.let { owner ->
                                    if (owner.id == userId) owner.copy(role = updated.user.role)
                                    else owner
                                },
                        )
                }
                adminNotice =
                    if (updated.user.role == Kairos.UserRole.Admin) {
                        "${updated.user.displayName} is now an admin."
                    } else {
                        "${updated.user.displayName} is now a regular user."
                    }
            } catch (error: Throwable) {
                adminSnapshot = previousSnapshot
                recordApiFailure(error, "Could not update admin role.")
            } finally {
                adminRoleSavingUserId = null
            }
        }
    }

    suspend fun completeAuthenticatedSession(session: KairosSessionInfo, successNotice: String) {
        sessionInfo = session
        saveStoredAccountPreferences(session.user)
        val loaded = fetchKairosWorkspace()
        apiConnected = true
        authRequired = false
        initialLoadState = InitialWebLoadState.Ready
        initialLoadErrorMessage = null
        authModalOpen = false
        sessionModalTab = SessionModalTab.Profile
        authSubmissionState = AuthSubmissionState.Idle
        applyWorkspace(loaded)
        notice = successNotice
    }

    fun signIn(username: String, password: String) {
        authSubmissionState = AuthSubmissionState.Loading(AuthScreenMode.SignIn)
        scope.launch {
            try {
                val created = createKairosSession(username, password)
                completeAuthenticatedSession(
                    session = created.session,
                    successNotice = "Signed in as ${created.session.user?.displayName ?: "Kairos"}.",
                )
            } catch (error: Throwable) {
                val reachedApi = error.reachedKairosApi()
                authSubmissionState =
                    AuthSubmissionState.Error(
                        mode = AuthScreenMode.SignIn,
                        message =
                            if (reachedApi) {
                                error.kairosApiMessageOrNull()
                                    ?: "Incorrect username or password. Try again."
                            } else {
                                "Couldn't reach Kairos. Check your connection and try again."
                            },
                    )
                apiConnected = reachedApi
                if (!error.isUnauthorizedKairosApiError()) {
                    propagateUiThrowable(error)
                }
            }
        }
    }

    fun signUp(displayName: String, username: String, password: String, workspaceName: String) {
        authSubmissionState = AuthSubmissionState.Loading(AuthScreenMode.SignUp)
        scope.launch {
            try {
                val created =
                    createKairosAccount(
                        displayName = displayName,
                        username = username,
                        password = password,
                        workspaceName = workspaceName,
                    )
                completeAuthenticatedSession(
                    session = created.session,
                    successNotice = "Created workspace ${workspaceName.trim()}.",
                )
            } catch (error: Throwable) {
                val reachedApi = error.reachedKairosApi()
                authSubmissionState =
                    AuthSubmissionState.Error(
                        mode = AuthScreenMode.SignUp,
                        message =
                            if (reachedApi) {
                                error.kairosApiMessageOrNull()
                                    ?: "Could not create the account. Check the fields and try again."
                            } else {
                                "Couldn't reach Kairos. Check your connection and try again."
                            },
                    )
                apiConnected = reachedApi
                if (!error.isUnauthorizedKairosApiError()) {
                    propagateUiThrowable(error)
                }
            }
        }
    }

    fun updateUserProfile(
        userId: Kairos.UserId,
        avatarUrl: String?,
        timeZoneId: String?,
        localeTag: String?,
    ) {
        launchApiMutationWithFallback(
            message = "Could not update profile in API.",
            onDisconnectedFallback = {},
        ) {
            val updatedUser =
                updateKairosUser(
                    userId = userId,
                    request =
                        PatchKairosUserRequest(
                            avatarUrl = avatarUrl?.trim()?.takeIf { it.isNotEmpty() },
                            clearAvatarUrl = avatarUrl?.trim().isNullOrEmpty(),
                            timeZoneId = timeZoneId?.trim()?.takeIf { it.isNotEmpty() },
                            clearTimeZoneId = timeZoneId?.trim().isNullOrEmpty(),
                            localeTag = localeTag?.trim()?.takeIf { it.isNotEmpty() },
                            clearLocaleTag = localeTag?.trim().isNullOrEmpty(),
                        ),
                )
            saveStoredAccountPreferences(updatedUser)
            val loaded = fetchKairosWorkspace()
            val loadedSession = fetchKairosSession()
            sessionInfo = loadedSession
            loadedSession.currentWorkspace?.id?.let(::saveStoredCurrentWorkspaceId)
            applyWorkspace(loaded, selectedTicketId)
            notice = "Updated profile."
        }
    }

    fun createProject(name: String, parentProjectId: Kairos.ProjectId?) {
        val trimmed = name.trim()
        if (trimmed.isBlank()) return
        launchApiMutationWithFallback(
            message = "Could not create project in API.",
            onDisconnectedFallback = {
                val created =
                    Kairos.Project(
                        id = Kairos.ProjectId("web-project-${workspace.projects.size + 1}"),
                        createdAt = Clock.System.now(),
                        workspaceId = workspace.workspace.id,
                        name = trimmed,
                        parentProjectId = parentProjectId,
                    )
                workspace = workspace.copy(projects = workspace.projects + created)
                selectedProjectId = created.id
                selectedArea = WorkspaceArea.Project
                routeCommitMode = WebRouteCommitMode.Push
                notice = "Created project"
            },
        ) {
            val created = createKairosProject(trimmed, parentProjectId)
            val loaded = fetchKairosWorkspace()
            selectedProjectId = created.id
            selectedArea = WorkspaceArea.Project
            routeCommitMode = WebRouteCommitMode.Push
            applyWorkspace(loaded, selectedTicketId)
            notice = "Created project"
        }
    }

    fun updateProjectMetadata(
        projectId: Kairos.ProjectId,
        name: String?,
        parentProjectId: Kairos.ProjectId?,
        clearParent: Boolean,
    ) {
        val trimmedName = name?.trim()?.takeIf { it.isNotBlank() }
        launchApiMutationWithFallback(
            message = "Could not update project in API.",
            onDisconnectedFallback = {
                workspace =
                    workspace.copy(
                        projects =
                            workspace.projects.map { project ->
                                if (project.id == projectId) {
                                    project.copy(
                                        name = trimmedName ?: project.name,
                                        parentProjectId =
                                            when {
                                                clearParent -> null
                                                parentProjectId != null -> parentProjectId
                                                else -> project.parentProjectId
                                            },
                                    )
                                } else {
                                    project
                                }
                            }
                    )
                selectedProjectId = projectId
                notice = "Updated project"
            },
        ) {
            updateKairosProject(
                projectId = projectId,
                name = trimmedName,
                parentProjectId = parentProjectId,
                clearParent = clearParent,
            )
            val loaded = fetchKairosWorkspace()
            selectedProjectId = projectId
            applyWorkspace(loaded, selectedTicketId)
            notice = "Updated project"
        }
    }

    fun addTask(projectId: Kairos.ProjectId? = workspace.defaultTaskProjectId(selectedProjectId)) {
        val targetProjectId = projectId ?: return
        val ticketNumber = workspace.tickets.size + 1
        val title = "New ticket $ticketNumber"
        val defaultAssigneeUserId = sessionInfo?.user?.id ?: workspace.users.firstOrNull()?.id
        launchApiMutationWithFallback(
            message = "Could not create ticket in API.",
            onDisconnectedFallback = {
                val ticket =
                    Kairos.Ticket(
                        id = Kairos.TicketId("web-ticket-$ticketNumber"),
                        createdAt = Clock.System.now(),
                        projectId = targetProjectId,
                        title = title,
                        stage = Kairos.TicketStage.Todo,
                        description = "New ticket captured in Kairos.",
                        deadline = null,
                        priority = Kairos.TicketPriority.Medium,
                        urgency = Kairos.TicketUrgency.Medium,
                        completedAt = null,
                        assigneeUserId = defaultAssigneeUserId,
                    )
                routeCommitMode = WebRouteCommitMode.Push
                workspace = workspace.copy(tickets = workspace.tickets + ticket)
                selectedTicketId = ticket.id
                selectedProjectId = targetProjectId
                detailsOpen = true
                selectedScheduleSelection = null
                notice = "Created ${ticket.title}"
            },
        ) {
            val created = createKairosTicket(targetProjectId, title, defaultAssigneeUserId)
            val loaded = fetchKairosWorkspace()
            routeCommitMode = WebRouteCommitMode.Push
            applyWorkspace(loaded, created.id)
            selectedProjectId = targetProjectId
            detailsOpen = true
            selectedScheduleSelection = null
            notice = "Created ${created.title}"
        }
    }

    fun openCreateProjectModal() {
        createProjectNameDraft = ""
        createProjectModalOpen = true
        createTicketProjectModalOpen = false
    }

    fun openCreateTicketFlow() {
        val defaultProjectId = workspace.defaultTaskProjectId(selectedProjectId)
        when {
            defaultProjectId != null -> addTask(defaultProjectId)
            workspace.projects.isNotEmpty() -> {
                createTicketProjectDraft =
                    (selectedProjectId ?: workspace.primaryProject()?.id)?.value
                        ?: workspace.projects.first().id.value
                createTicketProjectModalOpen = true
                createProjectModalOpen = false
            }
            else -> {
                notice = "Create a project before adding tickets."
                openCreateProjectModal()
            }
        }
    }

    fun submitCreateProjectFromGlobalMenu() {
        val trimmed = createProjectNameDraft.trim()
        if (trimmed.isBlank()) return
        createProject(trimmed, null)
        createProjectNameDraft = ""
        createProjectModalOpen = false
    }

    fun submitCreateTicketForProject() {
        val projectId = createTicketProjectDraft.takeIf { it.isNotBlank() }?.let(Kairos::ProjectId)
        if (projectId == null) return
        addTask(projectId)
        createTicketProjectModalOpen = false
    }

    fun setTaskCompleted(task: WebTask, completed: Boolean) {
        if (apiConnected) {
            launchApiJob("Could not update completion in API.") {
                setKairosTicketCompleted(task.ticket.id, completed)
                val loaded = fetchKairosWorkspace()
                applyWorkspace(loaded, task.ticket.id)
                notice =
                    if (completed) {
                        "Completed ${task.ticket.title}"
                    } else {
                        "Reopened ${task.ticket.title}"
                    }
            }
            return
        }
        workspace =
            workspace.copy(
                tickets =
                    workspace.tickets.map { ticket ->
                        if (ticket.id == task.ticket.id) {
                            ticket.copy(completedAt = if (completed) Clock.System.now() else null)
                        } else {
                            ticket
                        }
                    }
            )
        workspace =
            workspace.copy(
                activities =
                    workspace.activities +
                        Kairos.TicketActivity(
                            id =
                                Kairos.TicketActivityId(
                                    "web-activity-${workspace.activities.size + 1}"
                                ),
                            createdAt = Clock.System.now(),
                            ticketId = task.ticket.id,
                            actorUserId = task.ticket.assigneeUserId,
                            kind =
                                if (completed) {
                                    Kairos.TicketActivityKind.Completed
                                } else {
                                    Kairos.TicketActivityKind.Reopened
                                },
                            summary = if (completed) "Ticket completed" else "Ticket reopened",
                        )
            )
        notice =
            if (completed) "Completed ${task.ticket.title}" else "Reopened ${task.ticket.title}"
    }

    fun updateTitle(task: WebTask, title: String) {
        val trimmed = title.trim()
        if (trimmed.isBlank() || trimmed == task.ticket.title) return
        if (apiConnected) {
            launchApiJob("Could not save title in API.") {
                updateKairosTicketTitle(task.ticket.id, trimmed)
                val loaded = fetchKairosWorkspace()
                applyWorkspace(loaded, task.ticket.id)
                notice = "Saved title"
            }
            return
        }
        workspace =
            workspace.copy(
                tickets =
                    workspace.tickets.map { ticket ->
                        if (ticket.id == task.ticket.id) {
                            ticket.copy(title = trimmed)
                        } else {
                            ticket
                        }
                    },
                activities =
                    workspace.activities +
                        Kairos.TicketActivity(
                            id =
                                Kairos.TicketActivityId(
                                    "web-activity-${workspace.activities.size + 1}"
                                ),
                            createdAt = Clock.System.now(),
                            ticketId = task.ticket.id,
                            actorUserId = task.ticket.assigneeUserId,
                            kind = Kairos.TicketActivityKind.Updated,
                            summary = "Title updated",
                        ),
            )
        notice = "Saved title"
    }

    fun updateDescription(task: WebTask, description: String) {
        if (apiConnected) {
            launchApiJob("Could not save description in API.") {
                updateKairosTicketDescription(task.ticket.id, description)
                val loaded = fetchKairosWorkspace()
                applyWorkspace(loaded, task.ticket.id)
                notice = "Saved description"
            }
            return
        }
        workspace =
            workspace.copy(
                tickets =
                    workspace.tickets.map { ticket ->
                        if (ticket.id == task.ticket.id) {
                            ticket.copy(description = description)
                        } else {
                            ticket
                        }
                    }
            )
        workspace =
            workspace.copy(
                activities =
                    workspace.activities +
                        Kairos.TicketActivity(
                            id =
                                Kairos.TicketActivityId(
                                    "web-activity-${workspace.activities.size + 1}"
                                ),
                            createdAt = Clock.System.now(),
                            ticketId = task.ticket.id,
                            actorUserId = task.ticket.assigneeUserId,
                            kind = Kairos.TicketActivityKind.Updated,
                            summary = "Description updated",
                        )
            )
        notice = "Saved description"
    }

    fun updateStage(task: WebTask, stage: Kairos.TicketStage) {
        if (apiConnected) {
            launchApiJob("Could not change stage in API.") {
                updateKairosTicketStage(task.ticket.id, stage)
                val loaded = fetchKairosWorkspace()
                applyWorkspace(loaded, task.ticket.id)
                notice = "Moved to ${stage.uiLabel()}"
            }
            return
        }
        workspace =
            workspace.copy(
                tickets =
                    workspace.tickets.map { ticket ->
                        if (ticket.id == task.ticket.id) {
                            ticket.copy(stage = stage)
                        } else {
                            ticket
                        }
                    },
                activities =
                    workspace.activities +
                        Kairos.TicketActivity(
                            id =
                                Kairos.TicketActivityId(
                                    "web-activity-${workspace.activities.size + 1}"
                                ),
                            createdAt = Clock.System.now(),
                            ticketId = task.ticket.id,
                            actorUserId = task.ticket.assigneeUserId,
                            kind = Kairos.TicketActivityKind.Updated,
                            summary = "Stage changed to ${stage.uiLabel()}",
                        ),
            )
        notice = "Moved to ${stage.uiLabel()}"
    }

    fun updateAssignee(task: WebTask, assigneeUserId: Kairos.UserId?) {
        if (assigneeUserId == task.ticket.assigneeUserId) return
        if (apiConnected) {
            launchApiJob("Could not update assignee in API.") {
                updateKairosTicketAssignee(task.ticket.id, assigneeUserId)
                val loaded = fetchKairosWorkspace()
                applyWorkspace(loaded, task.ticket.id)
                notice = "Updated assignee"
            }
            return
        }
        val assigneeName =
            workspace.users.firstOrNull { it.id == assigneeUserId }?.displayName ?: "Unassigned"
        workspace =
            workspace.copy(
                tickets =
                    workspace.tickets.map { ticket ->
                        if (ticket.id == task.ticket.id) {
                            ticket.copy(assigneeUserId = assigneeUserId)
                        } else {
                            ticket
                        }
                    },
                activities =
                    workspace.activities +
                        Kairos.TicketActivity(
                            id =
                                Kairos.TicketActivityId(
                                    "web-activity-${workspace.activities.size + 1}"
                                ),
                            createdAt = Clock.System.now(),
                            ticketId = task.ticket.id,
                            actorUserId = assigneeUserId,
                            kind = Kairos.TicketActivityKind.Updated,
                            summary =
                                if (assigneeUserId == null) {
                                    "Cleared assignee"
                                } else {
                                    "Assigned to $assigneeName"
                                },
                        ),
            )
        notice = "Updated assignee"
    }

    fun updateProject(task: WebTask, projectId: Kairos.ProjectId) {
        if (projectId == task.ticket.projectId) return
        val projectName = workspace.projects.firstOrNull { it.id == projectId }?.name ?: return
        if (apiConnected) {
            launchApiJob("Could not move ticket in API.") {
                updateKairosTicketProject(task.ticket.id, projectId)
                val loaded = fetchKairosWorkspace()
                applyWorkspace(loaded, task.ticket.id)
                notice = "Moved to $projectName"
            }
            return
        }
        workspace =
            workspace.copy(
                tickets =
                    workspace.tickets.map { ticket ->
                        if (ticket.id == task.ticket.id) {
                            ticket.copy(projectId = projectId)
                        } else {
                            ticket
                        }
                    },
                activities =
                    workspace.activities +
                        Kairos.TicketActivity(
                            id =
                                Kairos.TicketActivityId(
                                    "web-activity-${workspace.activities.size + 1}"
                                ),
                            createdAt = Clock.System.now(),
                            ticketId = task.ticket.id,
                            actorUserId = task.ticket.assigneeUserId,
                            kind = Kairos.TicketActivityKind.Updated,
                            summary = "Moved to $projectName",
                        ),
            )
        notice = "Moved to $projectName"
    }

    fun updatePriority(task: WebTask, priority: Kairos.TicketPriority) {
        if (priority == task.ticket.priority) return
        if (apiConnected) {
            launchApiJob("Could not update priority in API.") {
                updateKairosTicketPriority(task.ticket.id, priority)
                val loaded = fetchKairosWorkspace()
                applyWorkspace(loaded, task.ticket.id)
                notice = "Updated priority"
            }
            return
        }
        workspace =
            workspace.copy(
                tickets =
                    workspace.tickets.map { ticket ->
                        if (ticket.id == task.ticket.id) {
                            ticket.copy(priority = priority)
                        } else {
                            ticket
                        }
                    },
                activities =
                    workspace.activities +
                        Kairos.TicketActivity(
                            id =
                                Kairos.TicketActivityId(
                                    "web-activity-${workspace.activities.size + 1}"
                                ),
                            createdAt = Clock.System.now(),
                            ticketId = task.ticket.id,
                            actorUserId = task.ticket.assigneeUserId,
                            kind = Kairos.TicketActivityKind.Updated,
                            summary = "Priority changed to ${priority.uiLabel()}",
                        ),
            )
        notice = "Updated priority"
    }

    fun updateUrgency(task: WebTask, urgency: Kairos.TicketUrgency) {
        if (urgency == task.ticket.urgency) return
        if (apiConnected) {
            launchApiJob("Could not update urgency in API.") {
                updateKairosTicketUrgency(task.ticket.id, urgency)
                val loaded = fetchKairosWorkspace()
                applyWorkspace(loaded, task.ticket.id)
                notice = "Updated urgency"
            }
            return
        }
        workspace =
            workspace.copy(
                tickets =
                    workspace.tickets.map { ticket ->
                        if (ticket.id == task.ticket.id) {
                            ticket.copy(urgency = urgency)
                        } else {
                            ticket
                        }
                    },
                activities =
                    workspace.activities +
                        Kairos.TicketActivity(
                            id =
                                Kairos.TicketActivityId(
                                    "web-activity-${workspace.activities.size + 1}"
                                ),
                            createdAt = Clock.System.now(),
                            ticketId = task.ticket.id,
                            actorUserId = task.ticket.assigneeUserId,
                            kind = Kairos.TicketActivityKind.Updated,
                            summary = "Urgency changed to ${urgency.uiLabel()}",
                        ),
            )
        notice = "Updated urgency"
    }

    fun updateDeadline(task: WebTask, deadline: ZonedDateTime?) {
        if (deadline == task.ticket.deadline) return
        if (apiConnected) {
            launchApiJob("Could not update due date in API.") {
                updateKairosTicketDeadline(task.ticket.id, deadline)
                val loaded = fetchKairosWorkspace()
                applyWorkspace(loaded, task.ticket.id)
                notice = "Updated due date"
            }
            return
        }
        workspace =
            workspace.copy(
                tickets =
                    workspace.tickets.map { ticket ->
                        if (ticket.id == task.ticket.id) {
                            ticket.copy(deadline = deadline)
                        } else {
                            ticket
                        }
                    },
                activities =
                    workspace.activities +
                        Kairos.TicketActivity(
                            id =
                                Kairos.TicketActivityId(
                                    "web-activity-${workspace.activities.size + 1}"
                                ),
                            createdAt = Clock.System.now(),
                            ticketId = task.ticket.id,
                            actorUserId = task.ticket.assigneeUserId,
                            kind = Kairos.TicketActivityKind.Updated,
                            summary = "Due date changed",
                        ),
            )
        notice = "Updated due date"
    }

    fun setTicketBlocker(task: WebTask, blockingTicketId: Kairos.TicketId, blocked: Boolean) {
        val blockingTicket = workspace.tickets.firstOrNull { it.id == blockingTicketId } ?: return
        if (apiConnected) {
            launchApiJob("Could not update blocker in API.") {
                if (blocked) {
                    addKairosTicketBlocker(task.ticket.id, blockingTicketId)
                } else {
                    removeKairosTicketBlocker(task.ticket.id, blockingTicketId)
                }
                val loaded = fetchKairosWorkspace()
                applyWorkspace(loaded, task.ticket.id)
                notice =
                    if (blocked) {
                        "Blocked by ${blockingTicket.title}"
                    } else {
                        "Removed blocker"
                    }
            }
            return
        }
        val edge = Kairos.TicketBlocker(task.ticket.id, blockingTicketId, Clock.System.now())
        workspace =
            workspace.copy(
                ticketBlockers =
                    if (blocked) {
                        (workspace.ticketBlockers + edge).distinctBy {
                            it.blockedTicketId to it.blockingTicketId
                        }
                    } else {
                        workspace.ticketBlockers.filterNot {
                            it.blockedTicketId == task.ticket.id &&
                                it.blockingTicketId == blockingTicketId
                        }
                    },
                activities =
                    workspace.activities +
                        Kairos.TicketActivity(
                            id =
                                Kairos.TicketActivityId(
                                    "web-activity-${workspace.activities.size + 1}"
                                ),
                            createdAt = Clock.System.now(),
                            ticketId = task.ticket.id,
                            actorUserId = task.ticket.assigneeUserId,
                            kind = Kairos.TicketActivityKind.Updated,
                            summary =
                                if (blocked) {
                                    "Blocked by ${blockingTicket.title}"
                                } else {
                                    "Removed blocker ${blockingTicket.title}"
                                },
                        ),
            )
        notice = if (blocked) "Blocked by ${blockingTicket.title}" else "Removed blocker"
    }

    fun createObjective(
        description: String,
        projectId: Kairos.ProjectId?,
        deadline: ZonedDateTime?,
        tickets: List<Kairos.TicketId> = emptyList(),
    ) {
        val trimmed = description.trim()
        if (trimmed.isBlank()) return
        if (apiConnected) {
            launchApiJob("Could not create objective in API.") {
                createKairosObjective(
                    description = trimmed,
                    projectId = projectId,
                    deadline = deadline.inputValue().ifBlank { null },
                    tickets = tickets,
                )
                val loaded = fetchKairosWorkspace()
                applyWorkspace(loaded, tickets.firstOrNull() ?: selectedTicketId)
                notice = "Created objective"
            }
            return
        }
        workspace =
            workspace.copy(
                objectives =
                    workspace.objectives +
                        Kairos.Objective(
                            id =
                                Kairos.ObjectiveId(
                                    "web-objective-${workspace.objectives.size + 1}"
                                ),
                            createdAt = Clock.System.now(),
                            workspaceId = workspace.workspace.id,
                            projectId = projectId,
                            description = trimmed,
                            kpiTargets = emptyList(),
                            tickets = tickets.distinct(),
                            deadline = deadline,
                        )
            )
        notice = "Created objective"
    }

    fun updateObjective(
        objectiveId: Kairos.ObjectiveId,
        description: String,
        deadline: ZonedDateTime?,
    ) {
        val trimmed = description.trim()
        if (trimmed.isBlank()) return
        if (workspace.objectives.none { it.id == objectiveId }) return
        if (apiConnected) {
            launchApiJob("Could not update objective in API.") {
                updateKairosObjective(
                    objectiveId = objectiveId,
                    description = trimmed,
                    deadline = deadline.inputValue().ifBlank { null },
                )
                val loaded = fetchKairosWorkspace()
                applyWorkspace(loaded, selectedTicketId)
                notice = "Updated objective"
            }
            return
        }
        workspace =
            workspace.copy(
                objectives =
                    workspace.objectives.map { objective ->
                        if (objective.id == objectiveId) {
                            objective.copy(description = trimmed, deadline = deadline)
                        } else {
                            objective
                        }
                    }
            )
        notice = "Updated objective"
    }

    fun handleCreateObjectiveKpi(
        objective: Kairos.Objective,
        name: String,
        targetValue: Kairos.KpiTarget,
    ) {
        val trimmed = name.trim()
        if (trimmed.isBlank()) return
        launchApiMutationWithFallback(
            message = "Could not create KPI in API.",
            onDisconnectedFallback = {
                val nextKpi =
                    Kairos.Kpi(
                        id = Kairos.KpiId("web-kpi-${workspace.kpis.size + 1}"),
                        createdAt = Clock.System.now(),
                        workspaceId = workspace.workspace.id,
                        projectId = objective.projectId,
                        name = trimmed,
                        origin = defaultMeasuredKpiOrigin(),
                        targetValue = targetValue,
                    )
                workspace =
                    workspace
                        .copy(
                            kpis = workspace.kpis + nextKpi,
                            objectives =
                                workspace.objectives.map { current ->
                                    if (current.id == objective.id) {
                                        current.copy(
                                            kpiTargets =
                                                current.kpiTargets +
                                                    Kairos.KpiWithTarget(
                                                        kpi = nextKpi.id,
                                                        target = targetValue,
                                                    )
                                        )
                                    } else {
                                        current
                                    }
                                },
                        )
                        .rebuildKpiEvaluations()
                notice = "Created KPI and linked it to objective locally"
            },
        ) {
            val created =
                createKairosKpi(
                    projectId = objective.projectId,
                    name = trimmed,
                    targetValue = targetValue,
                )
            updateKairosObjectiveKpiTargets(
                objectiveId = objective.id,
                kpiTargets =
                    objective.kpiTargets +
                        Kairos.KpiWithTarget(kpi = created.id, target = targetValue),
            )
            val loaded = fetchKairosWorkspace()
            applyWorkspace(loaded, selectedTicketId)
            notice = "Created KPI and linked it to objective"
        }
    }

    fun handleCreateKpiMeasurement(kpiId: Kairos.KpiId, value: Double) {
        launchApiMutationWithFallback(
            message = "Could not record KPI measurement in API.",
            onDisconnectedFallback = {
                workspace =
                    workspace
                        .copy(
                            measurements =
                                workspace.measurements +
                                    Kairos.KpiMeasurement(
                                        id =
                                            Kairos.KpiMeasurementId(
                                                "web-kpi-measurement-${workspace.measurements.size + 1}"
                                            ),
                                        kpiId = kpiId,
                                        value = value,
                                        recordedAt = Clock.System.now(),
                                    )
                        )
                        .rebuildKpiEvaluations()
                notice = "Recorded KPI measurement locally"
            },
        ) {
            createKairosKpiMeasurement(kpiId = kpiId, value = value)
            val loaded = fetchKairosWorkspace()
            applyWorkspace(loaded, selectedTicketId)
            notice = "Recorded KPI measurement"
        }
    }

    fun createWorkspaceKpi(
        projectId: Kairos.ProjectId?,
        name: String,
        targetValue: Kairos.KpiTarget?,
        origin: Kairos.KpiOrigin?,
    ) {
        val trimmed = name.trim()
        if (trimmed.isBlank()) return
        launchApiMutationWithFallback(
            message = "Could not create KPI in API.",
            onDisconnectedFallback = {
                workspace =
                    workspace
                        .copy(
                            kpis =
                                workspace.kpis +
                                    Kairos.Kpi(
                                        id = Kairos.KpiId("web-kpi-${workspace.kpis.size + 1}"),
                                        createdAt = Clock.System.now(),
                                        workspaceId = workspace.workspace.id,
                                        projectId = projectId,
                                        name = trimmed,
                                        origin = origin ?: defaultMeasuredKpiOrigin(),
                                        targetValue = targetValue,
                                    )
                        )
                        .rebuildKpiEvaluations()
                notice = "Created KPI locally"
            },
        ) {
            createKairosKpi(
                projectId = projectId,
                name = trimmed,
                targetValue = targetValue,
                origin = origin,
            )
            val loaded = fetchKairosWorkspace()
            applyWorkspace(loaded, selectedTicketId)
            notice = "Created KPI"
        }
    }

    fun updateWorkspaceKpi(
        kpiId: Kairos.KpiId,
        name: String?,
        targetValue: Kairos.KpiTarget?,
        clearTarget: Boolean,
        origin: Kairos.KpiOrigin?,
    ) {
        launchApiMutationWithFallback(
            message = "Could not update KPI in API.",
            onDisconnectedFallback = {
                workspace =
                    workspace
                        .copy(
                            kpis =
                                workspace.kpis.map { kpi ->
                                    if (kpi.id == kpiId) {
                                        kpi.copy(
                                            name =
                                                name?.trim()?.takeIf { it.isNotBlank() }
                                                    ?: kpi.name,
                                            targetValue =
                                                when {
                                                    clearTarget -> null
                                                    targetValue != null -> targetValue
                                                    else -> kpi.targetValue
                                                },
                                            origin = origin ?: kpi.origin,
                                        )
                                    } else {
                                        kpi
                                    }
                                }
                        )
                        .rebuildKpiEvaluations()
                notice = "Updated KPI locally"
            },
        ) {
            updateKairosKpi(
                kpiId = kpiId,
                name = name?.trim(),
                targetValue = targetValue,
                clearTarget = clearTarget,
                origin = origin,
            )
            val loaded = fetchKairosWorkspace()
            applyWorkspace(loaded, selectedTicketId)
            notice = "Updated KPI"
        }
    }

    fun handleSetObjectiveKpiTargets(
        objectiveId: Kairos.ObjectiveId,
        kpiTargets: List<Kairos.KpiWithTarget>,
    ) {
        if (apiConnected) {
            launchApiJob("Could not update objective KPIs in API.") {
                updateKairosObjectiveKpiTargets(objectiveId, kpiTargets.distinctBy { it.kpi })
                val loaded = fetchKairosWorkspace()
                applyWorkspace(loaded, selectedTicketId)
                notice = "Updated objective KPIs"
            }
            return
        }
        workspace =
            workspace.copy(
                objectives =
                    workspace.objectives.map { current ->
                        if (current.id == objectiveId) {
                            current.copy(kpiTargets = kpiTargets.distinctBy { it.kpi })
                        } else {
                            current
                        }
                    }
            )
        notice = "Updated objective KPIs"
    }

    fun setTicketObjectiveLink(task: WebTask, objective: Kairos.Objective, linked: Boolean) {
        val updatedTickets =
            if (linked) {
                (objective.tickets + task.ticket.id).distinct()
            } else {
                objective.tickets.filterNot { it == task.ticket.id }
            }
        if (apiConnected) {
            launchApiJob("Could not update objective links in API.") {
                updateKairosObjectiveTickets(objective.id, updatedTickets)
                val loaded = fetchKairosWorkspace()
                applyWorkspace(loaded, task.ticket.id)
                notice =
                    if (linked) {
                        "Added ${task.ticket.title} to objective"
                    } else {
                        "Removed ${task.ticket.title} from objective"
                    }
            }
            return
        }
        workspace =
            workspace.copy(
                objectives =
                    workspace.objectives.map { current ->
                        if (current.id == objective.id) {
                            current.copy(tickets = updatedTickets)
                        } else {
                            current
                        }
                    }
            )
        notice =
            if (linked) {
                "Added ${task.ticket.title} to objective"
            } else {
                "Removed ${task.ticket.title} from objective"
            }
    }

    fun createEvent(
        projectId: Kairos.ProjectId?,
        title: String,
        summary: String,
        dtStart: ZonedDateTime,
        dtEnd: ZonedDateTime?,
        description: String,
        location: String,
    ) {
        val trimmedTitle = title.trim()
        val trimmedSummary = summary.trim()
        if (trimmedTitle.isBlank() || trimmedSummary.isBlank()) return
        if (apiConnected) {
            launchApiJob("Could not create event in API.") {
                createKairosEvent(
                    projectId = projectId,
                    title = trimmedTitle,
                    summary = trimmedSummary,
                    dtStart = dtStart,
                    dtEnd = dtEnd,
                    description = description.trim().takeIf { it.isNotBlank() },
                    location = location.trim().takeIf { it.isNotBlank() },
                )
                applyWorkspace(fetchKairosWorkspace(), selectedTicketId)
                notice = "Created event"
            }
            return
        }
        workspace =
            workspace.copy(
                events =
                    workspace.events +
                        Kairos.Event(
                            id = Kairos.EventId("web-event-${workspace.events.size + 1}"),
                            createdAt = Clock.System.now(),
                            workspaceId = workspace.workspace.id,
                            projectId = projectId,
                            title = trimmedTitle,
                            uniqueId = "web-event-${workspace.events.size + 1}@kairos.local",
                            dtStamp = nowUiZoned(),
                            dtStart = dtStart,
                            dtEnd = dtEnd,
                            summary = trimmedSummary,
                            description = description.trim().takeIf { it.isNotBlank() },
                            location = location.trim().takeIf { it.isNotBlank() },
                        )
            )
        notice = "Created event"
    }

    fun updateEvent(
        eventId: Kairos.EventId,
        title: String,
        summary: String,
        dtStart: ZonedDateTime,
        dtEnd: ZonedDateTime?,
    ) {
        val trimmedTitle = title.trim()
        val trimmedSummary = summary.trim()
        if (trimmedTitle.isBlank() || trimmedSummary.isBlank()) return
        if (apiConnected) {
            launchApiJob("Could not update event in API.") {
                updateKairosEvent(
                    eventId = eventId,
                    title = trimmedTitle,
                    summary = trimmedSummary,
                    dtStart = dtStart,
                    dtEnd = dtEnd,
                )
                applyWorkspace(fetchKairosWorkspace(), selectedTicketId)
                notice = "Updated event"
            }
            return
        }
        workspace =
            workspace.copy(
                events =
                    workspace.events.map { event ->
                        if (event.id == eventId) {
                            event.copy(
                                title = trimmedTitle,
                                summary = trimmedSummary,
                                dtStart = dtStart,
                                dtEnd = dtEnd,
                            )
                        } else {
                            event
                        }
                    }
            )
        notice = "Updated event"
    }

    fun createTimebox(
        projectId: Kairos.ProjectId?,
        ticketIds: List<Kairos.TicketId>,
        description: String,
        startTime: ZonedDateTime,
        durationHours: Int?,
    ) {
        val trimmedDescription = description.trim()
        if (trimmedDescription.isBlank()) return
        if (apiConnected) {
            launchApiJob("Could not create timebox in API.") {
                createKairosTimebox(
                    projectId = projectId,
                    tickets = ticketIds,
                    description = trimmedDescription,
                    startTime = startTime,
                    durationHours = durationHours,
                )
                applyWorkspace(fetchKairosWorkspace(), selectedTicketId)
                notice = "Created timebox"
            }
            return
        }
        workspace =
            workspace.copy(
                timeboxes =
                    workspace.timeboxes +
                        Kairos.Timebox(
                            id = Kairos.TimeboxId("web-timebox-${workspace.timeboxes.size + 1}"),
                            createdAt = Clock.System.now(),
                            workspaceId = workspace.workspace.id,
                            projectId = projectId,
                            tickets = ticketIds.distinct(),
                            description = trimmedDescription,
                            startTime = startTime.toInstant(),
                            recordedDuration = durationHours.toTimeSpanOrNull(),
                        )
            )
        notice = "Created timebox"
    }

    fun updateTimebox(
        timeboxId: Kairos.TimeboxId,
        description: String,
        startTime: ZonedDateTime,
        durationHours: Int?,
    ) {
        val trimmedDescription = description.trim()
        if (trimmedDescription.isBlank()) return
        if (apiConnected) {
            launchApiJob("Could not update timebox in API.") {
                updateKairosTimebox(
                    timeboxId = timeboxId,
                    description = trimmedDescription,
                    startTime = startTime,
                    durationHours = durationHours,
                )
                applyWorkspace(fetchKairosWorkspace(), selectedTicketId)
                notice = "Updated timebox"
            }
            return
        }
        workspace =
            workspace.copy(
                timeboxes =
                    workspace.timeboxes.map { timebox ->
                        if (timebox.id == timeboxId) {
                            timebox.copy(
                                description = trimmedDescription,
                                startTime = startTime.toInstant(),
                                recordedDuration = durationHours.toTimeSpanOrNull(),
                            )
                        } else {
                            timebox
                        }
                    }
            )
        notice = "Updated timebox"
    }

    fun createRoutine(projectId: Kairos.ProjectId?, description: String) {
        val trimmedDescription = description.trim()
        if (trimmedDescription.isBlank()) return
        if (apiConnected) {
            launchApiJob("Could not create routine in API.") {
                createKairosRoutine(projectId = projectId, description = trimmedDescription)
                applyWorkspace(fetchKairosWorkspace(), selectedTicketId)
                notice = "Created routine"
            }
            return
        }
        workspace =
            workspace.copy(
                routines =
                    workspace.routines +
                        Kairos.Routine(
                            id = Kairos.RoutineId("web-routine-${workspace.routines.size + 1}"),
                            createdAt = Clock.System.now(),
                            workspaceId = workspace.workspace.id,
                            projectId = projectId,
                            description = trimmedDescription,
                        )
            )
        notice = "Created routine"
    }

    fun updateRoutine(routineId: Kairos.RoutineId, description: String) {
        val trimmedDescription = description.trim()
        if (trimmedDescription.isBlank()) return
        if (apiConnected) {
            launchApiJob("Could not update routine in API.") {
                updateKairosRoutine(routineId = routineId, description = trimmedDescription)
                applyWorkspace(fetchKairosWorkspace(), selectedTicketId)
                notice = "Updated routine"
            }
            return
        }
        workspace =
            workspace.copy(
                routines =
                    workspace.routines.map { routine ->
                        if (routine.id == routineId) {
                            routine.copy(description = trimmedDescription)
                        } else {
                            routine
                        }
                    }
            )
        notice = "Updated routine"
    }

    val columns =
        workspace.toColumns(
            selectedProjectId = selectedProjectId,
            searchQuery = searchQuery,
            includeCompleted = includeCompleted,
            hideEmptyColumns = hideEmptyColumns,
            sort = sort,
        )
    val filteredTasks =
        workspace.filteredTasks(
            searchQuery = searchQuery,
            includeCompleted = includeCompleted,
            sort = sort,
        )
    val authenticatedUser =
        sessionInfo?.user?.id?.let { userId ->
            workspace.users.firstOrNull { user -> user.id.value == userId.value }
        } ?: sessionInfo?.user
    val availableWorkspaceAccess =
        sessionInfo?.availableWorkspaceAccess?.takeIf { access -> access.isNotEmpty() }
            ?: listOf(
                KairosWorkspaceAccess(
                    workspace = workspace.workspace,
                    role =
                        workspace.memberships
                            .firstOrNull { membership ->
                                membership.userId.value == authenticatedUser?.id?.value &&
                                    membership.workspaceId == workspace.workspace.id
                            }
                            ?.role ?: Kairos.WorkspaceMembershipRole.Member,
                )
            )
    val inboxTasks =
        filteredTasks.filter { task ->
            task.ticket.completedAt == null && task.ticket.stage == Kairos.TicketStage.Inbox
        }
    val scopeTasks =
        when (selectedArea) {
            WorkspaceArea.Home -> filteredTasks
            WorkspaceArea.Inbox -> inboxTasks
            WorkspaceArea.Objectives -> filteredTasks
            WorkspaceArea.Kpis -> filteredTasks
            WorkspaceArea.Admin -> emptyList()
            WorkspaceArea.Downloads -> emptyList()
            WorkspaceArea.Project -> columns.flatMap { it.tasks }
            WorkspaceArea.WorkspaceSettings -> emptyList()
        }
    val selectedTask =
        scopeTasks.firstOrNull { it.ticket.id == selectedTicketId }
            ?: filteredTasks.firstOrNull { it.ticket.id == selectedTicketId }
            ?: workspace.filteredTasks("", includeCompleted = true, WebSort.Created).firstOrNull {
                task ->
                task.ticket.id == selectedTicketId
            }
    val defaultCreateProject =
        workspace.defaultTaskProjectId(selectedProjectId)?.let { projectId ->
            workspace.projects.firstOrNull { project -> project.id == projectId }
        }
    val createTicketScopeHint =
        when {
            defaultCreateProject != null ->
                "In ${defaultCreateProject.projectDisplayName(workspace.projects)}"
            workspace.projects.isNotEmpty() -> "Choose project first"
            else -> "Create a project first"
        }
    val selectedTaskActivities =
        selectedTask?.let { workspace.activitiesFor(it.ticket.id) }.orEmpty()
    val projectView = normalizedProjectView(view)
    val scheduleEntries =
        remember(workspace, columns, selectedArea) {
            if (selectedArea == WorkspaceArea.Project) {
                workspace.scheduleEntries(columns)
            } else {
                emptyList()
            }
        }
    val selectedScheduleEntry =
        selectedScheduleSelection?.let { selection ->
            scheduleEntries.firstOrNull { entry ->
                entry.kind == selection.kind && entry.key == selection.key
            }
        }
    val scheduleManagerVisible =
        scheduleToolsOpen &&
            selectedArea == WorkspaceArea.Project &&
            (projectView == "Timeline" || projectView == "Calendar")
    val desktopRightPanelVisible =
        !compactLayout && ((detailsOpen && selectedTask != null) || selectedScheduleEntry != null)
    LaunchedEffect(selectedTask?.ticket?.id, detailsOpen) {
        if (selectedTask == null && detailsOpen) {
            detailsOpen = false
        }
    }
    LaunchedEffect(selectedScheduleEntry?.key, selectedScheduleSelection) {
        if (selectedScheduleSelection != null && selectedScheduleEntry == null) {
            selectedScheduleSelection = null
        }
    }
    LaunchedEffect(
        authModalOpen,
        sessionModalTab,
        sessionInfo?.user?.id?.value,
        apiConnected,
        authRequired,
    ) {
        if (
            authModalOpen &&
                sessionModalTab == SessionModalTab.Agents &&
                sessionInfo?.user?.kind == Kairos.UserKind.Human &&
                apiConnected &&
                !authRequired
        ) {
            loadAgentSubaccounts(force = true)
        }
    }
    val showWorkspaceShell = initialLoadState == InitialWebLoadState.Ready && !authRequired
    val standaloneDownloadsPage =
        initialLoadState == InitialWebLoadState.Ready &&
            authRequired &&
            selectedArea == WorkspaceArea.Downloads

    Div(
        attrs = {
            style {
                props(
                    "height" to if (standaloneDownloadsPage) "auto" else "100vh",
                    "min-height" to "100vh",
                    "width" to "100%",
                    "display" to "flex",
                    "flex-direction" to "column",
                    "font-family" to "Inter, Arial, Helvetica, sans-serif",
                    "color" to Ink,
                    "background" to Canvas,
                    "overflow" to if (standaloneDownloadsPage) "visible" else "hidden",
                )
            }
        }
    ) {
        when (initialLoadState) {
            InitialWebLoadState.Loading -> InitialLoadScreen()
            InitialWebLoadState.Unavailable ->
                ServiceUnavailableScreen(
                    message = initialLoadErrorMessage ?: "Kairos is temporarily unavailable.",
                    onRetry = {
                        initialLoadState = InitialWebLoadState.Loading
                        scope.launch { loadWorkspaceFromApi() }
                    },
                )
            InitialWebLoadState.Ready ->
                if (authRequired) {
                    if (selectedArea == WorkspaceArea.Downloads) {
                        DownloadsScreen(
                            compact = compactLayout,
                            manifest = downloadsManifest,
                            loading = downloadsLoading,
                            error = downloadsError,
                            onRetry = { scope.launch { loadDownloads(force = true) } },
                            onBackToSignIn = {
                                routeCommitMode = WebRouteCommitMode.Push
                                selectedArea = WorkspaceArea.Home
                            },
                        )
                    } else {
                        AuthRequiredScreen(
                            compact = compactLayout,
                            submissionState = authSubmissionState,
                            onSignIn = { username, password -> signIn(username, password) },
                            onSignUp = { displayName, username, password, workspaceName ->
                                signUp(displayName, username, password, workspaceName)
                            },
                            onDraftChanged = { authSubmissionState = AuthSubmissionState.Idle },
                            onOpenDownloads = {
                                routeCommitMode = WebRouteCommitMode.Push
                                selectedArea = WorkspaceArea.Downloads
                            },
                        )
                    }
                } else {
                    GlobalBar(
                        workspace = workspace,
                        searchQuery = searchQuery,
                        onSearchChanged = { searchQuery = it },
                        compact = compactLayout,
                        sidebarOpen = sidebarOpen,
                        onToggleSidebar = { sidebarOpen = !sidebarOpen },
                        ticketScopeHint = createTicketScopeHint,
                        onCreateTicket = ::openCreateTicketFlow,
                        onCreateProject = ::openCreateProjectModal,
                        availableWorkspaceAccess = availableWorkspaceAccess,
                        onWorkspaceChanged = { workspaceId -> switchWorkspace(workspaceId) },
                        onOpenWorkspaceSettings = {
                            routeCommitMode = WebRouteCommitMode.Push
                            selectedArea = WorkspaceArea.WorkspaceSettings
                            workspaceSettingsTab = WebWorkspaceSettingsTab.Members
                            selectedTicketId = null
                            detailsOpen = false
                            selectedScheduleSelection = null
                            scheduleToolsOpen = false
                        },
                        onOpenWorkspaceShare = {
                            selectedTicketId = null
                            detailsOpen = false
                            selectedScheduleSelection = null
                            scheduleToolsOpen = false
                            openWorkspaceShareModal()
                        },
                        sessionInfo = sessionInfo,
                        authenticatedUser = authenticatedUser,
                        onAuthClicked = {
                            detailsOpen = false
                            selectedScheduleSelection = null
                            scheduleToolsOpen = false
                            agentSettingsSelection = null
                            agentSettingsFromWorkspace = false
                            authModalOpen = true
                        },
                    )
                }
        }
        if (showWorkspaceShell && authModalOpen && !agentCreateModalOpen) {
            SessionModal(
                compact = compactLayout,
                authenticatedUser = authenticatedUser,
                sessionInfo = sessionInfo,
                activeTab = sessionModalTab,
                agentSubaccounts = agentSubaccounts,
                agentSubaccountsLoading = agentSubaccountsLoading,
                agentSubaccountsNotice = agentSubaccountsNotice,
                agentSubaccountsError = agentSubaccountsError,
                savingAgentName = agentNameSaving,
                savedAgentName = agentNameSaved,
                savingWorkspaceAccess = agentWorkspaceAccessSaving,
                savedWorkspaceAccess = agentWorkspaceAccessSaved,
                availableWorkspaceAccess = availableWorkspaceAccess,
                initialManagedAgentId = agentSettingsSelection,
                openedFromWorkspaceSettings = agentSettingsFromWorkspace,
                currentWorkspaceId = workspace.workspace.id,
                currentWorkspaceName = workspace.workspace.name,
                onDismiss = {
                    authModalOpen = false
                    agentSettingsSelection = null
                    agentSettingsFromWorkspace = false
                },
                onTabChanged = {
                    sessionModalTab = it
                    if (it != SessionModalTab.Agents) {
                        agentSettingsSelection = null
                        agentSettingsFromWorkspace = false
                    }
                },
                onClearToken = { clearSessionToken() },
                onOpenWorkspaceSettings = {
                    authModalOpen = false
                    sessionModalTab = SessionModalTab.Profile
                    agentSettingsSelection = null
                    agentSettingsFromWorkspace = false
                    routeCommitMode = WebRouteCommitMode.Push
                    selectedArea = WorkspaceArea.WorkspaceSettings
                    workspaceSettingsTab = WebWorkspaceSettingsTab.Members
                    selectedTicketId = null
                    detailsOpen = false
                    selectedScheduleSelection = null
                    scheduleToolsOpen = false
                },
                onSaveProfile = { userId, avatarUrl, timeZoneId, localeTag ->
                    updateUserProfile(userId, avatarUrl, timeZoneId, localeTag)
                },
                onOpenCreateAgent = {
                    agentSubaccountsNotice = null
                    agentSubaccountsError = null
                    agentSettingsSelection = null
                    agentSettingsFromWorkspace = false
                    agentCreateModalOpen = true
                },
                onCreateAgent = ::createAgentSubaccount,
                onUpdateAgentName = ::updateAgentSubaccountName,
                onRotateAgentToken = { agentRotateConfirm = it },
                onUpdateAgentWorkspaceAccess = ::updateAgentWorkspaceAccess,
                onDeleteAgent = { userId ->
                    agentDeleteConfirm =
                        agentSubaccounts.firstOrNull { agent -> agent.user.id == userId }
                },
            )
        }
        if (showWorkspaceShell && agentCreateModalOpen) {
            ModalBackdrop(
                zIndex = 94,
                background = "rgba(20,22,28,0.62)",
                onDismiss = { agentCreateModalOpen = false },
            )
            AgentSubaccountCreateModal(
                availableWorkspaceAccess = availableWorkspaceAccess,
                defaultWorkspaceId = workspace.workspace.id,
                compact = compactLayout,
                busy = agentSubaccountsLoading,
                onCreate = ::createAgentSubaccount,
                onDismiss = { agentCreateModalOpen = false },
            )
        }
        if (showWorkspaceShell) {
            agentTokenReveal?.let { reveal ->
                ModalBackdrop(zIndex = 96, background = "rgba(20,22,28,0.68)", onDismiss = {})
                AgentTokenRevealModal(reveal = reveal, onDismiss = { agentTokenReveal = null })
            }
        }
        if (showWorkspaceShell) {
            agentRotateConfirm?.let { agent ->
                ModalBackdrop(
                    zIndex = 96,
                    background = "rgba(20,22,28,0.68)",
                    onDismiss = { agentRotateConfirm = null },
                )
                AgentRotateTokenConfirmModal(
                    agent = agent,
                    busy = agentSubaccountsLoading,
                    onDismiss = { agentRotateConfirm = null },
                    onConfirm = {
                        agentRotateConfirm = null
                        rotateAgentSubaccountToken(agent.user.id)
                    },
                )
            }
        }
        if (showWorkspaceShell) {
            agentDeleteConfirm?.let { agent ->
                ModalBackdrop(
                    zIndex = 96,
                    background = "rgba(20,22,28,0.68)",
                    onDismiss = { agentDeleteConfirm = null },
                )
                AgentDeleteConfirmModal(
                    agent = agent,
                    busy = agentSubaccountsLoading,
                    onDismiss = { agentDeleteConfirm = null },
                    onConfirm = {
                        agentDeleteConfirm = null
                        removeAgentSubaccount(agent.user.id)
                    },
                )
            }
        }
        if (showWorkspaceShell && workspaceShareModalOpen) {
            OverlayScrim(zIndex = 92, onDismiss = ::dismissWorkspaceShareModal)
            WorkspaceShareModal(
                workspace = workspace,
                compact = compactLayout,
                identifierDraft = workspaceShareIdentifierDraft,
                lookupState = workspaceShareLookupState,
                busy = workspaceMembersLoading,
                onIdentifierChanged = {
                    workspaceShareIdentifierDraft = it
                    workspaceShareLookupState = WebWorkspaceShareLookupState.Idle
                },
                onLookup = { lookupWorkspaceShareTarget(workspaceShareIdentifierDraft) },
                onConfirmShare = ::confirmWorkspaceShareTarget,
                onDismiss = ::dismissWorkspaceShareModal,
            )
        }
        if (showWorkspaceShell) {
            workspaceMemberAction?.let { action ->
                OverlayScrim(zIndex = 94, onDismiss = { workspaceMemberAction = null })
                WorkspaceMemberActionModal(
                    workspace = workspace,
                    compact = compactLayout,
                    action = action,
                    busy = workspaceMembersLoading,
                    onConfirm = {
                        when (action) {
                            is WebWorkspaceMemberAction.Promote ->
                                updateWorkspaceAccessRole(
                                    action.member.user.id,
                                    Kairos.WorkspaceMembershipRole.Owner,
                                )
                            is WebWorkspaceMemberAction.Demote ->
                                updateWorkspaceAccessRole(
                                    action.member.user.id,
                                    Kairos.WorkspaceMembershipRole.Member,
                                )
                            is WebWorkspaceMemberAction.Remove ->
                                removeWorkspaceAccess(action.member.user.id)
                        }
                        workspaceMemberAction = null
                    },
                    onDismiss = { workspaceMemberAction = null },
                )
            }
        }
        if (showWorkspaceShell && createProjectModalOpen) {
            ModalBackdrop(
                zIndex = 94,
                background = "rgba(20,22,28,0.62)",
                onDismiss = { createProjectModalOpen = false },
            )
            ModalCard(
                zIndex = 95,
                title = "Create project",
                onDismiss = { createProjectModalOpen = false },
            ) {
                Input(
                    type = InputType.Text,
                    attrs = {
                        attr("id", "web-global-create-project-name-input")
                        value(createProjectNameDraft)
                        onInput { createProjectNameDraft = it.value }
                        attr("placeholder", "Project name")
                        style { formInputStyle() }
                    },
                )
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "justify-content" to "flex-end",
                                "gap" to "10px",
                                "flex-wrap" to "wrap",
                            )
                        }
                    }
                ) {
                    Button(
                        attrs = {
                            onClick { createProjectModalOpen = false }
                            style { secondaryButton() }
                        }
                    ) {
                        Text("Cancel")
                    }
                    Button(
                        attrs = {
                            attr("id", "web-global-create-project-submit")
                            onClick { submitCreateProjectFromGlobalMenu() }
                            if (createProjectNameDraft.trim().isBlank()) disabled()
                            style { primaryButton() }
                        }
                    ) {
                        Text("Create project")
                    }
                }
            }
        }
        if (showWorkspaceShell && createTicketProjectModalOpen) {
            ModalBackdrop(
                zIndex = 94,
                background = "rgba(20,22,28,0.62)",
                onDismiss = { createTicketProjectModalOpen = false },
            )
            ModalCard(
                zIndex = 95,
                title = "Choose project",
                onDismiss = { createTicketProjectModalOpen = false },
            ) {
                Select(
                    attrs = {
                        attr("id", "web-global-create-ticket-project-select")
                        onInput { createTicketProjectDraft = it.value.orEmpty() }
                        style { formInputStyle() }
                    }
                ) {
                    workspace.projects.forEach { project ->
                        Option(
                            value = project.id.value,
                            attrs = {
                                if (project.id.value == createTicketProjectDraft) {
                                    attr("selected", "selected")
                                }
                            },
                        ) {
                            Text(project.projectDisplayName(workspace.projects))
                        }
                    }
                }
                Div(
                    attrs = {
                        style {
                            props("font-size" to "13px", "line-height" to "1.5", "color" to Muted)
                        }
                    }
                ) {
                    Text("New tickets need a project so they show up in the right place.")
                }
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "justify-content" to "space-between",
                                "gap" to "10px",
                                "flex-wrap" to "wrap",
                            )
                        }
                    }
                ) {
                    Button(
                        attrs = {
                            attr("id", "web-global-create-ticket-project-new-project")
                            onClick {
                                createTicketProjectModalOpen = false
                                openCreateProjectModal()
                            }
                            style { secondaryButton() }
                        }
                    ) {
                        Text("New project")
                    }
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "justify-content" to "flex-end",
                                    "gap" to "10px",
                                    "flex-wrap" to "wrap",
                                )
                            }
                        }
                    ) {
                        Button(
                            attrs = {
                                onClick { createTicketProjectModalOpen = false }
                                style { secondaryButton() }
                            }
                        ) {
                            Text("Cancel")
                        }
                        Button(
                            attrs = {
                                attr("id", "web-global-create-ticket-submit")
                                onClick { submitCreateTicketForProject() }
                                if (createTicketProjectDraft.isBlank()) disabled()
                                style { primaryButton() }
                            }
                        ) {
                            Text("Create ticket")
                        }
                    }
                }
            }
        }
        if (showWorkspaceShell) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "min-height" to "0",
                            "flex" to "1",
                            "position" to "relative",
                        )
                    }
                }
            ) {
                if (!compactLayout && sidebarOpen) {
                    Sidebar(
                        workspace = workspace,
                        compact = false,
                        showAdminArea = currentUserIsAdmin(),
                        selectedArea = selectedArea,
                        selectedProjectId = selectedProjectId,
                        onCreateProject = ::openCreateProjectModal,
                        onAreaSelected = {
                            routeCommitMode = WebRouteCommitMode.Push
                            selectedArea = it
                            selectedTicketId = null
                            detailsOpen = false
                            selectedScheduleSelection = null
                            scheduleToolsOpen = false
                        },
                        onProjectSelected = {
                            routeCommitMode = WebRouteCommitMode.Push
                            selectedArea = WorkspaceArea.Project
                            selectedProjectId = it
                            selectedTicketId = null
                            detailsOpen = false
                            selectedScheduleSelection = null
                            scheduleToolsOpen = false
                        },
                    )
                }
                Div(
                    attrs = {
                        style {
                            props(
                                "flex" to "1",
                                "min-width" to "0",
                                "min-height" to "0",
                                "transition" to "flex-basis 140ms ease, width 140ms ease",
                                "box-sizing" to "border-box",
                                "overflow-y" to "auto",
                                "overflow-x" to "hidden",
                            )
                        }
                    }
                ) {
                    MainWorkspace(
                        workspace = workspace,
                        compactLayout = compactLayout,
                        selectedArea = selectedArea,
                        columns = columns,
                        filteredTasks = filteredTasks,
                        inboxTasks = inboxTasks,
                        downloadsManifest = downloadsManifest,
                        downloadsLoading = downloadsLoading,
                        downloadsError = downloadsError,
                        adminSnapshot = adminSnapshot,
                        adminLoading = adminLoading,
                        adminError = adminError,
                        adminNotice = adminNotice,
                        adminRoleSavingUserId = adminRoleSavingUserId,
                        viewerUser = authenticatedUser,
                        view = view,
                        selectedProjectId = selectedProjectId,
                        selectedTask = selectedTask,
                        notice = notice,
                        includeCompleted = includeCompleted,
                        hideEmptyColumns = hideEmptyColumns,
                        showBoardInbox = showBoardInbox,
                        sort = sort,
                        detailsOpen = detailsOpen,
                        scheduleToolsOpen = scheduleToolsOpen,
                        onViewChanged = {
                            routeCommitMode = WebRouteCommitMode.Push
                            view = it
                            selectedTicketId = null
                            detailsOpen = false
                            selectedScheduleSelection = null
                            scheduleToolsOpen = false
                        },
                        onTaskSelected = {
                            routeCommitMode = WebRouteCommitMode.Push
                            selectedTicketId = it.ticket.id
                            selectedProjectId = it.ticket.projectId
                            detailsOpen = true
                            selectedScheduleSelection = null
                            scheduleToolsOpen = false
                        },
                        onScheduleEntrySelected = { entry ->
                            routeCommitMode = WebRouteCommitMode.Push
                            selectedTicketId = null
                            detailsOpen = false
                            scheduleToolsOpen = false
                            selectedScheduleSelection = WebScheduleSelection(entry.kind, entry.key)
                        },
                        onAddTask = { addTask(it) },
                        onSetCompleted = ::setTaskCompleted,
                        onToggleCompleted = { includeCompleted = !includeCompleted },
                        onToggleEmpty = { hideEmptyColumns = !hideEmptyColumns },
                        onToggleBoardInbox = { showBoardInbox = !showBoardInbox },
                        onToggleSort = {
                            sort =
                                when (sort) {
                                    WebSort.Created -> WebSort.Due
                                    WebSort.Due -> WebSort.Title
                                    WebSort.Title -> WebSort.Created
                                }
                        },
                        onToggleDetails = {
                            routeCommitMode = WebRouteCommitMode.Push
                            detailsOpen = selectedTask != null && !detailsOpen
                        },
                        onToggleScheduleTools = {
                            routeCommitMode = WebRouteCommitMode.Push
                            val nextOpen = !scheduleToolsOpen
                            scheduleToolsOpen = nextOpen
                            if (nextOpen) {
                                detailsOpen = false
                                selectedScheduleSelection = null
                            }
                        },
                        onReset = {
                            routeCommitMode = WebRouteCommitMode.Push
                            searchQuery = ""
                            includeCompleted = false
                            hideEmptyColumns = false
                            showBoardInbox = false
                            sort = WebSort.Created
                            selectedProjectId = workspace.primaryProject()?.id
                            selectedTicketId = null
                            detailsOpen = false
                            selectedScheduleSelection = null
                            scheduleToolsOpen = false
                            notice = "View reset"
                        },
                        onShowObjectives = {
                            routeCommitMode = WebRouteCommitMode.Push
                            selectedArea = WorkspaceArea.Objectives
                            selectedTicketId = null
                            detailsOpen = false
                            selectedScheduleSelection = null
                            scheduleToolsOpen = false
                        },
                        onShowSchedule = { projectId ->
                            val targetProjectId =
                                projectId ?: selectedProjectId ?: workspace.primaryProject()?.id
                            if (targetProjectId != null) {
                                routeCommitMode = WebRouteCommitMode.Push
                                selectedArea = WorkspaceArea.Project
                                selectedProjectId = targetProjectId
                                view = "Calendar"
                                selectedTicketId = null
                                detailsOpen = false
                                selectedScheduleSelection = null
                                scheduleToolsOpen = false
                                saveStoredCurrentProjectId(targetProjectId)
                            }
                        },
                        onCreateProject = ::createProject,
                        onUpdateProjectMetadata = ::updateProjectMetadata,
                        onUpdateStage = ::updateStage,
                        onCreateObjective = ::createObjective,
                        onUpdateObjective = ::updateObjective,
                        onCreateKpi = { projectId, name, targetValue, origin ->
                            createWorkspaceKpi(projectId, name, targetValue, origin)
                        },
                        onUpdateKpi = { kpiId, name, targetValue, clearTarget, origin ->
                            updateWorkspaceKpi(kpiId, name, targetValue, clearTarget, origin)
                        },
                        onCreateObjectiveKpi = { objective, name, target ->
                            handleCreateObjectiveKpi(objective, name, target)
                        },
                        onCreateKpiMeasurement = { kpiId, value ->
                            handleCreateKpiMeasurement(kpiId, value)
                        },
                        onSetObjectiveKpiTargets = { objectiveId, kpiTargets ->
                            handleSetObjectiveKpiTargets(objectiveId, kpiTargets)
                        },
                        onCreateEvent = {
                            projectId,
                            title,
                            summary,
                            dtStart,
                            dtEnd,
                            description,
                            location ->
                            createEvent(
                                projectId,
                                title,
                                summary,
                                dtStart,
                                dtEnd,
                                description,
                                location,
                            )
                        },
                        onUpdateEvent = { eventId, title, summary, dtStart, dtEnd ->
                            updateEvent(eventId, title, summary, dtStart, dtEnd)
                        },
                        onCreateTimebox = {
                            projectId,
                            ticketIds,
                            description,
                            startTime,
                            durationHours ->
                            createTimebox(
                                projectId,
                                ticketIds,
                                description,
                                startTime,
                                durationHours,
                            )
                        },
                        onUpdateTimebox = { timeboxId, description, startTime, durationHours ->
                            updateTimebox(timeboxId, description, startTime, durationHours)
                        },
                        onCreateRoutine = { projectId, description ->
                            createRoutine(projectId, description)
                        },
                        onUpdateRoutine = { routineId, description ->
                            updateRoutine(routineId, description)
                        },
                        workspaceSettingsTab = workspaceSettingsTab,
                        workspaceMembers = workspaceMembers,
                        workspaceMembersLoading = workspaceMembersLoading,
                        workspaceMembersNotice = workspaceMembersNotice,
                        workspaceMembersError = workspaceMembersError,
                        onWorkspaceSettingsTabChanged = {
                            routeCommitMode = WebRouteCommitMode.Push
                            workspaceSettingsTab = it
                        },
                        onOpenWorkspaceShare = ::openWorkspaceShareModal,
                        onOpenAgentSettings = { agentUserId ->
                            authModalOpen = true
                            sessionModalTab = SessionModalTab.Agents
                            agentSettingsSelection = agentUserId
                            agentSettingsFromWorkspace = true
                        },
                        onCopyOwnerContacts = ::copyWorkspaceOwnerContacts,
                        onUpdateWorkspaceMemberRole = ::updateWorkspaceAccessRole,
                        onRemoveWorkspaceMember = ::removeWorkspaceAccess,
                        onRequestWorkspaceMemberAction = { workspaceMemberAction = it },
                        onRetryDownloads = { scope.launch { loadDownloads(force = true) } },
                        onRetryAdmin = { scope.launch { loadAdminSnapshot(force = true) } },
                        onUpdateAdminRole = ::updateAdminRole,
                    )
                }
                if (!compactLayout && detailsOpen && selectedTask != null) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "width" to "clamp(380px, 36vw, 520px)",
                                    "min-width" to "380px",
                                    "max-width" to "44vw",
                                    "height" to "100%",
                                    "border-left" to "1px solid $Border",
                                    "overflow" to "hidden",
                                    "background" to Paper,
                                    "box-shadow" to "-12px 0 28px rgba(20,22,28,0.06)",
                                    "flex-shrink" to "0",
                                )
                            }
                        }
                    ) {
                        TaskDetails(
                            workspace = workspace,
                            compact = false,
                            task = selectedTask,
                            activities = selectedTaskActivities,
                            onSetCompleted = ::setTaskCompleted,
                            onUpdateTitle = ::updateTitle,
                            onUpdateDescription = ::updateDescription,
                            onUpdateAssignee = ::updateAssignee,
                            onUpdateProject = ::updateProject,
                            onUpdatePriority = ::updatePriority,
                            onUpdateUrgency = ::updateUrgency,
                            onUpdateDeadline = ::updateDeadline,
                            onUpdateStage = ::updateStage,
                            onSetTicketBlocker = ::setTicketBlocker,
                            onCreateObjective = ::createObjective,
                            onSetObjectiveLink = ::setTicketObjectiveLink,
                            onClose = {
                                routeCommitMode = WebRouteCommitMode.Push
                                detailsOpen = false
                            },
                        )
                    }
                }
                if (!compactLayout && selectedScheduleEntry != null) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "width" to "clamp(380px, 36vw, 520px)",
                                    "min-width" to "380px",
                                    "max-width" to "44vw",
                                    "height" to "100%",
                                    "border-left" to "1px solid $Border",
                                    "overflow" to "hidden",
                                    "background" to Paper,
                                    "box-shadow" to "-12px 0 28px rgba(20,22,28,0.06)",
                                    "flex-shrink" to "0",
                                )
                            }
                        }
                    ) {
                        ScheduleEntryDetails(
                            workspace = workspace,
                            compact = false,
                            entry = selectedScheduleEntry,
                            onUpdateEvent = ::updateEvent,
                            onUpdateTimebox = ::updateTimebox,
                            onClose = ::closeRightPanel,
                        )
                    }
                }
                if (scheduleManagerVisible) {
                    ScheduleManagerOverlay(
                        workspace = workspace,
                        compact = compactLayout,
                        selectedProjectId = selectedProjectId,
                        selectedTask = selectedTask,
                        onCreateEvent = ::createEvent,
                        onUpdateEvent = ::updateEvent,
                        onCreateTimebox = ::createTimebox,
                        onUpdateTimebox = ::updateTimebox,
                        onCreateRoutine = ::createRoutine,
                        onUpdateRoutine = ::updateRoutine,
                        onClose = { scheduleToolsOpen = false },
                    )
                }
                if (compactLayout && sidebarOpen) {
                    OverlayScrim(zIndex = 20, onDismiss = { sidebarOpen = false })
                    Div(
                        attrs = {
                            style {
                                props(
                                    "position" to "absolute",
                                    "top" to "0",
                                    "left" to "0",
                                    "bottom" to "0",
                                    "z-index" to "24",
                                    "box-shadow" to "0 16px 36px rgba(20,22,28,0.32)",
                                )
                            }
                        }
                    ) {
                        Sidebar(
                            workspace = workspace,
                            compact = true,
                            showAdminArea = currentUserIsAdmin(),
                            selectedArea = selectedArea,
                            selectedProjectId = selectedProjectId,
                            onCreateProject = ::openCreateProjectModal,
                            onAreaSelected = {
                                routeCommitMode = WebRouteCommitMode.Push
                                selectedArea = it
                                selectedTicketId = null
                                detailsOpen = false
                                selectedScheduleSelection = null
                                scheduleToolsOpen = false
                                sidebarOpen = false
                            },
                            onProjectSelected = {
                                routeCommitMode = WebRouteCommitMode.Push
                                selectedArea = WorkspaceArea.Project
                                selectedProjectId = it
                                selectedTicketId = null
                                detailsOpen = false
                                selectedScheduleSelection = null
                                scheduleToolsOpen = false
                                sidebarOpen = false
                            },
                        )
                    }
                }
                if (detailsOpen && selectedTask != null && compactLayout) {
                    OverlayScrim(
                        zIndex = 30,
                        onDismiss = {
                            routeCommitMode = WebRouteCommitMode.Push
                            detailsOpen = false
                        },
                    )
                    Div(
                        attrs = {
                            style {
                                props(
                                    "position" to "absolute",
                                    "top" to "0",
                                    "left" to "0",
                                    "right" to "0",
                                    "bottom" to "0",
                                    "width" to "100%",
                                    "max-width" to "100%",
                                    "z-index" to "34",
                                    "box-shadow" to "0 16px 36px rgba(20,22,28,0.28)",
                                )
                            }
                        }
                    ) {
                        TaskDetails(
                            workspace = workspace,
                            compact = true,
                            task = selectedTask,
                            activities = selectedTaskActivities,
                            onSetCompleted = ::setTaskCompleted,
                            onUpdateTitle = ::updateTitle,
                            onUpdateDescription = ::updateDescription,
                            onUpdateAssignee = ::updateAssignee,
                            onUpdateProject = ::updateProject,
                            onUpdatePriority = ::updatePriority,
                            onUpdateUrgency = ::updateUrgency,
                            onUpdateDeadline = ::updateDeadline,
                            onUpdateStage = ::updateStage,
                            onSetTicketBlocker = ::setTicketBlocker,
                            onCreateObjective = ::createObjective,
                            onSetObjectiveLink = ::setTicketObjectiveLink,
                            onClose = {
                                routeCommitMode = WebRouteCommitMode.Push
                                detailsOpen = false
                            },
                        )
                    }
                }
                if (selectedScheduleEntry != null && compactLayout) {
                    OverlayScrim(zIndex = 30, onDismiss = ::closeRightPanel)
                    Div(
                        attrs = {
                            style {
                                props(
                                    "position" to "absolute",
                                    "top" to "0",
                                    "left" to "0",
                                    "right" to "0",
                                    "bottom" to "0",
                                    "width" to "100%",
                                    "max-width" to "100%",
                                    "z-index" to "34",
                                    "box-shadow" to "0 16px 36px rgba(20,22,28,0.28)",
                                )
                            }
                        }
                    ) {
                        ScheduleEntryDetails(
                            workspace = workspace,
                            compact = true,
                            entry = selectedScheduleEntry,
                            onUpdateEvent = ::updateEvent,
                            onUpdateTimebox = ::updateTimebox,
                            onClose = ::closeRightPanel,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun GlobalBar(
    workspace: WebWorkspace,
    searchQuery: String,
    onSearchChanged: (String) -> Unit,
    compact: Boolean,
    sidebarOpen: Boolean,
    onToggleSidebar: () -> Unit,
    ticketScopeHint: String,
    onCreateTicket: () -> Unit,
    onCreateProject: () -> Unit,
    availableWorkspaceAccess: List<KairosWorkspaceAccess>,
    onWorkspaceChanged: (Kairos.WorkspaceId) -> Unit,
    onOpenWorkspaceSettings: () -> Unit,
    onOpenWorkspaceShare: () -> Unit,
    sessionInfo: KairosSessionInfo?,
    authenticatedUser: Kairos.User?,
    onAuthClicked: () -> Unit,
) {
    val sessionAuthenticated = sessionInfo?.authenticated == true
    val topBarUser = authenticatedUser
    var compactSearchOpen by remember(compact) { mutableStateOf(false) }
    var createExpanded by remember { mutableStateOf(false) }
    LaunchedEffect(compact) { if (!compact) compactSearchOpen = false }
    Div(
        attrs = {
            style {
                props(
                    "height" to "56px",
                    "display" to "flex",
                    "align-items" to "center",
                    "gap" to if (compact) "10px" else "16px",
                    "padding" to if (compact) "0 12px" else "0 18px",
                    "background" to Charcoal,
                    "color" to Paper,
                    "box-sizing" to "border-box",
                    "flex-shrink" to "0",
                    "position" to "relative",
                    "z-index" to "40",
                    "overflow" to "visible",
                )
            }
        }
    ) {
        Button(
            attrs = {
                onClick { onToggleSidebar() }
                style {
                    props(
                        "width" to "36px",
                        "height" to "36px",
                        "border" to "1px solid rgba(255,255,255,0.18)",
                        "border-radius" to "8px",
                        "background" to "rgba(255,255,255,0.08)",
                        "color" to Paper,
                        "padding" to "0",
                        "font-size" to "18px",
                        "font-weight" to "700",
                        "cursor" to "pointer",
                    )
                }
            }
        ) {
            Text("≡")
        }
        if (compact && !compactSearchOpen) {
            Div(attrs = { style { props("position" to "relative") } }) {
                Button(
                    attrs = {
                        attr("id", "web-top-create-button")
                        attr("aria-label", "Create")
                        onClick { createExpanded = !createExpanded }
                        style {
                            props(
                                "width" to "36px",
                                "height" to "36px",
                                "border" to "1px solid rgba(255,255,255,0.2)",
                                "border-radius" to "8px",
                                "background" to Coral,
                                "color" to Paper,
                                "padding" to "0",
                                "font-size" to "22px",
                                "font-weight" to "700",
                                "line-height" to "1",
                                "cursor" to "pointer",
                                "flex-shrink" to "0",
                            )
                        }
                    }
                ) {
                    Text("+")
                }
                if (createExpanded) {
                    Div(
                        attrs = {
                            onClick { createExpanded = false }
                            style {
                                props(
                                    "position" to "fixed",
                                    "inset" to "0",
                                    "background" to "transparent",
                                    "z-index" to "79",
                                )
                            }
                        }
                    )
                    GlobalCreateMenu(
                        compact = true,
                        ticketScopeHint = ticketScopeHint,
                        onCreateTicket = {
                            createExpanded = false
                            onCreateTicket()
                        },
                        onCreateProject = {
                            createExpanded = false
                            onCreateProject()
                        },
                    )
                }
            }
        }
        if (!compact) {
            Div(attrs = { style { props("position" to "relative") } }) {
                Button(
                    attrs = {
                        attr("id", "web-top-create-button")
                        onClick { createExpanded = !createExpanded }
                        style {
                            props(
                                "height" to "36px",
                                "border" to "1px solid rgba(255,255,255,0.2)",
                                "border-radius" to "8px",
                                "background" to Coral,
                                "color" to Paper,
                                "padding" to "0 14px",
                                "font-size" to "14px",
                                "font-weight" to "700",
                            )
                        }
                    }
                ) {
                    Text("+ Create")
                }
                if (createExpanded) {
                    Div(
                        attrs = {
                            onClick { createExpanded = false }
                            style {
                                props(
                                    "position" to "fixed",
                                    "inset" to "0",
                                    "background" to "transparent",
                                    "z-index" to "79",
                                )
                            }
                        }
                    )
                    GlobalCreateMenu(
                        compact = false,
                        ticketScopeHint = ticketScopeHint,
                        onCreateTicket = {
                            createExpanded = false
                            onCreateTicket()
                        },
                        onCreateProject = {
                            createExpanded = false
                            onCreateProject()
                        },
                    )
                }
            }
        }
        if (compact) {
            if (compactSearchOpen) {
                Input(
                    type = InputType.Search,
                    attrs = {
                        attr("id", "web-top-search-input")
                        value(searchQuery)
                        onInput { onSearchChanged(it.value) }
                        attr("placeholder", "Search tickets, projects, and routines")
                        style {
                            props(
                                "height" to "36px",
                                "min-width" to "0",
                                "flex" to "1 1 auto",
                                "border" to "0",
                                "border-radius" to "8px",
                                "background" to "rgba(255,255,255,0.18)",
                                "color" to Paper,
                                "padding" to "0 16px",
                                "font-size" to "14px",
                                "outline" to "none",
                            )
                        }
                    },
                )
                Button(
                    attrs = {
                        attr("aria-label", "Close search")
                        onClick { compactSearchOpen = false }
                        style {
                            props(
                                "height" to "36px",
                                "border" to "1px solid rgba(255,255,255,0.18)",
                                "border-radius" to "8px",
                                "background" to "rgba(255,255,255,0.08)",
                                "color" to Paper,
                                "padding" to "0 12px",
                                "font-size" to "13px",
                                "font-weight" to "700",
                            )
                        }
                    }
                ) {
                    Text("Close")
                }
            } else {
                Button(
                    attrs = {
                        attr("id", "web-top-search-toggle")
                        attr("aria-label", "Open search")
                        onClick { compactSearchOpen = true }
                        style {
                            props(
                                "width" to "36px",
                                "height" to "36px",
                                "border" to "1px solid rgba(255,255,255,0.18)",
                                "border-radius" to "8px",
                                "background" to "rgba(255,255,255,0.08)",
                                "color" to Paper,
                                "padding" to "0",
                                "font-size" to "16px",
                                "font-weight" to "700",
                                "cursor" to "pointer",
                            )
                        }
                    }
                ) {
                    Text("⌕")
                }
            }
        } else {
            Input(
                type = InputType.Search,
                attrs = {
                    attr("id", "web-top-search-input")
                    value(searchQuery)
                    onInput { onSearchChanged(it.value) }
                    attr("placeholder", "Search tickets, projects, and routines")
                    style {
                        props(
                            "height" to "36px",
                            "max-width" to "640px",
                            "min-width" to "0",
                            "flex" to "1 1 160px",
                            "border" to "0",
                            "border-radius" to "8px",
                            "background" to "rgba(255,255,255,0.18)",
                            "color" to Paper,
                            "padding" to "0 16px",
                            "font-size" to "14px",
                            "outline" to "none",
                        )
                    }
                },
            )
        }
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "gap" to "10px",
                        "min-width" to "0",
                        "flex-shrink" to "0",
                    )
                }
            }
        ) {
            WorkspaceSwitcherMenu(
                compact = compact,
                currentWorkspaceId = workspace.workspace.id,
                availableWorkspaceAccess = availableWorkspaceAccess,
                onWorkspaceChanged = onWorkspaceChanged,
                onOpenWorkspaceSettings = onOpenWorkspaceSettings,
                onOpenWorkspaceShare = onOpenWorkspaceShare,
            )
            Button(
                attrs = {
                    attr("id", "web-auth-open-button")
                    onClick { onAuthClicked() }
                    title(
                        if (sessionAuthenticated) {
                            "Open account and workspace settings"
                        } else {
                            "Open sign-in"
                        }
                    )
                    style {
                        props(
                            "width" to "36px",
                            "height" to "36px",
                            "border" to "1px solid rgba(255,255,255,0.16)",
                            "border-radius" to "999px",
                            "background" to "rgba(255,255,255,0.08)",
                            "padding" to "0",
                            "display" to "inline-flex",
                            "align-items" to "center",
                            "justify-content" to "center",
                            "cursor" to "pointer",
                        )
                    }
                }
            ) {
                Avatar(topBarUser, 30)
            }
        }
    }
}

@Composable
private fun GlobalCreateMenu(
    compact: Boolean,
    ticketScopeHint: String,
    onCreateTicket: () -> Unit,
    onCreateProject: () -> Unit,
) {
    Div(
        attrs = {
            attr("id", "web-top-create-menu")
            style {
                props(
                    "position" to "fixed",
                    "top" to "44px",
                    "left" to if (compact) "54px" else "64px",
                    "width" to "220px",
                    "background" to Paper,
                    "color" to Ink,
                    "border" to "1px solid $Border",
                    "border-radius" to "8px",
                    "box-shadow" to "0 18px 60px rgba(20,22,28,0.18)",
                    "padding" to "8px",
                    "box-sizing" to "border-box",
                    "z-index" to "80",
                    "display" to "grid",
                    "gap" to "4px",
                )
            }
        }
    ) {
        Button(
            attrs = {
                attr("id", "web-top-create-ticket-button")
                onClick { onCreateTicket() }
                style { dropdownOptionButton() }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "gap" to "2px",
                            "text-align" to "left",
                            "justify-items" to "start",
                        )
                    }
                }
            ) {
                Span(
                    attrs = {
                        style {
                            props("font-size" to "14px", "font-weight" to "700", "color" to Ink)
                        }
                    }
                ) {
                    Text("New ticket")
                }
                Span(
                    attrs = {
                        style {
                            props("font-size" to "12px", "line-height" to "1.4", "color" to Muted)
                        }
                    }
                ) {
                    Text(ticketScopeHint)
                }
            }
        }
        Button(
            attrs = {
                attr("id", "web-top-create-project-button")
                onClick { onCreateProject() }
                style { dropdownOptionButton() }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "gap" to "2px",
                            "text-align" to "left",
                            "justify-items" to "start",
                        )
                    }
                }
            ) {
                Span(
                    attrs = {
                        style {
                            props("font-size" to "14px", "font-weight" to "700", "color" to Ink)
                        }
                    }
                ) {
                    Text("New project")
                }
                Span(
                    attrs = {
                        style {
                            props("font-size" to "12px", "line-height" to "1.4", "color" to Muted)
                        }
                    }
                ) {
                    Text("Add a board, calendar, and ticket space")
                }
            }
        }
    }
}

@Composable
private fun WorkspaceSwitcherMenu(
    compact: Boolean,
    currentWorkspaceId: Kairos.WorkspaceId,
    availableWorkspaceAccess: List<KairosWorkspaceAccess>,
    onWorkspaceChanged: (Kairos.WorkspaceId) -> Unit,
    onOpenWorkspaceSettings: () -> Unit,
    onOpenWorkspaceShare: () -> Unit,
) {
    var expanded by
        remember(currentWorkspaceId.value, availableWorkspaceAccess) { mutableStateOf(false) }
    val currentAccess =
        availableWorkspaceAccess.firstOrNull { access -> access.workspace.id == currentWorkspaceId }
            ?: availableWorkspaceAccess.firstOrNull()
    val canShareWorkspace = currentAccess?.role == Kairos.WorkspaceMembershipRole.Owner
    Div(
        attrs = {
            style {
                props(
                    "position" to "relative",
                    "display" to "inline-flex",
                    "align-items" to "center",
                    "max-width" to "220px",
                )
            }
        }
    ) {
        Button(
            attrs = {
                attr("id", "web-top-workspace-menu-button")
                onClick { expanded = !expanded }
                style {
                    props(
                        "height" to "36px",
                        "max-width" to "220px",
                        "border" to "1px solid rgba(255,255,255,0.18)",
                        "border-radius" to "8px",
                        "background" to "rgba(255,255,255,0.08)",
                        "color" to Paper,
                        "padding" to "0 12px",
                        "font-size" to "13px",
                        "font-weight" to "600",
                        "display" to "inline-flex",
                        "align-items" to "center",
                        "gap" to "10px",
                        "cursor" to "pointer",
                    )
                }
            }
        ) {
            Span(
                attrs = {
                    style {
                        props(
                            "overflow" to "hidden",
                            "text-overflow" to "ellipsis",
                            "white-space" to "nowrap",
                            "max-width" to "140px",
                        )
                    }
                }
            ) {
                Text(currentAccess?.workspace?.name ?: "Workspace")
            }
            currentAccess?.let { access -> WorkspaceRoleBadge(access.role, muted = true) }
            Span { Text("▾") }
        }
        if (expanded) {
            Div(
                attrs = {
                    onClick { expanded = false }
                    style {
                        props(
                            "position" to "fixed",
                            "inset" to "0",
                            "background" to "transparent",
                            "z-index" to "79",
                        )
                    }
                }
            )
            Div(
                attrs = {
                    attr("id", "web-top-workspace-menu")
                    style {
                        props(
                            "position" to "fixed",
                            "top" to "44px",
                            "right" to if (compact) "58px" else "64px",
                            "width" to "280px",
                            "background" to Paper,
                            "color" to Ink,
                            "border" to "1px solid $Border",
                            "border-radius" to "8px",
                            "box-shadow" to "0 18px 60px rgba(20,22,28,0.18)",
                            "padding" to "8px",
                            "box-sizing" to "border-box",
                            "z-index" to "80",
                            "display" to "grid",
                            "gap" to "4px",
                        )
                    }
                }
            ) {
                availableWorkspaceAccess.forEach { access ->
                    Button(
                        attrs = {
                            attr("id", "web-top-workspace-option-${access.workspace.id.value}")
                            onClick {
                                expanded = false
                                onWorkspaceChanged(access.workspace.id)
                            }
                            style {
                                props(
                                    "width" to "100%",
                                    "border" to "0",
                                    "border-radius" to "8px",
                                    "background" to
                                        if (access.workspace.id == currentWorkspaceId) "#f3f5f8"
                                        else "transparent",
                                    "padding" to "10px 12px",
                                    "display" to "flex",
                                    "align-items" to "center",
                                    "justify-content" to "space-between",
                                    "gap" to "12px",
                                    "cursor" to "pointer",
                                    "text-align" to "left",
                                )
                            }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "flex",
                                        "align-items" to "center",
                                        "gap" to "10px",
                                        "min-width" to "0",
                                    )
                                }
                            }
                        ) {
                            Span(
                                attrs = {
                                    style {
                                        props(
                                            "width" to "16px",
                                            "color" to
                                                if (access.workspace.id == currentWorkspaceId) Blue
                                                else "transparent",
                                            "font-weight" to "700",
                                        )
                                    }
                                }
                            ) {
                                Text("✓")
                            }
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "min-width" to "0",
                                            "overflow" to "hidden",
                                            "text-overflow" to "ellipsis",
                                            "white-space" to "nowrap",
                                            "font-size" to "14px",
                                            "font-weight" to "600",
                                        )
                                    }
                                }
                            ) {
                                Text(access.workspace.name)
                            }
                        }
                        WorkspaceRoleBadge(access.role, muted = false)
                    }
                }
                Div(
                    attrs = {
                        style {
                            props("height" to "1px", "background" to Border, "margin" to "6px 0")
                        }
                    }
                )
                if (canShareWorkspace) {
                    Button(
                        attrs = {
                            attr("id", "web-top-workspace-share-button")
                            onClick {
                                expanded = false
                                onOpenWorkspaceShare()
                            }
                            style { workspaceMenuActionStyle() }
                        }
                    ) {
                        Text("Invite people")
                        Text("+")
                    }
                }
                Button(
                    attrs = {
                        attr("id", "web-top-workspace-settings-button")
                        onClick {
                            expanded = false
                            onOpenWorkspaceSettings()
                        }
                        style { workspaceMenuActionStyle() }
                    }
                ) {
                    Text(if (canShareWorkspace) "Manage members" else "View members")
                    Text("→")
                }
            }
        }
    }
}

@Composable
private fun WorkspaceRoleBadge(role: Kairos.WorkspaceMembershipRole, muted: Boolean) {
    val background =
        when {
            muted && role == Kairos.WorkspaceMembershipRole.Owner -> "rgba(255,255,255,0.14)"
            muted -> "rgba(255,255,255,0.08)"
            role == Kairos.WorkspaceMembershipRole.Owner -> "#fef1cf"
            else -> "#eef1f5"
        }
    val color =
        when {
            muted -> Paper
            role == Kairos.WorkspaceMembershipRole.Owner -> Ink
            else -> Muted
        }
    Span(
        attrs = {
            style {
                props(
                    "display" to "inline-flex",
                    "align-items" to "center",
                    "height" to "22px",
                    "padding" to "0 8px",
                    "border-radius" to "999px",
                    "background" to background,
                    "color" to color,
                    "font-size" to "12px",
                    "font-weight" to "700",
                    "letter-spacing" to "0",
                )
            }
        }
    ) {
        Text(if (role == Kairos.WorkspaceMembershipRole.Owner) "Owner" else "Member")
    }
}

private fun StyleScope.workspaceMenuActionStyle() {
    props(
        "width" to "100%",
        "border" to "0",
        "border-radius" to "8px",
        "background" to "transparent",
        "padding" to "10px 12px",
        "display" to "flex",
        "align-items" to "center",
        "justify-content" to "space-between",
        "gap" to "12px",
        "cursor" to "pointer",
        "text-align" to "left",
        "font-size" to "14px",
        "font-weight" to "600",
        "color" to Ink,
    )
}

@Composable
private fun InitialLoadScreen() {
    Div(
        attrs = {
            style {
                props(
                    "min-height" to "100vh",
                    "display" to "flex",
                    "align-items" to "center",
                    "justify-content" to "center",
                    "padding" to "24px",
                    "background" to "linear-gradient(180deg, #f6f7f8 0%, #eef1f5 100%)",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "width" to "min(420px, 100%)",
                        "border-radius" to "18px",
                        "border" to "1px solid rgba(18,22,33,0.08)",
                        "background" to Paper,
                        "padding" to "28px",
                        "box-shadow" to "0 18px 48px rgba(18,22,33,0.08)",
                        "display" to "grid",
                        "gap" to "12px",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style { props("font-size" to "28px", "font-weight" to "800", "color" to Ink) }
                }
            ) {
                Text("Kairos")
            }
            Div(
                attrs = {
                    style { props("font-size" to "15px", "line-height" to "1.5", "color" to Muted) }
                }
            ) {
                Text("Checking your session…")
            }
        }
    }
}

@Composable
private fun ServiceUnavailableScreen(message: String, onRetry: () -> Unit) {
    Div(
        attrs = {
            style {
                props(
                    "min-height" to "100vh",
                    "display" to "flex",
                    "align-items" to "center",
                    "justify-content" to "center",
                    "padding" to "24px",
                    "background" to "linear-gradient(180deg, #f6f7f8 0%, #eef1f5 100%)",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "width" to "min(440px, 100%)",
                        "border-radius" to "18px",
                        "border" to "1px solid rgba(18,22,33,0.08)",
                        "background" to Paper,
                        "padding" to "28px",
                        "box-shadow" to "0 18px 48px rgba(18,22,33,0.08)",
                        "display" to "grid",
                        "gap" to "16px",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "font-size" to "28px",
                            "font-weight" to "800",
                            "line-height" to "1.15",
                            "color" to Ink,
                        )
                    }
                }
            ) {
                Text("Kairos is temporarily unavailable")
            }
            Div(
                attrs = {
                    style { props("font-size" to "15px", "line-height" to "1.5", "color" to Muted) }
                }
            ) {
                Text(message)
            }
            Button(
                attrs = {
                    onClick { onRetry() }
                    style { primaryButton(enabled = true) }
                }
            ) {
                Text("Try again")
            }
        }
    }
}

@Composable
private fun AuthRequiredScreen(
    compact: Boolean,
    submissionState: AuthSubmissionState,
    onSignIn: (String, String) -> Unit,
    onSignUp: (String, String, String, String) -> Unit,
    onDraftChanged: () -> Unit = {},
    onOpenDownloads: () -> Unit,
) {
    var mode by remember { mutableStateOf(AuthScreenMode.SignIn) }
    var signInUsername by remember { mutableStateOf("") }
    var signInPassword by remember { mutableStateOf("") }
    var signInPasswordVisible by remember { mutableStateOf(false) }
    var signUpDisplayName by remember { mutableStateOf("") }
    var signUpWorkspaceName by remember { mutableStateOf("") }
    var signUpUsername by remember { mutableStateOf("") }
    var signUpPassword by remember { mutableStateOf("") }
    var signUpConfirmPassword by remember { mutableStateOf("") }
    var signUpPasswordVisible by remember { mutableStateOf(false) }
    var signUpConfirmVisible by remember { mutableStateOf(false) }
    var signUpStep by remember { mutableStateOf(SignUpStep.Account) }
    var signInAttempted by remember { mutableStateOf(false) }
    var signUpAccountAttempted by remember { mutableStateOf(false) }
    var signUpWorkspaceAttempted by remember { mutableStateOf(false) }

    val activeError =
        (submissionState as? AuthSubmissionState.Error)?.takeIf { it.mode == mode }?.message
    val isLoading = (submissionState as? AuthSubmissionState.Loading)?.mode == mode
    val signInPasswordError =
        activeError?.takeIf {
            mode == AuthScreenMode.SignIn && it.contains("password", ignoreCase = true)
        }
    val signUpValidationMessage =
        when {
            signUpPassword.isNotEmpty() && signUpPassword.length < 8 -> "Use at least 8 characters."
            signUpConfirmPassword.isNotEmpty() && signUpPassword != signUpConfirmPassword ->
                "Passwords must match."
            else -> null
        }
    val signInEnabled = !isLoading
    val signInStepHint =
        when {
            !signInAttempted -> null
            signInUsername.isBlank() && signInPassword.isBlank() ->
                "Enter your account credentials to continue."
            signInUsername.isBlank() -> "Enter your username to continue."
            signInPassword.isBlank() -> "Enter your password to continue."
            else -> null
        }
    val signUpAccountStepEnabled =
        !isLoading &&
            signUpDisplayName.trim().isNotEmpty() &&
            signUpUsername.trim().isNotEmpty() &&
            signUpPassword.length >= 8 &&
            signUpPassword == signUpConfirmPassword
    val signUpEnabled = !isLoading && signUpWorkspaceName.trim().isNotEmpty()
    val signUpAccountStepHint =
        when {
            !signUpAccountAttempted -> null
            signUpDisplayName.isBlank() -> "Add your name to continue."
            signUpUsername.isBlank() -> "Pick a username to continue."
            signUpPassword.isBlank() -> "Add a password to continue."
            signUpConfirmPassword.isBlank() -> "Confirm the password to continue."
            signUpValidationMessage != null -> signUpValidationMessage
            else -> null
        }
    val signUpWorkspaceStepHint =
        if (signUpWorkspaceAttempted && signUpWorkspaceName.isBlank()) {
            "Name the workspace to finish creating the account."
        } else {
            null
        }

    LaunchedEffect(submissionState) {
        when (submissionState) {
            is AuthSubmissionState.Error -> {
                mode = submissionState.mode
                if (submissionState.mode == AuthScreenMode.SignIn) {
                    signInPassword = ""
                    signInPasswordVisible = false
                }
            }
            AuthSubmissionState.Idle,
            is AuthSubmissionState.Loading -> Unit
        }
    }

    Div(
        attrs = {
            attr("id", "web-auth-required-screen")
            style {
                props(
                    "flex" to "1",
                    "min-height" to "0",
                    "display" to "flex",
                    "align-items" to "flex-start",
                    "justify-content" to "center",
                    "padding" to if (compact) "24px 16px" else "40px 24px",
                    "box-sizing" to "border-box",
                    "background" to "linear-gradient(180deg, #f5f7fa 0%, #eef2f7 100%)",
                    "overflow-y" to "auto",
                    "overflow-x" to "hidden",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "width" to "min(460px, 100%)",
                        "display" to "grid",
                        "gap" to if (compact) "18px" else "20px",
                        "justify-items" to "stretch",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "justify-items" to if (compact) "start" else "center",
                            "gap" to "10px",
                            "padding" to if (compact) "0 4px" else "0",
                        )
                    }
                }
            ) {
                Div(
                    attrs = {
                        attr("id", "web-auth-brand")
                        style {
                            props(
                                "display" to "inline-flex",
                                "align-items" to "center",
                                "gap" to "12px",
                                "font-size" to "14px",
                                "font-weight" to "800",
                                "letter-spacing" to "0.08em",
                                "text-transform" to "uppercase",
                                "color" to Muted,
                            )
                        }
                    }
                ) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "width" to "40px",
                                    "height" to "40px",
                                    "border-radius" to "12px",
                                    "background" to Coral,
                                    "display" to "inline-flex",
                                    "align-items" to "center",
                                    "justify-content" to "center",
                                    "color" to Paper,
                                    "font-size" to "18px",
                                    "font-weight" to "900",
                                    "box-shadow" to "0 12px 24px rgba(242,106,87,0.24)",
                                )
                            }
                        }
                    ) {
                        Text("K")
                    }
                    Text("Kairos")
                }
                Div(
                    attrs = {
                        style {
                            props(
                                "font-size" to if (compact) "34px" else "38px",
                                "line-height" to "1.05",
                                "font-weight" to "800",
                                "color" to Ink,
                                "text-align" to if (compact) "left" else "center",
                            )
                        }
                    }
                ) {
                    Text(
                        when {
                            mode == AuthScreenMode.SignIn -> "Welcome back"
                            signUpStep == SignUpStep.Account -> "Create your account"
                            else -> "Name your first workspace"
                        }
                    )
                }
                Div(
                    attrs = {
                        style {
                            props(
                                "font-size" to "15px",
                                "line-height" to "1.5",
                                "color" to Muted,
                                "text-align" to if (compact) "left" else "center",
                                "max-width" to "420px",
                            )
                        }
                    }
                ) {
                    Text(
                        when {
                            mode == AuthScreenMode.SignIn -> "Sign in to your Kairos account."
                            signUpStep == SignUpStep.Account -> "This takes less than a minute."
                            else -> "Pick the workspace name you want to open first."
                        }
                    )
                }
            }

            Div(
                attrs = {
                    style {
                        props(
                            "background" to Paper,
                            "border" to "1px solid $Border",
                            "border-radius" to "16px",
                            "padding" to if (compact) "20px" else "24px",
                            "box-shadow" to "0 22px 60px rgba(20,22,28,0.10)",
                            "display" to "grid",
                            "gap" to "16px",
                        )
                    }
                }
            ) {
                if (mode == AuthScreenMode.SignUp && signUpStep == SignUpStep.Workspace) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "justify-content" to "space-between",
                                    "align-items" to "center",
                                    "gap" to "12px",
                                    "flex-wrap" to "wrap",
                                    "padding-bottom" to "4px",
                                )
                            }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "font-size" to "12px",
                                        "font-weight" to "800",
                                        "letter-spacing" to "0.06em",
                                        "text-transform" to "uppercase",
                                        "color" to Blue,
                                    )
                                }
                            }
                        ) {
                            Text("Step 2 of 2")
                        }
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "flex",
                                        "align-items" to "center",
                                        "gap" to "6px",
                                    )
                                }
                            }
                        ) {
                            repeat(2) { index ->
                                Div(
                                    attrs = {
                                        style {
                                            props(
                                                "width" to if (index == 1) "24px" else "18px",
                                                "height" to "6px",
                                                "border-radius" to "999px",
                                                "background" to if (index <= 1) Blue else "#d9deea",
                                            )
                                        }
                                    }
                                )
                            }
                        }
                    }
                } else {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "inline-flex",
                                    "gap" to "8px",
                                    "padding" to "4px",
                                    "border-radius" to "999px",
                                    "background" to "#f3f5f8",
                                    "align-self" to "start",
                                )
                            }
                        }
                    ) {
                        Button(
                            attrs = {
                                attr("id", "web-auth-mode-signin")
                                onClick {
                                    mode = AuthScreenMode.SignIn
                                    signUpStep = SignUpStep.Account
                                    signInAttempted = false
                                    onDraftChanged()
                                }
                                style { tabButton(mode == AuthScreenMode.SignIn) }
                            }
                        ) {
                            Text("Sign in")
                        }
                        Button(
                            attrs = {
                                attr("id", "web-auth-mode-signup")
                                onClick {
                                    mode = AuthScreenMode.SignUp
                                    signUpStep = SignUpStep.Account
                                    signUpAccountAttempted = false
                                    signUpWorkspaceAttempted = false
                                    onDraftChanged()
                                }
                                style { tabButton(mode == AuthScreenMode.SignUp) }
                            }
                        ) {
                            Text("Create account")
                        }
                    }
                }

                if (
                    activeError != null &&
                        (mode != AuthScreenMode.SignIn || signInPasswordError == null)
                ) {
                    Div(
                        attrs = {
                            attr("id", "web-auth-error-banner")
                            style {
                                props(
                                    "padding" to "12px 14px",
                                    "border" to "1px solid #f2b8b5",
                                    "border-radius" to "10px",
                                    "background" to "#fff1ef",
                                    "color" to "#a33d33",
                                    "font-size" to "14px",
                                    "font-weight" to "600",
                                    "line-height" to "1.4",
                                )
                            }
                        }
                    ) {
                        Text(activeError)
                    }
                }

                if (mode == AuthScreenMode.SignIn) {
                    AuthField(
                        label = "Username",
                        inputId = "web-auth-signin-username-input",
                        value = signInUsername,
                        placeholder = "jamie",
                        enabled = !isLoading,
                        onValueChanged = {
                            signInUsername = it
                            onDraftChanged()
                        },
                    )
                    AuthPasswordField(
                        label = "Password",
                        inputId = "web-auth-signin-password-input",
                        value = signInPassword,
                        visible = signInPasswordVisible,
                        enabled = !isLoading,
                        onVisibilityChanged = { signInPasswordVisible = it },
                        onValueChanged = {
                            signInPassword = it
                            onDraftChanged()
                        },
                    )
                    signInPasswordError?.let { message ->
                        Div(
                            attrs = {
                                attr("id", "web-auth-error-banner")
                                style {
                                    props(
                                        "padding" to "12px 14px",
                                        "border" to "1px solid #f2b8b5",
                                        "border-radius" to "10px",
                                        "background" to "#fff1ef",
                                        "color" to "#a33d33",
                                        "font-size" to "14px",
                                        "font-weight" to "600",
                                        "line-height" to "1.4",
                                    )
                                }
                            }
                        ) {
                            Text(message)
                        }
                    }
                    Div(
                        attrs = {
                            style {
                                props(
                                    "font-size" to "12px",
                                    "line-height" to "1.5",
                                    "color" to Muted,
                                )
                            }
                        }
                    ) {
                        Text("Need a password reset? Ask a workspace admin.")
                    }
                    Div(
                        attrs = {
                            style {
                                props(
                                    "font-size" to "12px",
                                    "line-height" to "1.5",
                                    "color" to Muted,
                                )
                            }
                        }
                    ) {
                        Text(
                            "Ask your workspace owner for access if you do not have an account yet."
                        )
                    }
                    signInStepHint?.let { hint ->
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "font-size" to "12px",
                                        "line-height" to "1.5",
                                        "color" to Muted,
                                    )
                                }
                            }
                        ) {
                            Text(hint)
                        }
                    }
                    Button(
                        attrs = {
                            attr("id", "web-auth-signin-submit")
                            onClick {
                                val trimmedUsername = signInUsername.trim()
                                if (trimmedUsername.isBlank() || signInPassword.isBlank()) {
                                    signInAttempted = true
                                    return@onClick
                                }
                                onSignIn(trimmedUsername, signInPassword)
                            }
                            style {
                                primaryButton(enabled = signInEnabled)
                                props(
                                    "width" to if (compact) "100%" else "auto",
                                    "justify-self" to if (compact) "stretch" else "start",
                                )
                            }
                        }
                    ) {
                        Text(if (isLoading) "Signing in…" else "Sign in")
                    }
                } else {
                    if (signUpStep == SignUpStep.Account) {
                        AuthField(
                            label = "Your name",
                            inputId = "web-auth-signup-display-name-input",
                            value = signUpDisplayName,
                            placeholder = "Jamie Staples",
                            enabled = !isLoading,
                            onValueChanged = {
                                signUpDisplayName = it
                                onDraftChanged()
                            },
                        )
                        AuthField(
                            label = "Username",
                            inputId = "web-auth-signup-username-input",
                            value = signUpUsername,
                            placeholder = "jamie",
                            enabled = !isLoading,
                            onValueChanged = {
                                signUpUsername = it
                                onDraftChanged()
                            },
                        )
                        AuthPasswordField(
                            label = "Password",
                            inputId = "web-auth-signup-password-input",
                            value = signUpPassword,
                            visible = signUpPasswordVisible,
                            enabled = !isLoading,
                            highlightError =
                                signUpPassword.isNotEmpty() && signUpPassword.length < 8,
                            errorMessage =
                                signUpValidationMessage?.takeIf { signUpPassword.length < 8 },
                            onVisibilityChanged = { signUpPasswordVisible = it },
                            onValueChanged = {
                                signUpPassword = it
                                onDraftChanged()
                            },
                        )
                        AuthPasswordField(
                            label = "Confirm password",
                            inputId = "web-auth-signup-confirm-password-input",
                            value = signUpConfirmPassword,
                            visible = signUpConfirmVisible,
                            enabled = !isLoading,
                            highlightError =
                                signUpConfirmPassword.isNotEmpty() &&
                                    signUpPassword != signUpConfirmPassword,
                            errorMessage =
                                signUpValidationMessage?.takeIf {
                                    signUpConfirmPassword.isNotEmpty() &&
                                        signUpPassword != signUpConfirmPassword
                                },
                            placeholder = "Confirm password",
                            onVisibilityChanged = { signUpConfirmVisible = it },
                            onValueChanged = {
                                signUpConfirmPassword = it
                                onDraftChanged()
                            },
                        )
                        signUpAccountStepHint?.let { message ->
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "font-size" to "12px",
                                            "line-height" to "1.5",
                                            "color" to Muted,
                                        )
                                    }
                                }
                            ) {
                                Text(message)
                            }
                        }
                        Button(
                            attrs = {
                                attr("id", "web-auth-signup-next")
                                if (!signUpAccountStepEnabled) disabled()
                                onClick {
                                    signUpAccountAttempted = true
                                    if (!signUpAccountStepEnabled) {
                                        return@onClick
                                    }
                                    signUpStep = SignUpStep.Workspace
                                }
                                style {
                                    primaryButton(enabled = signUpAccountStepEnabled)
                                    props(
                                        "width" to if (compact) "100%" else "auto",
                                        "justify-self" to if (compact) "stretch" else "start",
                                    )
                                }
                            }
                        ) {
                            Text("Continue")
                        }
                    } else {
                        AuthField(
                            label = "Workspace name",
                            inputId = "web-auth-signup-workspace-name-input",
                            value = signUpWorkspaceName,
                            placeholder = "Phoenix",
                            enabled = !isLoading,
                            helperText = "You can add more workspaces later.",
                            onValueChanged = {
                                signUpWorkspaceName = it
                                onDraftChanged()
                            },
                        )
                        signUpWorkspaceStepHint?.let { message ->
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "font-size" to "12px",
                                            "line-height" to "1.5",
                                            "color" to Muted,
                                        )
                                    }
                                }
                            ) {
                                Text(message)
                            }
                        }
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "grid",
                                        "gap" to "10px",
                                        "grid-template-columns" to
                                            if (compact) "1fr 1fr" else "120px 1fr",
                                        "align-items" to "center",
                                    )
                                }
                            }
                        ) {
                            Button(
                                attrs = {
                                    attr("id", "web-auth-signup-back")
                                    onClick {
                                        signUpWorkspaceAttempted = false
                                        signUpStep = SignUpStep.Account
                                    }
                                    style { secondaryButton() }
                                }
                            ) {
                                Text("Back")
                            }
                            Button(
                                attrs = {
                                    attr("id", "web-auth-signup-submit")
                                    if (!signUpEnabled) disabled()
                                    onClick {
                                        signUpWorkspaceAttempted = true
                                        if (!signUpEnabled) {
                                            return@onClick
                                        }
                                        onSignUp(
                                            signUpDisplayName.trim(),
                                            signUpUsername.trim(),
                                            signUpPassword,
                                            signUpWorkspaceName.trim(),
                                        )
                                    }
                                    style {
                                        primaryButton(enabled = signUpEnabled)
                                        props(
                                            "width" to if (compact) "100%" else "auto",
                                            "justify-self" to if (compact) "stretch" else "start",
                                        )
                                    }
                                }
                            ) {
                                Text(if (isLoading) "Creating account…" else "Create account")
                            }
                        }
                    }
                }

                Button(
                    attrs = {
                        attr("id", "web-auth-open-downloads")
                        onClick { onOpenDownloads() }
                        style {
                            props(
                                "justify-self" to "start",
                                "border" to "0",
                                "background" to "transparent",
                                "padding" to "0",
                                "color" to Blue,
                                "font-size" to "13px",
                                "font-weight" to "700",
                                "cursor" to "pointer",
                            )
                        }
                    }
                ) {
                    Text("Downloads")
                }
            }
        }
    }
}

private fun formatDownloadSize(bytes: Long): String =
    when {
        bytes >= 1024L * 1024L * 1024L ->
            "${((bytes / (1024.0 * 1024.0 * 1024.0)) * 10.0).roundToInt() / 10.0} GB"
        bytes >= 1024L * 1024L -> "${((bytes / (1024.0 * 1024.0)) * 10.0).roundToInt() / 10.0} MB"
        bytes >= 1024L -> "${((bytes / 1024.0) * 10.0).roundToInt() / 10.0} KB"
        else -> "$bytes B"
    }

@Composable
private fun DownloadsScreen(
    compact: Boolean,
    manifest: WebDownloadsManifest?,
    loading: Boolean,
    error: String?,
    onRetry: () -> Unit,
    onBackToSignIn: () -> Unit,
) {
    Div(
        attrs = {
            style {
                props(
                    "flex" to "1",
                    "min-height" to "0",
                    "display" to "flex",
                    "align-items" to "flex-start",
                    "justify-content" to "center",
                    "padding" to if (compact) "24px 16px" else "40px 24px",
                    "box-sizing" to "border-box",
                    "background" to "linear-gradient(180deg, #f5f7fa 0%, #eef2f7 100%)",
                    "overflow-y" to "auto",
                    "overflow-x" to "hidden",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style { props("width" to "min(960px, 100%)", "display" to "grid", "gap" to "18px") }
            }
        ) {
            Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
                Div(attrs = { style { props("font-size" to "40px", "font-weight" to "800") } }) {
                    Text("Downloads")
                }
                Div(
                    attrs = {
                        style {
                            props("font-size" to "15px", "line-height" to "1.6", "color" to Muted)
                        }
                    }
                ) {
                    Text(
                        "Download the Kairos CLI and native clients that have been published from the current release pipeline."
                    )
                }
                Button(
                    attrs = {
                        onClick { onBackToSignIn() }
                        style {
                            props(
                                "justify-self" to "start",
                                "border" to "0",
                                "background" to "transparent",
                                "padding" to "0",
                                "color" to Blue,
                                "font-size" to "13px",
                                "font-weight" to "700",
                                "cursor" to "pointer",
                            )
                        }
                    }
                ) {
                    Text("Back to sign in")
                }
            }
            DownloadsCatalogPanel(
                manifest = manifest,
                loading = loading,
                error = error,
                compact = compact,
                onRetry = onRetry,
            )
        }
    }
}

@Composable
private fun DownloadsCatalogPanel(
    manifest: WebDownloadsManifest?,
    loading: Boolean,
    error: String?,
    compact: Boolean,
    onRetry: () -> Unit,
) {
    val artifacts = manifest?.artifacts.orEmpty()
    val macArtifacts =
        artifacts
            .filter { artifact -> artifact.platform == "macOS" }
            .sortedWith(
                compareBy<WebDownloadArtifact> { artifact -> artifact.downloadPriority() }
                    .thenBy { it.filename }
            )
    val androidArtifacts =
        artifacts
            .filter { artifact -> artifact.platform == "Android" }
            .sortedBy { artifact -> artifact.filename }
    val otherArtifacts =
        artifacts
            .filter { artifact -> artifact.platform != "macOS" && artifact.platform != "Android" }
            .sortedBy { artifact -> artifact.filename }
    Div(
        attrs = { style { props("display" to "grid", "gap" to if (compact) "24px" else "16px") } }
    ) {
        when {
            loading -> {
                PanelCard("Downloads") { MutedText("Loading published artifacts…") }
            }
            error != null -> {
                PanelCard("Downloads") {
                    MutedText(error)
                    Button(
                        attrs = {
                            onClick { onRetry() }
                            style {
                                secondaryButton()
                                props("justify-self" to "start")
                            }
                        }
                    ) {
                        Text("Retry")
                    }
                }
            }
            artifacts.isEmpty() -> {
                PanelCard("Downloads") {
                    MutedText("No packaged downloads have been published yet.")
                }
            }
            else -> {
                if (macArtifacts.isNotEmpty()) {
                    DownloadPlatformSection(
                        title = "macOS",
                        subtitle = "Apple Silicon builds",
                        artifacts = macArtifacts,
                        compact = compact,
                    )
                }
                if (androidArtifacts.isNotEmpty()) {
                    DownloadPlatformSection(
                        title = "Android",
                        subtitle = "Sideload APK",
                        artifacts = androidArtifacts,
                        compact = compact,
                    )
                }
                if (otherArtifacts.isNotEmpty()) {
                    DownloadPlatformSection(
                        title = "Other builds",
                        subtitle = "Additional packaged clients",
                        artifacts = otherArtifacts,
                        compact = compact,
                    )
                }
                DownloadComingSoonSection(compact)
            }
        }
    }
}

@Composable
private fun AdminConsoleView(
    snapshot: KairosAdminSnapshot?,
    loading: Boolean,
    error: String?,
    notice: String?,
    savingUserId: Kairos.UserId?,
    compact: Boolean,
    onRetry: () -> Unit,
    onUpdateUserRole: (Kairos.UserId, Kairos.UserRole) -> Unit,
) {
    val users = snapshot?.users.orEmpty()
    val workspaces = snapshot?.workspaces.orEmpty()
    Div(
        attrs = {
            style {
                props(
                    "display" to "grid",
                    "gap" to if (compact) "16px" else "20px",
                    "padding" to if (compact) "18px 16px 28px" else "24px",
                    "box-sizing" to "border-box",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "grid",
                        "grid-template-columns" to
                            if (compact) "1fr" else "repeat(4, minmax(0, 1fr))",
                        "gap" to "12px",
                    )
                }
            }
        ) {
            SummaryCard("Users", users.size.toString(), compact = compact)
            SummaryCard(
                "Admins",
                users.count { row -> row.user.role == Kairos.UserRole.Admin }.toString(),
                compact = compact,
            )
            SummaryCard("Workspaces", workspaces.size.toString(), compact = compact)
            SummaryCard(
                "Agents",
                users.count { row -> row.user.kind == Kairos.UserKind.Agent }.toString(),
                compact = compact,
            )
        }
        notice?.let { PanelCard("Admin notice") { MutedText(it) } }
        when {
            loading && snapshot == null -> {
                PanelCard("Admin console") { MutedText("Loading users and workspaces…") }
            }
            error != null && snapshot == null -> {
                PanelCard("Admin console") {
                    MutedText(error)
                    Button(
                        attrs = {
                            onClick { onRetry() }
                            style {
                                secondaryButton()
                                props("justify-self" to "start")
                            }
                        }
                    ) {
                        Text("Retry")
                    }
                }
            }
            else -> {
                error?.let { PanelCard("Admin console") { MutedText(it) } }
                PanelCard("Users") {
                    if (users.isEmpty()) {
                        MutedText("No users found.")
                    } else {
                        Div(attrs = { style { props("display" to "grid", "gap" to "10px") } }) {
                            users.forEach { row ->
                                val accessSummary =
                                    row.workspaceAccess.joinToString(", ") { access ->
                                        "${access.workspace.name} (${access.role.name})"
                                    }
                                Div(
                                    attrs = {
                                        style {
                                            props(
                                                "display" to "grid",
                                                "grid-template-columns" to
                                                    if (compact) "1fr" else "minmax(0, 1.4fr) auto",
                                                "gap" to "14px",
                                                "padding" to if (compact) "14px" else "16px",
                                                "border" to "1px solid $Border",
                                                "border-radius" to "8px",
                                                "background" to "#fbfcfd",
                                            )
                                        }
                                    }
                                ) {
                                    Div(
                                        attrs = {
                                            style { props("display" to "grid", "gap" to "10px") }
                                        }
                                    ) {
                                        Div(
                                            attrs = {
                                                style {
                                                    props(
                                                        "display" to "flex",
                                                        "align-items" to "center",
                                                        "gap" to "10px",
                                                        "flex-wrap" to "wrap",
                                                    )
                                                }
                                            }
                                        ) {
                                            Avatar(row.user, 32)
                                            Div(
                                                attrs = {
                                                    style {
                                                        props(
                                                            "display" to "grid",
                                                            "gap" to "2px",
                                                            "min-width" to "0",
                                                        )
                                                    }
                                                }
                                            ) {
                                                Div(
                                                    attrs = {
                                                        style {
                                                            props(
                                                                "font-size" to "15px",
                                                                "font-weight" to "700",
                                                                "color" to Ink,
                                                            )
                                                        }
                                                    }
                                                ) {
                                                    Text(row.user.displayName)
                                                }
                                                MutedText(
                                                    listOfNotNull(
                                                            row.username?.let { username ->
                                                                "@$username"
                                                            },
                                                            row.user.email,
                                                        )
                                                        .joinToString(" • ")
                                                )
                                            }
                                            MetaChip(
                                                if (row.user.role == Kairos.UserRole.Admin) "Admin"
                                                else "User",
                                                background =
                                                    if (row.user.role == Kairos.UserRole.Admin)
                                                        "#eef3ff"
                                                    else "#f3f4f6",
                                                textColor =
                                                    if (row.user.role == Kairos.UserRole.Admin) Blue
                                                    else Ink,
                                            )
                                            if (row.user.kind == Kairos.UserKind.Agent) {
                                                MetaChip(
                                                    "Agent",
                                                    background = "#eef8f6",
                                                    textColor = Teal,
                                                )
                                            }
                                        }
                                        Div(
                                            attrs = {
                                                style {
                                                    props(
                                                        "display" to "grid",
                                                        "grid-template-columns" to
                                                            if (compact) "1fr"
                                                            else "repeat(3, minmax(0, 1fr))",
                                                        "gap" to "8px",
                                                    )
                                                }
                                            }
                                        ) {
                                            SummaryCard(
                                                "Workspaces",
                                                row.workspaceAccess.size.toString(),
                                                detail =
                                                    accessSummary.ifBlank { "No workspace access" },
                                                compact = true,
                                            )
                                            SummaryCard(
                                                "Owned agents",
                                                row.ownedAgentCount.toString(),
                                                compact = true,
                                            )
                                            SummaryCard(
                                                "Last seen",
                                                row.lastAuthenticatedAt?.let(::formatUiDate)
                                                    ?: "Never",
                                                compact = true,
                                            )
                                        }
                                    }
                                    Div(
                                        attrs = {
                                            style {
                                                props(
                                                    "display" to "flex",
                                                    "justify-content" to
                                                        if (compact) "flex-start" else "flex-end",
                                                    "align-items" to "center",
                                                )
                                            }
                                        }
                                    ) {
                                        if (row.user.kind == Kairos.UserKind.Human) {
                                            Button(
                                                attrs = {
                                                    attr(
                                                        "id",
                                                        "web-admin-role-toggle-${row.user.id.value}",
                                                    )
                                                    if (savingUserId == row.user.id) disabled()
                                                    onClick {
                                                        onUpdateUserRole(
                                                            row.user.id,
                                                            if (
                                                                row.user.role ==
                                                                    Kairos.UserRole.Admin
                                                            ) {
                                                                Kairos.UserRole.User
                                                            } else {
                                                                Kairos.UserRole.Admin
                                                            },
                                                        )
                                                    }
                                                    style {
                                                        secondaryButton()
                                                        props("min-width" to "124px")
                                                    }
                                                }
                                            ) {
                                                Text(
                                                    if (savingUserId == row.user.id) {
                                                        "Saving…"
                                                    } else if (
                                                        row.user.role == Kairos.UserRole.Admin
                                                    ) {
                                                        "Make user"
                                                    } else {
                                                        "Make admin"
                                                    }
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                PanelCard("Workspaces") {
                    if (workspaces.isEmpty()) {
                        MutedText("No workspaces found.")
                    } else {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "grid",
                                        "grid-template-columns" to
                                            if (compact) "1fr" else "repeat(2, minmax(0, 1fr))",
                                        "gap" to "12px",
                                    )
                                }
                            }
                        ) {
                            workspaces.forEach { row ->
                                Div(
                                    attrs = {
                                        style {
                                            props(
                                                "display" to "grid",
                                                "gap" to "10px",
                                                "padding" to "16px",
                                                "border" to "1px solid $Border",
                                                "border-radius" to "8px",
                                                "background" to "#fbfcfd",
                                            )
                                        }
                                    }
                                ) {
                                    Div(
                                        attrs = {
                                            style {
                                                props(
                                                    "display" to "flex",
                                                    "justify-content" to "space-between",
                                                    "align-items" to "center",
                                                    "gap" to "12px",
                                                )
                                            }
                                        }
                                    ) {
                                        Div(
                                            attrs = {
                                                style {
                                                    props(
                                                        "font-size" to "16px",
                                                        "font-weight" to "700",
                                                        "color" to Ink,
                                                    )
                                                }
                                            }
                                        ) {
                                            Text(row.workspace.name)
                                        }
                                        CountBadge(row.memberCount.toString())
                                    }
                                    Div(
                                        attrs = {
                                            style {
                                                props(
                                                    "display" to "grid",
                                                    "grid-template-columns" to
                                                        "repeat(3, minmax(0, 1fr))",
                                                    "gap" to "8px",
                                                )
                                            }
                                        }
                                    ) {
                                        SummaryCard(
                                            "Owners",
                                            row.ownerCount.toString(),
                                            compact = true,
                                        )
                                        SummaryCard(
                                            "Projects",
                                            row.projectCount.toString(),
                                            compact = true,
                                        )
                                        SummaryCard(
                                            "Tickets",
                                            row.ticketCount.toString(),
                                            compact = true,
                                        )
                                        SummaryCard(
                                            "Objectives",
                                            row.objectiveCount.toString(),
                                            compact = true,
                                        )
                                        SummaryCard("KPIs", row.kpiCount.toString(), compact = true)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun DownloadPlatformSection(
    title: String,
    subtitle: String,
    artifacts: List<WebDownloadArtifact>,
    compact: Boolean,
) {
    val heading = if (compact) "$title ($subtitle)" else title
    Div(attrs = { style { props("display" to "grid", "gap" to "10px") } }) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "baseline",
                        "justify-content" to "space-between",
                        "gap" to "12px",
                        "flex-wrap" to "wrap",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style { props("font-size" to "18px", "font-weight" to "800", "color" to Ink) }
                }
            ) {
                Text(heading)
            }
            if (!compact) {
                Div(
                    attrs = {
                        style {
                            props("font-size" to "13px", "color" to Muted, "font-weight" to "700")
                        }
                    }
                ) {
                    Text(subtitle)
                }
            }
        }
        Div(
            attrs = {
                style {
                    props(
                        "display" to "grid",
                        "gap" to "14px",
                        "grid-template-columns" to
                            if (compact) "1fr" else "repeat(auto-fit, minmax(280px, 1fr))",
                    )
                }
            }
        ) {
            artifacts.forEach { artifact -> DownloadArtifactCard(artifact) }
        }
    }
}

@Composable
private fun DownloadArtifactCard(artifact: WebDownloadArtifact) {
    val recommended = artifact.kind == "desktop"
    val extension = artifact.filename.substringAfterLast(".").uppercase()
    PanelCard(
        title = artifact.downloadTitle(),
        tone = if (recommended) WebPanelTone.Primary else WebPanelTone.Secondary,
    ) {
        Div(attrs = { style { props("display" to "grid", "gap" to "12px") } }) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "gap" to "8px",
                            "flex-wrap" to "wrap",
                            "align-items" to "center",
                        )
                    }
                }
            ) {
                if (recommended) {
                    Chip("Recommended", "#e8f7f0", "#087443")
                }
                Chip(extension, "#eef7ff", Blue)
                Chip(formatDownloadSize(artifact.sizeBytes), "#eef2f7", Ink)
            }
            Div(
                attrs = {
                    style { props("font-size" to "15px", "line-height" to "1.55", "color" to Ink) }
                }
            ) {
                Text(artifact.downloadDescription())
            }
            Div(
                attrs = {
                    style { props("font-size" to "13px", "line-height" to "1.5", "color" to Muted) }
                }
            ) {
                Text(artifact.downloadMeta())
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "gap" to "6px",
                            "padding" to "10px",
                            "background" to "#f7f8fb",
                            "border" to "1px solid #e6e9ef",
                            "border-radius" to "8px",
                        )
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            props(
                                "font-size" to "11px",
                                "font-weight" to "800",
                                "color" to Muted,
                                "text-transform" to "uppercase",
                            )
                        }
                    }
                ) {
                    Text("SHA256")
                }
                Div(
                    attrs = {
                        style {
                            props(
                                "font-family" to "ui-monospace, SFMono-Regular, Menlo, monospace",
                                "font-size" to "12px",
                                "line-height" to "1.45",
                                "color" to Ink,
                                "overflow-wrap" to "anywhere",
                            )
                        }
                    }
                ) {
                    Text(artifact.sha256)
                }
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "gap" to "10px",
                            "flex-wrap" to "wrap",
                            "align-items" to "center",
                        )
                    }
                }
            ) {
                Button(
                    attrs = {
                        onClick { window.location.href = artifact.url }
                        style { primaryButton(enabled = true) }
                    }
                ) {
                    Text(artifact.downloadButtonLabel())
                }
                Button(
                    attrs = {
                        onClick { copyTextToClipboard(artifact.sha256) }
                        style { secondaryButton() }
                    }
                ) {
                    Text("Copy SHA256")
                }
            }
        }
    }
}

@Composable
private fun DownloadComingSoonSection(compact: Boolean) {
    Div(attrs = { style { props("display" to "grid", "gap" to "10px") } }) {
        Div(
            attrs = {
                style { props("font-size" to "18px", "font-weight" to "800", "color" to Ink) }
            }
        ) {
            Text("More platforms")
        }
        Div(
            attrs = {
                style {
                    props(
                        "display" to "grid",
                        "gap" to "14px",
                        "grid-template-columns" to
                            if (compact) "1fr" else "repeat(auto-fit, minmax(220px, 1fr))",
                    )
                }
            }
        ) {
            listOf(
                    "Windows" to "EXE build",
                    "Linux" to "Debian package",
                    "Intel macOS" to "DMG build",
                )
                .forEach { (platform, packageType) ->
                    Div(
                        attrs = {
                            style {
                                props(
                                    "background" to "#fbfcfd",
                                    "border" to "1px dashed #ccd3dc",
                                    "border-radius" to "8px",
                                    "padding" to "16px",
                                    "display" to "grid",
                                    "gap" to "6px",
                                )
                            }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "font-size" to "15px",
                                        "font-weight" to "800",
                                        "color" to Ink,
                                    )
                                }
                            }
                        ) {
                            Text(platform)
                        }
                        Div(attrs = { style { props("font-size" to "13px", "color" to Muted) } }) {
                            Text("$packageType pending a matching build runner.")
                        }
                    }
                }
        }
    }
}

private fun WebDownloadArtifact.downloadPriority(): Int =
    when (kind) {
        "desktop" -> 0
        "cli" -> 1
        "cli-jar" -> 2
        "desktop-jar" -> 3
        "android" -> 4
        else -> 5
    }

private fun WebDownloadArtifact.downloadTitle(): String =
    when (kind) {
        "desktop" -> "Desktop app"
        "cli" -> "CLI"
        "cli-jar" -> "CLI jar"
        "desktop-jar" -> "Desktop jar"
        "android" -> "Android app"
        else -> title
    }

private fun WebDownloadArtifact.downloadDescription(): String =
    when (kind) {
        "desktop" -> "Install with the macOS disk image. This is the normal choice for the GUI app."
        "cli" -> "Unzip the terminal client and put the kairos binary on your PATH."
        "cli-jar" -> "Run the terminal client with Java 21 or newer using java -jar."
        "desktop-jar" -> "Run the desktop client with Java 21 or newer using java -jar."
        "android" ->
            "Install the APK directly on Android. Sideloading must be enabled on the device."
        else -> description
    }

private fun WebDownloadArtifact.downloadButtonLabel(): String =
    when (kind) {
        "desktop" -> "Download DMG"
        "cli" -> "Download CLI"
        "cli-jar" -> "Download CLI JAR"
        "desktop-jar" -> "Download JAR"
        "android" -> "Download APK"
        else -> "Download"
    }

private fun WebDownloadArtifact.downloadMeta(): String {
    val versionPart = version?.takeIf { it.isNotBlank() }?.let { "v$it" }
    val datePart = releasedAt?.substringBefore("T")?.takeIf { it.isNotBlank() }
    return listOfNotNull(platform, architecture, versionPart, datePart, filename)
        .joinToString(" - ")
}

@Composable
private fun AuthField(
    label: String,
    inputId: String,
    value: String,
    placeholder: String,
    enabled: Boolean,
    helperText: String? = null,
    highlightError: Boolean = false,
    onValueChanged: (String) -> Unit,
) {
    Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
        Div(
            attrs = {
                style { props("font-size" to "13px", "font-weight" to "700", "color" to Ink) }
            }
        ) {
            Text(label)
        }
        Input(
            type = InputType.Text,
            attrs = {
                attr("id", inputId)
                value(value)
                onInput { onValueChanged(it.value) }
                attr("placeholder", placeholder)
                if (!enabled) disabled()
                style {
                    formInputStyle()
                    if (highlightError) {
                        props("border" to "1px solid #df6a62", "background" to "#fff8f7")
                    }
                }
            },
        )
        helperText?.let { helper ->
            Div(
                attrs = {
                    style { props("font-size" to "12px", "line-height" to "1.5", "color" to Muted) }
                }
            ) {
                Text(helper)
            }
        }
    }
}

@Composable
private fun AuthPasswordField(
    label: String,
    inputId: String,
    value: String,
    visible: Boolean,
    enabled: Boolean,
    highlightError: Boolean = false,
    errorMessage: String? = null,
    placeholder: String = "Password",
    toggleLabel: String = if (visible) "Hide" else "Show",
    onVisibilityChanged: (Boolean) -> Unit,
    onValueChanged: (String) -> Unit,
) {
    Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
        Div(
            attrs = {
                style { props("font-size" to "13px", "font-weight" to "700", "color" to Ink) }
            }
        ) {
            Text(label)
        }
        Div(attrs = { style { props("position" to "relative") } }) {
            Input(
                type = if (visible) InputType.Text else InputType.Password,
                attrs = {
                    attr("id", inputId)
                    value(value)
                    onInput { onValueChanged(it.value) }
                    attr("placeholder", placeholder)
                    if (!enabled) disabled()
                    style {
                        formInputStyle()
                        props(
                            "padding-right" to "88px",
                            "border" to
                                if (highlightError) "1px solid #df6a62" else "1px solid $Border",
                            "background" to if (highlightError) "#fff8f7" else Paper,
                        )
                    }
                },
            )
            Button(
                attrs = {
                    onClick { onVisibilityChanged(!visible) }
                    if (!enabled) disabled()
                    style {
                        props(
                            "position" to "absolute",
                            "right" to "6px",
                            "top" to "50%",
                            "transform" to "translateY(-50%)",
                            "min-width" to "48px",
                            "height" to "32px",
                            "border" to "0",
                            "border-radius" to "8px",
                            "background" to "transparent",
                            "padding" to "0 10px",
                            "font-size" to "12px",
                            "font-weight" to "700",
                            "color" to Blue,
                            "cursor" to if (enabled) "pointer" else "not-allowed",
                        )
                    }
                }
            ) {
                Text(toggleLabel)
            }
        }
        errorMessage?.let { message ->
            Div(
                attrs = {
                    style {
                        props("font-size" to "12px", "line-height" to "1.5", "color" to "#b4453f")
                    }
                }
            ) {
                Text(message)
            }
        }
    }
}

@Composable
private fun SessionModal(
    compact: Boolean,
    authenticatedUser: Kairos.User?,
    sessionInfo: KairosSessionInfo?,
    activeTab: SessionModalTab,
    agentSubaccounts: List<KairosAgentSubaccount>,
    agentSubaccountsLoading: Boolean,
    agentSubaccountsNotice: String?,
    agentSubaccountsError: String?,
    savingAgentName: Kairos.UserId?,
    savedAgentName: Kairos.UserId?,
    savingWorkspaceAccess: WebAgentWorkspaceAccessMutation?,
    savedWorkspaceAccess: WebAgentWorkspaceAccessMutation?,
    availableWorkspaceAccess: List<KairosWorkspaceAccess>,
    initialManagedAgentId: Kairos.UserId?,
    openedFromWorkspaceSettings: Boolean,
    currentWorkspaceId: Kairos.WorkspaceId,
    currentWorkspaceName: String,
    onDismiss: () -> Unit,
    onTabChanged: (SessionModalTab) -> Unit,
    onClearToken: () -> Unit,
    onOpenWorkspaceSettings: () -> Unit,
    onSaveProfile: (Kairos.UserId, String?, String?, String?) -> Unit,
    onOpenCreateAgent: () -> Unit,
    onCreateAgent: (String, String, List<WebAgentWorkspaceDraft>) -> Unit,
    onUpdateAgentName: (Kairos.UserId, String) -> Unit,
    onRotateAgentToken: (KairosAgentSubaccount) -> Unit,
    onUpdateAgentWorkspaceAccess:
        (Kairos.UserId, Kairos.WorkspaceId, Kairos.WorkspaceMembershipRole?) -> Unit,
    onDeleteAgent: (Kairos.UserId) -> Unit,
) {
    if (sessionInfo?.authenticated != true) {
        return
    }
    val scope = rememberCoroutineScope()
    val canManageAgents = authenticatedUser?.kind == Kairos.UserKind.Human
    var avatarDraft by
        remember(authenticatedUser?.id?.value, authenticatedUser?.avatarUrl) {
            mutableStateOf(authenticatedUser?.avatarUrl.orEmpty())
        }
    var avatarUploadState by
        remember(authenticatedUser?.id?.value) { mutableStateOf<String?>(null) }
    var avatarUploadError by
        remember(authenticatedUser?.id?.value) { mutableStateOf<String?>(null) }
    var timeZoneDraft by
        remember(authenticatedUser?.id?.value, authenticatedUser?.timeZoneId) {
            mutableStateOf(authenticatedUser?.timeZoneId ?: loadStoredCurrentTimeZoneId().orEmpty())
        }
    var localeDraft by
        remember(authenticatedUser?.id?.value, authenticatedUser?.localeTag) {
            mutableStateOf(authenticatedUser?.localeTag ?: loadStoredCurrentLocaleTag().orEmpty())
        }

    Div(
        attrs = {
            onClick { onDismiss() }
            style {
                props(
                    "position" to "fixed",
                    "inset" to "0",
                    "background" to "rgba(20,22,28,0.4)",
                    "display" to "flex",
                    "justify-content" to if (compact) "stretch" else "center",
                    "align-items" to if (compact) "stretch" else "flex-start",
                    "padding" to if (compact) "0" else "80px 20px 20px",
                    "box-sizing" to "border-box",
                    "z-index" to "90",
                )
            }
        }
    ) {
        Div(
            attrs = {
                attr("id", "web-session-modal")
                onClick { it.stopPropagation() }
                style {
                    props(
                        "width" to if (compact) "100%" else "min(720px, 100%)",
                        "height" to if (compact) "100%" else "auto",
                        "max-height" to if (compact) "100vh" else "calc(100vh - 100px)",
                        "overflow" to "auto",
                        "background" to Paper,
                        "border-radius" to if (compact) "0" else "8px",
                        "box-shadow" to "0 18px 60px rgba(20,22,28,0.18)",
                        "padding" to if (compact) "20px 16px 24px" else "24px",
                        "box-sizing" to "border-box",
                        "display" to "grid",
                        "gap" to "18px",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "space-between",
                            "align-items" to "center",
                            "gap" to "12px",
                        )
                    }
                }
            ) {
                Div {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "font-size" to "12px",
                                    "font-weight" to "700",
                                    "color" to Muted,
                                    "line-height" to "1.4",
                                )
                            }
                        }
                    ) {
                        Text(
                            if (canManageAgents && activeTab == SessionModalTab.Agents) {
                                "Account › Agents"
                            } else {
                                "Account"
                            }
                        )
                    }
                    Div(
                        attrs = {
                            style {
                                props(
                                    "font-size" to "26px",
                                    "font-weight" to "700",
                                    "line-height" to "1.2",
                                )
                            }
                        }
                    ) {
                        Text(
                            if (sessionInfo?.authenticated == true) "Account settings"
                            else "Sign in"
                        )
                    }
                    if (sessionInfo?.authenticated == true) {
                        MutedText(
                            if (authenticatedUser?.kind == Kairos.UserKind.Human) {
                                "Profile and agents for ${authenticatedUser?.displayName ?: "Kairos"}. Workspace members and sharing live in workspace settings."
                            } else {
                                "${authenticatedUser?.displayName ?: "Kairos"} account settings."
                            }
                        )
                    } else {
                        MutedText("Manage your account profile.")
                    }
                }
                Button(
                    attrs = {
                        attr("id", "web-session-modal-close-button")
                        onClick { onDismiss() }
                        title("Close account settings")
                        style {
                            props(
                                "width" to "44px",
                                "min-width" to "44px",
                                "height" to "44px",
                                "border" to "0",
                                "border-radius" to "8px",
                                "background" to "#f3f5f8",
                                "padding" to "0",
                                "font-size" to "20px",
                                "font-weight" to "700",
                                "cursor" to "pointer",
                                "color" to Ink,
                                "align-self" to "start",
                            )
                        }
                    }
                ) {
                    Text("×")
                }
            }

            if (canManageAgents) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "inline-flex",
                                "gap" to "8px",
                                "padding" to "4px",
                                "border-radius" to "999px",
                                "background" to "#f3f5f8",
                                "align-self" to "start",
                            )
                        }
                    }
                ) {
                    SessionModalTabButton(
                        id = "web-session-modal-tab-profile",
                        label = "Profile",
                        selected = activeTab == SessionModalTab.Profile,
                        onClick = { onTabChanged(SessionModalTab.Profile) },
                    )
                    SessionModalTabButton(
                        id = "web-session-modal-tab-agents",
                        label = "Agents",
                        selected = activeTab == SessionModalTab.Agents,
                        onClick = { onTabChanged(SessionModalTab.Agents) },
                    )
                }
            }

            authenticatedUser?.let { user ->
                when {
                    activeTab == SessionModalTab.Agents && canManageAgents ->
                        AgentSubaccountsPanel(
                            compact = compact,
                            ownerUser = user,
                            availableWorkspaceAccess = availableWorkspaceAccess,
                            agents = agentSubaccounts,
                            initialManagedAgentId = initialManagedAgentId,
                            openedFromWorkspaceSettings = openedFromWorkspaceSettings,
                            currentWorkspaceId = currentWorkspaceId,
                            currentWorkspaceName = currentWorkspaceName,
                            onReturnToWorkspaceSettings = onOpenWorkspaceSettings,
                            loading = agentSubaccountsLoading,
                            notice = agentSubaccountsNotice,
                            error = agentSubaccountsError,
                            savingAgentName = savingAgentName,
                            savedAgentName = savedAgentName,
                            savingWorkspaceAccess = savingWorkspaceAccess,
                            savedWorkspaceAccess = savedWorkspaceAccess,
                            onOpenCreate = onOpenCreateAgent,
                            onUpdateAgentName = onUpdateAgentName,
                            onRotateToken = onRotateAgentToken,
                            onUpdateWorkspaceAccess = onUpdateAgentWorkspaceAccess,
                            onDeleteAgent = onDeleteAgent,
                        )
                    else ->
                        Div(attrs = { style { cardSurface() } }) {
                            SectionLabel("Identity")
                            if (user.kind == Kairos.UserKind.Agent) {
                                MutedText(
                                    "This is an automated identity. It signs in with a token and acts within the workspace access granted by its owner."
                                )
                                Div(attrs = { style { modalReadonlyBox() } }) {
                                    Text(user.displayName)
                                }
                                sessionInfo.ownerUser?.let { ownerUser ->
                                    SectionLabel("Owner")
                                    Div(attrs = { style { modalReadonlyBox() } }) {
                                        Text(ownerUser.displayName)
                                    }
                                }
                            } else {
                                MutedText(
                                    "Actions are recorded as this account. Agents and personal settings live here; workspace access is managed from workspace settings."
                                )
                                Div(
                                    attrs = {
                                        style {
                                            props(
                                                "display" to "flex",
                                                "justify-content" to "space-between",
                                                "align-items" to "center",
                                                "gap" to "10px",
                                                "flex-wrap" to "wrap",
                                                "margin-top" to "12px",
                                                "margin-bottom" to "6px",
                                            )
                                        }
                                    }
                                ) {
                                    Div(
                                        attrs = {
                                            style {
                                                props(
                                                    "font-size" to "13px",
                                                    "line-height" to "1.5",
                                                    "color" to Muted,
                                                )
                                            }
                                        }
                                    ) {
                                        Text(
                                            "Need to invite people or change workspace access? Open workspace settings."
                                        )
                                    }
                                    Button(
                                        attrs = {
                                            attr("id", "web-session-open-workspace-settings")
                                            onClick { onOpenWorkspaceSettings() }
                                            style { secondaryButton() }
                                        }
                                    ) {
                                        Text("Open workspace settings")
                                    }
                                }
                                Div(attrs = { style { modalReadonlyBox() } }) {
                                    Text(user.displayName)
                                }
                            }
                            SectionLabel("Profile")
                            MutedText(
                                "Upload a PNG or SVG avatar. PNG uploads are downscaled in the browser before saving."
                            )
                            Input(
                                type = InputType.File,
                                attrs = {
                                    attr("id", ProfileAvatarInputId)
                                    attr("accept", ".png,.svg,image/png,image/svg+xml")
                                    onChange {
                                        val input =
                                            document
                                                .getElementById(ProfileAvatarInputId)
                                                ?.unsafeCast<HTMLInputElement?>() ?: return@onChange
                                        val file = input.files?.item(0) ?: return@onChange
                                        avatarUploadState = "Processing avatar..."
                                        avatarUploadError = null
                                        scope.launch {
                                            try {
                                                avatarDraft = prepareAvatarUpload(file)
                                                avatarUploadState = "Avatar ready to save."
                                            } catch (error: Throwable) {
                                                when (error) {
                                                    is IllegalArgumentException ->
                                                        avatarUploadError =
                                                            error.message
                                                                ?: "Could not prepare avatar."
                                                    else -> {
                                                        avatarUploadError =
                                                            "Could not prepare avatar."
                                                        propagateUiThrowable(error)
                                                    }
                                                }
                                            } finally {
                                                input.value = ""
                                            }
                                        }
                                    }
                                    style { props("display" to "none") }
                                },
                            )
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "display" to "grid",
                                            "grid-template-columns" to "72px minmax(0, 1fr)",
                                            "align-items" to "center",
                                            "gap" to "14px",
                                        )
                                    }
                                }
                            ) {
                                Img(
                                    src = normalizeAvatarValue(avatarDraft) ?: user.avatarUrl(),
                                    alt = "${user.displayName} avatar preview",
                                    attrs = {
                                        attr("id", "web-profile-avatar-preview")
                                        style {
                                            props(
                                                "width" to "72px",
                                                "height" to "72px",
                                                "border-radius" to "50%",
                                                "object-fit" to "cover",
                                                "border" to "1px solid $Border",
                                                "background" to "#eef1f5",
                                                "display" to "block",
                                            )
                                        }
                                    },
                                )
                                Div(
                                    attrs = {
                                        style {
                                            props(
                                                "display" to "grid",
                                                "gap" to "10px",
                                                "align-content" to "start",
                                            )
                                        }
                                    }
                                ) {
                                    Div(
                                        attrs = {
                                            style {
                                                props(
                                                    "display" to "flex",
                                                    "gap" to "10px",
                                                    "flex-wrap" to "wrap",
                                                )
                                            }
                                        }
                                    ) {
                                        Button(
                                            attrs = {
                                                attr("id", "web-profile-avatar-upload-button")
                                                onClick {
                                                    document
                                                        .getElementById(ProfileAvatarInputId)
                                                        ?.unsafeCast<HTMLInputElement?>()
                                                        ?.click()
                                                }
                                                style { secondaryButton() }
                                            }
                                        ) {
                                            Text("Upload avatar")
                                        }
                                        Button(
                                            attrs = {
                                                attr("id", "web-profile-avatar-remove-button")
                                                onClick {
                                                    avatarDraft = ""
                                                    avatarUploadState =
                                                        "Avatar will reset to the default after save."
                                                    avatarUploadError = null
                                                }
                                                style { secondaryButton() }
                                            }
                                        ) {
                                            Text("Remove avatar")
                                        }
                                    }
                                    Div(
                                        attrs = {
                                            style {
                                                props(
                                                    "font-size" to "12px",
                                                    "line-height" to "1.5",
                                                    "color" to
                                                        if (avatarUploadError == null) Muted
                                                        else "#b4453f",
                                                )
                                            }
                                        }
                                    ) {
                                        Text(
                                            avatarUploadError
                                                ?: avatarUploadState
                                                ?: "Supports PNG and SVG. Removing the avatar restores the default portrait."
                                        )
                                    }
                                }
                            }
                            SectionLabel("Time zone")
                            Input(
                                type = InputType.Text,
                                attrs = {
                                    attr("id", "web-profile-timezone-input")
                                    value(timeZoneDraft)
                                    onInput { timeZoneDraft = it.value }
                                    attr(
                                        "placeholder",
                                        "IANA time zone, for example America/Toronto",
                                    )
                                    style { formInputStyle() }
                                },
                            )
                            SectionLabel("Locale")
                            Input(
                                type = InputType.Text,
                                attrs = {
                                    attr("id", "web-profile-locale-input")
                                    value(localeDraft)
                                    onInput { localeDraft = it.value }
                                    attr("placeholder", "Locale tag, for example en-CA")
                                    style { formInputStyle() }
                                },
                            )
                            Button(
                                attrs = {
                                    attr("id", "web-profile-save-button")
                                    onClick {
                                        onSaveProfile(
                                            user.id,
                                            avatarDraft,
                                            timeZoneDraft,
                                            localeDraft,
                                        )
                                    }
                                    style { primaryButton() }
                                }
                            ) {
                                Text("Save profile")
                            }
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "display" to "flex",
                                            "gap" to "10px",
                                            "flex-wrap" to "wrap",
                                        )
                                    }
                                }
                            ) {
                                Button(
                                    attrs = {
                                        attr("id", "web-session-clear-token-button")
                                        onClick { onClearToken() }
                                        style { secondaryButton() }
                                    }
                                ) {
                                    Text("Sign out")
                                }
                            }
                        }
                }
            }
        }
    }
}

@Composable
private fun Sidebar(
    workspace: WebWorkspace,
    compact: Boolean,
    showAdminArea: Boolean,
    selectedArea: WorkspaceArea,
    selectedProjectId: Kairos.ProjectId?,
    onCreateProject: () -> Unit,
    onAreaSelected: (WorkspaceArea) -> Unit,
    onProjectSelected: (Kairos.ProjectId) -> Unit,
) {
    val rootProjects = remember(workspace.projects) { workspace.rootProjects() }
    val selectedRootId =
        remember(workspace.projects, selectedProjectId?.value) {
            selectedProjectId?.let(workspace::projectRootId) ?: workspace.primaryProject()?.id
        }
    val primaryRoot = rootProjects.firstOrNull { project -> project.id == selectedRootId }
    val secondaryRoots = rootProjects.filter { project -> project.id != primaryRoot?.id }
    val activeRoots =
        remember(workspace, secondaryRoots) {
            secondaryRoots.filter { project -> workspace.projectHasVisibleWork(project.id) }
        }
    val dormantRoots =
        remember(workspace, secondaryRoots) {
            secondaryRoots.filterNot { project -> workspace.projectHasVisibleWork(project.id) }
        }
    var showDormantProjects by remember(workspace.projects) { mutableStateOf(false) }

    Div(
        attrs = {
            style {
                props(
                    "width" to if (compact) "min(296px, 88vw)" else "258px",
                    "max-width" to "100%",
                    "background" to Sidebar,
                    "color" to Paper,
                    "padding" to "18px 12px",
                    "box-sizing" to "border-box",
                    "overflow" to "auto",
                    "flex-shrink" to "0",
                    "height" to "100%",
                )
            }
        }
    ) {
        SidebarItem(
            label = "Today",
            selected = selectedArea == WorkspaceArea.Home,
            areaToken = "today",
            onSelect = { onAreaSelected(WorkspaceArea.Home) },
        )
        SidebarItem(
            label = "Inbox",
            selected = selectedArea == WorkspaceArea.Inbox,
            areaToken = "inbox",
            onSelect = { onAreaSelected(WorkspaceArea.Inbox) },
        )
        SidebarItem(
            label = "Objectives",
            selected = selectedArea == WorkspaceArea.Objectives,
            areaToken = "objectives",
            onSelect = { onAreaSelected(WorkspaceArea.Objectives) },
        )
        SidebarItem(
            label = "KPIs",
            selected = selectedArea == WorkspaceArea.Kpis,
            areaToken = "kpis",
            onSelect = { onAreaSelected(WorkspaceArea.Kpis) },
        )
        if (showAdminArea) {
            SidebarItem(
                label = "Admin",
                selected = selectedArea == WorkspaceArea.Admin,
                areaToken = "admin",
                onSelect = { onAreaSelected(WorkspaceArea.Admin) },
            )
        }
        SidebarItem(
            label = "Downloads",
            selected = selectedArea == WorkspaceArea.Downloads,
            areaToken = "downloads",
            onSelect = { onAreaSelected(WorkspaceArea.Downloads) },
        )
        SidebarHeading(
            label = "Projects",
            actionLabel = "+",
            actionId = "web-sidebar-create-project-button",
            onAction = onCreateProject,
        )
        primaryRoot?.let { project ->
            SidebarProjectTree(
                workspace = workspace,
                project = project,
                depth = 0,
                selectedProjectId = selectedProjectId,
                selectedArea = selectedArea,
                onProjectSelected = onProjectSelected,
            )
        }
        activeRoots.forEach { project ->
            SidebarProjectTree(
                workspace = workspace,
                project = project,
                depth = 0,
                selectedProjectId = selectedProjectId,
                selectedArea = selectedArea,
                onProjectSelected = onProjectSelected,
            )
        }
        if (dormantRoots.isNotEmpty()) {
            Button(
                attrs = {
                    attr("id", "web-sidebar-toggle-dormant-projects-button")
                    onClick { showDormantProjects = !showDormantProjects }
                    style {
                        props(
                            "height" to "30px",
                            "margin" to "12px 12px 0",
                            "border" to "1px solid rgba(255,255,255,0.12)",
                            "border-radius" to "8px",
                            "background" to "transparent",
                            "color" to "#d7dae0",
                            "padding" to "0 10px",
                            "font-size" to "12px",
                            "font-weight" to "700",
                            "text-align" to "left",
                            "cursor" to "pointer",
                        )
                    }
                }
            ) {
                Text(
                    if (showDormantProjects) {
                        "Hide dormant (${dormantRoots.size})"
                    } else {
                        "Show dormant (${dormantRoots.size})"
                    }
                )
            }
            if (showDormantProjects) {
                dormantRoots.forEach { project ->
                    SidebarProjectTree(
                        workspace = workspace,
                        project = project,
                        depth = 0,
                        selectedProjectId = selectedProjectId,
                        selectedArea = selectedArea,
                        onProjectSelected = onProjectSelected,
                    )
                }
            }
        }
    }
}

@Composable
private fun SessionModalTabButton(
    id: String,
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
) {
    Button(
        attrs = {
            attr("id", id)
            if (!selected) {
                onClick { onClick() }
            }
            style { tabButton(selected) }
        }
    ) {
        Text(label)
    }
}

@Composable
private fun AgentSubaccountsPanel(
    compact: Boolean,
    ownerUser: Kairos.User,
    availableWorkspaceAccess: List<KairosWorkspaceAccess>,
    agents: List<KairosAgentSubaccount>,
    initialManagedAgentId: Kairos.UserId?,
    openedFromWorkspaceSettings: Boolean,
    currentWorkspaceId: Kairos.WorkspaceId,
    currentWorkspaceName: String,
    onReturnToWorkspaceSettings: () -> Unit,
    loading: Boolean,
    notice: String?,
    error: String?,
    savingAgentName: Kairos.UserId?,
    savedAgentName: Kairos.UserId?,
    savingWorkspaceAccess: WebAgentWorkspaceAccessMutation?,
    savedWorkspaceAccess: WebAgentWorkspaceAccessMutation?,
    onOpenCreate: () -> Unit,
    onUpdateAgentName: (Kairos.UserId, String) -> Unit,
    onRotateToken: (KairosAgentSubaccount) -> Unit,
    onUpdateWorkspaceAccess:
        (Kairos.UserId, Kairos.WorkspaceId, Kairos.WorkspaceMembershipRole?) -> Unit,
    onDeleteAgent: (Kairos.UserId) -> Unit,
) {
    var managedAgentId by
        remember(initialManagedAgentId?.value) { mutableStateOf(initialManagedAgentId) }
    val managedAgent = managedAgentId?.let { userId -> agents.firstOrNull { it.user.id == userId } }
    val visibleAgents = managedAgent?.let { listOf(it) } ?: agents
    Div(attrs = { style { cardSurface() } }) {
        if (managedAgent == null) {
            SectionLabel("Agents")
            MutedText("Token-based automation identities you own.")
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "space-between",
                            "align-items" to "start",
                            "gap" to "14px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                Div(attrs = { style { props("display" to "grid", "gap" to "4px") } }) {
                    Div(
                        attrs = { style { props("font-size" to "14px", "font-weight" to "700") } }
                    ) {
                        Text("You have ${agents.size} agent${if (agents.size == 1) "" else "s"}")
                    }
                    MutedText(
                        "Agents sign in with tokens and inherit only the workspace access you grant."
                    )
                }
                Button(
                    attrs = {
                        attr("id", "web-agent-open-create-button")
                        if (loading) disabled()
                        onClick { onOpenCreate() }
                        style { primaryButton(enabled = !loading) }
                    }
                ) {
                    Text("New agent")
                }
            }
        } else {
            if (openedFromWorkspaceSettings) {
                Button(
                    attrs = {
                        onClick { onReturnToWorkspaceSettings() }
                        style {
                            secondaryButton()
                            props("justify-self" to "start", "height" to "44px")
                        }
                    }
                ) {
                    Text("← $currentWorkspaceName members")
                }
            }
            Button(
                attrs = {
                    onClick { managedAgentId = null }
                    style {
                        secondaryButton()
                        props("justify-self" to "start", "height" to "44px")
                    }
                }
            ) {
                Text("← Agents")
            }
            Div(attrs = { style { props("display" to "grid", "gap" to "4px") } }) {
                Div(
                    attrs = {
                        style {
                            props(
                                "font-size" to "20px",
                                "font-weight" to "700",
                                "line-height" to "1.25",
                            )
                        }
                    }
                ) {
                    Text(managedAgent.user.displayName)
                }
                MutedText("Automation identity settings")
                Div(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                    Text("Changes save automatically.")
                }
            }
            if (openedFromWorkspaceSettings) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "grid",
                                "gap" to "6px",
                                "border" to "1px solid #d7e6fb",
                                "background" to "#f5f9ff",
                                "border-radius" to "8px",
                                "padding" to "12px",
                            )
                        }
                    }
                ) {
                    Div(
                        attrs = { style { props("font-size" to "13px", "font-weight" to "700") } }
                    ) {
                        Text("Opened from $currentWorkspaceName workspace")
                    }
                    MutedText(
                        "The $currentWorkspaceName access row is highlighted below. Rename, token rotation, and broader workspace access stay here in account settings."
                    )
                }
            }
        }
        notice?.let { message ->
            Div(
                attrs = {
                    attr("id", "web-agent-notice")
                    style {
                        props("font-size" to "13px", "line-height" to "1.5", "color" to "#2f7a52")
                    }
                }
            ) {
                Text(message)
            }
        }
        error?.let { message ->
            Div(
                attrs = {
                    attr("id", "web-agent-error")
                    style {
                        props("font-size" to "13px", "line-height" to "1.5", "color" to "#b4453f")
                    }
                }
            ) {
                Text(message)
            }
        }
        if (agents.isEmpty()) {
            PanelEmptyState(
                label = "No agents yet",
                detail =
                    "Create an agent when you want an automated identity with its own token, avatar, and limited workspace access.",
            )
        } else {
            Div(attrs = { style { props("display" to "grid", "gap" to "12px") } }) {
                visibleAgents.forEach { agent ->
                    AgentSubaccountCard(
                        compact = compact,
                        expanded = managedAgentId == agent.user.id,
                        agent = agent,
                        availableWorkspaceAccess = availableWorkspaceAccess,
                        busy = loading,
                        savingName = savingAgentName == agent.user.id,
                        savedName = savedAgentName == agent.user.id,
                        savingWorkspaceAccess = savingWorkspaceAccess,
                        savedWorkspaceAccess = savedWorkspaceAccess,
                        highlightWorkspaceId =
                            if (openedFromWorkspaceSettings) {
                                currentWorkspaceId
                            } else {
                                null
                            },
                        onManage = { managedAgentId = agent.user.id },
                        onUpdateName = onUpdateAgentName,
                        onRotateToken = onRotateToken,
                        onUpdateWorkspaceAccess = onUpdateWorkspaceAccess,
                        onDelete = onDeleteAgent,
                    )
                }
            }
        }
    }
}

@Composable
private fun AgentSubaccountCard(
    compact: Boolean,
    expanded: Boolean,
    agent: KairosAgentSubaccount,
    availableWorkspaceAccess: List<KairosWorkspaceAccess>,
    busy: Boolean,
    savingName: Boolean,
    savedName: Boolean,
    savingWorkspaceAccess: WebAgentWorkspaceAccessMutation?,
    savedWorkspaceAccess: WebAgentWorkspaceAccessMutation?,
    highlightWorkspaceId: Kairos.WorkspaceId?,
    onManage: () -> Unit,
    onUpdateName: (Kairos.UserId, String) -> Unit,
    onRotateToken: (KairosAgentSubaccount) -> Unit,
    onUpdateWorkspaceAccess:
        (Kairos.UserId, Kairos.WorkspaceId, Kairos.WorkspaceMembershipRole?) -> Unit,
    onDelete: (Kairos.UserId) -> Unit,
) {
    var nameDraft by
        remember(agent.user.id.value, agent.user.displayName) {
            mutableStateOf(agent.user.displayName)
        }
    val workspaceRolesById =
        remember(agent.workspaceAccess) {
            agent.workspaceAccess.associateBy(
                { access -> access.workspace.id },
                { access -> access.configuredRole },
            )
        }

    Div(
        attrs = {
            attr("id", "web-agent-card-${agent.user.id.value}")
            if (!expanded) {
                onClick { onManage() }
                title("Manage ${agent.user.displayName}")
            }
            style {
                props(
                    "display" to "grid",
                    "gap" to "14px",
                    "padding" to "16px",
                    "border" to "1px solid $Border",
                    "border-radius" to "10px",
                    "background" to Paper,
                    "cursor" to if (expanded) "default" else "pointer",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "grid",
                        "grid-template-columns" to
                            if (compact) "minmax(0, 1fr)" else "minmax(0, 1fr) auto",
                        "gap" to "12px",
                        "align-items" to "start",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "align-items" to "center",
                            "gap" to "12px",
                            "min-width" to "0",
                        )
                    }
                }
            ) {
                Avatar(agent.user, 42)
                Div(
                    attrs = {
                        style { props("display" to "grid", "gap" to "3px", "min-width" to "0") }
                    }
                ) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "align-items" to "center",
                                    "gap" to "8px",
                                    "flex-wrap" to "wrap",
                                )
                            }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "font-size" to "15px",
                                        "font-weight" to "700",
                                        "line-height" to "1.4",
                                    )
                                }
                            }
                        ) {
                            Text(agent.user.displayName)
                        }
                        MetaChip("Agent", background = "#eef4ff", textColor = Blue)
                    }
                    Div(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                        Text("@${agent.username}")
                    }
                    Div(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                        Text(
                            "Last seen " +
                                (agent.lastSeenAt?.let { seen ->
                                    relativeLabel(seen.toEpochMilliseconds())
                                } ?: "never")
                        )
                    }
                }
            }
            if (!expanded) {
                Button(
                    attrs = {
                        attr("id", "web-agent-edit-${agent.user.id.value}")
                        onClick {
                            it.stopPropagation()
                            onManage()
                        }
                        style {
                            secondaryButton()
                            props("height" to "32px", "padding" to "0 12px")
                        }
                    }
                ) {
                    Text("Manage")
                }
            }
        }
        if (!expanded) {
            Div(
                attrs = {
                    style { props("display" to "flex", "gap" to "8px", "flex-wrap" to "wrap") }
                }
            ) {
                if (agent.workspaceAccess.isEmpty()) {
                    MetaChip("No workspace access")
                } else {
                    agent.workspaceAccess.forEach { access ->
                        val roleLabel =
                            when (access.effectiveRole) {
                                Kairos.WorkspaceMembershipRole.Owner -> "Owner"
                                Kairos.WorkspaceMembershipRole.Member -> "Member"
                            }
                        MetaChip("${access.workspace.name} • $roleLabel")
                    }
                }
            }
        }
        if (expanded) {
            Div(
                attrs = {
                    style { props("display" to "grid", "gap" to "14px", "padding-top" to "2px") }
                }
            ) {
                Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
                    SectionLabel("Name")
                    Input(
                        type = InputType.Text,
                        attrs = {
                            attr("id", "web-agent-name-input-${agent.user.id.value}")
                            value(nameDraft)
                            onInput { nameDraft = it.value }
                            onBlur {
                                val trimmed = nameDraft.trim()
                                if (
                                    trimmed.isNotEmpty() &&
                                        trimmed != agent.user.displayName &&
                                        !busy &&
                                        !savingName
                                ) {
                                    onUpdateName(agent.user.id, trimmed)
                                }
                            }
                            onKeyDown { event ->
                                if (event.key == "Enter") {
                                    event.preventDefault()
                                    val trimmed = nameDraft.trim()
                                    if (
                                        trimmed.isNotEmpty() &&
                                            trimmed != agent.user.displayName &&
                                            !busy &&
                                            !savingName
                                    ) {
                                        onUpdateName(agent.user.id, trimmed)
                                    }
                                }
                            }
                            attr("placeholder", "Agent name")
                            style { formInputStyle() }
                        },
                    )
                    if (savingName || savedName) {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "font-size" to "11px",
                                        "font-weight" to "700",
                                        "color" to if (savingName) Muted else "#2f7a52",
                                    )
                                }
                            }
                        ) {
                            Text(if (savingName) "Saving…" else "Saved")
                        }
                    }
                }
                Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
                    SectionLabel("Workspace Access")
                    MutedText(
                        "You can grant this agent up to your own role in each workspace. Owner access is only available where you are also an owner."
                    )
                    availableWorkspaceAccess
                        .sortedBy { it.workspace.name }
                        .forEach { access ->
                            AgentWorkspaceAccessRow(
                                agent = agent,
                                access = access,
                                compact = compact,
                                highlighted = access.workspace.id == highlightWorkspaceId,
                                selectedRole = workspaceRolesById[access.workspace.id],
                                busy = busy,
                                saving =
                                    savingWorkspaceAccess?.let { mutation ->
                                        mutation.agentUserId == agent.user.id &&
                                            mutation.workspaceId == access.workspace.id
                                    } == true,
                                saved =
                                    savedWorkspaceAccess?.let { mutation ->
                                        mutation.agentUserId == agent.user.id &&
                                            mutation.workspaceId == access.workspace.id
                                    } == true,
                                onChanged = { role ->
                                    onUpdateWorkspaceAccess(
                                        agent.user.id,
                                        access.workspace.id,
                                        role,
                                    )
                                },
                            )
                        }
                }
                Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
                    SectionLabel("Token")
                    MutedText(
                        "Rotating immediately invalidates the current token. Any automation using it must be updated."
                    )
                    Button(
                        attrs = {
                            attr("id", "web-agent-rotate-token-${agent.user.id.value}")
                            if (busy) disabled()
                            onClick { onRotateToken(agent) }
                            style { secondaryButton() }
                        }
                    ) {
                        Text("Rotate token…")
                    }
                }
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "grid",
                                "gap" to "8px",
                                "padding" to "14px",
                                "border-top" to "1px solid $Border",
                                "border" to "1px solid #f0d1cb",
                                "background" to "#fff5f3",
                                "border-radius" to "8px",
                            )
                        }
                    }
                ) {
                    SectionLabel("Danger zone")
                    Div(attrs = { style { props("display" to "grid", "gap" to "4px") } }) {
                        MutedText(
                            "Permanently deletes this agent and revokes all workspace access."
                        )
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "font-size" to "12px",
                                        "font-weight" to "800",
                                        "color" to Coral,
                                    )
                                }
                            }
                        ) {
                            Text("This cannot be undone.")
                        }
                    }
                    Button(
                        attrs = {
                            attr("id", "web-agent-delete-${agent.user.id.value}")
                            if (busy) disabled()
                            onClick { onDelete(agent.user.id) }
                            style {
                                destructiveButton()
                                props("justify-self" to "start", "width" to "fit-content")
                            }
                        }
                    ) {
                        Text("Delete agent…")
                    }
                }
            }
        }
    }
}

@Composable
private fun AgentWorkspaceAccessRow(
    agent: KairosAgentSubaccount,
    access: KairosWorkspaceAccess,
    compact: Boolean = false,
    highlighted: Boolean = false,
    selectedRole: Kairos.WorkspaceMembershipRole?,
    busy: Boolean,
    saving: Boolean,
    saved: Boolean,
    onChanged: (Kairos.WorkspaceMembershipRole?) -> Unit,
) {
    val effectiveRole =
        agent.workspaceAccess.firstOrNull { it.workspace.id == access.workspace.id }?.effectiveRole
    Div(
        attrs = {
            style {
                props(
                    "display" to "grid",
                    "grid-template-columns" to
                        if (compact) "minmax(0, 1fr) 136px" else "minmax(0, 1fr) 150px",
                    "gap" to "12px",
                    "align-items" to "start",
                    "padding" to
                        if (highlighted || compact) {
                            "10px 12px"
                        } else {
                            "0"
                        },
                    "border" to
                        when {
                            highlighted -> "1px solid #d7e6fb"
                            compact -> "1px solid $Border"
                            else -> "0"
                        },
                    "border-radius" to if (highlighted || compact) "8px" else "0",
                    "background" to
                        when {
                            highlighted -> "#f5f9ff"
                            compact -> "#fbfcfd"
                            else -> "transparent"
                        },
                )
            }
        }
    ) {
        Div(attrs = { style { props("display" to "grid", "gap" to "2px") } }) {
            Div(attrs = { style { props("font-size" to "14px", "font-weight" to "700") } }) {
                Text(access.workspace.name)
            }
            if (effectiveRole == null) {
                Div(attrs = { style { props("font-size" to "11px", "color" to Muted) } }) {
                    Text(
                        if (access.role == Kairos.WorkspaceMembershipRole.Owner) {
                            "Choose Member or Owner here."
                        } else {
                            "Choose Member here."
                        }
                    )
                }
            }
            if (saving || saved) {
                Div(
                    attrs = {
                        style {
                            props(
                                "font-size" to "11px",
                                "font-weight" to "700",
                                "color" to if (saving) Muted else "#2f7a52",
                            )
                        }
                    }
                ) {
                    Text(if (saving) "Saving…" else "Saved")
                }
            }
        }
        Select(
            attrs = {
                attr(
                    "id",
                    "web-agent-workspace-role-${agent.user.id.value}-${access.workspace.id.value}",
                )
                if (busy) disabled()
                onChange {
                    onChanged(
                        when (it.value) {
                            "none" -> null
                            "owner" -> Kairos.WorkspaceMembershipRole.Owner
                            else -> Kairos.WorkspaceMembershipRole.Member
                        }
                    )
                }
                style { formInputStyle() }
            }
        ) {
            Option(
                value = "none",
                attrs = {
                    if (selectedRole == null) {
                        attr("selected", "selected")
                    }
                },
            ) {
                Text("No access")
            }
            Option(
                value = "member",
                attrs = {
                    if (selectedRole == Kairos.WorkspaceMembershipRole.Member) {
                        attr("selected", "selected")
                    }
                },
            ) {
                Text("Member")
            }
            if (access.role == Kairos.WorkspaceMembershipRole.Owner) {
                Option(
                    value = "owner",
                    attrs = {
                        if (selectedRole == Kairos.WorkspaceMembershipRole.Owner) {
                            attr("selected", "selected")
                        }
                    },
                ) {
                    Text("Owner")
                }
            }
        }
    }
}

@Composable
private fun AgentSubaccountCreateModal(
    availableWorkspaceAccess: List<KairosWorkspaceAccess>,
    defaultWorkspaceId: Kairos.WorkspaceId,
    compact: Boolean,
    busy: Boolean,
    onCreate: (String, String, List<WebAgentWorkspaceDraft>) -> Unit,
    onDismiss: () -> Unit,
) {
    var step by remember { mutableStateOf(1) }
    var displayName by remember { mutableStateOf("") }
    var username by remember { mutableStateOf("") }
    var nameTouched by remember { mutableStateOf(false) }
    var usernameTouched by remember { mutableStateOf(false) }
    var workspaceDrafts by
        remember(availableWorkspaceAccess, defaultWorkspaceId) {
            mutableStateOf(
                listOf(
                    WebAgentWorkspaceDraft(
                        workspaceId = defaultWorkspaceId,
                        role = Kairos.WorkspaceMembershipRole.Member,
                    )
                )
            )
        }
    val trimmedName = displayName.trim()
    val trimmedUsername = username.trim()
    val showIdentityHints = trimmedName.isNotEmpty() || trimmedUsername.isNotEmpty()
    val nameError =
        if ((nameTouched || showIdentityHints) && trimmedName.isEmpty()) "Required." else null
    val usernameError =
        if ((usernameTouched || showIdentityHints) && trimmedUsername.isEmpty()) "Required."
        else null
    val workspaceChoices = availableWorkspaceAccess.sortedBy { it.workspace.name }
    ModalCard(zIndex = 95, title = "New agent", onDismiss = onDismiss, showCloseButton = false) {
        if (step == 1) {
            Div(attrs = { style { props("display" to "grid", "gap" to "12px") } }) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "inline-flex",
                                "justify-self" to "start",
                                "padding" to "5px 12px",
                                "border-radius" to "999px",
                                "background" to "#eef4ff",
                                "color" to Blue,
                                "font-size" to "13px",
                                "font-weight" to "800",
                            )
                        }
                    }
                ) {
                    Text("Step 1 of 2 · Identity")
                }
                MutedText(
                    "Agents are token-based identities for automation. Give the agent a clear name and a stable username used at sign-in and in activity history."
                )
                SectionLabel("Agent name")
                Input(
                    type = InputType.Text,
                    attrs = {
                        attr("id", "web-agent-create-name-input")
                        value(displayName)
                        onInput { displayName = it.value }
                        onBlur { nameTouched = true }
                        attr("placeholder", "Campaign triage agent")
                        style { formInputStyle() }
                    },
                )
                nameError?.let { message ->
                    Div(attrs = { style { props("font-size" to "12px", "color" to "#b4453f") } }) {
                        Text(message)
                    }
                }
                SectionLabel("Username")
                Input(
                    type = InputType.Text,
                    attrs = {
                        attr("id", "web-agent-create-username-input")
                        value(username)
                        onInput { username = it.value }
                        onBlur { usernameTouched = true }
                        attr("placeholder", "campaign_triage")
                        style { formInputStyle() }
                    },
                )
                usernameError?.let { message ->
                    Div(attrs = { style { props("font-size" to "12px", "color" to "#b4453f") } }) {
                        Text(message)
                    }
                }
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "justify-content" to "space-between",
                                "gap" to "10px",
                                "flex-wrap" to "wrap",
                            )
                        }
                    }
                ) {
                    Button(
                        attrs = {
                            onClick { onDismiss() }
                            style { secondaryButton() }
                        }
                    ) {
                        Text("Cancel setup")
                    }
                    Button(
                        attrs = {
                            attr("id", "web-agent-create-next")
                            if (trimmedName.isEmpty() || trimmedUsername.isEmpty()) disabled()
                            onClick { step = 2 }
                            style {
                                primaryButton(
                                    enabled =
                                        trimmedName.isNotEmpty() && trimmedUsername.isNotEmpty()
                                )
                            }
                        }
                    ) {
                        Text("Continue")
                    }
                }
            }
        } else {
            Div(attrs = { style { props("display" to "grid", "gap" to "12px") } }) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "inline-flex",
                                "justify-self" to "start",
                                "padding" to "5px 12px",
                                "border-radius" to "999px",
                                "background" to "#eef4ff",
                                "color" to Blue,
                                "font-size" to "13px",
                                "font-weight" to "800",
                            )
                        }
                    }
                ) {
                    Text("Step 2 of 2 · Workspace Access")
                }
                MutedText(
                    "Choose the workspaces this agent can access. Leave a workspace on “No access” to keep it out entirely."
                )
                MutedText(
                    "Agents can only be added to workspaces you already belong to. " +
                        "Assign the least access they need: Member can work inside the workspace, " +
                        "while Owner can also manage workspace membership."
                )
                workspaceChoices.forEach { access ->
                    AgentWorkspaceDraftRow(
                        compact = compact,
                        access = access,
                        selectedRole = workspaceDrafts.roleFor(access.workspace.id),
                        onChanged = { role ->
                            workspaceDrafts =
                                if (role == null) {
                                    workspaceDrafts.without(access.workspace.id)
                                } else {
                                    workspaceDrafts.withRole(access.workspace.id, role)
                                }
                        },
                    )
                }
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "justify-content" to "space-between",
                                "align-items" to "center",
                                "gap" to "10px",
                                "flex-wrap" to "wrap",
                            )
                        }
                    }
                ) {
                    Div(
                        attrs = {
                            style {
                                props("display" to "flex", "gap" to "10px", "flex-wrap" to "wrap")
                            }
                        }
                    ) {
                        Button(
                            attrs = {
                                onClick { onDismiss() }
                                style { secondaryButton() }
                            }
                        ) {
                            Text("Cancel")
                        }
                        Button(
                            attrs = {
                                onClick { step = 1 }
                                style { ghostButton() }
                            }
                        ) {
                            Text("Back")
                        }
                    }
                    Button(
                        attrs = {
                            attr("id", "web-agent-create-submit")
                            if (busy) disabled()
                            onClick { onCreate(trimmedName, trimmedUsername, workspaceDrafts) }
                            style { primaryButton(enabled = !busy) }
                        }
                    ) {
                        Text(if (busy) "Creating…" else "Create agent")
                    }
                }
            }
        }
    }
}

@Composable
private fun AgentWorkspaceDraftRow(
    compact: Boolean,
    access: KairosWorkspaceAccess,
    selectedRole: Kairos.WorkspaceMembershipRole?,
    onChanged: (Kairos.WorkspaceMembershipRole?) -> Unit,
) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "grid",
                    "grid-template-columns" to
                        if (compact) "minmax(0, 1fr)" else "minmax(0, 1fr) 150px",
                    "gap" to "12px",
                    "align-items" to "center",
                )
            }
        }
    ) {
        Div(attrs = { style { props("display" to "grid", "gap" to "2px") } }) {
            Div(attrs = { style { props("font-size" to "14px", "font-weight" to "700") } }) {
                Text(access.workspace.name)
            }
            Div(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                Text(
                    if (access.role == Kairos.WorkspaceMembershipRole.Owner) {
                        "You can grant Member or Owner here."
                    } else {
                        "You can grant Member here. Owner isn't available because your own access is limited."
                    }
                )
            }
        }
        Select(
            attrs = {
                attr("id", "web-agent-create-workspace-${access.workspace.id.value}")
                onChange {
                    onChanged(
                        when (it.value) {
                            "none" -> null
                            "owner" -> Kairos.WorkspaceMembershipRole.Owner
                            else -> Kairos.WorkspaceMembershipRole.Member
                        }
                    )
                }
                style { formInputStyle() }
            }
        ) {
            Option(
                value = "none",
                attrs = { if (selectedRole == null) attr("selected", "selected") },
            ) {
                Text("No access")
            }
            Option(
                value = "member",
                attrs = {
                    if (selectedRole == Kairos.WorkspaceMembershipRole.Member) {
                        attr("selected", "selected")
                    }
                },
            ) {
                Text("Member")
            }
            if (access.role == Kairos.WorkspaceMembershipRole.Owner) {
                Option(
                    value = "owner",
                    attrs = {
                        if (selectedRole == Kairos.WorkspaceMembershipRole.Owner) {
                            attr("selected", "selected")
                        }
                    },
                ) {
                    Text("Owner")
                }
            }
        }
    }
}

@Composable
private fun AgentTokenRevealModal(reveal: WebAgentTokenReveal, onDismiss: () -> Unit) {
    var copied by remember(reveal.token) { mutableStateOf(false) }
    var confirmCloseWithoutCopy by remember(reveal.token) { mutableStateOf(false) }
    var revealed by remember(reveal.token) { mutableStateOf(true) }
    val maskedToken = "•".repeat(reveal.token.length.coerceAtLeast(16))
    ModalCard(
        zIndex = 97,
        title = if (reveal.rotated) "Save your new agent token" else "Save your agent token",
        onDismiss = onDismiss,
        showCloseButton = false,
    ) {
        Div(attrs = { style { props("display" to "grid", "gap" to "12px") } }) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "gap" to "6px",
                            "border" to "1px solid #e5978f",
                            "background" to "#fff1ee",
                            "border-radius" to "8px",
                            "padding" to "12px",
                        )
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "align-items" to "center",
                                "gap" to "8px",
                                "font-size" to "13px",
                                "font-weight" to "700",
                            )
                        }
                    }
                ) {
                    Text("⚠")
                    Text("Copy this token now — it cannot be recovered.")
                }
                MutedText(
                    "If lost, you'll need to rotate the token and update any scripts using it. The agent signs in as @${reveal.username} with this token as the password."
                )
            }
            Div(attrs = { style { modalReadonlyBox() } }) { Text(reveal.displayName) }
            Div(attrs = { style { modalReadonlyBox() } }) { Text("@${reveal.username}") }
            Div(
                attrs = {
                    attr("id", "web-agent-token-value")
                    style {
                        props(
                            "border" to "1px solid $Border",
                            "border-radius" to "8px",
                            "padding" to "12px",
                            "font-family" to "ui-monospace, SFMono-Regular, Menlo, monospace",
                            "font-size" to "13px",
                            "line-height" to "1.5",
                            "overflow-wrap" to "anywhere",
                            "background" to "#fffaf2",
                        )
                    }
                }
            ) {
                Text(if (revealed) reveal.token else maskedToken)
            }
            MutedText(
                "Store it in a password manager or secrets manager before you close this screen."
            )
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "space-between",
                            "gap" to "10px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                Div(
                    attrs = {
                        style { props("display" to "flex", "gap" to "10px", "flex-wrap" to "wrap") }
                    }
                ) {
                    Button(
                        attrs = {
                            attr("id", "web-agent-token-visibility-button")
                            onClick { revealed = !revealed }
                            style { secondaryButton() }
                        }
                    ) {
                        Text(if (revealed) "Hide token" else "Show token")
                    }
                    if (copied) {
                        Button(
                            attrs = {
                                attr("id", "web-agent-token-close-button")
                                onClick { onDismiss() }
                                style { secondaryButton() }
                            }
                        ) {
                            Text("Done")
                        }
                    }
                }
                Button(
                    attrs = {
                        attr("id", "web-agent-token-copy-button")
                        onClick {
                            copyTextToClipboard(
                                reveal.token,
                                onSuccess = {
                                    copied = true
                                    confirmCloseWithoutCopy = false
                                },
                                onFailure = { copied = false },
                            )
                        }
                        style { primaryButton() }
                    }
                ) {
                    Text(if (copied) "Copied ✓" else "Copy token")
                }
            }
            if (!copied) {
                Button(
                    attrs = {
                        attr("id", "web-agent-token-close-button")
                        onClick { confirmCloseWithoutCopy = true }
                        style {
                            inlineLinkButton()
                            props("justify-self" to "start", "color" to "#97554e")
                        }
                    }
                ) {
                    Text("Close without saving token…")
                }
            }
            if (confirmCloseWithoutCopy && !copied) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "grid",
                                "gap" to "10px",
                                "border" to "1px solid #e5978f",
                                "background" to "#fff1ee",
                                "border-radius" to "8px",
                                "padding" to "12px",
                            )
                        }
                    }
                ) {
                    MutedText(
                        "Have you saved the token? If you close now, you will need to rotate it and update any scripts using the old token before the agent can sign in again."
                    )
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "justify-content" to "flex-end",
                                    "gap" to "10px",
                                    "flex-wrap" to "wrap",
                                )
                            }
                        }
                    ) {
                        Button(
                            attrs = {
                                onClick { confirmCloseWithoutCopy = false }
                                style { secondaryButton() }
                            }
                        ) {
                            Text("Keep token visible")
                        }
                        Button(
                            attrs = {
                                attr("id", "web-agent-token-close-confirm-button")
                                onClick { onDismiss() }
                                style { destructiveButton() }
                            }
                        ) {
                            Text("Discard and close")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AgentRotateTokenConfirmModal(
    agent: KairosAgentSubaccount,
    busy: Boolean,
    onDismiss: () -> Unit,
    onConfirm: () -> Unit,
) {
    var acknowledged by remember(agent.user.id.value) { mutableStateOf(false) }
    ModalCard(
        zIndex = 97,
        title = "Rotate token for ${agent.user.displayName}?",
        onDismiss = onDismiss,
        showCloseButton = false,
    ) {
        Div(attrs = { style { props("display" to "grid", "gap" to "12px") } }) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "gap" to "6px",
                            "border" to "1px solid #f3c88d",
                            "background" to "#fff7ea",
                            "border-radius" to "8px",
                            "padding" to "12px",
                        )
                    }
                }
            ) {
                Div(attrs = { style { props("font-size" to "13px", "font-weight" to "700") } }) {
                    Text("Rotating immediately invalidates the current token.")
                }
                MutedText(
                    "Any scripts or integrations using @${agent.username} will stop working until you replace the old token."
                )
            }
            Div(attrs = { style { props("display" to "grid", "gap" to "6px") } }) {
                MutedText("Before you continue:")
                Div(attrs = { style { props("font-size" to "13px", "line-height" to "1.5") } }) {
                    Text("• The current token stops working immediately.")
                }
                Div(attrs = { style { props("font-size" to "13px", "line-height" to "1.5") } }) {
                    Text("• Any running scripts must be updated with the replacement token.")
                }
                Div(attrs = { style { props("font-size" to "13px", "line-height" to "1.5") } }) {
                    Text("• The new token will only be shown once after rotation.")
                }
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "align-items" to "start",
                            "gap" to "10px",
                            "font-size" to "13px",
                            "line-height" to "1.5",
                        )
                    }
                }
            ) {
                Input(
                    type = InputType.Checkbox,
                    attrs = {
                        attr("id", "web-agent-rotate-acknowledge")
                        checked(acknowledged)
                        onInput { acknowledged = !acknowledged }
                        style { props("margin-top" to "2px") }
                    },
                )
                Div(attrs = { style { props("padding-top" to "1px") } }) {
                    Text("I understand the current token will stop working immediately.")
                }
            }
            if (!acknowledged) {
                Div(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                    Text("Check the box above to continue.")
                }
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "flex-end",
                            "gap" to "10px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                Button(
                    attrs = {
                        onClick { onDismiss() }
                        style { secondaryButton() }
                    }
                ) {
                    Text("Cancel")
                }
                Button(
                    attrs = {
                        attr("id", "web-agent-rotate-confirm-${agent.user.id.value}")
                        if (busy || !acknowledged) disabled()
                        onClick { onConfirm() }
                        style {
                            destructiveButton()
                            if (!acknowledged) {
                                props(
                                    "opacity" to "0.48",
                                    "box-shadow" to "none",
                                    "cursor" to "not-allowed",
                                )
                            }
                        }
                    }
                ) {
                    Text("Rotate token")
                }
            }
        }
    }
}

@Composable
private fun AgentDeleteConfirmModal(
    agent: KairosAgentSubaccount,
    busy: Boolean,
    onDismiss: () -> Unit,
    onConfirm: () -> Unit,
) {
    var acknowledgement by remember(agent.user.id.value) { mutableStateOf("") }
    val confirmed = acknowledgement.trim() == agent.user.displayName
    ModalCard(
        zIndex = 97,
        title = "Delete ${agent.user.displayName}?",
        onDismiss = onDismiss,
        showCloseButton = false,
    ) {
        Div(attrs = { style { props("display" to "grid", "gap" to "12px") } }) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "gap" to "6px",
                            "border" to "1px solid #efc0bb",
                            "background" to "#fff4f2",
                            "border-radius" to "8px",
                            "padding" to "12px",
                        )
                    }
                }
            ) {
                Div(attrs = { style { props("font-size" to "13px", "font-weight" to "700") } }) {
                    Text("Deleting this agent immediately invalidates its token.")
                }
                MutedText(
                    "Scripts using @${agent.username} will stop working, and the agent will lose access to every workspace."
                )
            }
            Div(attrs = { style { props("display" to "grid", "gap" to "6px") } }) {
                MutedText("Type the agent name to confirm:")
                Input(
                    type = InputType.Text,
                    attrs = {
                        attr("id", "web-agent-delete-confirm-input-${agent.user.id.value}")
                        value(acknowledgement)
                        onInput { acknowledgement = it.value }
                        attr("placeholder", agent.user.displayName)
                        style { formInputStyle() }
                    },
                )
            }
            if (!confirmed) {
                Div(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                    Text("Enter ${agent.user.displayName} exactly to continue.")
                }
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "flex-end",
                            "gap" to "10px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                Button(
                    attrs = {
                        onClick { onDismiss() }
                        style { secondaryButton() }
                    }
                ) {
                    Text("Cancel")
                }
                Button(
                    attrs = {
                        attr("id", "web-agent-delete-confirm-${agent.user.id.value}")
                        if (busy || !confirmed) disabled()
                        onClick { onConfirm() }
                        style {
                            destructiveButton()
                            if (!confirmed) {
                                props(
                                    "opacity" to "0.48",
                                    "box-shadow" to "none",
                                    "cursor" to "not-allowed",
                                )
                            }
                        }
                    }
                ) {
                    Text("Delete agent")
                }
            }
        }
    }
}

@Composable
private fun OverlayScrim(zIndex: Int, onDismiss: () -> Unit) {
    Div(
        attrs = {
            onClick { onDismiss() }
            style {
                props(
                    "position" to "fixed",
                    "inset" to "0",
                    "z-index" to zIndex.toString(),
                    "background" to "rgba(20,22,28,0.44)",
                    "backdrop-filter" to "blur(2px)",
                )
            }
        }
    )
}

@Composable
private fun ModalBackdrop(
    zIndex: Int,
    background: String = "rgba(20,22,28,0.24)",
    onDismiss: () -> Unit,
) {
    Div(
        attrs = {
            onClick { onDismiss() }
            style {
                props(
                    "position" to "fixed",
                    "inset" to "0",
                    "z-index" to zIndex.toString(),
                    "background" to background,
                )
            }
        }
    )
}

@Composable
private fun ModalCard(
    zIndex: Int,
    title: String,
    onDismiss: () -> Unit,
    showCloseButton: Boolean = true,
    content: @Composable () -> Unit,
) {
    Div(
        attrs = {
            onClick { it.stopPropagation() }
            style {
                props(
                    "position" to "fixed",
                    "top" to "96px",
                    "left" to "50%",
                    "transform" to "translateX(-50%)",
                    "width" to "min(460px, calc(100vw - 32px))",
                    "max-height" to "calc(100vh - 120px)",
                    "overflow" to "auto",
                    "z-index" to zIndex.toString(),
                    "display" to "grid",
                    "gap" to "14px",
                    "padding" to "20px",
                    "border" to "1px solid $Border",
                    "border-radius" to "12px",
                    "background" to Paper,
                    "box-shadow" to "0 24px 60px rgba(20,22,28,0.18)",
                    "box-sizing" to "border-box",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "justify-content" to "space-between",
                        "gap" to "12px",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style { props("font-size" to "20px", "font-weight" to "700", "color" to Ink) }
                }
            ) {
                Text(title)
            }
            if (showCloseButton) {
                Button(
                    attrs = {
                        onClick { onDismiss() }
                        style { tertiaryButton() }
                    }
                ) {
                    Text("Close")
                }
            }
        }
        content()
    }
}

@Composable
private fun SidebarHeading(
    label: String,
    actionLabel: String? = null,
    actionId: String? = null,
    onAction: (() -> Unit)? = null,
) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "flex",
                    "align-items" to "center",
                    "justify-content" to "space-between",
                    "gap" to "10px",
                    "margin" to "22px 12px 8px",
                )
            }
        }
    ) {
        Span(
            attrs = {
                style {
                    props(
                        "font-size" to "11px",
                        "font-weight" to "700",
                        "color" to "#b9bdc5",
                        "letter-spacing" to "0.02em",
                    )
                }
            }
        ) {
            Text(label)
        }
        if (actionLabel != null && onAction != null) {
            Button(
                attrs = {
                    actionId?.let { attr("id", it) }
                    onClick { onAction() }
                    title("Create project")
                    style {
                        props(
                            "width" to "24px",
                            "height" to "24px",
                            "border" to "1px solid rgba(255,255,255,0.12)",
                            "border-radius" to "6px",
                            "background" to "transparent",
                            "color" to "#d9dee6",
                            "padding" to "0",
                            "font-size" to "18px",
                            "line-height" to "1",
                            "cursor" to "pointer",
                        )
                    }
                }
            ) {
                Text(actionLabel)
            }
        }
    }
}

private fun copyTextToClipboard(
    text: String,
    onSuccess: () -> Unit = {},
    onFailure: () -> Unit = {},
) {
    val trimmed = text.trim()
    if (trimmed.isEmpty()) {
        onFailure()
        return
    }
    val clipboard = window.navigator.asDynamic().clipboard
    if (clipboard == null) {
        onFailure()
        return
    }
    clipboard
        .writeText(trimmed)
        .then(
            {
                onSuccess()
                null
            },
            { _ ->
                onFailure()
                null
            },
        )
}

@Composable
private fun SidebarMetaItem(label: String) {
    Div(
        attrs = {
            style {
                props(
                    "padding" to "6px 12px",
                    "font-size" to "13px",
                    "color" to "#eef0f3",
                    "white-space" to "nowrap",
                    "overflow" to "hidden",
                    "text-overflow" to "ellipsis",
                )
            }
        }
    ) {
        Text(label)
    }
}

@Composable
private fun SidebarItem(
    label: String,
    selected: Boolean,
    areaToken: String? = null,
    onSelect: (() -> Unit)? = null,
) {
    Div(
        attrs = {
            areaToken?.let { token -> attr("data-sidebar-area", token) }
            if (onSelect != null) {
                onClick { onSelect() }
            }
            style {
                props(
                    "height" to "34px",
                    "display" to "flex",
                    "align-items" to "center",
                    "gap" to "10px",
                    "padding" to "0 12px 0 14px",
                    "border-radius" to "8px",
                    "border-left" to
                        if (selected) {
                            "3px solid $Coral"
                        } else {
                            "3px solid transparent"
                        },
                    "background" to if (selected) "rgba(255,255,255,0.1)" else "transparent",
                    "font-size" to "14px",
                    "font-weight" to if (selected) "700" else "600",
                    "color" to "#eef0f3",
                    "cursor" to if (onSelect != null) "pointer" else "default",
                )
            }
        }
    ) {
        Text(label)
    }
}

@Composable
private fun SidebarProjectTree(
    workspace: WebWorkspace,
    project: Kairos.Project,
    depth: Int,
    selectedProjectId: Kairos.ProjectId?,
    selectedArea: WorkspaceArea,
    onProjectSelected: (Kairos.ProjectId) -> Unit,
) {
    SidebarProjectRow(
        workspace = workspace,
        project = project,
        depth = depth,
        selected = selectedArea == WorkspaceArea.Project && project.id == selectedProjectId,
        emphasized = depth == 0,
        onProjectSelected = onProjectSelected,
    )
    workspace.childProjects(project.id).forEach { child ->
        SidebarProjectTree(
            workspace = workspace,
            project = child,
            depth = depth + 1,
            selectedProjectId = selectedProjectId,
            selectedArea = selectedArea,
            onProjectSelected = onProjectSelected,
        )
    }
}

@Composable
private fun SidebarProjectRow(
    workspace: WebWorkspace,
    project: Kairos.Project,
    depth: Int,
    selected: Boolean,
    emphasized: Boolean,
    onProjectSelected: (Kairos.ProjectId) -> Unit,
) {
    val color = project.projectAccent()
    val openTicketCount =
        remember(workspace, project.id) { workspace.projectOpenTicketCount(project.id) }
    val hasVisibleWork =
        remember(workspace, project.id) { workspace.projectHasVisibleWork(project.id) }

    Div(
        attrs = {
            attr("data-sidebar-project-id", project.id.value)
            attr("title", project.projectDisplayName(workspace.projects))
            onClick { onProjectSelected(project.id) }
            style {
                props(
                    "min-height" to "34px",
                    "display" to "flex",
                    "align-items" to "start",
                    "gap" to "10px",
                    "padding" to "7px 12px 7px ${16 + (depth * 24)}px",
                    "border-radius" to "8px",
                    "background" to if (selected) "rgba(255,255,255,0.08)" else "transparent",
                    "font-size" to if (depth == 0) "14px" else "13px",
                    "color" to "#eef0f3",
                    "cursor" to "pointer",
                )
            }
        }
    ) {
        Span(
            attrs = {
                style {
                    props(
                        "display" to "inline-block",
                        "width" to "8px",
                        "height" to "8px",
                        "background" to color,
                        "border-radius" to "999px",
                        "opacity" to if (selected) "1" else "0.55",
                        "margin-top" to "4px",
                        "flex-shrink" to "0",
                    )
                }
            }
        )
        Div(
            attrs = {
                style {
                    props("display" to "grid", "gap" to "2px", "min-width" to "0", "flex" to "1")
                }
            }
        ) {
            Span(
                attrs = {
                    attr("title", project.projectDisplayName(workspace.projects))
                    style {
                        props(
                            "font-weight" to if (selected || emphasized) "700" else "600",
                            "line-height" to "1.3",
                            "display" to "-webkit-box",
                            "-webkit-line-clamp" to "2",
                            "-webkit-box-orient" to "vertical",
                            "overflow" to "hidden",
                        )
                    }
                }
            ) {
                Text(project.name)
            }
            if (hasVisibleWork && depth > 0) {
                Span(attrs = { style { props("font-size" to "11px", "color" to "#aab0ba") } }) {
                    Text(workspace.projectScopeSummary(project.id))
                }
            }
        }
        if (openTicketCount > 0) {
            Span(
                attrs = {
                    style {
                        props(
                            "display" to "inline-flex",
                            "align-items" to "center",
                            "justify-content" to "center",
                            "min-width" to "22px",
                            "height" to "20px",
                            "padding" to "0 6px",
                            "border-radius" to "999px",
                            "background" to "rgba(255,255,255,0.08)",
                            "font-size" to "11px",
                            "font-weight" to "700",
                            "color" to "#d7dae0",
                            "flex-shrink" to "0",
                        )
                    }
                }
            ) {
                Text(openTicketCount.toString())
            }
        }
    }
}

@Composable
private fun MainWorkspace(
    workspace: WebWorkspace,
    compactLayout: Boolean,
    selectedArea: WorkspaceArea,
    columns: List<WebColumn>,
    filteredTasks: List<WebTask>,
    inboxTasks: List<WebTask>,
    downloadsManifest: WebDownloadsManifest?,
    downloadsLoading: Boolean,
    downloadsError: String?,
    adminSnapshot: KairosAdminSnapshot?,
    adminLoading: Boolean,
    adminError: String?,
    adminNotice: String?,
    adminRoleSavingUserId: Kairos.UserId?,
    viewerUser: Kairos.User?,
    view: String,
    selectedProjectId: Kairos.ProjectId?,
    selectedTask: WebTask?,
    notice: String?,
    includeCompleted: Boolean,
    hideEmptyColumns: Boolean,
    showBoardInbox: Boolean,
    sort: WebSort,
    detailsOpen: Boolean,
    scheduleToolsOpen: Boolean,
    onViewChanged: (String) -> Unit,
    onTaskSelected: (WebTask) -> Unit,
    onScheduleEntrySelected: (WebScheduleEntry) -> Unit,
    onAddTask: (Kairos.ProjectId) -> Unit,
    onSetCompleted: (WebTask, Boolean) -> Unit,
    onToggleCompleted: () -> Unit,
    onToggleEmpty: () -> Unit,
    onToggleBoardInbox: () -> Unit,
    onToggleSort: () -> Unit,
    onToggleDetails: () -> Unit,
    onToggleScheduleTools: () -> Unit,
    onReset: () -> Unit,
    onShowObjectives: () -> Unit,
    onShowSchedule: (Kairos.ProjectId?) -> Unit,
    onCreateProject: (String, Kairos.ProjectId?) -> Unit,
    onUpdateProjectMetadata: (Kairos.ProjectId, String?, Kairos.ProjectId?, Boolean) -> Unit,
    onUpdateStage: (WebTask, Kairos.TicketStage) -> Unit,
    onCreateObjective: (String, Kairos.ProjectId?, ZonedDateTime?, List<Kairos.TicketId>) -> Unit,
    onUpdateObjective: (Kairos.ObjectiveId, String, ZonedDateTime?) -> Unit,
    onCreateKpi: (Kairos.ProjectId?, String, Kairos.KpiTarget?, Kairos.KpiOrigin?) -> Unit,
    onUpdateKpi: (Kairos.KpiId, String?, Kairos.KpiTarget?, Boolean, Kairos.KpiOrigin?) -> Unit,
    onCreateObjectiveKpi: (Kairos.Objective, String, Kairos.KpiTarget) -> Unit,
    onCreateKpiMeasurement: (Kairos.KpiId, Double) -> Unit,
    onSetObjectiveKpiTargets: (Kairos.ObjectiveId, List<Kairos.KpiWithTarget>) -> Unit,
    onCreateEvent:
        (Kairos.ProjectId?, String, String, ZonedDateTime, ZonedDateTime?, String, String) -> Unit,
    onUpdateEvent: (Kairos.EventId, String, String, ZonedDateTime, ZonedDateTime?) -> Unit,
    onCreateTimebox:
        (Kairos.ProjectId?, List<Kairos.TicketId>, String, ZonedDateTime, Int?) -> Unit,
    onUpdateTimebox: (Kairos.TimeboxId, String, ZonedDateTime, Int?) -> Unit,
    onCreateRoutine: (Kairos.ProjectId?, String) -> Unit,
    onUpdateRoutine: (Kairos.RoutineId, String) -> Unit,
    workspaceSettingsTab: WebWorkspaceSettingsTab,
    workspaceMembers: List<KairosWorkspaceMember>,
    workspaceMembersLoading: Boolean,
    workspaceMembersNotice: String?,
    workspaceMembersError: String?,
    onWorkspaceSettingsTabChanged: (WebWorkspaceSettingsTab) -> Unit,
    onOpenWorkspaceShare: () -> Unit,
    onOpenAgentSettings: (Kairos.UserId?) -> Unit,
    onCopyOwnerContacts: (String) -> Unit,
    onUpdateWorkspaceMemberRole: (Kairos.UserId, Kairos.WorkspaceMembershipRole) -> Unit,
    onRemoveWorkspaceMember: (Kairos.UserId) -> Unit,
    onRequestWorkspaceMemberAction: (WebWorkspaceMemberAction) -> Unit,
    onRetryDownloads: () -> Unit,
    onRetryAdmin: () -> Unit,
    onUpdateAdminRole: (Kairos.UserId, Kairos.UserRole) -> Unit,
) {
    val projectView = normalizedProjectView(view)
    val runQuickAction: (WebTask) -> Unit = { task ->
        when (val action = task.ticket.quickAction()) {
            null -> Unit
            else ->
                if (action.markCompleted) {
                    onSetCompleted(task, true)
                } else {
                    action.stage?.let { stage -> onUpdateStage(task, stage) }
                }
        }
    }
    val workspaceSnapshot = remember(workspace) { workspace.toSnapshot() }
    val dailyPlan =
        remember(workspaceSnapshot, viewerUser?.id) {
            buildKairosDailyPlan(
                workspace = workspaceSnapshot,
                today = todayDate(),
                userId = viewerUser?.id,
            )
        }
    val executionFlow =
        remember(workspaceSnapshot, dailyPlan) {
            buildKairosExecutionFlow(workspaceSnapshot, dailyPlan)
        }
    Div(
        attrs = {
            style {
                props(
                    "display" to "flex",
                    "flex-direction" to "column",
                    "min-width" to "0",
                    "min-height" to "0",
                    "flex" to "1",
                    "background" to Paper,
                    "overflow-y" to "auto",
                    "overflow-x" to "hidden",
                )
            }
        }
    ) {
        when (selectedArea) {
            WorkspaceArea.Home -> {
                WorkspaceHeader(
                    title = "Today",
                    subtitle =
                        viewerUser?.displayName?.let { name -> "Focus for $name" }
                            ?: "Kairos overview",
                )
                HomeView(
                    workspace = workspace,
                    dailyPlan = dailyPlan,
                    executionFlow = executionFlow,
                    allTasks = filteredTasks,
                    compact = compactLayout,
                    detailOpen = selectedTask != null,
                    onTaskSelected = onTaskSelected,
                    onRunQuickAction = runQuickAction,
                    onShowObjectives = onShowObjectives,
                    onShowSchedule = onShowSchedule,
                )
            }
            WorkspaceArea.Inbox -> {
                WorkspaceHeader(title = "Inbox", subtitle = "New and untriaged work")
                InboxView(
                    workspace = workspace,
                    tasks = inboxTasks,
                    onTaskSelected = onTaskSelected,
                    onRunQuickAction = runQuickAction,
                )
            }
            WorkspaceArea.Objectives -> {
                WorkspaceHeader(
                    title = "Objectives",
                    subtitle =
                        "${workspace.objectives.size} objectives linked to real projects and tickets",
                )
                ObjectivesView(
                    workspace = workspace,
                    compact = compactLayout,
                    onTaskSelected = onTaskSelected,
                    onCreateObjective = onCreateObjective,
                    onUpdateObjective = onUpdateObjective,
                    onCreateKpi = onCreateKpi,
                    onUpdateKpi = onUpdateKpi,
                    onCreateObjectiveKpi = onCreateObjectiveKpi,
                    onCreateKpiMeasurement = onCreateKpiMeasurement,
                    onSetObjectiveKpiTargets = onSetObjectiveKpiTargets,
                )
            }
            WorkspaceArea.Kpis -> {
                WorkspaceHeader(
                    title = "KPIs",
                    subtitle = "${workspace.kpis.size} KPIs across projects and objective rollups",
                )
                KpisView(
                    workspace = workspace,
                    compact = compactLayout,
                    onCreateKpi = onCreateKpi,
                    onUpdateKpi = onUpdateKpi,
                    onCreateKpiMeasurement = onCreateKpiMeasurement,
                )
            }
            WorkspaceArea.Admin -> {
                WorkspaceHeader(
                    title = "Admin console",
                    subtitle =
                        "Global users, workspaces, and access health for this Kairos instance.",
                )
                AdminConsoleView(
                    snapshot = adminSnapshot,
                    loading = adminLoading,
                    error = adminError,
                    notice = adminNotice,
                    savingUserId = adminRoleSavingUserId,
                    compact = compactLayout,
                    onRetry = onRetryAdmin,
                    onUpdateUserRole = onUpdateAdminRole,
                )
            }
            WorkspaceArea.Downloads -> {
                WorkspaceHeader(
                    title = "Downloads",
                    subtitle = "CLI and native builds that have been published from this release.",
                )
                DownloadsCatalogPanel(
                    manifest = downloadsManifest,
                    loading = downloadsLoading,
                    error = downloadsError,
                    compact = compactLayout,
                    onRetry = onRetryDownloads,
                )
            }
            WorkspaceArea.WorkspaceSettings -> {
                val viewerRole =
                    viewerUser
                        ?.id
                        ?.let { userId ->
                            workspace.memberships.firstOrNull { membership ->
                                membership.workspaceId == workspace.workspace.id &&
                                    membership.userId == userId
                            }
                        }
                        ?.role
                WorkspaceHeader(
                    title =
                        if (viewerRole == Kairos.WorkspaceMembershipRole.Owner) {
                            "Workspace settings"
                        } else {
                            "Workspace members"
                        },
                    subtitle = workspace.workspace.name,
                )
                WorkspaceSettingsView(
                    workspace = workspace,
                    viewerUser = viewerUser,
                    compact = compactLayout,
                    tab = workspaceSettingsTab,
                    members = workspaceMembers,
                    loading = workspaceMembersLoading,
                    notice = workspaceMembersNotice,
                    error = workspaceMembersError,
                    onTabChanged = onWorkspaceSettingsTabChanged,
                    onOpenShare = onOpenWorkspaceShare,
                    onOpenAgentSettings = onOpenAgentSettings,
                    onCopyOwnerContacts = onCopyOwnerContacts,
                    onRequestMemberAction = onRequestWorkspaceMemberAction,
                )
            }
            WorkspaceArea.Project -> {
                ProjectHeader(
                    workspace = workspace,
                    selectedProjectId = selectedProjectId,
                    compact = compactLayout,
                    onCreateProject = onCreateProject,
                    onUpdateProjectMetadata = onUpdateProjectMetadata,
                )
                Tabs(projectView, onViewChanged)
                Toolbar(
                    workspace = workspace,
                    selectedProjectId = selectedProjectId,
                    selectedTask = selectedTask,
                    compact = compactLayout,
                    view = projectView,
                    notice = notice,
                    includeCompleted = includeCompleted,
                    hideEmptyColumns = hideEmptyColumns,
                    showBoardInbox = showBoardInbox,
                    sort = sort,
                    detailsOpen = detailsOpen,
                    onAddTask = onAddTask,
                    onToggleCompleted = onToggleCompleted,
                    onToggleEmpty = onToggleEmpty,
                    onToggleBoardInbox = onToggleBoardInbox,
                    onToggleSort = onToggleSort,
                    onToggleDetails = onToggleDetails,
                    onReset = onReset,
                )
                val scopedTasks = columns.flatMap { column -> column.tasks }
                if (projectView == "Timeline" || projectView == "Calendar") {
                    ScheduleToolsBar(
                        workspace = workspace,
                        selectedProjectId = selectedProjectId,
                        scheduleToolsOpen = scheduleToolsOpen,
                        onToggleScheduleTools = onToggleScheduleTools,
                    )
                }
                when (projectView) {
                    "Overview" ->
                        OverviewView(
                            workspace = workspace,
                            selectedProjectId = selectedProjectId,
                            tasks = scopedTasks,
                            onTaskSelected = onTaskSelected,
                            onRunQuickAction = runQuickAction,
                        )
                    "List" ->
                        ListView(
                            workspace = workspace,
                            columns = columns,
                            selectedTask = selectedTask,
                            compact = compactLayout,
                            onTaskSelected = onTaskSelected,
                            onRunQuickAction = runQuickAction,
                        )
                    "Timeline" ->
                        TimelineView(
                            workspace = workspace,
                            columns = columns,
                            compact = compactLayout,
                            onTaskSelected = onTaskSelected,
                            onScheduleEntrySelected = { onScheduleEntrySelected(it) },
                        )
                    "Calendar" ->
                        CalendarView(
                            workspace,
                            columns,
                            compactLayout,
                            onTaskSelected,
                            onScheduleEntrySelected = { onScheduleEntrySelected(it) },
                        )
                    else ->
                        BoardView(
                            columns =
                                scopedTasks
                                    .toStageColumns(showEmptyColumns = !hideEmptyColumns)
                                    .filter { column ->
                                        showBoardInbox || column.stage != Kairos.TicketStage.Inbox
                                    },
                            compact = compactLayout,
                            onMoveTask = onUpdateStage,
                            onTaskSelected = onTaskSelected,
                            onRunQuickAction = runQuickAction,
                        )
                }
            }
        }
    }
}

@Composable
private fun WorkspaceHeader(title: String, subtitle: String) {
    Div(
        attrs = {
            style {
                props(
                    "padding" to "22px 28px",
                    "border-bottom" to "1px solid $Border",
                    "background" to Paper,
                    "display" to "grid",
                    "gap" to "6px",
                )
            }
        }
    ) {
        Div(attrs = { style { props("font-size" to "28px", "font-weight" to "700") } }) {
            Text(title)
        }
        Div(attrs = { style { props("font-size" to "14px", "color" to Muted) } }) { Text(subtitle) }
    }
}

@Composable
private fun WorkspaceShareModal(
    workspace: WebWorkspace,
    compact: Boolean,
    identifierDraft: String,
    lookupState: WebWorkspaceShareLookupState,
    busy: Boolean,
    onIdentifierChanged: (String) -> Unit,
    onLookup: () -> Unit,
    onConfirmShare: (KairosWorkspaceShareTarget) -> Unit,
    onDismiss: () -> Unit,
) {
    ModalCard(
        zIndex = 93,
        title = "Invite people to ${workspace.workspace.name}",
        onDismiss = onDismiss,
    ) {
        MutedText("We’ll verify the account before adding access.")
        Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
            SectionLabel("Username or email address")
            MutedText("Enter their Kairos username or the email they signed up with.")
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "grid-template-columns" to
                                if (compact) "1fr" else "minmax(0, 1fr) auto",
                            "gap" to "10px",
                            "align-items" to "center",
                        )
                    }
                }
            ) {
                Input(
                    type = InputType.Text,
                    attrs = {
                        attr("id", "web-workspace-share-identifier-input")
                        value(identifierDraft)
                        onInput { onIdentifierChanged(it.value) }
                        onKeyDown { event ->
                            val keyboardEvent = event.unsafeCast<KeyboardEvent>()
                            if (keyboardEvent.key == "Enter") {
                                keyboardEvent.preventDefault()
                                onLookup()
                            }
                        }
                        attr("placeholder", "e.g. mina or mina@company.com")
                        style { formInputStyle() }
                    },
                )
                Button(
                    attrs = {
                        attr("id", "web-workspace-share-lookup-button")
                        if (identifierDraft.trim().isEmpty() || busy) disabled()
                        onClick { onLookup() }
                        style {
                            primaryButton(enabled = identifierDraft.trim().isNotEmpty() && !busy)
                            props(
                                "height" to "40px",
                                "width" to if (compact) "100%" else "auto",
                                "min-width" to if (compact) "0" else "112px",
                            )
                        }
                    }
                ) {
                    Text(
                        if (lookupState == WebWorkspaceShareLookupState.Loading) "Looking up…"
                        else "Look up"
                    )
                }
            }
        }
        when (lookupState) {
            WebWorkspaceShareLookupState.Idle -> Unit
            WebWorkspaceShareLookupState.Loading -> {
                Div(attrs = { style { cardSurface() } }) {
                    SectionLabel("Checking account")
                    MutedText("Looking up the account without browsing the global directory.")
                }
            }
            is WebWorkspaceShareLookupState.NotFound -> {
                Div(
                    attrs = {
                        attr("id", "web-workspace-share-not-found")
                        style { cardSurface() }
                    }
                ) {
                    SectionLabel("No account found")
                    Div(
                        attrs = { style { props("font-size" to "14px", "line-height" to "1.6") } }
                    ) {
                        Text("No account found for \"${lookupState.identifier}\".")
                    }
                    MutedText("Check the username or email and try again.")
                }
            }
            is WebWorkspaceShareLookupState.Error -> {
                Div(
                    attrs = {
                        attr("id", "web-workspace-share-error")
                        style { cardSurface() }
                    }
                ) {
                    SectionLabel("Could not complete lookup")
                    Div(
                        attrs = {
                            style {
                                props(
                                    "font-size" to "14px",
                                    "line-height" to "1.6",
                                    "color" to Coral,
                                )
                            }
                        }
                    ) {
                        Text(lookupState.message)
                    }
                }
            }
            is WebWorkspaceShareLookupState.Resolved -> {
                val target = lookupState.target
                val alreadyMember = target.currentRole != null
                Div(
                    attrs = {
                        attr("id", "web-workspace-share-target-card")
                        style { cardSurface() }
                    }
                ) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "grid",
                                    "grid-template-columns" to "auto minmax(0, 1fr)",
                                    "gap" to "12px",
                                    "align-items" to "center",
                                )
                            }
                        }
                    ) {
                        Avatar(target.user, 42)
                        Div(attrs = { style { props("display" to "grid", "gap" to "4px") } }) {
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "font-size" to "16px",
                                            "font-weight" to "700",
                                            "line-height" to "1.35",
                                        )
                                    }
                                }
                            ) {
                                Text(target.user.displayName)
                            }
                            MutedText("@${target.username}")
                            target.currentRole?.let { role ->
                                Div(
                                    attrs = {
                                        style {
                                            props(
                                                "font-size" to "13px",
                                                "line-height" to "1.5",
                                                "color" to Ink,
                                            )
                                        }
                                    }
                                ) {
                                    Text(
                                        if (role == Kairos.WorkspaceMembershipRole.Owner) {
                                            "${target.user.displayName} already owns this workspace."
                                        } else {
                                            "${target.user.displayName} is already a member of this workspace."
                                        }
                                    )
                                }
                            } ?: run { MutedText("Is this the right person?") }
                        }
                    }
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "gap" to "10px",
                                    "justify-content" to "flex-end",
                                    "flex-wrap" to "wrap",
                                )
                            }
                        }
                    ) {
                        if (!alreadyMember) {
                            Button(
                                attrs = {
                                    attr("id", "web-workspace-share-confirm-button")
                                    if (busy) disabled()
                                    onClick { onConfirmShare(target) }
                                    style { primaryButton(enabled = !busy) }
                                }
                            ) {
                                Text("Add to ${workspace.workspace.name}")
                            }
                        }
                        Button(
                            attrs = {
                                attr("id", "web-workspace-share-dismiss-button")
                                onClick { onDismiss() }
                                style { secondaryButton() }
                            }
                        ) {
                            Text(if (alreadyMember) "Close" else "Not them")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun WorkspaceMemberActionModal(
    workspace: WebWorkspace,
    compact: Boolean,
    action: WebWorkspaceMemberAction,
    busy: Boolean,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
) {
    val title: String
    val detail: String
    val confirmLabel: String
    val confirmDestructive: Boolean
    when (action) {
        is WebWorkspaceMemberAction.Promote -> {
            title = "Make ${action.member.user.displayName} an owner?"
            detail =
                "${action.member.user.displayName} will have the same permissions as you, including adding, removing, and changing other members."
            confirmLabel = "Make owner"
            confirmDestructive = false
        }
        is WebWorkspaceMemberAction.Demote -> {
            title = "Set ${action.member.user.displayName} as a member?"
            detail =
                "${action.member.user.displayName} will lose owner permissions and will no longer be able to manage workspace access."
            confirmLabel = "Set as member"
            confirmDestructive = false
        }
        is WebWorkspaceMemberAction.Remove -> {
            title = "Remove ${action.member.user.displayName}?"
            detail =
                "${action.member.user.displayName} will lose access to all projects, tickets, and objectives in ${workspace.workspace.name}."
            confirmLabel = "Remove"
            confirmDestructive = true
        }
    }
    ModalCard(zIndex = 95, title = title, onDismiss = onDismiss) {
        Div(attrs = { style { props("display" to "grid", "gap" to "12px") } }) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "grid-template-columns" to "auto minmax(0, 1fr)",
                            "gap" to "12px",
                            "align-items" to "center",
                        )
                    }
                }
            ) {
                Avatar(action.member.user, 42)
                Div(attrs = { style { props("display" to "grid", "gap" to "4px") } }) {
                    Div(
                        attrs = { style { props("font-size" to "16px", "font-weight" to "700") } }
                    ) {
                        Text(action.member.user.displayName)
                    }
                    MutedText("@${action.member.username}")
                }
            }
            Div(
                attrs = {
                    style { props("font-size" to "14px", "line-height" to "1.6", "color" to Ink) }
                }
            ) {
                Text(detail)
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to if (compact) "stretch" else "flex-end",
                            "gap" to "10px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                Button(
                    attrs = {
                        onClick { onDismiss() }
                        style { secondaryButton() }
                    }
                ) {
                    Text(if (action is WebWorkspaceMemberAction.Remove) "Keep member" else "Cancel")
                }
                Button(
                    attrs = {
                        attr(
                            "id",
                            when (action) {
                                is WebWorkspaceMemberAction.Promote ->
                                    "web-workspace-member-promote-confirm-${action.member.user.id.value}"
                                is WebWorkspaceMemberAction.Demote ->
                                    "web-workspace-member-demote-confirm-${action.member.user.id.value}"
                                is WebWorkspaceMemberAction.Remove ->
                                    "web-workspace-member-remove-confirm-${action.member.user.id.value}"
                            },
                        )
                        if (busy) disabled()
                        onClick { onConfirm() }
                        style {
                            if (confirmDestructive) {
                                destructiveButton()
                            } else {
                                primaryButton(enabled = !busy)
                            }
                            if (compact) {
                                props("flex" to "1")
                            }
                        }
                    }
                ) {
                    Text(confirmLabel)
                }
            }
        }
    }
}

@Composable
private fun WorkspaceSettingsView(
    workspace: WebWorkspace,
    viewerUser: Kairos.User?,
    compact: Boolean,
    tab: WebWorkspaceSettingsTab,
    members: List<KairosWorkspaceMember>,
    loading: Boolean,
    notice: String?,
    error: String?,
    onTabChanged: (WebWorkspaceSettingsTab) -> Unit,
    onOpenShare: () -> Unit,
    onOpenAgentSettings: (Kairos.UserId?) -> Unit,
    onCopyOwnerContacts: (String) -> Unit,
    onRequestMemberAction: (WebWorkspaceMemberAction) -> Unit,
) {
    val currentMembership =
        viewerUser?.id?.let { userId ->
            workspace.memberships.firstOrNull { membership ->
                membership.workspaceId == workspace.workspace.id && membership.userId == userId
            }
        }
    val effectiveMembers =
        if (members.isNotEmpty()) members else workspace.inferredWorkspaceMembers()
    val canManageMembers = currentMembership?.role == Kairos.WorkspaceMembershipRole.Owner
    val visibleTabs =
        if (canManageMembers) {
            listOf(WebWorkspaceSettingsTab.General, WebWorkspaceSettingsTab.Members)
        } else {
            listOf(WebWorkspaceSettingsTab.Members)
        }
    val ownerContacts =
        effectiveMembers
            .filter { member -> member.role == Kairos.WorkspaceMembershipRole.Owner }
            .joinToString(", ") { member -> member.user.displayName }
    val ownerHandles =
        effectiveMembers
            .filter { member -> member.role == Kairos.WorkspaceMembershipRole.Owner }
            .joinToString(", ") { member -> "@${member.username}" }
    val ownerCount =
        effectiveMembers.count { member -> member.role == Kairos.WorkspaceMembershipRole.Owner }
    val humanMembers =
        effectiveMembers.filter { member -> member.user.kind == Kairos.UserKind.Human }
    val agentMembers =
        effectiveMembers.filter { member -> member.user.kind == Kairos.UserKind.Agent }

    Div(
        attrs = {
            style {
                props(
                    "display" to "grid",
                    "gap" to "18px",
                    "padding" to "20px",
                    "box-sizing" to "border-box",
                )
            }
        }
    ) {
        Div(
            attrs = { style { props("display" to "flex", "gap" to "10px", "flex-wrap" to "wrap") } }
        ) {
            visibleTabs.forEach { item ->
                val selected = item == tab
                Button(
                    attrs = {
                        attr(
                            "id",
                            if (item == WebWorkspaceSettingsTab.General) {
                                "web-workspace-settings-tab-general"
                            } else {
                                "web-workspace-settings-tab-members"
                            },
                        )
                        if (!selected) {
                            onClick { onTabChanged(item) }
                        }
                        style {
                            props(
                                "height" to "36px",
                                "border" to
                                    if (selected) "1px solid $Blue" else "1px solid $Border",
                                "border-radius" to "999px",
                                "background" to if (selected) "#eef4ff" else Paper,
                                "color" to if (selected) Blue else Ink,
                                "padding" to "0 14px",
                                "font-size" to "13px",
                                "font-weight" to "700",
                                "cursor" to if (selected) "default" else "pointer",
                            )
                        }
                    }
                ) {
                    Text(if (item == WebWorkspaceSettingsTab.General) "General" else "Members")
                }
            }
        }

        when (tab) {
            WebWorkspaceSettingsTab.General -> {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "grid",
                                "grid-template-columns" to "repeat(auto-fit, minmax(220px, 1fr))",
                                "gap" to "16px",
                            )
                        }
                    }
                ) {
                    SettingsSummaryCard("Workspace", workspace.workspace.name)
                    SettingsSummaryCard(
                        "Your access",
                        if (currentMembership?.role == Kairos.WorkspaceMembershipRole.Owner) {
                            "Owner"
                        } else {
                            "Member"
                        },
                    )
                    SettingsSummaryCard("Members", effectiveMembers.size.toString())
                }
            }
            WebWorkspaceSettingsTab.Members -> {
                Div(attrs = { style { cardSurface() } }) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "grid",
                                    "grid-template-columns" to
                                        if (compact) "minmax(0, 1fr)" else "minmax(0, 1fr) auto",
                                    "gap" to "12px",
                                    "flex-wrap" to "wrap",
                                )
                            }
                        }
                    ) {
                        Div(attrs = { style { props("display" to "grid", "gap" to "4px") } }) {
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "display" to "flex",
                                            "align-items" to "baseline",
                                            "gap" to "8px",
                                            "flex-wrap" to "wrap",
                                        )
                                    }
                                }
                            ) {
                                SectionLabel("Members")
                                MutedText("${effectiveMembers.size} total")
                            }
                            if (canManageMembers) {
                                MutedText(
                                    "Use a Kairos username or email address to add someone to this workspace."
                                )
                            } else {
                                Div(
                                    attrs = {
                                        style {
                                            props(
                                                "font-size" to "14px",
                                                "line-height" to "1.6",
                                                "color" to Ink,
                                            )
                                        }
                                    }
                                ) {
                                    Text(
                                        "You're a member of this workspace. Only workspace owners can change access."
                                    )
                                }
                                if (ownerContacts.isNotBlank()) {
                                    MutedText("To change workspace access, contact $ownerContacts.")
                                }
                                if (ownerHandles.isNotBlank()) {
                                    Button(
                                        attrs = {
                                            attr(
                                                "id",
                                                "web-workspace-members-copy-owner-contact-button",
                                            )
                                            onClick { onCopyOwnerContacts(ownerHandles) }
                                            style {
                                                inlineLinkButton()
                                                props("justify-self" to "start")
                                            }
                                        }
                                    ) {
                                        Text(
                                            if (ownerCount == 1) {
                                                "Copy $ownerHandles"
                                            } else {
                                                "Copy owner usernames"
                                            }
                                        )
                                    }
                                }
                            }
                        }
                        if (canManageMembers && !compact) {
                            Button(
                                attrs = {
                                    attr("id", "web-workspace-members-open-share-button")
                                    onClick { onOpenShare() }
                                    style {
                                        primaryButton(enabled = true)
                                        props(
                                            "height" to "40px",
                                            "width" to "auto",
                                            "min-width" to "132px",
                                            "justify-self" to if (compact) "start" else "end",
                                        )
                                    }
                                }
                            ) {
                                Text("Invite people")
                            }
                        }
                    }
                    notice?.let { message ->
                        Div(
                            attrs = {
                                attr("id", "web-workspace-members-notice")
                                style {
                                    props(
                                        "margin-top" to "10px",
                                        "font-size" to "13px",
                                        "line-height" to "1.5",
                                        "color" to "#2f7a52",
                                    )
                                }
                            }
                        ) {
                            Text(message)
                        }
                    }
                    error?.let { message ->
                        Div(
                            attrs = {
                                attr("id", "web-workspace-members-error")
                                style {
                                    props(
                                        "margin-top" to "10px",
                                        "font-size" to "13px",
                                        "line-height" to "1.5",
                                        "color" to "#b4453f",
                                    )
                                }
                            }
                        ) {
                            Text(message)
                        }
                    }
                    Div(
                        attrs = {
                            style {
                                props("display" to "grid", "gap" to "8px", "margin-top" to "10px")
                            }
                        }
                    ) {
                        SectionLabel("People")
                        if (canManageMembers && compact) {
                            Button(
                                attrs = {
                                    attr("id", "web-workspace-members-open-share-button")
                                    onClick { onOpenShare() }
                                    style {
                                        primaryButton(enabled = true)
                                        props(
                                            "height" to "40px",
                                            "width" to "100%",
                                            "max-width" to "220px",
                                            "justify-self" to "start",
                                        )
                                    }
                                }
                            ) {
                                Text("Invite people")
                            }
                        }
                        humanMembers.forEach { member ->
                            WorkspaceMemberRow(
                                member = member,
                                isCurrentUser = member.user.id == viewerUser?.id,
                                ownerCount = ownerCount,
                                canManageMembers = canManageMembers,
                                compact = compact,
                                busy = loading,
                                onRequestAction = onRequestMemberAction,
                            )
                        }
                        if (agentMembers.isNotEmpty()) {
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "display" to "grid",
                                            "gap" to "6px",
                                            "margin-top" to "10px",
                                        )
                                    }
                                }
                            ) {
                                SectionLabel("Agents")
                                MutedText(
                                    "Agents can access this workspace only when their owner grants access from account settings."
                                )
                                Button(
                                    attrs = {
                                        attr("id", "web-workspace-members-manage-agents-button")
                                        onClick { onOpenAgentSettings(null) }
                                        style { secondaryButton() }
                                    }
                                ) {
                                    Text("Manage all agents")
                                }
                            }
                            agentMembers.forEach { member ->
                                WorkspaceAgentRow(
                                    member = member,
                                    ownedByViewer = member.ownerUser?.id == viewerUser?.id,
                                    onManage = { onOpenAgentSettings(member.user.id) },
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun WorkspaceAgentRow(
    member: KairosWorkspaceMember,
    ownedByViewer: Boolean,
    onManage: () -> Unit,
) {
    Div(
        attrs = {
            attr("id", "web-workspace-agent-row-${member.user.id.value}")
            style {
                props(
                    "display" to "grid",
                    "grid-template-columns" to "minmax(0, 1fr) auto",
                    "gap" to "12px",
                    "align-items" to "center",
                    "padding" to "12px 14px",
                    "border" to "1px solid $Border",
                    "border-radius" to "8px",
                    "background" to "#fbfcfd",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "gap" to "12px",
                        "min-width" to "0",
                    )
                }
            }
        ) {
            Avatar(member.user, 34)
            Div(
                attrs = { style { props("display" to "grid", "gap" to "2px", "min-width" to "0") } }
            ) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "align-items" to "center",
                                "gap" to "8px",
                                "flex-wrap" to "wrap",
                            )
                        }
                    }
                ) {
                    Div(
                        attrs = { style { props("font-size" to "14px", "font-weight" to "700") } }
                    ) {
                        Text(member.user.displayName)
                    }
                    MetaChip("Agent", background = "#eef4ff", textColor = Blue)
                }
                Div(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                    Text("@${member.username}")
                }
                Div(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                    Text(
                        member.ownerUser?.displayName?.let { ownerName -> "Managed by $ownerName" }
                            ?: "Managed by another account"
                    )
                }
            }
        }
        Div(
            attrs = {
                style {
                    props(
                        "display" to "grid",
                        "gap" to "4px",
                        "justify-items" to "end",
                        "text-align" to "right",
                    )
                }
            }
        ) {
            WorkspaceRoleBadge(member.role, muted = false)
            Div(
                attrs = {
                    title("Only the agent owner can change workspace access for this agent.")
                    style { props("font-size" to "11px", "line-height" to "1.4", "color" to Muted) }
                }
            ) {
                Text(
                    member.ownerUser?.displayName?.let { ownerName -> "Managed by $ownerName" }
                        ?: "Managed by agent owner"
                )
            }
            if (ownedByViewer) {
                Button(
                    attrs = {
                        attr("id", "web-workspace-agent-manage-${member.user.id.value}")
                        onClick { onManage() }
                        style {
                            inlineLinkButton()
                            props("justify-self" to "end")
                        }
                    }
                ) {
                    Text("Manage →")
                }
            } else {
                Div(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                    Text("Owner-controlled")
                }
            }
        }
    }
}

@Composable
private fun WorkspaceMemberRow(
    member: KairosWorkspaceMember,
    isCurrentUser: Boolean,
    ownerCount: Int,
    canManageMembers: Boolean,
    compact: Boolean,
    busy: Boolean,
    onRequestAction: (WebWorkspaceMemberAction) -> Unit,
) {
    var roleMenuExpanded by remember(member.user.id.value) { mutableStateOf(false) }
    var moreMenuExpanded by remember(member.user.id.value) { mutableStateOf(false) }
    val canChangeRole = canManageMembers && !isCurrentUser && !busy
    val canDemote = member.role != Kairos.WorkspaceMembershipRole.Owner || ownerCount > 1
    val roleLabel =
        when (member.role) {
            Kairos.WorkspaceMembershipRole.Owner -> "Owner"
            Kairos.WorkspaceMembershipRole.Member -> "Member"
        }

    Div(
        attrs = {
            attr("id", "web-workspace-member-row-${member.user.id.value}")
            style {
                props(
                    "display" to "grid",
                    "grid-template-columns" to
                        if (compact) "minmax(0, 1fr)" else "minmax(0, 1fr) auto",
                    "gap" to "12px",
                    "align-items" to "center",
                    "padding" to "12px 14px",
                    "border" to "1px solid $Border",
                    "border-radius" to "8px",
                    "background" to Paper,
                    "position" to "relative",
                )
            }
        }
    ) {
        if (roleMenuExpanded || moreMenuExpanded) {
            Div(
                attrs = {
                    onClick {
                        roleMenuExpanded = false
                        moreMenuExpanded = false
                    }
                    style {
                        props(
                            "position" to "fixed",
                            "inset" to "0",
                            "background" to "transparent",
                            "z-index" to "81",
                        )
                    }
                }
            )
        }
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "gap" to "12px",
                        "min-width" to "0",
                    )
                }
            }
        ) {
            Avatar(member.user, 34)
            Div(
                attrs = { style { props("display" to "grid", "gap" to "2px", "min-width" to "0") } }
            ) {
                Div(
                    attrs = {
                        style {
                            props(
                                "font-size" to "14px",
                                "font-weight" to "700",
                                "line-height" to "1.4",
                                "overflow-wrap" to "anywhere",
                            )
                        }
                    }
                ) {
                    Text(member.user.displayName + if (isCurrentUser) " (you)" else "")
                }
                Div(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                    Text("@${member.username}")
                }
            }
        }
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "gap" to "10px",
                        "justify-content" to if (compact) "flex-start" else "flex-end",
                        "flex-wrap" to "wrap",
                        "position" to "relative",
                    )
                }
            }
        ) {
            if (canChangeRole) {
                Button(
                    attrs = {
                        attr("id", "web-workspace-member-role-${member.user.id.value}")
                        if (busy) disabled()
                        onClick {
                            roleMenuExpanded = !roleMenuExpanded
                            moreMenuExpanded = false
                        }
                        style {
                            secondaryButton(selected = roleMenuExpanded)
                            props("height" to "44px", "min-height" to "44px")
                        }
                    }
                ) {
                    Text("$roleLabel ▾")
                }
                Button(
                    attrs = {
                        attr("id", "web-workspace-member-more-${member.user.id.value}")
                        attr("aria-label", "More member actions")
                        title("More member actions")
                        if (busy) disabled()
                        onClick {
                            moreMenuExpanded = !moreMenuExpanded
                            roleMenuExpanded = false
                        }
                        style {
                            overflowButton(selected = moreMenuExpanded)
                            props("height" to "44px", "width" to "44px", "min-width" to "44px")
                        }
                    }
                ) {
                    Text("⋯")
                }
                if (roleMenuExpanded) {
                    Div(
                        attrs = {
                            attr("id", "web-workspace-member-role-menu-${member.user.id.value}")
                            style {
                                props(
                                    "position" to "absolute",
                                    "top" to "calc(100% + 8px)",
                                    "right" to "0",
                                    "width" to if (compact) "220px" else "240px",
                                    "background" to Paper,
                                    "border" to "1px solid $Border",
                                    "border-radius" to "8px",
                                    "box-shadow" to "0 16px 40px rgba(20,22,28,0.16)",
                                    "padding" to "8px",
                                    "display" to "grid",
                                    "gap" to "4px",
                                    "z-index" to "82",
                                )
                            }
                        }
                    ) {
                        if (member.role == Kairos.WorkspaceMembershipRole.Owner) {
                            Button(
                                attrs = {
                                    attr(
                                        "id",
                                        "web-workspace-member-demote-${member.user.id.value}",
                                    )
                                    if (!canDemote) disabled()
                                    onClick {
                                        roleMenuExpanded = false
                                        if (canDemote) {
                                            onRequestAction(WebWorkspaceMemberAction.Demote(member))
                                        }
                                    }
                                    style {
                                        workspaceMenuActionStyle()
                                        props("color" to if (canDemote) Ink else Muted)
                                    }
                                }
                            ) {
                                Text("Set as member")
                            }
                        } else {
                            Button(
                                attrs = {
                                    attr(
                                        "id",
                                        "web-workspace-member-promote-${member.user.id.value}",
                                    )
                                    onClick {
                                        roleMenuExpanded = false
                                        onRequestAction(WebWorkspaceMemberAction.Promote(member))
                                    }
                                    style { workspaceMenuActionStyle() }
                                }
                            ) {
                                Text("Make owner")
                            }
                        }
                    }
                }
                if (moreMenuExpanded) {
                    Div(
                        attrs = {
                            attr("id", "web-workspace-member-more-menu-${member.user.id.value}")
                            style {
                                props(
                                    "position" to "absolute",
                                    "top" to "calc(100% + 8px)",
                                    "right" to "0",
                                    "width" to if (compact) "220px" else "240px",
                                    "background" to Paper,
                                    "border" to "1px solid $Border",
                                    "border-radius" to "8px",
                                    "box-shadow" to "0 16px 40px rgba(20,22,28,0.16)",
                                    "padding" to "8px",
                                    "display" to "grid",
                                    "gap" to "4px",
                                    "z-index" to "82",
                                )
                            }
                        }
                    ) {
                        Button(
                            attrs = {
                                attr("id", "web-workspace-member-remove-${member.user.id.value}")
                                onClick {
                                    moreMenuExpanded = false
                                    onRequestAction(WebWorkspaceMemberAction.Remove(member))
                                }
                                style {
                                    workspaceMenuActionStyle()
                                    props("color" to Coral)
                                }
                            }
                        ) {
                            Text("Remove from workspace")
                        }
                    }
                }
            } else if (canManageMembers || member.role == Kairos.WorkspaceMembershipRole.Owner) {
                WorkspaceRoleBadge(member.role, muted = false)
            }
        }
    }
}

@Composable
private fun SettingsSummaryCard(label: String, value: String) {
    Div(attrs = { style { cardSurface() } }) {
        SectionLabel(label)
        Div(
            attrs = {
                style {
                    props("font-size" to "24px", "font-weight" to "700", "line-height" to "1.2")
                }
            }
        ) {
            Text(value)
        }
    }
}

@Composable
private fun HomeView(
    workspace: WebWorkspace,
    dailyPlan: KairosDailyPlan,
    executionFlow: kairos.KairosExecutionFlow,
    allTasks: List<WebTask>,
    compact: Boolean,
    detailOpen: Boolean,
    onTaskSelected: (WebTask) -> Unit,
    onRunQuickAction: (WebTask) -> Unit,
    onShowObjectives: () -> Unit,
    onShowSchedule: (Kairos.ProjectId?) -> Unit,
) {
    val taskIndex = remember(allTasks) { allTasks.associateBy { task -> task.ticket.id } }
    val sections =
        remember(executionFlow, taskIndex) { buildWebExecutionSections(executionFlow, taskIndex) }
    val recentActivity =
        remember(workspace, allTasks) {
            workspace.activityEntriesFor(allTasks.map { task -> task.ticket.id }.toSet()).take(6)
        }
    val objectiveSummaries = remember(workspace) { workspace.objectiveSummaries().take(4) }

    Div(
        attrs = {
            style {
                props(
                    "flex" to "1",
                    "overflow" to "auto",
                    "background" to Canvas,
                    "padding" to "24px",
                    "box-sizing" to "border-box",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "width" to "min(1240px, 100%)",
                        "margin" to "0 auto",
                        "display" to "grid",
                        "gap" to "20px",
                    )
                }
            }
        ) {
            if (compact) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "grid",
                                "grid-template-columns" to "repeat(2, minmax(0, 1fr))",
                                "gap" to "12px",
                            )
                        }
                    }
                ) {
                    SummaryCard(
                        "Now",
                        executionFlow.now.size.toString(),
                        detail = "Needs attention first",
                        accent = Coral,
                        compact = true,
                    )
                    SummaryCard(
                        "In flight",
                        executionFlow.inFlight.size.toString(),
                        detail = "Already moving",
                        compact = true,
                    )
                    SummaryCard(
                        "Next",
                        executionFlow.next.size.toString(),
                        detail = "Ready to pull",
                        compact = true,
                    )
                    SummaryCard(
                        "Scheduled",
                        (dailyPlan.eventsToday.size + dailyPlan.timeboxesToday.size).toString(),
                        detail = "Calendar today",
                        accent = Blue,
                        compact = true,
                    )
                }
            } else {
                StatGrid(minColumnWidthPx = if (detailOpen) 320 else 260) {
                    SummaryCard(
                        "Now",
                        executionFlow.now.size.toString(),
                        detail = "Needs attention first",
                        accent = Coral,
                    )
                    SummaryCard(
                        "In flight",
                        executionFlow.inFlight.size.toString(),
                        detail = "Already moving",
                    )
                    SummaryCard(
                        "Next",
                        executionFlow.next.size.toString(),
                        detail = "Ready to pull",
                    )
                    SummaryCard(
                        "Scheduled",
                        (dailyPlan.eventsToday.size + dailyPlan.timeboxesToday.size).toString(),
                        detail = "Calendar today",
                        accent = Blue,
                    )
                }
            }
            val visibleExecutionSections =
                listOf(
                        Triple("Now", sections.now, "Nothing needs attention right now"),
                        Triple("In flight", sections.inFlight, "Nothing actively moving"),
                        Triple("Next", sections.next, "No near-term follow-up"),
                        Triple("Later", sections.later, "No additional open work in scope"),
                        Triple("Inbox", sections.inbox, "Inbox is clear"),
                    )
                    .filter { (_, tasks, _) -> tasks.isNotEmpty() }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "grid-template-columns" to
                                if (compact || window.innerWidth < 1200) {
                                    "minmax(0, 1fr)"
                                } else {
                                    "minmax(0, 1.52fr) minmax(360px, 0.98fr)"
                                },
                            "gap" to "16px",
                            "align-items" to "start",
                        )
                    }
                }
            ) {
                PanelCard("Execution queue", tone = WebPanelTone.Primary) {
                    if (visibleExecutionSections.isEmpty()) {
                        PanelEmptyState(
                            "Nothing is competing for attention right now.",
                            "New work, inbox items, and planned follow-ups will appear here.",
                        )
                    } else {
                        visibleExecutionSections.forEach { (label, tasks, emptyLabel) ->
                            TaskSection(
                                label = label,
                                tasks = tasks,
                                emptyLabel = emptyLabel,
                                onTaskSelected = onTaskSelected,
                                onRunQuickAction = onRunQuickAction,
                            )
                        }
                    }
                }
                Div(
                    attrs = {
                        style { props("display" to "grid", "gap" to "16px", "min-width" to "0") }
                    }
                ) {
                    PanelCard(
                        "Schedule",
                        tone = WebPanelTone.Primary,
                        actionLabel = "View all",
                        onAction = { onShowSchedule(null) },
                    ) {
                        DailySchedulePanel(workspace = workspace, dailyPlan = dailyPlan)
                    }
                    PanelCard(
                        "Objectives",
                        tone = WebPanelTone.Secondary,
                        actionLabel = "View all",
                        onAction = onShowObjectives,
                    ) {
                        if (dailyPlan.objectivePressure.isEmpty()) {
                            if (objectiveSummaries.isEmpty()) {
                                MutedText("No objectives in scope")
                            } else {
                                objectiveSummaries.forEach { summary ->
                                    ObjectiveSummaryRow(
                                        summary = summary,
                                        onOpen = onShowObjectives,
                                    )
                                }
                            }
                        } else {
                            dailyPlan.objectivePressure.take(6).forEach { pressure ->
                                ObjectivePressureRow(
                                    workspace = workspace,
                                    pressure = pressure,
                                    onOpen = onShowObjectives,
                                )
                            }
                        }
                    }
                    PanelCard(
                        "Routines",
                        tone = WebPanelTone.Secondary,
                        actionLabel = "View all",
                        onAction = { onShowSchedule(null) },
                    ) {
                        if (dailyPlan.routines.isEmpty()) {
                            MutedText("No routines in scope")
                        } else {
                            NoticeBadge("${dailyPlan.routines.size} in scope")
                            dailyPlan.routines.take(3).forEach { routine ->
                                RoutinePlanRow(
                                    workspace = workspace,
                                    routine = routine,
                                    onOpen = { onShowSchedule(routine.projectId) },
                                )
                            }
                        }
                    }
                }
            }
            PanelCard("Recent activity", tone = WebPanelTone.Secondary) {
                ActivityFeed(entries = recentActivity, emptyLabel = "No recent activity yet")
            }
        }
    }
}

private fun buildWebExecutionSections(
    executionFlow: kairos.KairosExecutionFlow,
    taskIndex: Map<Kairos.TicketId, WebTask>,
): WebExecutionSections =
    WebExecutionSections(
        now = executionFlow.now.mapNotNull { ticket -> taskIndex[ticket.id] },
        inFlight = executionFlow.inFlight.mapNotNull { ticket -> taskIndex[ticket.id] },
        next = executionFlow.next.mapNotNull { ticket -> taskIndex[ticket.id] },
        later = executionFlow.later.mapNotNull { ticket -> taskIndex[ticket.id] },
        inbox = executionFlow.inbox.mapNotNull { ticket -> taskIndex[ticket.id] },
    )

@Composable
private fun InboxView(
    workspace: WebWorkspace,
    tasks: List<WebTask>,
    onTaskSelected: (WebTask) -> Unit,
    onRunQuickAction: (WebTask) -> Unit,
) {
    val emptyPanelHeight = "200px"
    val recentActivity =
        remember(workspace, tasks) {
            workspace.activityEntriesFor(tasks.map { task -> task.ticket.id }.toSet()).take(6)
        }

    Div(
        attrs = {
            style {
                props(
                    "flex" to "1",
                    "overflow" to "auto",
                    "background" to Canvas,
                    "padding" to "24px",
                    "box-sizing" to "border-box",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "width" to "min(1240px, 100%)",
                        "margin" to "0 auto",
                        "display" to "grid",
                        "gap" to "20px",
                    )
                }
            }
        ) {
            StatGrid {
                SummaryCard("Needs planning", tasks.size.toString())
                SummaryCard("Assigned", tasks.count { it.ticket.assigneeUserId != null }.toString())
                SummaryCard(
                    "Unassigned",
                    tasks.count { it.ticket.assigneeUserId == null }.toString(),
                )
                SummaryCard(
                    "With activity",
                    tasks
                        .count { task -> workspace.activitiesFor(task.ticket.id).isNotEmpty() }
                        .toString(),
                )
            }
            TwoPanelGrid {
                TaskPanel(
                    "Inbox tickets",
                    tasks.take(10),
                    "Inbox is clear",
                    "Use + Create or move work here when it still needs planning.",
                    onTaskSelected,
                    onRunQuickAction,
                    minHeight = emptyPanelHeight,
                )
                PanelCard("Recent updates", minHeight = emptyPanelHeight) {
                    ActivityFeed(entries = recentActivity, emptyLabel = "No inbox activity yet")
                }
            }
        }
    }
}

@Composable
private fun KpisView(
    workspace: WebWorkspace,
    compact: Boolean,
    onCreateKpi: (Kairos.ProjectId?, String, Kairos.KpiTarget?, Kairos.KpiOrigin?) -> Unit,
    onUpdateKpi: (Kairos.KpiId, String?, Kairos.KpiTarget?, Boolean, Kairos.KpiOrigin?) -> Unit,
    onCreateKpiMeasurement: (Kairos.KpiId, Double) -> Unit,
) {
    val workspaceKpis = remember(workspace) { workspace.workspaceKpiSummaries() }
    val projectOptions = remember(workspace) { workspace.objectiveProjectOptions() }
    val measuredCount =
        remember(workspaceKpis) {
            workspaceKpis.count { summary -> summary.kpi.origin is Kairos.KpiOrigin.Measured }
        }
    val derivedCount =
        remember(workspaceKpis) {
            workspaceKpis.count { summary -> summary.kpi.origin is Kairos.KpiOrigin.Derived }
        }
    val attentionCount =
        remember(workspaceKpis) {
            workspaceKpis.count { summary ->
                summary.statusLabel == "Awaiting measurement" ||
                    summary.statusLabel == "Off track" ||
                    summary.statusLabel == "Error"
            }
        }
    var activeFilter by remember(workspace) { mutableStateOf(WebKpiFilter.All) }
    val filteredKpis =
        remember(workspaceKpis, activeFilter) {
            workspaceKpis.filter { summary ->
                when (activeFilter) {
                    WebKpiFilter.All -> true
                    WebKpiFilter.Measured -> summary.kpi.origin is Kairos.KpiOrigin.Measured
                    WebKpiFilter.Derived -> summary.kpi.origin is Kairos.KpiOrigin.Derived
                    WebKpiFilter.Attention ->
                        summary.statusLabel == "Awaiting measurement" ||
                            summary.statusLabel == "Off track" ||
                            summary.statusLabel == "Error"
                }
            }
        }
    var kpiModalOpen by remember(workspace) { mutableStateOf(false) }
    var editingKpiId by remember(workspace) { mutableStateOf<Kairos.KpiId?>(null) }
    var kpiNameDraft by remember(workspace) { mutableStateOf("") }
    var kpiTargetDraft by remember(workspace) { mutableStateOf("") }
    var kpiFormulaDraft by remember(workspace) { mutableStateOf("") }
    var kpiDerivedDraft by remember(workspace) { mutableStateOf(false) }
    var kpiProjectIdDraft by remember(workspace) { mutableStateOf<Kairos.ProjectId?>(null) }
    var kpiError by remember(workspace) { mutableStateOf<String?>(null) }

    fun resetKpiDraft() {
        editingKpiId = null
        kpiNameDraft = ""
        kpiTargetDraft = ""
        kpiFormulaDraft = ""
        kpiDerivedDraft = false
        kpiProjectIdDraft = workspace.primaryProject()?.id
        kpiError = null
    }

    fun dismissKpiModal() {
        kpiModalOpen = false
        resetKpiDraft()
    }

    fun openCreateKpiModal() {
        resetKpiDraft()
        kpiModalOpen = true
    }

    fun openEditKpiModal(summary: WebWorkspaceKpiSummary) {
        editingKpiId = summary.kpi.id
        kpiNameDraft = summary.kpi.name
        kpiTargetDraft = summary.kpi.targetValue?.toUiDraft().orEmpty()
        kpiProjectIdDraft = summary.kpi.projectId
        val derivedOrigin = summary.kpi.origin as? Kairos.KpiOrigin.Derived
        kpiDerivedDraft = derivedOrigin != null
        kpiFormulaDraft =
            derivedOrigin
                ?.let { origin ->
                    formatKairosKpiFormula(origin.formula) { dependencyId ->
                        workspace.kpis.firstOrNull { kpi -> kpi.id == dependencyId }?.name
                            ?: dependencyId.value
                    }
                }
                .orEmpty()
        kpiError = null
        kpiModalOpen = true
    }

    fun submitKpiModal() {
        val trimmedName = kpiNameDraft.trim()
        if (trimmedName.isBlank()) {
            kpiError = "KPI name is required."
            return
        }
        val existingKpi =
            editingKpiId?.let { kpiId -> workspace.kpis.firstOrNull { kpi -> kpi.id == kpiId } }
        val targetValue =
            try {
                kpiTargetDraft
                    .trim()
                    .takeIf { value -> value.isNotBlank() }
                    ?.let(::parseWebKpiTargetSpec)
            } catch (error: IllegalArgumentException) {
                kpiError = error.message ?: "Invalid KPI target."
                return
            }
        val origin =
            if (kpiDerivedDraft) {
                val formulaText = kpiFormulaDraft.trim()
                if (formulaText.isBlank()) {
                    kpiError = "Derived KPIs need a formula."
                    return
                }
                try {
                    Kairos.KpiOrigin.Derived(parseWebKpiFormula(workspace, formulaText))
                } catch (error: IllegalArgumentException) {
                    kpiError = error.message ?: "Invalid KPI formula."
                    return
                }
            } else if (existingKpi?.origin is Kairos.KpiOrigin.Derived) {
                defaultMeasuredKpiOrigin()
            } else {
                null
            }
        if (editingKpiId != null) {
            onUpdateKpi(
                requireNotNull(editingKpiId),
                trimmedName,
                targetValue,
                targetValue == null && kpiTargetDraft.trim().isEmpty(),
                origin,
            )
        } else {
            onCreateKpi(kpiProjectIdDraft, trimmedName, targetValue, origin)
        }
        dismissKpiModal()
    }

    Div(
        attrs = {
            style {
                props(
                    "position" to "relative",
                    "flex" to "1",
                    "min-height" to "0",
                    "background" to Canvas,
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "height" to "100%",
                        "overflow" to "auto",
                        "padding" to if (compact) "16px" else "24px",
                        "box-sizing" to "border-box",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "width" to "min(1240px, 100%)",
                            "margin" to "0 auto",
                            "display" to "grid",
                            "gap" to "20px",
                        )
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "justify-content" to "space-between",
                                "align-items" to "end",
                                "gap" to "16px",
                                "flex-wrap" to "wrap",
                            )
                        }
                    }
                ) {
                    InlineGuidance(
                        "Review measured and derived signals here instead of digging through objective cards."
                    )
                    Button(
                        attrs = {
                            attr("id", "web-kpi-open-create-button")
                            onClick { openCreateKpiModal() }
                            style { primaryButton() }
                        }
                    ) {
                        Text("Create KPI")
                    }
                }
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "gap" to "24px",
                                "row-gap" to "12px",
                                "flex-wrap" to "wrap",
                            )
                        }
                    }
                ) {
                    ObjectiveOverviewMetric("All KPIs", workspaceKpis.size.toString())
                    ObjectiveOverviewMetric("Measured", measuredCount.toString())
                    ObjectiveOverviewMetric("Derived", derivedCount.toString())
                    ObjectiveOverviewMetric("Needs attention", attentionCount.toString())
                }
                Div(
                    attrs = {
                        style { props("display" to "flex", "gap" to "8px", "flex-wrap" to "wrap") }
                    }
                ) {
                    TogglePill(
                        label = "All",
                        active = activeFilter == WebKpiFilter.All,
                        onClick = { activeFilter = WebKpiFilter.All },
                    )
                    TogglePill(
                        label = "Measured",
                        active = activeFilter == WebKpiFilter.Measured,
                        onClick = { activeFilter = WebKpiFilter.Measured },
                    )
                    TogglePill(
                        label = "Derived",
                        active = activeFilter == WebKpiFilter.Derived,
                        onClick = { activeFilter = WebKpiFilter.Derived },
                    )
                    TogglePill(
                        label = "Needs attention",
                        active = activeFilter == WebKpiFilter.Attention,
                        onClick = { activeFilter = WebKpiFilter.Attention },
                    )
                }
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "align-items" to "start",
                                "gap" to "20px",
                                "flex-wrap" to "wrap",
                            )
                        }
                    }
                ) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "grid",
                                    "gap" to "14px",
                                    "flex" to "1 1 760px",
                                    "min-width" to "0",
                                )
                            }
                        }
                    ) {
                        PanelCard("KPI list") {
                            if (filteredKpis.isEmpty()) {
                                MutedText("No KPIs match this filter")
                            } else {
                                filteredKpis.forEach { summary ->
                                    WorkspaceKpiRow(
                                        summary = summary,
                                        onOpenDetails = { openEditKpiModal(summary) },
                                        onCreateKpiMeasurement = onCreateKpiMeasurement,
                                    )
                                }
                            }
                        }
                    }
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "grid",
                                    "gap" to "20px",
                                    "flex" to if (compact) "1 1 100%" else "1 1 320px",
                                    "width" to if (compact) "100%" else "min(100%, 340px)",
                                    "align-self" to "start",
                                    "position" to if (compact) "static" else "sticky",
                                    "top" to if (compact) "auto" else "24px",
                                )
                            }
                        }
                    ) {
                        PanelCard("Coverage") {
                            ObjectiveHealthMetric(
                                label = "Measured",
                                value = measuredCount.toString(),
                                detail =
                                    "KPIs that expect direct check-ins or imported measurements.",
                                accentColor = Blue,
                                background = "#eaf1ff",
                            )
                            ObjectiveHealthMetric(
                                label = "Derived",
                                value = derivedCount.toString(),
                                detail = "KPIs calculated from other KPI inputs.",
                                accentColor = Teal,
                                background = "#e6f8f3",
                            )
                            ObjectiveHealthMetric(
                                label = "Needs attention",
                                value = attentionCount.toString(),
                                detail =
                                    "KPIs that are off track, missing check-ins, or failing evaluation.",
                                accentColor = Coral,
                                background = "#ffeee9",
                            )
                        }
                    }
                }
            }
        }
        if (kpiModalOpen) {
            OverlayScrim(zIndex = 42, onDismiss = ::dismissKpiModal)
            KpiDetailsModal(
                workspace = workspace,
                summary =
                    editingKpiId?.let { kpiId ->
                        workspaceKpis.firstOrNull { summary -> summary.kpi.id == kpiId }
                    },
                projects = projectOptions,
                nameDraft = kpiNameDraft,
                targetDraft = kpiTargetDraft,
                formulaDraft = kpiFormulaDraft,
                derivedDraft = kpiDerivedDraft,
                projectIdDraft = kpiProjectIdDraft,
                error = kpiError,
                onNameChanged = {
                    kpiNameDraft = it
                    kpiError = null
                },
                onTargetChanged = {
                    kpiTargetDraft = it
                    kpiError = null
                },
                onFormulaChanged = {
                    kpiFormulaDraft = it
                    kpiError = null
                },
                onDerivedChanged = {
                    kpiDerivedDraft = it
                    kpiError = null
                },
                onProjectSelected = { kpiProjectIdDraft = it },
                onSaveMeasurement = onCreateKpiMeasurement,
                onSave = ::submitKpiModal,
                onDismiss = ::dismissKpiModal,
            )
        }
    }
}

@Composable
private fun FloatingCreateButton(onClick: () -> Unit) {
    Button(
        attrs = {
            attr("id", "web-floating-create-button")
            onClick { onClick() }
            style {
                props(
                    "position" to "fixed",
                    "right" to "16px",
                    "bottom" to "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                    "z-index" to "32",
                    "width" to "56px",
                    "height" to "56px",
                    "border" to "0",
                    "border-radius" to "999px",
                    "background" to Coral,
                    "color" to Paper,
                    "font-size" to "28px",
                    "font-weight" to "700",
                    "box-shadow" to "0 14px 28px rgba(242,106,87,0.32)",
                    "cursor" to "pointer",
                    "padding" to "0",
                    "display" to "grid",
                    "place-items" to "center",
                )
            }
        }
    ) {
        Text("+")
    }
}

@Composable
private fun ObjectivesView(
    workspace: WebWorkspace,
    compact: Boolean,
    onTaskSelected: (WebTask) -> Unit,
    onCreateObjective: (String, Kairos.ProjectId?, ZonedDateTime?, List<Kairos.TicketId>) -> Unit,
    onUpdateObjective: (Kairos.ObjectiveId, String, ZonedDateTime?) -> Unit,
    onCreateKpi: (Kairos.ProjectId?, String, Kairos.KpiTarget?, Kairos.KpiOrigin?) -> Unit,
    onUpdateKpi: (Kairos.KpiId, String?, Kairos.KpiTarget?, Boolean, Kairos.KpiOrigin?) -> Unit,
    onCreateObjectiveKpi: (Kairos.Objective, String, Kairos.KpiTarget) -> Unit,
    onCreateKpiMeasurement: (Kairos.KpiId, Double) -> Unit,
    onSetObjectiveKpiTargets: (Kairos.ObjectiveId, List<Kairos.KpiWithTarget>) -> Unit,
) {
    val objectiveSummaries = remember(workspace) { workspace.objectiveSummaries() }
    val workspaceKpis = remember(workspace) { workspace.workspaceKpiSummaries() }
    val linkedTicketCount =
        remember(objectiveSummaries) { objectiveSummaries.sumOf { it.linkedTasks.size } }
    val linkedKpiCount = remember(objectiveSummaries) { objectiveSummaries.sumOf { it.kpis.size } }
    val completedObjectives =
        remember(objectiveSummaries) { objectiveSummaries.count { it.statusLabel == "Complete" } }
    val attentionObjectives =
        remember(objectiveSummaries) {
            objectiveSummaries.count { summary ->
                summary.statusLabel == "Overdue" ||
                    summary.statusLabel == "Awaiting measurement" ||
                    summary.statusLabel == "No rollup yet" ||
                    summary.statusLabel == "Off track" ||
                    summary.statusLabel == "Error"
            }
        }
    val unlinkedObjectives =
        remember(objectiveSummaries) {
            objectiveSummaries.count { summary ->
                summary.linkedTasks.isEmpty() && summary.kpis.isEmpty()
            }
        }
    val projectOptions = remember(workspace) { workspace.objectiveProjectOptions() }
    var createModalOpen by remember(workspace) { mutableStateOf(false) }
    var descriptionDraft by remember(workspace) { mutableStateOf("") }
    var deadlineDraft by remember(workspace) { mutableStateOf("") }
    var projectIdDraft by remember(workspace) { mutableStateOf<Kairos.ProjectId?>(null) }
    var kpiModalOpen by remember(workspace) { mutableStateOf(false) }
    var editingKpiId by remember(workspace) { mutableStateOf<Kairos.KpiId?>(null) }
    var kpiNameDraft by remember(workspace) { mutableStateOf("") }
    var kpiTargetDraft by remember(workspace) { mutableStateOf("") }
    var kpiFormulaDraft by remember(workspace) { mutableStateOf("") }
    var kpiDerivedDraft by remember(workspace) { mutableStateOf(false) }
    var kpiProjectIdDraft by remember(workspace) { mutableStateOf<Kairos.ProjectId?>(null) }
    var kpiError by remember(workspace) { mutableStateOf<String?>(null) }

    fun resetCreateDraft() {
        descriptionDraft = ""
        deadlineDraft = ""
        projectIdDraft = null
    }

    fun dismissCreateModal() {
        createModalOpen = false
        resetCreateDraft()
    }

    fun resetKpiDraft() {
        editingKpiId = null
        kpiNameDraft = ""
        kpiTargetDraft = ""
        kpiFormulaDraft = ""
        kpiDerivedDraft = false
        kpiProjectIdDraft = workspace.primaryProject()?.id
        kpiError = null
    }

    fun dismissKpiModal() {
        kpiModalOpen = false
        resetKpiDraft()
    }

    fun openCreateKpiModal() {
        resetKpiDraft()
        kpiModalOpen = true
    }

    fun openEditKpiModal(summary: WebWorkspaceKpiSummary) {
        editingKpiId = summary.kpi.id
        kpiNameDraft = summary.kpi.name
        kpiTargetDraft = summary.kpi.targetValue?.toUiDraft().orEmpty()
        kpiProjectIdDraft = summary.kpi.projectId
        val derivedOrigin = summary.kpi.origin as? Kairos.KpiOrigin.Derived
        kpiDerivedDraft = derivedOrigin != null
        kpiFormulaDraft =
            derivedOrigin
                ?.let { origin ->
                    formatKairosKpiFormula(origin.formula) { dependencyId ->
                        workspace.kpis.firstOrNull { kpi -> kpi.id == dependencyId }?.name
                            ?: dependencyId.value
                    }
                }
                .orEmpty()
        kpiError = null
        kpiModalOpen = true
    }

    fun submitKpiModal() {
        val trimmedName = kpiNameDraft.trim()
        if (trimmedName.isBlank()) {
            kpiError = "KPI name is required."
            return
        }
        val existingKpi =
            editingKpiId?.let { kpiId -> workspace.kpis.firstOrNull { kpi -> kpi.id == kpiId } }
        val targetValue =
            try {
                kpiTargetDraft
                    .trim()
                    .takeIf { value -> value.isNotBlank() }
                    ?.let(::parseWebKpiTargetSpec)
            } catch (error: IllegalArgumentException) {
                kpiError = error.message ?: "Invalid KPI target."
                return
            }
        val origin =
            if (kpiDerivedDraft) {
                val formulaText = kpiFormulaDraft.trim()
                if (formulaText.isBlank()) {
                    kpiError = "Derived KPIs need a formula."
                    return
                }
                try {
                    Kairos.KpiOrigin.Derived(parseWebKpiFormula(workspace, formulaText))
                } catch (error: IllegalArgumentException) {
                    kpiError = error.message ?: "Invalid KPI formula."
                    return
                }
            } else if (existingKpi?.origin is Kairos.KpiOrigin.Derived) {
                defaultMeasuredKpiOrigin()
            } else {
                null
            }
        if (editingKpiId != null) {
            onUpdateKpi(
                requireNotNull(editingKpiId),
                trimmedName,
                targetValue,
                targetValue == null && kpiTargetDraft.trim().isEmpty(),
                origin,
            )
        } else {
            onCreateKpi(kpiProjectIdDraft, trimmedName, targetValue, origin)
        }
        dismissKpiModal()
    }

    fun submitCreateObjective() {
        val trimmed = descriptionDraft.trim()
        if (trimmed.isBlank()) return
        onCreateObjective(trimmed, projectIdDraft, parseDeadlineInput(deadlineDraft), emptyList())
        createModalOpen = false
        resetCreateDraft()
    }

    Div(
        attrs = {
            style {
                props(
                    "position" to "relative",
                    "flex" to "1",
                    "min-height" to "0",
                    "background" to Canvas,
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "height" to "100%",
                        "overflow" to "auto",
                        "padding" to if (compact) "16px" else "24px",
                        "box-sizing" to "border-box",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "width" to "min(1240px, 100%)",
                            "margin" to "0 auto",
                            "display" to "grid",
                            "gap" to "20px",
                        )
                    }
                }
            ) {
                Div(attrs = { style { props("display" to "grid", "gap" to "12px") } }) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "justify-content" to "space-between",
                                    "align-items" to "end",
                                    "gap" to "16px",
                                    "flex-wrap" to "wrap",
                                )
                            }
                        }
                    ) {
                        InlineGuidance(
                            "Create the outcome first, then link tickets and KPI targets after it exists."
                        )
                        Button(
                            attrs = {
                                attr("id", "web-objective-open-create-button")
                                onClick { createModalOpen = true }
                                style { primaryButton() }
                            }
                        ) {
                            Text("Create objective")
                        }
                    }
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "gap" to "24px",
                                    "row-gap" to "12px",
                                    "flex-wrap" to "wrap",
                                )
                            }
                        }
                    ) {
                        ObjectiveOverviewMetric("Objectives", objectiveSummaries.size.toString())
                        ObjectiveOverviewMetric("Needs setup", unlinkedObjectives.toString())
                        ObjectiveOverviewMetric("Linked tickets", linkedTicketCount.toString())
                        ObjectiveOverviewMetric("KPI targets", linkedKpiCount.toString())
                    }
                }
                if (objectiveSummaries.isEmpty()) {
                    PanelCard("Objectives") {
                        Div(attrs = { style { props("display" to "grid", "gap" to "10px") } }) {
                            MutedText("No objectives yet")
                            Button(
                                attrs = {
                                    attr("id", "web-objective-empty-create-button")
                                    onClick { createModalOpen = true }
                                    style { primaryButton() }
                                }
                            ) {
                                Text("Create the first objective")
                            }
                        }
                    }
                } else {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "align-items" to "start",
                                    "gap" to "20px",
                                    "flex-wrap" to "wrap",
                                )
                            }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "grid",
                                        "gap" to "20px",
                                        "flex" to "1 1 760px",
                                        "min-width" to "0",
                                    )
                                }
                            }
                        ) {
                            PanelCard("Objectives") {
                                objectiveSummaries.forEach { summary ->
                                    ObjectiveRow(
                                        workspace = workspace,
                                        compact = compact,
                                        summary = summary,
                                        onTaskSelected = onTaskSelected,
                                        onUpdateObjective = onUpdateObjective,
                                        onCreateObjectiveKpi = onCreateObjectiveKpi,
                                        onCreateKpiMeasurement = onCreateKpiMeasurement,
                                        onSetObjectiveKpiTargets = onSetObjectiveKpiTargets,
                                    )
                                }
                            }
                        }
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "grid",
                                        "gap" to "20px",
                                        "flex" to if (compact) "1 1 100%" else "1 1 320px",
                                        "width" to if (compact) "100%" else "min(100%, 340px)",
                                        "align-self" to "start",
                                        "position" to if (compact) "static" else "sticky",
                                        "top" to if (compact) "auto" else "24px",
                                    )
                                }
                            }
                        ) {
                            PanelCard("Objective health") {
                                ObjectiveHealthMetric(
                                    label = "Needs attention",
                                    value = attentionObjectives.toString(),
                                    detail =
                                        "Overdue objectives, off-track KPIs, missing measurements, or no rollup inputs yet.",
                                    accentColor = Coral,
                                    background = "#ffeee9",
                                )
                                ObjectiveHealthMetric(
                                    label = "Complete",
                                    value = completedObjectives.toString(),
                                    detail =
                                        "Objectives whose linked work and KPI targets have fully rolled up.",
                                    accentColor = Green,
                                    background = "#e8f6ec",
                                )
                                ObjectiveHealthMetric(
                                    label = "Unlinked",
                                    value = unlinkedObjectives.toString(),
                                    detail =
                                        "Objectives that still need linked tickets or KPI targets.",
                                    accentColor = Muted,
                                    background = "#f1f2f4",
                                )
                            }
                            PanelCard("KPI library") {
                                Div(
                                    attrs = {
                                        style {
                                            props(
                                                "display" to "flex",
                                                "justify-content" to "space-between",
                                                "align-items" to "center",
                                                "gap" to "12px",
                                                "margin-bottom" to "8px",
                                            )
                                        }
                                    }
                                ) {
                                    MutedText("Measured and derived KPIs live here.")
                                    Button(
                                        attrs = {
                                            attr("id", "web-objective-open-kpi-button")
                                            onClick { openCreateKpiModal() }
                                            style { primaryButton() }
                                        }
                                    ) {
                                        Text("New KPI")
                                    }
                                }
                                if (workspaceKpis.isEmpty()) {
                                    MutedText("No KPIs yet")
                                } else {
                                    workspaceKpis.forEach { summary ->
                                        WorkspaceKpiRow(
                                            summary = summary,
                                            compact = true,
                                            onOpenDetails = { openEditKpiModal(summary) },
                                            onCreateKpiMeasurement = onCreateKpiMeasurement,
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        if (createModalOpen) {
            OverlayScrim(zIndex = 40, onDismiss = ::dismissCreateModal)
            ObjectiveCreateModal(
                projects = projectOptions,
                allProjects = workspace.projects,
                descriptionDraft = descriptionDraft,
                deadlineDraft = deadlineDraft,
                projectIdDraft = projectIdDraft,
                onDescriptionChanged = { descriptionDraft = it },
                onDeadlineChanged = { deadlineDraft = it },
                onProjectSelected = { projectIdDraft = it },
                onDismiss = ::dismissCreateModal,
                onCreate = ::submitCreateObjective,
            )
        }
        if (kpiModalOpen) {
            OverlayScrim(zIndex = 42, onDismiss = ::dismissKpiModal)
            KpiDetailsModal(
                workspace = workspace,
                summary =
                    editingKpiId?.let { kpiId ->
                        workspaceKpis.firstOrNull { summary -> summary.kpi.id == kpiId }
                    },
                projects = projectOptions,
                nameDraft = kpiNameDraft,
                targetDraft = kpiTargetDraft,
                formulaDraft = kpiFormulaDraft,
                derivedDraft = kpiDerivedDraft,
                projectIdDraft = kpiProjectIdDraft,
                error = kpiError,
                onNameChanged = {
                    kpiNameDraft = it
                    kpiError = null
                },
                onTargetChanged = {
                    kpiTargetDraft = it
                    kpiError = null
                },
                onFormulaChanged = {
                    kpiFormulaDraft = it
                    kpiError = null
                },
                onDerivedChanged = {
                    kpiDerivedDraft = it
                    kpiError = null
                },
                onProjectSelected = { kpiProjectIdDraft = it },
                onSaveMeasurement = onCreateKpiMeasurement,
                onSave = ::submitKpiModal,
                onDismiss = ::dismissKpiModal,
            )
        }
    }
}

@Composable
private fun KpiDetailsModal(
    workspace: WebWorkspace,
    summary: WebWorkspaceKpiSummary?,
    projects: List<Kairos.Project>,
    nameDraft: String,
    targetDraft: String,
    formulaDraft: String,
    derivedDraft: Boolean,
    projectIdDraft: Kairos.ProjectId?,
    error: String?,
    onNameChanged: (String) -> Unit,
    onTargetChanged: (String) -> Unit,
    onFormulaChanged: (String) -> Unit,
    onDerivedChanged: (Boolean) -> Unit,
    onProjectSelected: (Kairos.ProjectId?) -> Unit,
    onSaveMeasurement: (Kairos.KpiId, Double) -> Unit,
    onSave: () -> Unit,
    onDismiss: () -> Unit,
) {
    val kpi = summary?.kpi
    val evaluation = summary?.evaluation
    val dependencyKpis =
        remember(workspace, evaluation?.dependencyKpis) {
            evaluation
                ?.dependencyKpis
                ?.mapNotNull { dependencyId ->
                    workspace.kpis.firstOrNull { candidate -> candidate.id == dependencyId }
                }
                .orEmpty()
        }
    val linkedObjectives =
        remember(workspace, kpi?.id) {
            kpi?.let { current ->
                    workspace.objectives.filter { objective ->
                        objective.kpiTargets.any { target -> target.kpi == current.id }
                    }
                }
                .orEmpty()
        }
    val linkedTicketActivity =
        remember(workspace, linkedObjectives.map { objective -> objective.id.value }) {
            workspace
                .activityEntriesFor(
                    linkedObjectives.flatMap { objective -> objective.tickets }.toSet()
                )
                .take(6)
        }
    var measurementDraft by remember(kpi?.id) { mutableStateOf("") }
    val measurementValue = measurementDraft.toDoubleOrNull()

    Div(
        attrs = {
            attr("id", "web-kpi-modal-overlay")
            onClick { it.stopPropagation() }
            style {
                props(
                    "position" to "fixed",
                    "inset" to "0",
                    "z-index" to "46",
                    "display" to "flex",
                    "justify-content" to "center",
                    "align-items" to "center",
                    "padding" to "24px 20px",
                    "overflow" to "auto",
                    "box-sizing" to "border-box",
                )
            }
        }
    ) {
        Div(
            attrs = {
                attr("id", "web-kpi-modal")
                onClick { it.stopPropagation() }
                style {
                    props(
                        "width" to "min(860px, 100%)",
                        "border" to "1px solid $Border",
                        "border-radius" to "8px",
                        "background" to Paper,
                        "box-shadow" to "0 28px 70px rgba(22, 27, 45, 0.18)",
                        "display" to "grid",
                        "gap" to "20px",
                        "padding" to "24px",
                        "box-sizing" to "border-box",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "space-between",
                            "align-items" to "start",
                            "gap" to "16px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                Div(attrs = { style { props("display" to "grid", "gap" to "6px") } }) {
                    Div(
                        attrs = { style { props("font-size" to "26px", "font-weight" to "700") } }
                    ) {
                        Text(if (summary == null) "Create KPI" else "KPI details")
                    }
                    MutedText(
                        if (summary == null) {
                            "Keep names concrete. Use formulas only when the KPI is genuinely derived from existing inputs."
                        } else {
                            "Current ${summary.latestValueLabel} - ${summary.statusLabel.lowercase()}."
                        }
                    )
                }
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "grid-template-columns" to "repeat(auto-fit, minmax(220px, 1fr))",
                            "gap" to "14px",
                        )
                    }
                }
            ) {
                Div(attrs = { style { props("display" to "grid", "gap" to "6px") } }) {
                    FieldLabel("Name")
                    Input(
                        type = InputType.Text,
                        attrs = {
                            attr("id", "web-kpi-name-input")
                            value(nameDraft)
                            onInput { event -> onNameChanged(event.value) }
                            style { modalTextInput() }
                        },
                    )
                }
                if (summary == null) {
                    Div(attrs = { style { props("display" to "grid", "gap" to "6px") } }) {
                        FieldLabel("Project")
                        Select(
                            attrs = {
                                attr("id", "web-kpi-project-select")
                                onChange { event ->
                                    val nextValue = event.value ?: ""
                                    onProjectSelected(
                                        nextValue
                                            .takeIf { value -> value.isNotBlank() }
                                            ?.let(::routeProjectId)
                                    )
                                }
                                style { modalTextInput() }
                            }
                        ) {
                            Option(value = "") { Text("No project") }
                            projects.forEach { project ->
                                Option(
                                    value = project.id.value,
                                    attrs = {
                                        if (project.id == projectIdDraft) {
                                            attr("selected", "selected")
                                        }
                                    },
                                ) {
                                    Text(workspace.projectPathLabel(project))
                                }
                            }
                        }
                    }
                } else {
                    Div(attrs = { style { props("display" to "grid", "gap" to "6px") } }) {
                        FieldLabel("Project")
                        Div(attrs = { style { modalReadonlyBox() } }) {
                            Text(
                                kpi?.projectId?.let(workspace::projectName).orEmpty().ifBlank {
                                    "No project"
                                }
                            )
                        }
                    }
                }
                Div(attrs = { style { props("display" to "grid", "gap" to "6px") } }) {
                    FieldLabel("Target")
                    Input(
                        type = InputType.Text,
                        attrs = {
                            attr("id", "web-kpi-target-input")
                            value(targetDraft)
                            attr("placeholder", ">=12, <=5, 10..20")
                            onInput { event -> onTargetChanged(event.value) }
                            style { modalTextInput() }
                        },
                    )
                }
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "gap" to "10px",
                            "flex-wrap" to "wrap",
                            "align-items" to "center",
                        )
                    }
                }
            ) {
                FieldLabel("Origin")
                TogglePill(
                    label = "Measured",
                    active = !derivedDraft,
                    id = "web-kpi-origin-measured-button",
                    onClick = { onDerivedChanged(false) },
                )
                TogglePill(
                    label = "Derived",
                    active = derivedDraft,
                    id = "web-kpi-origin-derived-button",
                    onClick = { onDerivedChanged(true) },
                )
            }
            if (derivedDraft) {
                Div(attrs = { style { props("display" to "grid", "gap" to "6px") } }) {
                    FieldLabel("Formula")
                    TextArea(
                        value = formulaDraft,
                        attrs = {
                            attr("id", "web-kpi-formula-input")
                            onInput { event -> onFormulaChanged(event.value) }
                            attr("rows", "4")
                            attr(
                                "placeholder",
                                "kpi(\"Launch asset coverage\") / kpi(\"Open QA defects\")",
                            )
                            style {
                                props(
                                    "width" to "100%",
                                    "min-height" to "108px",
                                    "border" to "1px solid $Border",
                                    "border-radius" to "8px",
                                    "padding" to "12px 14px",
                                    "font-size" to "14px",
                                    "box-sizing" to "border-box",
                                    "resize" to "vertical",
                                )
                            }
                        },
                    )
                }
            }
            error?.let { message ->
                Div(
                    attrs = {
                        style {
                            props(
                                "padding" to "12px 14px",
                                "border-radius" to "8px",
                                "background" to "#ffeee9",
                                "color" to Coral,
                                "font-size" to "14px",
                                "font-weight" to "600",
                            )
                        }
                    }
                ) {
                    Text(message)
                }
            }
            if (summary != null) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "grid",
                                "grid-template-columns" to "repeat(auto-fit, minmax(180px, 1fr))",
                                "gap" to "12px",
                            )
                        }
                    }
                ) {
                    KpiMetricCard("Current", summary.latestValueLabel)
                    KpiMetricCard("Status", summary.statusLabel)
                    KpiMetricCard("Objectives", linkedObjectives.size.toString())
                }
                KpiSparkline(
                    points = summary.recentPoints,
                    target = summary.kpi.targetValue,
                    accentColor = summary.statusColor,
                    tall = true,
                )
                if (kpi?.origin !is Kairos.KpiOrigin.Derived) {
                    Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
                        FieldLabel("New measurement")
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "grid",
                                        "grid-template-columns" to "minmax(0, 1fr) auto",
                                        "gap" to "10px",
                                        "align-items" to "start",
                                    )
                                }
                            }
                        ) {
                            Div(attrs = { style { props("display" to "grid", "gap" to "6px") } }) {
                                Input(
                                    type = InputType.Text,
                                    attrs = {
                                        attr("id", "web-kpi-measurement-input")
                                        value(measurementDraft)
                                        attr("placeholder", "Check-in value")
                                        onInput { event -> measurementDraft = event.value }
                                        style { modalTextInput() }
                                    },
                                )
                                if (measurementValue == null) {
                                    MutedText("Type a value above to enable save.")
                                }
                            }
                            Button(
                                attrs = {
                                    attr("id", "web-kpi-save-measurement-button")
                                    onClick {
                                        onSaveMeasurement(
                                            requireNotNull(kpi).id,
                                            requireNotNull(measurementValue),
                                        )
                                        measurementDraft = ""
                                    }
                                    if (measurementValue == null) disabled()
                                    style { primaryButton(enabled = measurementValue != null) }
                                }
                            ) {
                                Text("Save measurement")
                            }
                        }
                    }
                }
                if (dependencyKpis.isNotEmpty()) {
                    Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
                        FieldLabel("Dependency inputs")
                        dependencyKpis.forEach { dependency ->
                            val dependencySummary =
                                workspace.workspaceKpiSummaries().firstOrNull { item ->
                                    item.kpi.id == dependency.id
                                }
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "display" to "flex",
                                            "justify-content" to "space-between",
                                            "gap" to "12px",
                                            "padding" to "10px 12px",
                                            "border" to "1px solid $Border",
                                            "border-radius" to "8px",
                                        )
                                    }
                                }
                            ) {
                                Text(dependency.name)
                                MutedText(dependencySummary?.latestValueLabel ?: "No value")
                            }
                        }
                    }
                }
                if (linkedObjectives.isNotEmpty()) {
                    Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
                        FieldLabel("Linked objectives")
                        linkedObjectives.forEach { objective ->
                            Div(attrs = { style { modalReadonlyBox() } }) {
                                Text(objective.description)
                            }
                        }
                    }
                }
                if (linkedObjectives.isNotEmpty()) {
                    Div(
                        attrs = {
                            attr("id", "web-kpi-linked-activity")
                            style { props("display" to "grid", "gap" to "8px") }
                        }
                    ) {
                        FieldLabel("Linked work history")
                        ActivityFeed(
                            entries = linkedTicketActivity,
                            emptyLabel = "No linked ticket history yet",
                        )
                    }
                }
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "flex-end",
                            "gap" to "10px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                Button(
                    attrs = {
                        attr("id", "web-kpi-cancel-button")
                        onClick { onDismiss() }
                        style { secondaryButton() }
                    }
                ) {
                    Text("Cancel")
                }
                Button(
                    attrs = {
                        attr("id", "web-kpi-submit-button")
                        onClick { onSave() }
                        style { primaryButton() }
                    }
                ) {
                    Text(if (summary == null) "Create KPI" else "Save changes")
                }
            }
        }
    }
}

@Composable
private fun KpiSparkline(
    points: List<KairosKpiValuePoint>,
    target: Kairos.KpiTarget? = null,
    accentColor: String = Teal,
    tall: Boolean = false,
) {
    val plotted =
        remember(points, tall) {
            points.sortedBy { point -> point.at }.takeLast(if (tall) 16 else 12)
        }
    if (plotted.isEmpty()) {
        MutedText("No recent values yet")
        return
    }
    var chartMode by remember(points, tall) { mutableStateOf(WebKpiChartMode.Line) }
    val referenceValues = target.referenceValues()
    val minValue = (plotted.map { point -> point.value } + referenceValues).minOrNull() ?: 0.0
    val maxValue = (plotted.map { point -> point.value } + referenceValues).maxOrNull() ?: 0.0
    val range = (maxValue - minValue).takeIf { value -> value > 0.0 } ?: 1.0
    if (tall) {
        val middlePoint = plotted[plotted.lastIndex / 2]
        Div(attrs = { style { props("display" to "grid", "gap" to "10px") } }) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "space-between",
                            "align-items" to "center",
                            "gap" to "12px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                FieldLabel("Trend over time")
                if (plotted.size >= 2) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "align-items" to "center",
                                    "gap" to "12px",
                                    "flex-wrap" to "wrap",
                                    "justify-content" to "flex-end",
                                )
                            }
                        }
                    ) {
                        MutedText(
                            "Low ${minValue.formatObjectiveMetric()} / High ${maxValue.formatObjectiveMetric()}"
                        )
                        target?.let { currentTarget ->
                            Chip("Target ${currentTarget.uiLabel()}", "#eef7f4", Teal)
                        }
                        KpiChartModePicker(
                            selected = chartMode,
                            onSelect = { nextMode -> chartMode = nextMode },
                        )
                    }
                }
            }
            if (plotted.size < 2) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "grid",
                                "gap" to "6px",
                                "padding" to "18px",
                                "border" to "1px solid $Border",
                                "border-radius" to "8px",
                                "background" to "#fafafb",
                            )
                        }
                    }
                ) {
                    Span(
                        attrs = {
                            style {
                                props("font-size" to "14px", "font-weight" to "700", "color" to Ink)
                            }
                        }
                    ) {
                        Text("Add a second check-in to see a trend")
                    }
                    MutedText(
                        "${plotted.first().value.formatObjectiveMetric()} recorded on ${formatMeasurementRecordedAt(plotted.first().at)}"
                    )
                }
            } else {
                when (chartMode) {
                    WebKpiChartMode.Line,
                    WebKpiChartMode.Scatter ->
                        KpiLineScatterChart(
                            plotted = plotted,
                            minValue = minValue,
                            range = range,
                            target = target,
                            accentColor = accentColor,
                            mode = chartMode,
                            tall = true,
                        )

                    WebKpiChartMode.Bars ->
                        KpiBarsChart(
                            plotted = plotted,
                            minValue = minValue,
                            range = range,
                            target = target,
                            accentColor = accentColor,
                            tall = true,
                        )
                }
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "justify-content" to "space-between",
                                "gap" to "12px",
                                "font-size" to "12px",
                                "color" to Muted,
                            )
                        }
                    }
                ) {
                    Span { Text(kpiSparklineDateLabel(plotted.first())) }
                    Span { Text(kpiSparklineDateLabel(middlePoint)) }
                    Span { Text(kpiSparklineDateLabel(plotted.last())) }
                }
            }
        }
        return
    }
    if (plotted.size < 2) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "gap" to "8px",
                        "font-size" to "12px",
                        "color" to Muted,
                    )
                }
            }
        ) {
            Chip("1 point", "#eef1f5", Muted)
            Text("Add a second check-in to see a trend")
        }
        return
    }
    KpiLineScatterChart(
        plotted = plotted,
        minValue = minValue,
        range = range,
        target = target,
        accentColor = accentColor,
        mode = WebKpiChartMode.Line,
        tall = false,
    )
}

@Composable
private fun KpiChartModePicker(selected: WebKpiChartMode, onSelect: (WebKpiChartMode) -> Unit) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "flex",
                    "align-items" to "center",
                    "gap" to "4px",
                    "padding" to "3px",
                    "border" to "1px solid $Border",
                    "border-radius" to "8px",
                    "background" to Paper,
                )
            }
        }
    ) {
        WebKpiChartMode.entries.forEach { mode ->
            Button(
                attrs = {
                    attr("data-kpi-chart-mode", mode.name.lowercase())
                    onClick {
                        it.stopPropagation()
                        onSelect(mode)
                    }
                    style {
                        props(
                            "height" to "28px",
                            "border" to "0",
                            "border-radius" to "6px",
                            "padding" to "0 10px",
                            "background" to if (selected == mode) "#e9f3ff" else "transparent",
                            "color" to if (selected == mode) Blue else Muted,
                            "font-size" to "12px",
                            "font-weight" to "700",
                            "cursor" to "pointer",
                        )
                    }
                }
            ) {
                Text(
                    when (mode) {
                        WebKpiChartMode.Line -> "Line"
                        WebKpiChartMode.Scatter -> "Scatter"
                        WebKpiChartMode.Bars -> "Bars"
                    }
                )
            }
        }
    }
}

@Composable
private fun KpiLineScatterChart(
    plotted: List<KairosKpiValuePoint>,
    minValue: Double,
    range: Double,
    target: Kairos.KpiTarget?,
    accentColor: String,
    mode: WebKpiChartMode,
    tall: Boolean,
) {
    val chartPoints =
        remember(plotted, minValue, range) {
            val firstTimestamp = plotted.first().at.toEpochMilliseconds()
            val lastTimestamp = plotted.last().at.toEpochMilliseconds()
            val timestampRange = (lastTimestamp - firstTimestamp).takeIf { value -> value > 0L }
            plotted.mapIndexed { index, point ->
                val x =
                    when {
                        timestampRange != null ->
                            ((point.at.toEpochMilliseconds() - firstTimestamp).toDouble() /
                                timestampRange.toDouble()) * 100.0

                        plotted.size == 1 -> 50.0
                        else -> (index.toDouble() / plotted.lastIndex.toDouble()) * 100.0
                    }.coerceIn(2.0, 98.0)
                val normalized = ((point.value - minValue) / range).coerceIn(0.0, 1.0)
                val y = (88.0 - normalized * 76.0).coerceIn(8.0, 92.0)
                WebKpiChartPoint(point = point, x = x, y = y)
            }
        }
    val path =
        remember(chartPoints) {
            chartPoints
                .mapIndexed { index, chartPoint ->
                    val prefix = if (index == 0) "M" else "L"
                    "$prefix ${chartPoint.x.formatSvgNumber()} ${chartPoint.y.formatSvgNumber()}"
                }
                .joinToString(" ")
        }
    val targetMarkers = remember(target, minValue, range) { target.toChartMarkers(minValue, range) }
    val trailingColor = accentColor
    val fadedColor = hexToRgba(accentColor, 0.28)

    Div(
        attrs = {
            attr("data-kpi-chart-kind", mode.name.lowercase())
            style {
                props(
                    "position" to "relative",
                    "height" to if (tall) "172px" else "64px",
                    "border" to "1px solid $Border",
                    "border-radius" to "8px",
                    "background" to "#fafafb",
                    "overflow" to "hidden",
                    "box-sizing" to "border-box",
                )
            }
        }
    ) {
        Svg(
            attrs = {
                attr("viewBox", "0 0 100 100")
                attr("preserveAspectRatio", "none")
                attr("aria-hidden", "true")
                style {
                    props(
                        "position" to "absolute",
                        "inset" to "0",
                        "width" to "100%",
                        "height" to "100%",
                    )
                }
            }
        ) {
            listOf(25, 50, 75).forEach { y ->
                Line {
                    attr("x1", "0")
                    attr("x2", "100")
                    attr("y1", y.toString())
                    attr("y2", y.toString())
                    attr("stroke", Border)
                    attr("stroke-width", "0.6")
                }
            }
            targetMarkers.forEach { marker ->
                Line {
                    attr("x1", "0")
                    attr("x2", "100")
                    attr("y1", marker.y.formatSvgNumber())
                    attr("y2", marker.y.formatSvgNumber())
                    attr("stroke", marker.stroke)
                    attr("stroke-width", "1.2")
                    attr("stroke-dasharray", "4 3")
                }
            }
            if (mode == WebKpiChartMode.Line && chartPoints.size > 1) {
                Path {
                    attr("d", path)
                    attr("fill", "none")
                    attr("stroke", trailingColor)
                    attr("stroke-width", "2.6")
                    attr("stroke-linecap", "round")
                    attr("stroke-linejoin", "round")
                    attr("vector-effect", "non-scaling-stroke")
                }
            }
        }
        targetMarkers.forEach { marker ->
            Div(
                attrs = {
                    style {
                        props(
                            "position" to "absolute",
                            "left" to "10px",
                            "top" to "calc(${marker.y.formatCssNumber()}% - 10px)",
                            "padding" to "2px 6px",
                            "border-radius" to "999px",
                            "background" to "#f5f6f8",
                            "border" to "1px solid #d7dce5",
                            "font-size" to "11px",
                            "font-weight" to "700",
                            "color" to Muted,
                            "pointer-events" to "none",
                        )
                    }
                }
            ) {
                Text(marker.label)
            }
        }
        chartPoints.forEachIndexed { index, chartPoint ->
            Div(
                attrs = {
                    title(
                        "${chartPoint.point.value.formatObjectiveMetric()} at ${formatMeasurementRecordedAt(chartPoint.point.at)}"
                    )
                    style {
                        props(
                            "position" to "absolute",
                            "left" to "${chartPoint.x.formatCssNumber()}%",
                            "top" to "${chartPoint.y.formatCssNumber()}%",
                            "width" to if (tall) "10px" else "8px",
                            "height" to if (tall) "10px" else "8px",
                            "transform" to "translate(-50%, -50%)",
                            "border-radius" to "999px",
                            "background" to
                                if (index == chartPoints.lastIndex) trailingColor else fadedColor,
                            "border" to "2px solid $Paper",
                            "box-shadow" to "0 1px 4px rgba(37, 39, 45, 0.18)",
                            "box-sizing" to "border-box",
                        )
                    }
                }
            )
        }
    }
}

@Composable
private fun KpiBarsChart(
    plotted: List<KairosKpiValuePoint>,
    minValue: Double,
    range: Double,
    target: Kairos.KpiTarget?,
    accentColor: String,
    tall: Boolean,
) {
    val targetMarkers = remember(target, minValue, range) { target.toChartMarkers(minValue, range) }
    Div(
        attrs = {
            attr("data-kpi-chart-kind", "bars")
            style {
                props(
                    "position" to "relative",
                    "display" to "flex",
                    "align-items" to "end",
                    "gap" to if (tall) "8px" else "6px",
                    "height" to if (tall) "172px" else "64px",
                    "padding" to if (tall) "18px 16px 12px" else "10px 12px",
                    "border" to "1px solid $Border",
                    "border-radius" to "8px",
                    "background" to "#fafafb",
                    "box-sizing" to "border-box",
                )
            }
        }
    ) {
        targetMarkers.forEach { marker ->
            Div(
                attrs = {
                    style {
                        props(
                            "position" to "absolute",
                            "left" to "0",
                            "right" to "0",
                            "top" to "calc(${marker.y.formatCssNumber()}% - 1px)",
                            "border-top" to "1px dashed ${marker.stroke}",
                            "pointer-events" to "none",
                        )
                    }
                }
            )
        }
        plotted.forEachIndexed { index, point ->
            val normalized = ((point.value - minValue) / range).coerceIn(0.0, 1.0)
            Div(
                attrs = {
                    title(
                        "${point.value.formatObjectiveMetric()} at ${formatMeasurementRecordedAt(point.at)}"
                    )
                    style {
                        props(
                            "flex" to "1",
                            "min-width" to "10px",
                            "height" to "${(26 + (normalized * if (tall) 104 else 24)).toInt()}px",
                            "border-radius" to "6px 6px 3px 3px",
                            "background" to
                                if (index == plotted.lastIndex) accentColor
                                else hexToRgba(accentColor, 0.28),
                            "box-shadow" to
                                if (index == plotted.lastIndex) {
                                    "0 0 0 1px ${hexToRgba(accentColor, 0.18)}"
                                } else {
                                    "none"
                                },
                        )
                    }
                }
            )
        }
    }
}

private data class WebKpiTargetMarker(
    val label: String,
    val y: Double,
    val stroke: String = "#98a2b3",
)

private fun Kairos.KpiTarget?.referenceValues(): List<Double> =
    when (this) {
        null -> emptyList()
        is Kairos.KpiTarget.AtLeast -> listOf(value)
        is Kairos.KpiTarget.AtMost -> listOf(value)
        is Kairos.KpiTarget.Range -> listOf(min, max)
    }

private fun Kairos.KpiTarget?.toChartMarkers(
    minValue: Double,
    range: Double,
): List<WebKpiTargetMarker> {
    fun marker(label: String, value: Double): WebKpiTargetMarker {
        val normalized = ((value - minValue) / range).coerceIn(0.0, 1.0)
        val y = (88.0 - normalized * 76.0).coerceIn(8.0, 92.0)
        return WebKpiTargetMarker(label = label, y = y)
    }

    return when (this) {
        null -> emptyList()
        is Kairos.KpiTarget.AtLeast ->
            listOf(marker("Target >= ${value.formatObjectiveMetric()}", value))
        is Kairos.KpiTarget.AtMost ->
            listOf(marker("Target <= ${value.formatObjectiveMetric()}", value))
        is Kairos.KpiTarget.Range ->
            listOf(
                marker("Min ${min.formatObjectiveMetric()}", min),
                marker("Max ${max.formatObjectiveMetric()}", max),
            )
    }
}

private data class WebKpiChartPoint(val point: KairosKpiValuePoint, val x: Double, val y: Double)

private fun Double.formatSvgNumber(): String = asDynamic().toFixed(2) as String

private fun Double.formatCssNumber(): String = asDynamic().toFixed(3) as String

private fun kpiSparklineDateLabel(point: KairosKpiValuePoint): String {
    val date = point.at.toLocalDateTime(currentUiTimeZone()).date
    val month =
        date.month.name.lowercase().take(3).replaceFirstChar { character -> character.titlecase() }
    return "$month ${date.day}"
}

@Composable
private fun KpiMetricCard(label: String, value: String) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "grid",
                    "gap" to "6px",
                    "padding" to "14px",
                    "border" to "1px solid $Border",
                    "border-radius" to "8px",
                    "background" to "#fafafb",
                )
            }
        }
    ) {
        FieldLabel(label)
        Div(attrs = { style { props("font-size" to "20px", "font-weight" to "700") } }) {
            Text(value)
        }
    }
}

@Composable
private fun FieldLabel(label: String) {
    Span(
        attrs = { style { props("font-size" to "12px", "font-weight" to "700", "color" to Muted) } }
    ) {
        Text(label)
    }
}

@Composable
private fun TogglePill(label: String, active: Boolean, id: String? = null, onClick: () -> Unit) {
    Button(
        attrs = {
            id?.let { value -> attr("id", value) }
            onClick { onClick() }
            style {
                tabButton(active)
                props("height" to "32px", "padding" to "0 12px", "font-size" to "13px")
            }
        }
    ) {
        Text(label)
    }
}

@Composable
private fun ObjectiveCreateModal(
    projects: List<Kairos.Project>,
    allProjects: List<Kairos.Project>,
    descriptionDraft: String,
    deadlineDraft: String,
    projectIdDraft: Kairos.ProjectId?,
    onDescriptionChanged: (String) -> Unit,
    onDeadlineChanged: (String) -> Unit,
    onProjectSelected: (Kairos.ProjectId?) -> Unit,
    onDismiss: () -> Unit,
    onCreate: () -> Unit,
) {
    Div(
        attrs = {
            attr("id", "web-objective-modal")
            onClick { it.stopPropagation() }
            style {
                props(
                    "position" to "fixed",
                    "inset" to "0",
                    "z-index" to "44",
                    "display" to "flex",
                    "justify-content" to "center",
                    "align-items" to "center",
                    "padding" to "24px",
                    "overflow" to "auto",
                    "box-sizing" to "border-box",
                )
            }
        }
    ) {
        Div(
            attrs = {
                onClick { it.stopPropagation() }
                style {
                    props(
                        "width" to "min(720px, 100%)",
                        "border" to "1px solid $Border",
                        "border-radius" to "8px",
                        "background" to Paper,
                        "box-shadow" to "0 20px 44px rgba(20,22,28,0.28)",
                        "padding" to "24px",
                        "display" to "grid",
                        "gap" to "18px",
                        "box-sizing" to "border-box",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "space-between",
                            "align-items" to "start",
                            "gap" to "16px",
                        )
                    }
                }
            ) {
                Div(attrs = { style { props("display" to "grid", "gap" to "6px") } }) {
                    Span(
                        attrs = {
                            style {
                                props("font-size" to "24px", "font-weight" to "700", "color" to Ink)
                            }
                        }
                    ) {
                        Text("Create objective")
                    }
                    Span(
                        attrs = {
                            style {
                                props(
                                    "font-size" to "14px",
                                    "color" to Muted,
                                    "line-height" to "1.5",
                                )
                            }
                        }
                    ) {
                        Text(
                            "Define the outcome first. You can link tickets and KPI targets once the objective is created."
                        )
                    }
                }
            }
            Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
                Span(
                    attrs = {
                        style {
                            props("font-size" to "13px", "font-weight" to "700", "color" to Ink)
                        }
                    }
                ) {
                    Text("Outcome")
                }
                TextArea(
                    attrs = {
                        attr("id", "web-objective-description-input")
                        value(descriptionDraft)
                        onInput { onDescriptionChanged(it.value) }
                        attr("placeholder", "What outcome are you driving?")
                        style {
                            props(
                                "border" to "1px solid $Border",
                                "border-radius" to "8px",
                                "padding" to "14px",
                                "min-height" to "132px",
                                "width" to "100%",
                                "box-sizing" to "border-box",
                                "font-size" to "15px",
                                "line-height" to "1.5",
                                "font-family" to "Inter, Arial, Helvetica, sans-serif",
                                "resize" to "vertical",
                            )
                        }
                    }
                )
            }
            Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
                Span(
                    attrs = {
                        style {
                            props("font-size" to "13px", "font-weight" to "700", "color" to Ink)
                        }
                    }
                ) {
                    Text("Scope")
                }
                ObjectiveScopeSelector(
                    projects = projects,
                    currentProjectId = projectIdDraft,
                    allProjects = allProjects,
                    onProjectSelected = onProjectSelected,
                )
            }
            Div(
                attrs = {
                    style { props("display" to "grid", "gap" to "8px", "max-width" to "220px") }
                }
            ) {
                Span(
                    attrs = {
                        style {
                            props("font-size" to "13px", "font-weight" to "700", "color" to Ink)
                        }
                    }
                ) {
                    Text("Deadline")
                }
                DatePickerField(
                    id = "web-objective-deadline-input",
                    value = deadlineDraft,
                    placeholder = "Deadline",
                    kind = WebDateControlKind.Date,
                    onChange = onDeadlineChanged,
                )
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "flex-end",
                            "gap" to "10px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                Button(
                    attrs = {
                        attr("id", "web-objective-cancel-button")
                        onClick { onDismiss() }
                        style { secondaryButton() }
                    }
                ) {
                    Text("Cancel")
                }
                Button(
                    attrs = {
                        attr("id", "web-objective-submit-button")
                        onClick { onCreate() }
                        if (descriptionDraft.trim().isBlank()) disabled()
                        style { primaryButton() }
                    }
                ) {
                    Text("Create objective")
                }
            }
        }
    }
}

@Composable
private fun ObjectiveScopeSelector(
    projects: List<Kairos.Project>,
    currentProjectId: Kairos.ProjectId?,
    allProjects: List<Kairos.Project>,
    onProjectSelected: (Kairos.ProjectId?) -> Unit,
) {
    var expanded by remember(currentProjectId, projects) { mutableStateOf(false) }
    var query by remember(currentProjectId, projects) { mutableStateOf("") }
    val selectedProject = projects.firstOrNull { project -> project.id == currentProjectId }
    val filteredProjects =
        remember(projects, query) {
            val needle = query.trim().lowercase()
            if (needle.isBlank()) {
                projects
            } else {
                projects.filter { project ->
                    project.projectDisplayName(allProjects).lowercase().contains(needle) ||
                        project.name.lowercase().contains(needle)
                }
            }
        }
    val groupedProjects =
        remember(filteredProjects, allProjects) {
            filteredProjects.groupBy { project ->
                project.parentProjectId?.let { parentId ->
                    allProjects.firstOrNull { candidate -> candidate.id == parentId }
                }
            }
        }

    Div(attrs = { style { props("position" to "relative", "width" to "100%") } }) {
        Button(
            attrs = {
                attr("id", "web-objective-scope-select")
                attr("aria-haspopup", "listbox")
                attr("aria-expanded", expanded.toString())
                attr("data-objective-scope", currentProjectId?.value ?: "workspace")
                onClick { expanded = !expanded }
                style {
                    props(
                        "min-height" to "56px",
                        "width" to "100%",
                        "border" to "1px solid $Border",
                        "border-radius" to "8px",
                        "padding" to "10px 12px",
                        "font-size" to "13px",
                        "font-weight" to "600",
                        "background" to Paper,
                        "color" to Ink,
                        "box-sizing" to "border-box",
                        "display" to "flex",
                        "align-items" to "center",
                        "justify-content" to "space-between",
                        "gap" to "12px",
                        "cursor" to "pointer",
                        "text-align" to "left",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "gap" to "2px",
                            "min-width" to "0",
                            "flex" to "1",
                        )
                    }
                }
            ) {
                Span(
                    attrs = {
                        style {
                            props(
                                "font-size" to "14px",
                                "font-weight" to "700",
                                "line-height" to "1.35",
                                "overflow" to "hidden",
                                "text-overflow" to "ellipsis",
                                "white-space" to "nowrap",
                            )
                        }
                    }
                ) {
                    Text(selectedProject?.name ?: "Workspace-wide")
                }
                Span(attrs = { style { props("font-size" to "11px", "color" to Muted) } }) {
                    Text(selectedProject?.projectContextLabel(allProjects) ?: "All projects")
                }
            }
            Span(attrs = { style { props("color" to Muted, "font-size" to "12px") } }) { Text("▾") }
        }
        if (expanded) {
            Div(
                attrs = {
                    style {
                        props(
                            "position" to "absolute",
                            "top" to "calc(100% + 6px)",
                            "left" to "0",
                            "right" to "0",
                            "z-index" to "20",
                            "display" to "grid",
                            "gap" to "4px",
                            "padding" to "6px",
                            "border" to "1px solid $Border",
                            "border-radius" to "8px",
                            "background" to Paper,
                            "box-shadow" to "0 10px 24px rgba(37,39,45,0.12)",
                            "max-height" to "280px",
                            "overflow" to "hidden",
                        )
                    }
                }
            ) {
                Input(
                    type = InputType.Text,
                    attrs = {
                        attr("id", "web-objective-scope-search")
                        value(query)
                        onInput { query = it.value }
                        attr("placeholder", "Search projects")
                        style { modalTextInput() }
                    },
                )
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "grid",
                                "gap" to "8px",
                                "overflow-y" to "auto",
                                "padding-right" to "2px",
                            )
                        }
                    }
                ) {
                    Button(
                        attrs = {
                            attr("data-objective-scope-option", "workspace")
                            onClick {
                                expanded = false
                                onProjectSelected(null)
                            }
                            style {
                                props(
                                    "min-height" to "42px",
                                    "width" to "100%",
                                    "border" to
                                        if (currentProjectId == null) "1px solid $Blue"
                                        else "1px solid transparent",
                                    "border-radius" to "8px",
                                    "background" to
                                        if (currentProjectId == null) "#eef4ff" else Paper,
                                    "padding" to "8px 10px",
                                    "display" to "grid",
                                    "gap" to "2px",
                                    "cursor" to "pointer",
                                    "text-align" to "left",
                                )
                            }
                        }
                    ) {
                        Span(
                            attrs = {
                                style {
                                    props(
                                        "font-size" to "13px",
                                        "font-weight" to "700",
                                        "color" to Ink,
                                    )
                                }
                            }
                        ) {
                            Text("Workspace-wide")
                        }
                        Span(attrs = { style { props("font-size" to "11px", "color" to Muted) } }) {
                            Text("All projects")
                        }
                    }
                    groupedProjects
                        .toList()
                        .sortedBy { (parent, _) -> parent?.name?.lowercase().orEmpty() }
                        .forEach { (parent, members) ->
                            Div(attrs = { style { props("display" to "grid", "gap" to "4px") } }) {
                                parent?.let { parentProject ->
                                    Span(
                                        attrs = {
                                            style {
                                                props(
                                                    "padding" to "4px 4px 2px",
                                                    "font-size" to "11px",
                                                    "font-weight" to "700",
                                                    "letter-spacing" to "0.04em",
                                                    "text-transform" to "uppercase",
                                                    "color" to Muted,
                                                )
                                            }
                                        }
                                    ) {
                                        Text(parentProject.name)
                                    }
                                }
                                members
                                    .sortedBy { project -> project.name.lowercase() }
                                    .forEach { project ->
                                        val selected = project.id == currentProjectId
                                        Button(
                                            attrs = {
                                                attr(
                                                    "data-objective-scope-option",
                                                    project.id.value,
                                                )
                                                onClick {
                                                    expanded = false
                                                    if (!selected) {
                                                        onProjectSelected(project.id)
                                                    }
                                                }
                                                style {
                                                    props(
                                                        "min-height" to "42px",
                                                        "width" to "100%",
                                                        "border" to
                                                            if (selected) "1px solid $Blue"
                                                            else "1px solid transparent",
                                                        "border-radius" to "8px",
                                                        "background" to
                                                            if (selected) "#eef4ff" else Paper,
                                                        "padding" to
                                                            "8px 10px 8px ${if (parent != null) 18 else 10}px",
                                                        "display" to "grid",
                                                        "gap" to "2px",
                                                        "cursor" to "pointer",
                                                        "text-align" to "left",
                                                    )
                                                }
                                            }
                                        ) {
                                            Span(
                                                attrs = {
                                                    style {
                                                        props(
                                                            "font-size" to "13px",
                                                            "font-weight" to
                                                                if (selected) "700" else "600",
                                                            "color" to Ink,
                                                        )
                                                    }
                                                }
                                            ) {
                                                Text(project.name)
                                            }
                                            project
                                                .projectContextLabel(allProjects)
                                                ?.takeIf { it.isNotBlank() }
                                                ?.let { context ->
                                                    Span(
                                                        attrs = {
                                                            style {
                                                                props(
                                                                    "font-size" to "11px",
                                                                    "color" to Muted,
                                                                )
                                                            }
                                                        }
                                                    ) {
                                                        Text(context)
                                                    }
                                                }
                                        }
                                    }
                            }
                        }
                }
            }
        }
    }
}

@Composable
private fun ObjectiveOverviewMetric(label: String, value: String) {
    Div(attrs = { style { props("display" to "grid", "gap" to "2px", "min-width" to "116px") } }) {
        Span(
            attrs = {
                style { props("font-size" to "12px", "font-weight" to "700", "color" to Muted) }
            }
        ) {
            Text(label)
        }
        Span(
            attrs = {
                style { props("font-size" to "24px", "font-weight" to "700", "color" to Ink) }
            }
        ) {
            Text(value)
        }
    }
}

@Composable
private fun ObjectiveHealthMetric(
    label: String,
    value: String,
    detail: String,
    accentColor: String,
    background: String,
) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "flex",
                    "justify-content" to "space-between",
                    "align-items" to "start",
                    "gap" to "12px",
                )
            }
        }
    ) {
        Div(attrs = { style { props("display" to "grid", "gap" to "4px", "min-width" to "0") } }) {
            Div(
                attrs = {
                    style { props("font-size" to "14px", "font-weight" to "700", "color" to Ink) }
                }
            ) {
                Text(label)
            }
            Span(
                attrs = {
                    style { props("font-size" to "12px", "line-height" to "1.5", "color" to Muted) }
                }
            ) {
                Text(detail)
            }
        }
        Span(
            attrs = {
                style {
                    props(
                        "display" to "inline-flex",
                        "align-items" to "center",
                        "justify-content" to "center",
                        "min-width" to "38px",
                        "height" to "30px",
                        "padding" to "0 10px",
                        "border-radius" to "999px",
                        "background" to background,
                        "color" to accentColor,
                        "font-size" to "14px",
                        "font-weight" to "700",
                        "flex-shrink" to "0",
                    )
                }
            }
        ) {
            Text(value)
        }
    }
}

@Composable
private fun ObjectiveRow(
    workspace: WebWorkspace,
    compact: Boolean,
    summary: WebObjectiveSummary,
    onTaskSelected: (WebTask) -> Unit,
    onUpdateObjective: (Kairos.ObjectiveId, String, ZonedDateTime?) -> Unit,
    onCreateObjectiveKpi: (Kairos.Objective, String, Kairos.KpiTarget) -> Unit,
    onCreateKpiMeasurement: (Kairos.KpiId, Double) -> Unit,
    onSetObjectiveKpiTargets: (Kairos.ObjectiveId, List<Kairos.KpiWithTarget>) -> Unit,
) {
    var descriptionDraft by
        remember(summary.objective.id, summary.description) { mutableStateOf(summary.description) }
    var deadlineDraft by
        remember(summary.objective.id, summary.objective.deadline) {
            mutableStateOf(summary.objective.deadline.inputValue())
        }
    var editingObjective by remember(summary.objective.id) { mutableStateOf(false) }
    var addingKpi by remember(summary.objective.id) { mutableStateOf(false) }
    var kpiNameDraft by remember(summary.objective.id) { mutableStateOf("") }
    var kpiTargetDraft by remember(summary.objective.id) { mutableStateOf("") }
    val availableKpis =
        remember(workspace, summary.objective.id) { workspace.availableKpisFor(summary.objective) }
    var measurementDrafts by
        remember(summary.objective.id, summary.kpis.map { it.kpi.id.value }) {
            mutableStateOf(summary.kpis.associate { kpi -> kpi.kpi.id to "" })
        }
    var expandedMeasurementKpis by
        remember(summary.objective.id, summary.kpis.map { it.kpi.id.value }) {
            mutableStateOf(emptySet<Kairos.KpiId>())
        }
    val parsedKpiTarget =
        kpiTargetDraft.toDoubleOrNull()?.let { value -> Kairos.KpiTarget.AtLeast(value) }
    val objectiveDirty =
        descriptionDraft.trim().isNotBlank() &&
            (descriptionDraft != summary.description ||
                deadlineDraft != summary.objective.deadline.inputValue())
    fun resetObjectiveDrafts() {
        descriptionDraft = summary.description
        deadlineDraft = summary.objective.deadline.inputValue()
    }

    val progressDetail =
        when {
            summary.linkedTasks.isEmpty() && summary.kpis.isEmpty() ->
                "Link tickets or KPI targets to make rollup meaningful."
            summary.progressPercent >= 100 -> "Rollup complete across linked work."
            else -> "${summary.progressPercent}% rolled up from linked tickets and KPI targets."
        }

    Div(
        attrs = {
            attr("data-objective-id", summary.objective.id.value)
            style {
                props(
                    "border" to "1px solid $Border",
                    "box-shadow" to "inset 4px 0 0 ${summary.statusColor}",
                    "border-radius" to "8px",
                    "background" to Paper,
                    "padding" to if (compact) "14px" else "18px",
                    "display" to "grid",
                    "gap" to "14px",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to if (compact) "grid" else "flex",
                        "justify-content" to if (compact) "start" else "space-between",
                        "align-items" to "start",
                        "gap" to if (compact) "12px" else "16px",
                        "flex-wrap" to if (compact) "nowrap" else "wrap",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "gap" to "8px",
                            "min-width" to "0",
                            "flex" to "1",
                        )
                    }
                }
            ) {
                if (editingObjective) {
                    TextArea(
                        attrs = {
                            attr("data-objective-description-input", summary.objective.id.value)
                            value(descriptionDraft)
                            onInput { descriptionDraft = it.value }
                            style {
                                props(
                                    "border" to "1px solid $Border",
                                    "border-radius" to "8px",
                                    "padding" to "12px",
                                    "min-height" to "96px",
                                    "width" to "100%",
                                    "box-sizing" to "border-box",
                                    "font-size" to "16px",
                                    "line-height" to "1.5",
                                    "font-weight" to "600",
                                    "font-family" to "Inter, Arial, Helvetica, sans-serif",
                                    "resize" to "vertical",
                                )
                            }
                        }
                    )
                } else {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "font-size" to "20px",
                                    "font-weight" to "700",
                                    "line-height" to "1.35",
                                    "color" to Ink,
                                )
                            }
                        }
                    ) {
                        Text(summary.description)
                    }
                }
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "gap" to "8px",
                                "flex-wrap" to "wrap",
                                "align-items" to "center",
                            )
                        }
                    }
                ) {
                    Chip(summary.projectName, "#f4f6f8", Ink)
                    Chip(summary.deadlineLabel, "#f4f6f8", Ink)
                    Chip("${summary.linkedTasks.size} tickets", "#f4f6f8", Ink)
                    Chip("${summary.kpis.size} KPIs", "#f4f6f8", Ink)
                }
            }
            if (compact) {
                Div(
                    attrs = {
                        style { props("display" to "grid", "gap" to "10px", "width" to "100%") }
                    }
                ) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "justify-content" to "space-between",
                                    "align-items" to "center",
                                    "gap" to "12px",
                                    "flex-wrap" to "wrap",
                                )
                            }
                        }
                    ) {
                        StatusPill(
                            summary.statusLabel,
                            summary.statusColor,
                            summary.statusBackground,
                        )
                        Span(
                            attrs = {
                                style { props("font-size" to "20px", "font-weight" to "700") }
                            }
                        ) {
                            Text("${summary.progressPercent}% of target")
                        }
                    }
                    Div(
                        attrs = {
                            style {
                                props("display" to "flex", "gap" to "8px", "flex-wrap" to "wrap")
                            }
                        }
                    ) {
                        Button(
                            attrs = {
                                attr("data-objective-action", "toggle-kpi")
                                attr("data-objective-toggle-kpi", summary.objective.id.value)
                                onClick { addingKpi = !addingKpi }
                                style {
                                    if (addingKpi) secondaryButton(selected = true)
                                    else primaryButton()
                                }
                            }
                        ) {
                            Text(if (addingKpi) "Hide KPI tools" else "Add KPI")
                        }
                        if (editingObjective) {
                            Button(
                                attrs = {
                                    attr("data-objective-action", "cancel-edit")
                                    attr("data-objective-edit-cancel", summary.objective.id.value)
                                    onClick {
                                        editingObjective = false
                                        resetObjectiveDrafts()
                                    }
                                    style { secondaryButton() }
                                }
                            ) {
                                Text("Cancel")
                            }
                            Button(
                                attrs = {
                                    attr("data-objective-action", "save-edit")
                                    attr("data-objective-edit-save", summary.objective.id.value)
                                    onClick {
                                        onUpdateObjective(
                                            summary.objective.id,
                                            descriptionDraft.trim(),
                                            parseDeadlineInput(deadlineDraft),
                                        )
                                        editingObjective = false
                                    }
                                    if (!objectiveDirty) disabled()
                                    style { primaryButton() }
                                }
                            ) {
                                Text("Save")
                            }
                        } else {
                            Button(
                                attrs = {
                                    attr("data-objective-action", "start-edit")
                                    attr("data-objective-edit-start", summary.objective.id.value)
                                    onClick { editingObjective = true }
                                    style { ghostButton() }
                                }
                            ) {
                                Text("Edit")
                            }
                        }
                    }
                }
            } else {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "grid",
                                "gap" to "8px",
                                "justify-items" to "end",
                                "flex-shrink" to "0",
                            )
                        }
                    }
                ) {
                    StatusPill(summary.statusLabel, summary.statusColor, summary.statusBackground)
                    Span(
                        attrs = { style { props("font-size" to "20px", "font-weight" to "700") } }
                    ) {
                        Text("${summary.progressPercent}% of target")
                    }
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "gap" to "8px",
                                    "flex-wrap" to "wrap",
                                    "justify-content" to "flex-end",
                                )
                            }
                        }
                    ) {
                        Button(
                            attrs = {
                                attr("data-objective-action", "toggle-kpi")
                                attr("data-objective-toggle-kpi", summary.objective.id.value)
                                onClick { addingKpi = !addingKpi }
                                style {
                                    if (addingKpi) secondaryButton(selected = true)
                                    else primaryButton()
                                }
                            }
                        ) {
                            Text(if (addingKpi) "Hide KPI tools" else "Add KPI")
                        }
                        if (editingObjective) {
                            Button(
                                attrs = {
                                    attr("data-objective-action", "cancel-edit")
                                    attr("data-objective-edit-cancel", summary.objective.id.value)
                                    onClick {
                                        editingObjective = false
                                        resetObjectiveDrafts()
                                    }
                                    style { secondaryButton() }
                                }
                            ) {
                                Text("Cancel")
                            }
                            Button(
                                attrs = {
                                    attr("data-objective-action", "save-edit")
                                    attr("data-objective-edit-save", summary.objective.id.value)
                                    onClick {
                                        onUpdateObjective(
                                            summary.objective.id,
                                            descriptionDraft.trim(),
                                            parseDeadlineInput(deadlineDraft),
                                        )
                                        editingObjective = false
                                    }
                                    if (!objectiveDirty) disabled()
                                    style { primaryButton() }
                                }
                            ) {
                                Text("Save")
                            }
                        } else {
                            Button(
                                attrs = {
                                    attr("data-objective-action", "start-edit")
                                    attr("data-objective-edit-start", summary.objective.id.value)
                                    onClick { editingObjective = true }
                                    style { ghostButton() }
                                }
                            ) {
                                Text("Edit")
                            }
                        }
                    }
                }
            }
        }
        Div(attrs = { style { props("display" to "grid", "gap" to "6px") } }) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "space-between",
                            "align-items" to "center",
                            "gap" to "12px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                Span(
                    attrs = {
                        style {
                            props("font-size" to "12px", "font-weight" to "700", "color" to Muted)
                        }
                    }
                ) {
                    Text("Rollup progress")
                }
                Span(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                    Text(progressDetail)
                }
            }
            ObjectiveProgressBar(summary.progressPercent, summary.statusColor)
        }
        if (editingObjective) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "gap" to "10px",
                            "align-items" to "center",
                            "flex-wrap" to "wrap",
                            "padding" to "12px",
                            "border" to "1px solid $Border",
                            "border-radius" to "8px",
                            "background" to "#fafbfc",
                        )
                    }
                }
            ) {
                Span(
                    attrs = {
                        style {
                            props("font-size" to "13px", "font-weight" to "700", "color" to Ink)
                        }
                    }
                ) {
                    Text("Deadline")
                }
                DatePickerField(
                    value = deadlineDraft,
                    placeholder = "Deadline",
                    kind = WebDateControlKind.Date,
                    width = "170px",
                    attrs = { attr("data-objective-deadline-input", summary.objective.id.value) },
                    onChange = { deadlineDraft = it },
                )
            }
        }
        if (summary.linkedTasks.isNotEmpty()) {
            Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
                Div(attrs = { style { props("font-size" to "15px", "font-weight" to "700") } }) {
                    Text("Linked tickets")
                }
                Div(
                    attrs = {
                        style { props("display" to "flex", "gap" to "8px", "flex-wrap" to "wrap") }
                    }
                ) {
                    summary.linkedTasks.forEach { task ->
                        Div(
                            attrs = {
                                onClick { onTaskSelected(task) }
                                style {
                                    props(
                                        "display" to "flex",
                                        "align-items" to "center",
                                        "gap" to "8px",
                                        "background" to "#f4f6f8",
                                        "border-radius" to "8px",
                                        "padding" to "6px 10px",
                                        "cursor" to "pointer",
                                    )
                                }
                            }
                        ) {
                            Span { Text(task.ticket.title) }
                            TaskStatusBadge(task.status, task.statusColor)
                        }
                    }
                }
            }
            Div(
                attrs = {
                    attr("data-objective-activity", summary.objective.id.value)
                    style { props("display" to "grid", "gap" to "8px") }
                }
            ) {
                Div(attrs = { style { props("font-size" to "15px", "font-weight" to "700") } }) {
                    Text("Recent linked history")
                }
                ActivityFeed(
                    entries =
                        workspace
                            .activityEntriesFor(
                                summary.linkedTasks.map { task -> task.ticket.id }.toSet()
                            )
                            .take(4),
                    emptyLabel = "No linked ticket history yet",
                )
            }
        }
        Div(attrs = { style { props("display" to "grid", "gap" to "10px") } }) {
            Div(attrs = { style { props("font-size" to "15px", "font-weight" to "700") } }) {
                Text("KPIs")
            }
            if (summary.kpis.isEmpty()) {
                MutedText("No KPIs linked yet")
            } else {
                summary.kpis.forEach { kpiSummary ->
                    val measurementDraft = measurementDrafts[kpiSummary.kpi.id].orEmpty()
                    val measurementValue = measurementDraft.toDoubleOrNull()
                    val measurementExpanded = kpiSummary.kpi.id in expandedMeasurementKpis
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "grid",
                                    "gap" to "8px",
                                    "border" to "1px solid $Border",
                                    "border-radius" to "8px",
                                    "padding" to "12px",
                                )
                            }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "flex",
                                        "justify-content" to "space-between",
                                        "align-items" to "start",
                                        "gap" to "12px",
                                        "flex-wrap" to "wrap",
                                    )
                                }
                            }
                        ) {
                            Div(attrs = { style { props("display" to "grid", "gap" to "4px") } }) {
                                Div(
                                    attrs = {
                                        style {
                                            props("font-size" to "14px", "font-weight" to "600")
                                        }
                                    }
                                ) {
                                    Text(kpiSummary.kpi.name)
                                }
                                MutedText(
                                    "Latest ${kpiSummary.latestValueLabel} / Target ${kpiSummary.targetLabel}"
                                )
                            }
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "display" to "grid",
                                            "gap" to "6px",
                                            "justify-items" to "end",
                                        )
                                    }
                                }
                            ) {
                                StatusPill(
                                    kpiSummary.statusLabel,
                                    kpiSummary.statusColor,
                                    kpiSummary.statusBackground,
                                )
                                Span(
                                    attrs = {
                                        style {
                                            props("font-size" to "16px", "font-weight" to "700")
                                        }
                                    }
                                ) {
                                    Text("${kpiSummary.progressPercent}% of target")
                                }
                            }
                        }
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "flex",
                                        "gap" to "10px",
                                        "align-items" to "start",
                                        "flex-wrap" to "wrap",
                                    )
                                }
                            }
                        ) {
                            if (kpiSummary.kpi.origin is Kairos.KpiOrigin.Derived) {
                                MutedText("Derived KPI")
                            } else {
                                Div(
                                    attrs = {
                                        style {
                                            props(
                                                "display" to "grid",
                                                "gap" to "8px",
                                                "min-width" to "160px",
                                            )
                                        }
                                    }
                                ) {
                                    if (measurementExpanded) {
                                        FieldLabel("New measurement")
                                        Div(
                                            attrs = {
                                                style {
                                                    props(
                                                        "display" to "flex",
                                                        "gap" to "10px",
                                                        "align-items" to "center",
                                                        "flex-wrap" to "wrap",
                                                    )
                                                }
                                            }
                                        ) {
                                            Input(
                                                type = InputType.Text,
                                                attrs = {
                                                    attr(
                                                        "data-objective-kpi-measurement-input",
                                                        kpiSummary.kpi.id.value,
                                                    )
                                                    value(measurementDraft)
                                                    attr("placeholder", "Check-in value")
                                                    onInput { event ->
                                                        measurementDrafts =
                                                            measurementDrafts +
                                                                (kpiSummary.kpi.id to event.value)
                                                    }
                                                    style {
                                                        props(
                                                            "height" to "36px",
                                                            "width" to "160px",
                                                            "border" to "1px solid $Border",
                                                            "border-radius" to "8px",
                                                            "padding" to "0 12px",
                                                            "font-size" to "14px",
                                                            "box-sizing" to "border-box",
                                                        )
                                                    }
                                                },
                                            )
                                            Button(
                                                attrs = {
                                                    attr(
                                                        "data-objective-kpi-measurement-button",
                                                        kpiSummary.kpi.id.value,
                                                    )
                                                    onClick {
                                                        onCreateKpiMeasurement(
                                                            kpiSummary.kpi.id,
                                                            requireNotNull(measurementValue),
                                                        )
                                                        measurementDrafts =
                                                            measurementDrafts +
                                                                (kpiSummary.kpi.id to "")
                                                        expandedMeasurementKpis =
                                                            expandedMeasurementKpis -
                                                                kpiSummary.kpi.id
                                                    }
                                                    if (measurementValue == null) disabled()
                                                    style {
                                                        primaryButton(
                                                            enabled = measurementValue != null
                                                        )
                                                    }
                                                }
                                            ) {
                                                Text("Save measurement")
                                            }
                                            Button(
                                                attrs = {
                                                    attr(
                                                        "data-objective-kpi-measurement-cancel",
                                                        kpiSummary.kpi.id.value,
                                                    )
                                                    onClick {
                                                        measurementDrafts =
                                                            measurementDrafts +
                                                                (kpiSummary.kpi.id to "")
                                                        expandedMeasurementKpis =
                                                            expandedMeasurementKpis -
                                                                kpiSummary.kpi.id
                                                    }
                                                    style { secondaryButton() }
                                                }
                                            ) {
                                                Text("Cancel")
                                            }
                                        }
                                        if (measurementValue == null) {
                                            MutedText("Type a value above to enable save.")
                                        }
                                    } else {
                                        Button(
                                            attrs = {
                                                attr(
                                                    "data-objective-kpi-check-in",
                                                    kpiSummary.kpi.id.value,
                                                )
                                                onClick {
                                                    expandedMeasurementKpis =
                                                        expandedMeasurementKpis + kpiSummary.kpi.id
                                                }
                                                style { primaryButton() }
                                            }
                                        ) {
                                            Text("Check in")
                                        }
                                    }
                                }
                            }
                            Button(
                                attrs = {
                                    attr("data-objective-kpi-unlink", kpiSummary.kpi.id.value)
                                    onClick {
                                        onSetObjectiveKpiTargets(
                                            summary.objective.id,
                                            summary.objective.kpiTargets.filterNot { target ->
                                                target.kpi == kpiSummary.kpi.id
                                            },
                                        )
                                    }
                                    style { destructiveButton() }
                                }
                            ) {
                                Text("Unlink")
                            }
                        }
                        if (
                            kpiSummary.recentPoints.isEmpty() &&
                                kpiSummary.kpi.origin !is Kairos.KpiOrigin.Derived
                        ) {
                            MutedText("No check-ins yet")
                        }
                    }
                }
            }
            if (addingKpi) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "grid",
                                "gap" to "8px",
                                "padding" to "12px",
                                "border" to "1px solid $Border",
                                "border-radius" to "8px",
                                "background" to "#fafbfc",
                            )
                        }
                    }
                ) {
                    Div(
                        attrs = { style { props("font-size" to "14px", "font-weight" to "600") } }
                    ) {
                        Text("Add KPI")
                    }
                    if (availableKpis.isNotEmpty()) {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "grid",
                                        "grid-template-columns" to
                                            "repeat(auto-fit, minmax(180px, 1fr))",
                                        "gap" to "8px",
                                    )
                                }
                            }
                        ) {
                            availableKpis.forEach { kpi ->
                                val attachTarget = parsedKpiTarget ?: kpi.targetValue
                                Button(
                                    attrs = {
                                        attr("data-objective-kpi-link", kpi.id.value)
                                        onClick {
                                            onSetObjectiveKpiTargets(
                                                summary.objective.id,
                                                summary.objective.kpiTargets +
                                                    Kairos.KpiWithTarget(
                                                        kpi = kpi.id,
                                                        target = requireNotNull(attachTarget),
                                                    ),
                                            )
                                            kpiTargetDraft = ""
                                            addingKpi = false
                                        }
                                        if (attachTarget == null) disabled()
                                        style { secondaryButton() }
                                    }
                                ) {
                                    Text("Link ${kpi.name}")
                                }
                            }
                        }
                    }
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "gap" to "10px",
                                    "align-items" to "center",
                                    "flex-wrap" to "wrap",
                                )
                            }
                        }
                    ) {
                        Input(
                            type = InputType.Text,
                            attrs = {
                                attr("data-objective-kpi-create-name", summary.objective.id.value)
                                value(kpiNameDraft)
                                attr("placeholder", "New KPI name")
                                onInput { kpiNameDraft = it.value }
                                style {
                                    props(
                                        "height" to "36px",
                                        "min-width" to "240px",
                                        "flex" to "1",
                                        "border" to "1px solid $Border",
                                        "border-radius" to "8px",
                                        "padding" to "0 12px",
                                        "font-size" to "14px",
                                        "box-sizing" to "border-box",
                                    )
                                }
                            },
                        )
                        Input(
                            type = InputType.Text,
                            attrs = {
                                attr("data-objective-kpi-create-target", summary.objective.id.value)
                                value(kpiTargetDraft)
                                attr("placeholder", "Target")
                                onInput { kpiTargetDraft = it.value }
                                style {
                                    props(
                                        "height" to "36px",
                                        "width" to "120px",
                                        "border" to "1px solid $Border",
                                        "border-radius" to "8px",
                                        "padding" to "0 12px",
                                        "font-size" to "14px",
                                        "box-sizing" to "border-box",
                                    )
                                }
                            },
                        )
                        Button(
                            attrs = {
                                attr("data-objective-kpi-create-button", summary.objective.id.value)
                                onClick {
                                    onCreateObjectiveKpi(
                                        summary.objective,
                                        kpiNameDraft.trim(),
                                        requireNotNull(parsedKpiTarget),
                                    )
                                    kpiNameDraft = ""
                                    kpiTargetDraft = ""
                                    addingKpi = false
                                }
                                if (kpiNameDraft.trim().isBlank() || parsedKpiTarget == null)
                                    disabled()
                                style { primaryButton() }
                            }
                        ) {
                            Text("Create KPI")
                        }
                    }
                    Div(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                        Text(
                            "Target uses an at-least threshold for now to keep measurements simple."
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ObjectiveProgressBar(progressPercent: Int, color: String) {
    val clampedProgress = progressPercent.coerceIn(0, 100)
    Div(
        attrs = {
            style {
                props(
                    "height" to "8px",
                    "border-radius" to "999px",
                    "background" to "#edf0f3",
                    "overflow" to "hidden",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "width" to "${clampedProgress}%",
                        "min-width" to if (clampedProgress > 0) "10px" else "0",
                        "height" to "100%",
                        "border-radius" to "999px",
                        "background" to color,
                        "transition" to "width 160ms ease",
                    )
                }
            }
        )
    }
}

@Composable
private fun WorkspaceKpiRow(
    summary: WebWorkspaceKpiSummary,
    compact: Boolean = false,
    onOpenDetails: () -> Unit,
    onCreateKpiMeasurement: (Kairos.KpiId, Double) -> Unit,
) {
    var measurementDraft by remember(summary.kpi.id) { mutableStateOf("") }
    var measurementExpanded by remember(summary.kpi.id) { mutableStateOf(false) }
    val measurementValue = measurementDraft.toDoubleOrNull()
    val derived = summary.kpi.origin is Kairos.KpiOrigin.Derived

    Div(
        attrs = {
            attr("data-kpi-id", summary.kpi.id.value)
            onClick { onOpenDetails() }
            style {
                props(
                    "display" to "grid",
                    "gap" to "10px",
                    "border" to "1px solid $Border",
                    "box-shadow" to "inset 4px 0 0 ${summary.statusColor}",
                    "border-radius" to "8px",
                    "padding" to "16px",
                    "background" to Paper,
                    "cursor" to "pointer",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "justify-content" to "space-between",
                        "align-items" to "start",
                        "gap" to "12px",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "gap" to "4px",
                            "min-width" to "0",
                            "flex" to "1",
                        )
                    }
                }
            ) {
                Div(attrs = { style { props("font-size" to "16px", "font-weight" to "600") } }) {
                    Text(summary.kpi.name)
                }
                MutedText(
                    "${summary.projectName} - ${summary.linkedObjectiveCount} linked objectives"
                )
                MutedText("Current ${summary.latestValueLabel} / Target ${summary.targetLabel}")
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "gap" to "4px",
                            "justify-items" to "end",
                            "align-content" to "start",
                        )
                    }
                }
            ) {
                Span(
                    attrs = {
                        style {
                            props(
                                "font-size" to "20px",
                                "font-weight" to "800",
                                "line-height" to "1.05",
                            )
                        }
                    }
                ) {
                    Text(summary.progressLabel)
                }
                StatusPill(summary.statusLabel, summary.statusColor, summary.statusBackground)
            }
        }
        if (!compact) {
            KpiSparkline(
                points = summary.recentPoints,
                target = summary.kpi.targetValue,
                accentColor = summary.statusColor,
            )
        }
        if (compact) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "space-between",
                            "align-items" to "center",
                            "gap" to "10px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                MutedText(if (derived) "Derived KPI" else "Measured KPI")
                Button(
                    attrs = {
                        attr("data-kpi-view-button", summary.kpi.id.value)
                        onClick {
                            it.stopPropagation()
                            onOpenDetails()
                        }
                        style { secondaryButton() }
                    }
                ) {
                    Text("Open KPI")
                }
            }
        } else if (derived) {
            MutedText("Derived KPI. Open details to edit the formula and inspect dependencies.")
        } else {
            Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
                if (measurementExpanded) {
                    FieldLabel("New measurement")
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "gap" to "10px",
                                    "align-items" to "center",
                                    "flex-wrap" to "wrap",
                                )
                            }
                        }
                    ) {
                        Input(
                            type = InputType.Text,
                            attrs = {
                                attr("data-kpi-measurement-input", summary.kpi.id.value)
                                value(measurementDraft)
                                attr("placeholder", "Check-in value")
                                onClick { it.stopPropagation() }
                                onInput { event -> measurementDraft = event.value }
                                style {
                                    props(
                                        "height" to "36px",
                                        "width" to "160px",
                                        "border" to "1px solid $Border",
                                        "border-radius" to "8px",
                                        "padding" to "0 12px",
                                        "font-size" to "14px",
                                        "box-sizing" to "border-box",
                                    )
                                }
                            },
                        )
                        Button(
                            attrs = {
                                attr("data-kpi-measurement-button", summary.kpi.id.value)
                                onClick {
                                    it.stopPropagation()
                                    onCreateKpiMeasurement(
                                        summary.kpi.id,
                                        requireNotNull(measurementValue),
                                    )
                                    measurementDraft = ""
                                    measurementExpanded = false
                                }
                                if (measurementValue == null) disabled()
                                style { primaryButton(enabled = measurementValue != null) }
                            }
                        ) {
                            Text("Save measurement")
                        }
                        Button(
                            attrs = {
                                attr("data-kpi-measurement-cancel", summary.kpi.id.value)
                                onClick {
                                    it.stopPropagation()
                                    measurementDraft = ""
                                    measurementExpanded = false
                                }
                                style { secondaryButton() }
                            }
                        ) {
                            Text("Cancel")
                        }
                    }
                    if (measurementValue == null) {
                        MutedText("Type a value above to enable save.")
                    }
                } else {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "gap" to "10px",
                                    "align-items" to "center",
                                    "justify-content" to "space-between",
                                    "flex-wrap" to "wrap",
                                )
                            }
                        }
                    ) {
                        MutedText(
                            summary.recentPoints.firstOrNull()?.let { point ->
                                "${point.value.formatObjectiveMetric()} on ${formatMeasurementRecordedAt(point.at)}"
                            } ?: "No check-ins yet"
                        )
                        Button(
                            attrs = {
                                attr("data-kpi-check-in", summary.kpi.id.value)
                                onClick {
                                    it.stopPropagation()
                                    measurementExpanded = true
                                }
                                style { secondaryButton() }
                            }
                        ) {
                            Text("Check in")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun StatGrid(minColumnWidthPx: Int = 260, content: @Composable () -> Unit) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "grid",
                    "grid-template-columns" to
                        "repeat(auto-fit, minmax(${minColumnWidthPx}px, 1fr))",
                    "gap" to "16px",
                )
            }
        }
    ) {
        content()
    }
}

@Composable
private fun TwoPanelGrid(content: @Composable () -> Unit) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "grid",
                    "grid-template-columns" to "repeat(auto-fit, minmax(280px, 1fr))",
                    "gap" to "16px",
                    "align-items" to "start",
                )
            }
        }
    ) {
        content()
    }
}

@Composable
private fun ThreePanelGrid(content: @Composable () -> Unit) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "grid",
                    "grid-template-columns" to "repeat(auto-fit, minmax(280px, 1fr))",
                    "gap" to "16px",
                )
            }
        }
    ) {
        content()
    }
}

@Composable
private fun TaskPanel(
    title: String,
    tasks: List<WebTask>,
    emptyLabel: String,
    emptyDetail: String? = null,
    onTaskSelected: (WebTask) -> Unit,
    onRunQuickAction: (WebTask) -> Unit,
    minHeight: String = "0",
) {
    PanelCard(title, minHeight = minHeight) {
        if (tasks.isEmpty()) {
            PanelEmptyState(emptyLabel, emptyDetail)
        } else {
            tasks.forEach { task ->
                CompactTaskRow(
                    task = task,
                    showProject = true,
                    onTaskSelected = onTaskSelected,
                    onRunQuickAction = onRunQuickAction,
                )
            }
        }
    }
}

@Composable
private fun ProjectHeader(
    workspace: WebWorkspace,
    selectedProjectId: Kairos.ProjectId?,
    compact: Boolean,
    onCreateProject: (String, Kairos.ProjectId?) -> Unit,
    onUpdateProjectMetadata: (Kairos.ProjectId, String?, Kairos.ProjectId?, Boolean) -> Unit,
) {
    val project =
        selectedProjectId?.let { id -> workspace.projects.firstOrNull { it.id == id } }
            ?: workspace.primaryProject()
    val projectIds = project?.let { workspace.projectAndChildIds(it.id) }.orEmpty()
    val scopedTickets =
        workspace.tickets.filter { ticket ->
            projectIds.isEmpty() || ticket.projectId in projectIds
        }
    val openTickets = scopedTickets.count { ticket -> ticket.completedAt == null }
    val inProgressTickets =
        scopedTickets.count { ticket ->
            ticket.completedAt == null && ticket.stage == Kairos.TicketStage.InProgress
        }
    val blockedTickets =
        scopedTickets.count { ticket ->
            ticket.completedAt == null && ticket.stage == Kairos.TicketStage.Blocked
        }
    val overdueTickets =
        scopedTickets.count { ticket ->
            ticket.completedAt == null &&
                ticket.deadline?.let { deadline -> deadline.toInstant() < Clock.System.now() } ==
                    true
        }
    val nowMs = Clock.System.now().toEpochMilliseconds()
    val soonCutoffMs = nowMs + (7L * 24L * 60L * 60L * 1000L)
    val dueSoonTickets =
        scopedTickets.count { ticket ->
            ticket.completedAt == null &&
                ticket.deadline?.toInstant()?.toEpochMilliseconds()?.let { deadlineMs ->
                    deadlineMs in nowMs..soonCutoffMs
                } == true
        }
    val projectName = project?.name ?: "Kairos"
    val projectContext = project?.projectContextLabel(workspace.projects)
    val projectBadgeColor = AccentColors[stableIndex(projectName, AccentColors.size)]
    val projectBadgeLabel =
        projectName
            .split(Regex("\\s+"))
            .filter { token -> token.isNotBlank() }
            .take(2)
            .joinToString("") { it.first().uppercase() }
            .ifBlank { projectName.take(1).uppercase() }
    val statusLabel =
        when {
            overdueTickets > 0 -> "Needs attention"
            openTickets == 0 -> "Clear"
            else -> "In flight"
        }
    val statusColor =
        when {
            overdueTickets > 0 -> Coral
            openTickets == 0 -> Muted
            else -> Green
        }
    val statusBackground =
        when {
            overdueTickets > 0 -> "#ffeee9"
            openTickets == 0 -> "#f1f2f4"
            else -> "#e8f6ec"
        }
    val descendantIds = project?.let { workspace.projectAndChildIds(it.id) }.orEmpty()
    val parentCandidates = workspace.projects.filter { candidate -> candidate.id !in descendantIds }
    var editingProject by remember(project?.id) { mutableStateOf(false) }
    var creatingChild by remember(project?.id) { mutableStateOf(false) }
    var actionsExpanded by remember(project?.id) { mutableStateOf(false) }
    var projectNameDraft by
        remember(project?.id, project?.name) { mutableStateOf(project?.name.orEmpty()) }
    var projectParentDraft by
        remember(project?.id, project?.parentProjectId) {
            mutableStateOf(project?.parentProjectId?.value.orEmpty())
        }
    var childProjectNameDraft by remember(project?.id) { mutableStateOf("") }
    val compactHeader = compact || window.innerWidth < 760
    val headerStats =
        mutableListOf<Triple<String, String, String?>>().apply {
            add(Triple("Open", openTickets.toString(), null))
            if (inProgressTickets > 0) {
                add(Triple("In flight", inProgressTickets.toString(), null))
            }
            if (dueSoonTickets > 0) {
                add(Triple("Due soon", dueSoonTickets.toString(), null))
            }
            val attentionCount = blockedTickets + overdueTickets
            if (attentionCount > 0) {
                add(Triple("At risk", attentionCount.toString(), Coral))
            }
        }

    Div(
        attrs = {
            style {
                if (compactHeader) {
                    props(
                        "padding" to "18px 16px 12px",
                        "border-bottom" to "1px solid $Border",
                        "display" to "grid",
                        "grid-template-columns" to "44px minmax(0, 1fr)",
                        "column-gap" to "16px",
                        "row-gap" to "16px",
                        "align-items" to "start",
                    )
                } else {
                    props(
                        "padding" to "22px 28px 14px",
                        "border-bottom" to "1px solid $Border",
                        "display" to "flex",
                        "align-items" to "start",
                        "gap" to "16px",
                        "flex-wrap" to "wrap",
                    )
                }
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "width" to "44px",
                        "height" to "44px",
                        "border-radius" to "8px",
                        "background" to projectBadgeColor,
                        "display" to "grid",
                        "place-items" to "center",
                        "font-size" to "16px",
                        "font-weight" to "700",
                    )
                }
            }
        ) {
            Text(projectBadgeLabel)
        }
        Div(
            attrs = {
                style {
                    props(
                        "flex" to "1",
                        "min-width" to if (compactHeader) "0" else "280px",
                        "display" to "grid",
                        "gap" to "10px",
                    )
                }
            }
        ) {
            projectContext?.let { context ->
                Div(
                    attrs = {
                        style {
                            props("font-size" to "13px", "color" to Muted, "margin-bottom" to "4px")
                        }
                    }
                ) {
                    Text(context)
                }
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "align-items" to "center",
                            "gap" to if (compactHeader) "10px" else "12px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                Span(
                    attrs = {
                        style {
                            props(
                                "font-size" to
                                    if (compactHeader) "clamp(18px, 7vw, 28px)" else "26px",
                                "font-weight" to "700",
                                "line-height" to if (compactHeader) "1.08" else "1.15",
                                "max-width" to "100%",
                            )
                        }
                    }
                ) {
                    Text(projectName)
                }
                StatusPill(statusLabel, statusColor, statusBackground)
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "gap" to if (compactHeader) "8px" else "10px",
                            "row-gap" to "8px",
                            "flex-wrap" to if (compactHeader) "nowrap" else "wrap",
                            "align-items" to "center",
                            "overflow-x" to if (compactHeader) "auto" else "visible",
                            "padding-bottom" to if (compactHeader) "2px" else "0",
                        )
                    }
                }
            ) {
                headerStats.forEach { (label, value, accent) ->
                    ProjectHeaderSummaryItem(label, value, accent ?: Ink)
                }
            }
        }
        Div(
            attrs = {
                style {
                    props(
                        "display" to "grid",
                        "gap" to "10px",
                        "min-width" to if (compactHeader) "0" else "260px",
                        "max-width" to if (compactHeader) "100%" else "420px",
                        "width" to if (compactHeader) "100%" else "auto",
                        "grid-column" to if (compactHeader) "1 / -1" else "auto",
                    )
                }
            }
        ) {
            if (compactHeader) {
                Div(attrs = { style { props("display" to "grid", "gap" to "10px") } }) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "align-items" to "center",
                                    "justify-content" to "space-between",
                                    "gap" to "10px",
                                    "flex-wrap" to "wrap",
                                )
                            }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "flex",
                                        "align-items" to "center",
                                        "gap" to "8px",
                                        "flex-wrap" to "wrap",
                                    )
                                }
                            }
                        ) {
                            AvatarStack(workspace.users.take(4), compact = true)
                            ScopeBadge("${workspace.users.size} people")
                        }
                        Div(attrs = { style { props("position" to "relative") } }) {
                            Button(
                                attrs = {
                                    attr("id", "web-project-actions-button")
                                    onClick { actionsExpanded = !actionsExpanded }
                                    if (project == null) disabled()
                                    style { secondaryButton(selected = actionsExpanded) }
                                }
                            ) {
                                Text("Manage")
                            }
                            if (actionsExpanded) {
                                ProjectHeaderActionsMenu(
                                    onEditProject = {
                                        actionsExpanded = false
                                        editingProject = true
                                        creatingChild = false
                                    },
                                    onCreateChild = {
                                        actionsExpanded = false
                                        creatingChild = true
                                        editingProject = false
                                    },
                                )
                            }
                        }
                    }
                }
            } else {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "gap" to "8px",
                                "justify-content" to "flex-end",
                                "flex-wrap" to "wrap",
                            )
                        }
                    }
                ) {
                    AvatarStack(workspace.users.take(5), compact = false)
                    ScopeBadge("${workspace.users.size} people")
                    Div(attrs = { style { props("position" to "relative") } }) {
                        Button(
                            attrs = {
                                attr("id", "web-project-actions-button")
                                onClick { actionsExpanded = !actionsExpanded }
                                if (project == null) disabled()
                                style { secondaryButton(selected = actionsExpanded) }
                            }
                        ) {
                            Text("Manage")
                        }
                        if (actionsExpanded) {
                            ProjectHeaderActionsMenu(
                                onEditProject = {
                                    actionsExpanded = false
                                    editingProject = true
                                    creatingChild = false
                                },
                                onCreateChild = {
                                    actionsExpanded = false
                                    creatingChild = true
                                    editingProject = false
                                },
                            )
                        }
                    }
                }
            }
        }
    }
    if (editingProject && project != null) {
        ModalBackdrop(zIndex = 46) { editingProject = false }
        ModalCard(zIndex = 47, title = "Edit project", onDismiss = { editingProject = false }) {
            Input(
                type = InputType.Text,
                attrs = {
                    attr("id", "web-project-name-input")
                    value(projectNameDraft)
                    onInput { projectNameDraft = it.value }
                    attr("placeholder", "Project name")
                    style { formInputStyle() }
                },
            )
            Select(
                attrs = {
                    attr("id", "web-project-parent-select")
                    onInput { projectParentDraft = it.value.orEmpty() }
                    style { formInputStyle() }
                }
            ) {
                Option(
                    value = "",
                    attrs = { if (projectParentDraft.isBlank()) attr("selected", "selected") },
                ) {
                    Text("Top-level project")
                }
                parentCandidates.forEach { candidate ->
                    Option(
                        value = candidate.id.value,
                        attrs = {
                            if (candidate.id.value == projectParentDraft) {
                                attr("selected", "selected")
                            }
                        },
                    ) {
                        Text(candidate.projectDisplayName(workspace.projects))
                    }
                }
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "flex-end",
                            "gap" to "10px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                Button(
                    attrs = {
                        onClick { editingProject = false }
                        style { secondaryButton() }
                    }
                ) {
                    Text("Cancel")
                }
                Button(
                    attrs = {
                        attr("id", "web-project-save-button")
                        onClick {
                            val selectedParent =
                                projectParentDraft
                                    .takeIf { it.isNotBlank() }
                                    ?.let(Kairos::ProjectId)
                            onUpdateProjectMetadata(
                                project.id,
                                projectNameDraft,
                                selectedParent,
                                projectParentDraft.isBlank() && project.parentProjectId != null,
                            )
                            editingProject = false
                        }
                        if (projectNameDraft.trim().isBlank()) disabled()
                        style { primaryButton() }
                    }
                ) {
                    Text("Save project")
                }
            }
        }
    }
    if (creatingChild && project != null) {
        ModalBackdrop(zIndex = 46) { creatingChild = false }
        ModalCard(zIndex = 47, title = "New child project", onDismiss = { creatingChild = false }) {
            Input(
                type = InputType.Text,
                attrs = {
                    attr("id", "web-project-child-name-input")
                    value(childProjectNameDraft)
                    onInput { childProjectNameDraft = it.value }
                    attr("placeholder", "Child project name")
                    style { formInputStyle() }
                },
            )
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "flex-end",
                            "gap" to "10px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                Button(
                    attrs = {
                        onClick { creatingChild = false }
                        style { secondaryButton() }
                    }
                ) {
                    Text("Cancel")
                }
                Button(
                    attrs = {
                        attr("id", "web-project-create-child-button")
                        onClick {
                            onCreateProject(childProjectNameDraft, project.id)
                            childProjectNameDraft = ""
                            creatingChild = false
                        }
                        if (childProjectNameDraft.trim().isBlank()) disabled()
                        style { primaryButton() }
                    }
                ) {
                    Text("Create child")
                }
            }
        }
    }
}

@Composable
private fun ProjectHeaderActionsMenu(onEditProject: () -> Unit, onCreateChild: () -> Unit) {
    Div(
        attrs = {
            style {
                props(
                    "position" to "absolute",
                    "top" to "calc(100% + 6px)",
                    "right" to "0",
                    "z-index" to "24",
                    "display" to "grid",
                    "gap" to "4px",
                    "padding" to "6px",
                    "min-width" to "184px",
                    "border" to "1px solid $Border",
                    "border-radius" to "8px",
                    "background" to Paper,
                    "box-shadow" to "0 10px 24px rgba(37,39,45,0.12)",
                )
            }
        }
    ) {
        Button(
            attrs = {
                attr("id", "web-project-edit-toggle-button")
                attr("data-project-action", "edit")
                onClick { onEditProject() }
                style { dropdownOptionButton() }
            }
        ) {
            Text("Edit project")
        }
        Button(
            attrs = {
                attr("id", "web-project-create-child-toggle-button")
                attr("data-project-action", "new-child")
                onClick { onCreateChild() }
                style { dropdownOptionButton() }
            }
        ) {
            Text("New child project")
        }
    }
}

@Composable
private fun Tabs(selected: String, onViewChanged: (String) -> Unit) {
    LaunchedEffect(selected) {
        val selectedTab =
            document.querySelector("[data-project-tab=\"${selected.lowercase()}\"]") as? HTMLElement
        selectedTab?.asDynamic()?.scrollIntoView(js("{ inline: 'center', block: 'nearest' }"))
    }
    Div(
        attrs = {
            style {
                props(
                    "display" to "flex",
                    "align-items" to "center",
                    "gap" to "18px",
                    "padding" to if (window.innerWidth < 760) "10px 16px 0" else "10px 28px 0",
                    "border-bottom" to "1px solid $Border",
                    "overflow-x" to "auto",
                    "flex-shrink" to "0",
                    "scroll-behavior" to "smooth",
                    "flex-wrap" to "nowrap",
                )
            }
        }
    ) {
        projectViews.forEach { tab ->
            Button(
                attrs = {
                    attr("data-project-tab", tab.lowercase())
                    onClick { onViewChanged(tab) }
                    style { tabButton(selected = selected == tab) }
                }
            ) {
                Text(tab)
            }
        }
    }
}

@Composable
private fun Toolbar(
    workspace: WebWorkspace,
    selectedProjectId: Kairos.ProjectId?,
    selectedTask: WebTask?,
    compact: Boolean,
    view: String,
    notice: String?,
    includeCompleted: Boolean,
    hideEmptyColumns: Boolean,
    showBoardInbox: Boolean,
    sort: WebSort,
    detailsOpen: Boolean,
    onAddTask: (Kairos.ProjectId) -> Unit,
    onToggleCompleted: () -> Unit,
    onToggleEmpty: () -> Unit,
    onToggleBoardInbox: () -> Unit,
    onToggleSort: () -> Unit,
    onToggleDetails: () -> Unit,
    onReset: () -> Unit,
) {
    val allowEmptyToggle = view == "Board" || view == "List"
    val allowSortToggle = view == "Board" || view == "List"
    val showBoardInboxToggle = view == "Board"
    val showDetailsToggle = selectedTask != null || detailsOpen
    val canAddTicket = workspace.defaultTaskProjectId(selectedProjectId) != null
    val showAddTicket = canAddTicket && view != "Calendar"
    val activeFilterCount =
        buildList {
                if (allowSortToggle && sort != WebSort.Created) add("sort")
                if (allowEmptyToggle && hideEmptyColumns) add("hide-empty")
                if (showBoardInboxToggle && showBoardInbox) add("board-inbox")
                if (showDetailsToggle && detailsOpen) add("details")
            }
            .size
    val hasActiveFilters = activeFilterCount > 0
    val showPrimaryFilterGroup = true
    val showSecondaryFilterGroup =
        showBoardInboxToggle || showDetailsToggle || (hasActiveFilters && !showPrimaryFilterGroup)
    var compactToolbarExpanded by remember(compact, selectedProjectId) { mutableStateOf(false) }
    val compactToolbarSummary =
        buildList {
                add(if (includeCompleted) "All tickets" else "Open only")
                if (allowSortToggle && sort != WebSort.Created) add("Sort ${sort.name.lowercase()}")
                if (allowEmptyToggle && hideEmptyColumns) add("Hide empty")
                if (showBoardInboxToggle && showBoardInbox) add("Inbox shown")
            }
            .joinToString(" · ")
            .ifBlank { "Filter and sort" }
    Div(
        attrs = {
            style {
                props(
                    "min-height" to "58px",
                    "display" to "flex",
                    "align-items" to "center",
                    "gap" to "12px",
                    "flex-wrap" to "wrap",
                    "padding" to if (compact) "10px 16px" else "10px 28px",
                    "border-bottom" to "1px solid $Border",
                    "background" to "#fbfbfc",
                    "box-sizing" to "border-box",
                    "flex-shrink" to "0",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "gap" to "10px",
                        "min-width" to "0",
                        "flex" to "1 1 260px",
                        "flex-wrap" to "wrap",
                    )
                }
            }
        ) {
            if (showAddTicket) {
                Button(
                    attrs = {
                        attr("id", "web-toolbar-add-ticket-button")
                        onClick {
                            workspace.defaultTaskProjectId(selectedProjectId)?.let(onAddTask)
                        }
                        if (!canAddTicket) disabled()
                        style { primaryButton() }
                    }
                ) {
                    Text("+ Add ticket")
                }
            }
            notice?.let { NoticeBadge(it) }
        }
        if (compact) {
            Div(
                attrs = { style { props("display" to "grid", "gap" to "10px", "width" to "100%") } }
            ) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "gap" to "10px",
                                "width" to "100%",
                                "align-items" to "center",
                                "flex-wrap" to "wrap",
                            )
                        }
                    }
                ) {
                    Button(
                        attrs = {
                            attr("id", "web-toolbar-compact-button")
                            onClick { compactToolbarExpanded = !compactToolbarExpanded }
                            style {
                                toolbarButton(selected = compactToolbarExpanded || hasActiveFilters)
                            }
                        }
                    ) {
                        Text(
                            if (hasActiveFilters) {
                                "Filters · $activeFilterCount"
                            } else {
                                "Filters & sort"
                            }
                        )
                    }
                    if (showDetailsToggle) {
                        Button(
                            attrs = {
                                attr("id", "web-toolbar-toggle-details-button")
                                onClick { onToggleDetails() }
                                style { toolbarButton(selected = detailsOpen) }
                            }
                        ) {
                            Text(if (detailsOpen) "Details open" else "Details closed")
                        }
                    }
                    Span(
                        attrs = {
                            style {
                                props(
                                    "font-size" to "12px",
                                    "color" to Muted,
                                    "line-height" to "1.35",
                                )
                            }
                        }
                    ) {
                        Text(compactToolbarSummary)
                    }
                }
                if (compactToolbarExpanded) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "grid",
                                    "gap" to "10px",
                                    "padding" to "12px",
                                    "border" to "1px solid $Border",
                                    "border-radius" to "12px",
                                    "background" to Paper,
                                )
                            }
                        }
                    ) {
                        if (showPrimaryFilterGroup) {
                            ToolbarGroup(label = "Filters") {
                                Button(
                                    attrs = {
                                        attr("id", "web-toolbar-toggle-completed-button")
                                        onClick { onToggleCompleted() }
                                        style { toolbarButton(selected = !includeCompleted) }
                                    }
                                ) {
                                    Text(if (includeCompleted) "All tickets" else "Open only")
                                }
                                Button(
                                    attrs = {
                                        attr("id", "web-toolbar-toggle-sort-button")
                                        if (!allowSortToggle) disabled()
                                        onClick { if (allowSortToggle) onToggleSort() }
                                        style {
                                            toolbarButton(
                                                selected =
                                                    allowSortToggle && sort != WebSort.Created
                                            )
                                        }
                                    }
                                ) {
                                    Text("Sort: ${sort.name.lowercase()}")
                                }
                            }
                        }
                        ToolbarGroup(label = "View") {
                            if (allowEmptyToggle) {
                                Button(
                                    attrs = {
                                        attr("id", "web-toolbar-toggle-empty-button")
                                        onClick { onToggleEmpty() }
                                        style { toolbarButton(selected = hideEmptyColumns) }
                                    }
                                ) {
                                    Text(if (hideEmptyColumns) "Show empty" else "Hide empty")
                                }
                            }
                            if (showBoardInboxToggle) {
                                Button(
                                    attrs = {
                                        attr("id", "web-toolbar-toggle-board-inbox-button")
                                        onClick { onToggleBoardInbox() }
                                        style { toolbarButton(selected = showBoardInbox) }
                                    }
                                ) {
                                    Text(if (showBoardInbox) "Hide inbox" else "Show inbox")
                                }
                            }
                            if (hasActiveFilters) {
                                Button(
                                    attrs = {
                                        attr("id", "web-toolbar-reset-button")
                                        onClick { onReset() }
                                        style { tertiaryButton() }
                                    }
                                ) {
                                    Text("Clear filters")
                                }
                            }
                        }
                    }
                }
            }
        } else {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "gap" to "10px",
                            "width" to "100%",
                            "overflow-x" to "visible",
                            "padding-bottom" to "0",
                            "flex-wrap" to "wrap",
                            "align-items" to "center",
                        )
                    }
                }
            ) {
                if (showPrimaryFilterGroup) {
                    ToolbarGroup(label = "Filters") {
                        Button(
                            attrs = {
                                attr("id", "web-toolbar-toggle-completed-button")
                                onClick { onToggleCompleted() }
                                style { toolbarButton(selected = !includeCompleted) }
                            }
                        ) {
                            Text(if (includeCompleted) "All tickets" else "Open only")
                        }
                        Button(
                            attrs = {
                                attr("id", "web-toolbar-toggle-sort-button")
                                if (!allowSortToggle) disabled()
                                onClick { if (allowSortToggle) onToggleSort() }
                                style {
                                    toolbarButton(
                                        selected = allowSortToggle && sort != WebSort.Created
                                    )
                                }
                            }
                        ) {
                            Text("Sort: ${sort.name.lowercase()}")
                        }
                        Button(
                            attrs = {
                                attr("id", "web-toolbar-toggle-empty-button")
                                if (!allowEmptyToggle) disabled()
                                onClick { if (allowEmptyToggle) onToggleEmpty() }
                                style {
                                    toolbarButton(selected = allowEmptyToggle && hideEmptyColumns)
                                }
                            }
                        ) {
                            Text(if (hideEmptyColumns) "Show empty" else "Hide empty")
                        }
                        if (hasActiveFilters) {
                            NoticeBadge("$activeFilterCount active")
                        }
                    }
                }
                if (showSecondaryFilterGroup) {
                    ToolbarGroup(label = "View") {
                        if (showBoardInboxToggle) {
                            Button(
                                attrs = {
                                    attr("id", "web-toolbar-toggle-board-inbox-button")
                                    onClick { onToggleBoardInbox() }
                                    style { toolbarButton(selected = showBoardInbox) }
                                }
                            ) {
                                Text(if (showBoardInbox) "Hide inbox" else "Show inbox")
                            }
                        }
                        if (showDetailsToggle) {
                            Button(
                                attrs = {
                                    attr("id", "web-toolbar-toggle-details-button")
                                    onClick { onToggleDetails() }
                                    style { toolbarButton(selected = detailsOpen) }
                                }
                            ) {
                                Text(if (detailsOpen) "Details open" else "Details closed")
                            }
                        }
                        if (hasActiveFilters) {
                            Button(
                                attrs = {
                                    attr("id", "web-toolbar-reset-button")
                                    onClick { onReset() }
                                    style { tertiaryButton() }
                                }
                            ) {
                                Text("Clear filters")
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ProjectHeaderMetric(label: String, value: String) {
    Div(attrs = { style { props("display" to "grid", "gap" to "2px", "min-width" to "90px") } }) {
        Span(
            attrs = {
                style { props("font-size" to "12px", "font-weight" to "700", "color" to Muted) }
            }
        ) {
            Text(label)
        }
        Span(
            attrs = {
                style { props("font-size" to "20px", "font-weight" to "700", "color" to Ink) }
            }
        ) {
            Text(value)
        }
    }
}

@Composable
private fun ProjectHeaderSummaryItem(label: String, value: String, color: String = Ink) {
    Span(
        attrs = {
            style {
                props(
                    "display" to "inline-flex",
                    "align-items" to "center",
                    "gap" to "6px",
                    "padding" to "0 10px",
                    "height" to "28px",
                    "border-radius" to "999px",
                    "background" to "#f5f7fa",
                    "font-size" to "12px",
                    "font-weight" to "700",
                    "color" to Muted,
                    "white-space" to "nowrap",
                )
            }
        }
    ) {
        Span(attrs = { style { props("color" to color) } }) { Text(value) }
        Text(label)
    }
}

@Composable
private fun ScheduleToolsBar(
    workspace: WebWorkspace,
    selectedProjectId: Kairos.ProjectId?,
    scheduleToolsOpen: Boolean,
    onToggleScheduleTools: () -> Unit,
) {
    val baseProjectId = selectedProjectId ?: workspace.primaryProject()?.id ?: return
    val scopeProjectIds =
        remember(workspace, baseProjectId) { workspace.projectAndChildIds(baseProjectId) }
    val eventCount =
        remember(workspace, scopeProjectIds) {
            workspace.events.count { event ->
                event.projectId != null && event.projectId in scopeProjectIds
            }
        }
    val timeboxCount =
        remember(workspace, scopeProjectIds) {
            workspace.timeboxes.count { timebox ->
                timebox.projectId != null && timebox.projectId in scopeProjectIds
            }
        }
    val routineCount =
        remember(workspace, scopeProjectIds) {
            workspace.routines.count { routine ->
                routine.projectId != null && routine.projectId in scopeProjectIds
            }
        }

    Div(
        attrs = {
            style {
                props(
                    "display" to "flex",
                    "align-items" to "center",
                    "justify-content" to "space-between",
                    "gap" to "12px",
                    "flex-wrap" to "wrap",
                    "padding" to "12px 28px",
                    "border-bottom" to "1px solid $Border",
                    "background" to Paper,
                    "box-sizing" to "border-box",
                    "flex-shrink" to "0",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "gap" to "10px",
                        "flex-wrap" to "wrap",
                    )
                }
            }
        ) {
            Span(
                attrs = {
                    style { props("font-size" to "14px", "font-weight" to "700", "color" to Ink) }
                }
            ) {
                Text("Schedule")
            }
            Span(
                attrs = {
                    style { props("font-size" to "12px", "color" to Muted, "line-height" to "1.4") }
                }
            ) {
                Text("$eventCount events • $timeboxCount timeboxes • $routineCount routines")
            }
        }
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "gap" to "10px",
                        "flex-wrap" to "wrap",
                        "margin-left" to "auto",
                    )
                }
            }
        ) {
            Button(
                attrs = {
                    attr("id", "web-schedule-manage-button")
                    onClick { onToggleScheduleTools() }
                    style { secondaryButton() }
                }
            ) {
                Text(if (scheduleToolsOpen) "Close manager" else "Manage")
            }
        }
    }
}

@Composable
private fun ScheduleManagerOverlay(
    workspace: WebWorkspace,
    compact: Boolean,
    selectedProjectId: Kairos.ProjectId?,
    selectedTask: WebTask?,
    onCreateEvent:
        (Kairos.ProjectId?, String, String, ZonedDateTime, ZonedDateTime?, String, String) -> Unit,
    onUpdateEvent: (Kairos.EventId, String, String, ZonedDateTime, ZonedDateTime?) -> Unit,
    onCreateTimebox:
        (Kairos.ProjectId?, List<Kairos.TicketId>, String, ZonedDateTime, Int?) -> Unit,
    onUpdateTimebox: (Kairos.TimeboxId, String, ZonedDateTime, Int?) -> Unit,
    onCreateRoutine: (Kairos.ProjectId?, String) -> Unit,
    onUpdateRoutine: (Kairos.RoutineId, String) -> Unit,
    onClose: () -> Unit,
) {
    val scrimZIndex = if (compact) 36 else 24
    val panelZIndex = if (compact) 40 else 28
    OverlayScrim(zIndex = scrimZIndex, onDismiss = onClose)
    Div(
        attrs = {
            style {
                props(
                    "position" to "absolute",
                    "inset" to if (compact) "0" else "20px 24px 20px 96px",
                    "display" to "flex",
                    "justify-content" to if (compact) "center" else "flex-end",
                    "align-items" to "stretch",
                    "z-index" to panelZIndex.toString(),
                    "pointer-events" to "none",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "pointer-events" to "auto",
                        "width" to if (compact) "100%" else "min(860px, calc(100vw - 132px))",
                        "height" to "100%",
                        "max-width" to "100%",
                        "background" to Paper,
                        "border" to if (compact) "0" else "1px solid $Border",
                        "border-radius" to if (compact) "0" else "16px 0 0 16px",
                        "box-shadow" to if (compact) "none" else "-10px 0 40px rgba(20,22,28,0.2)",
                        "display" to "flex",
                        "flex-direction" to "column",
                        "overflow" to "hidden",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "space-between",
                            "align-items" to "center",
                            "gap" to "16px",
                            "padding" to if (compact) "18px 20px" else "18px 24px",
                            "border-bottom" to "1px solid $Border",
                            "background" to Paper,
                            "flex-shrink" to "0",
                        )
                    }
                }
            ) {
                Div(attrs = { style { props("display" to "grid", "gap" to "4px") } }) {
                    Span(
                        attrs = { style { props("font-size" to "20px", "font-weight" to "700") } }
                    ) {
                        Text("Project schedule")
                    }
                    Span(attrs = { style { props("font-size" to "13px", "color" to Muted) } }) {
                        Text("Keep commitments, focus blocks, and recurring expectations together.")
                    }
                }
                Button(
                    attrs = {
                        attr("id", "web-schedule-manager-close-button")
                        onClick { onClose() }
                        style { secondaryButton() }
                    }
                ) {
                    Text("Close")
                }
            }
            Div(
                attrs = {
                    style {
                        props(
                            "flex" to "1",
                            "min-height" to "0",
                            "overflow" to "auto",
                            "background" to Canvas,
                        )
                    }
                }
            ) {
                ScheduleEditor(
                    workspace = workspace,
                    selectedProjectId = selectedProjectId,
                    selectedTask = selectedTask,
                    onCreateEvent = onCreateEvent,
                    onUpdateEvent = onUpdateEvent,
                    onCreateTimebox = onCreateTimebox,
                    onUpdateTimebox = onUpdateTimebox,
                    onCreateRoutine = onCreateRoutine,
                    onUpdateRoutine = onUpdateRoutine,
                )
            }
        }
    }
}

@Composable
private fun ScheduleEditor(
    workspace: WebWorkspace,
    selectedProjectId: Kairos.ProjectId?,
    selectedTask: WebTask?,
    onCreateEvent:
        (Kairos.ProjectId?, String, String, ZonedDateTime, ZonedDateTime?, String, String) -> Unit,
    onUpdateEvent: (Kairos.EventId, String, String, ZonedDateTime, ZonedDateTime?) -> Unit,
    onCreateTimebox:
        (Kairos.ProjectId?, List<Kairos.TicketId>, String, ZonedDateTime, Int?) -> Unit,
    onUpdateTimebox: (Kairos.TimeboxId, String, ZonedDateTime, Int?) -> Unit,
    onCreateRoutine: (Kairos.ProjectId?, String) -> Unit,
    onUpdateRoutine: (Kairos.RoutineId, String) -> Unit,
) {
    val baseProjectId = selectedProjectId ?: workspace.primaryProject()?.id ?: return
    val scopeProjectIds = workspace.projectAndChildIds(baseProjectId)
    val scopeProjects =
        workspace.projects
            .filter { project -> project.id in scopeProjectIds }
            .sortedBy { project -> project.projectDisplayName(workspace.projects) }
    if (scopeProjects.isEmpty()) return

    val defaultProjectId = scopeProjects.first().id
    var eventProjectId by remember(baseProjectId) { mutableStateOf(defaultProjectId) }
    var eventTitle by remember(baseProjectId) { mutableStateOf("") }
    var eventStart by
        remember(baseProjectId) {
            mutableStateOf(
                ZonedDateTime(LocalDateTime(2026, 4, 16, 10, 0), currentUiTimeZone())
                    .dateTimeInputValue()
            )
        }
    var eventEnd by remember(baseProjectId) { mutableStateOf("") }
    var eventDescription by remember(baseProjectId) { mutableStateOf("") }
    var eventLocation by remember(baseProjectId) { mutableStateOf("") }

    var timeboxProjectId by remember(baseProjectId) { mutableStateOf(defaultProjectId) }
    var timeboxDescription by remember(baseProjectId) { mutableStateOf("") }
    var timeboxStart by
        remember(baseProjectId) {
            mutableStateOf(
                ZonedDateTime(LocalDateTime(2026, 4, 16, 9, 0), currentUiTimeZone())
                    .dateTimeInputValue()
            )
        }
    var timeboxHours by remember(baseProjectId) { mutableStateOf("2") }

    var routineProjectId by remember(baseProjectId) { mutableStateOf(defaultProjectId) }
    var routineDescription by remember(baseProjectId) { mutableStateOf("") }

    val scopedEvents =
        workspace.events
            .filter { event -> event.projectId != null && event.projectId in scopeProjectIds }
            .sortedBy { it.dtStart.toInstant() }
    val scopedTimeboxes =
        workspace.timeboxes
            .filter { timebox -> timebox.projectId != null && timebox.projectId in scopeProjectIds }
            .sortedBy { it.startTime }
    val scopedRoutines =
        workspace.routines
            .filter { routine -> routine.projectId != null && routine.projectId in scopeProjectIds }
            .sortedBy { it.createdAt }
    var eventComposerOpen by remember(baseProjectId) { mutableStateOf(false) }
    var timeboxComposerOpen by remember(baseProjectId) { mutableStateOf(false) }
    var routineComposerOpen by remember(baseProjectId) { mutableStateOf(false) }
    var selectedTab by remember(baseProjectId) { mutableStateOf(WebScheduleManagerTab.Events) }
    val selectedTimeboxLinkTitle =
        selectedTask?.takeIf { task -> task.ticket.projectId == timeboxProjectId }?.ticket?.title

    Div(
        attrs = {
            style {
                props(
                    "padding" to "24px",
                    "background" to Canvas,
                    "border-bottom" to "1px solid $Border",
                    "box-sizing" to "border-box",
                )
            }
        }
    ) {
        Div(attrs = { style { props("display" to "grid", "gap" to "18px") } }) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "space-between",
                            "align-items" to "end",
                            "gap" to "16px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                Div(
                    attrs = {
                        style { props("display" to "grid", "gap" to "6px", "max-width" to "760px") }
                    }
                ) {
                    Span(
                        attrs = {
                            style {
                                props("font-size" to "18px", "font-weight" to "700", "color" to Ink)
                            }
                        }
                    ) {
                        Text("Schedule")
                    }
                    Span(
                        attrs = {
                            style {
                                props(
                                    "font-size" to "13px",
                                    "line-height" to "1.4",
                                    "color" to Muted,
                                )
                            }
                        }
                    ) {
                        Text(
                            "${scopedEvents.size} events • ${scopedTimeboxes.size} timeboxes • ${scopedRoutines.size} routines"
                        )
                    }
                }
            }
            Div(attrs = { style { props("display" to "grid", "gap" to "14px") } }) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "gap" to "8px",
                                "flex-wrap" to "wrap",
                                "align-items" to "center",
                            )
                        }
                    }
                ) {
                    Button(
                        attrs = {
                            attr("id", "web-schedule-tab-events")
                            onClick { selectedTab = WebScheduleManagerTab.Events }
                            style {
                                secondaryButton(
                                    selected = selectedTab == WebScheduleManagerTab.Events
                                )
                            }
                        }
                    ) {
                        Text("Events")
                    }
                    Button(
                        attrs = {
                            attr("id", "web-schedule-tab-timeboxes")
                            onClick { selectedTab = WebScheduleManagerTab.Timeboxes }
                            style {
                                secondaryButton(
                                    selected = selectedTab == WebScheduleManagerTab.Timeboxes
                                )
                            }
                        }
                    ) {
                        Text("Timeboxes")
                    }
                    Button(
                        attrs = {
                            attr("id", "web-schedule-tab-routines")
                            onClick { selectedTab = WebScheduleManagerTab.Routines }
                            style {
                                secondaryButton(
                                    selected = selectedTab == WebScheduleManagerTab.Routines
                                )
                            }
                        }
                    ) {
                        Text("Routines")
                    }
                }
                if (selectedTab == WebScheduleManagerTab.Events) {
                    PanelCard("Events") {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "flex",
                                        "justify-content" to "space-between",
                                        "align-items" to "center",
                                        "gap" to "10px",
                                        "flex-wrap" to "wrap",
                                    )
                                }
                            }
                        ) {
                            NoticeBadge("${scopedEvents.size} scheduled")
                            Button(
                                attrs = {
                                    attr("id", "web-schedule-new-event-button")
                                    onClick { eventComposerOpen = !eventComposerOpen }
                                    style { secondaryButton() }
                                }
                            ) {
                                Text(if (eventComposerOpen) "Hide form" else "New event")
                            }
                        }
                        if (eventComposerOpen) {
                            Div(attrs = { style { scheduleItemCard() } }) {
                                if (scopeProjects.size > 1) {
                                    ProjectSelector(
                                        projects = scopeProjects,
                                        currentProjectId = eventProjectId,
                                        allProjects = workspace.projects,
                                        onProjectSelected = { eventProjectId = it },
                                        id = "web-schedule-event-project-select",
                                    )
                                }
                                ScheduleInput(
                                    value = eventTitle,
                                    placeholder = "Event title",
                                    onChange = { eventTitle = it },
                                    id = "web-schedule-event-create-title-input",
                                )
                                DatePickerField(
                                    value = eventStart,
                                    label = "Start",
                                    placeholder = "Start date and time",
                                    kind = WebDateControlKind.DateTime,
                                    onChange = { eventStart = it },
                                    id = "web-schedule-event-create-start-input",
                                )
                                DatePickerField(
                                    value = eventEnd,
                                    label = "End",
                                    placeholder = "End date and time",
                                    kind = WebDateControlKind.DateTime,
                                    onChange = { eventEnd = it },
                                    id = "web-schedule-event-create-end-input",
                                )
                                ScheduleInput(
                                    value = eventDescription,
                                    placeholder = "Description (optional)",
                                    onChange = { eventDescription = it },
                                    id = "web-schedule-event-create-description-input",
                                )
                                ScheduleInput(
                                    value = eventLocation,
                                    placeholder = "Location (optional)",
                                    onChange = { eventLocation = it },
                                    id = "web-schedule-event-create-location-input",
                                )
                                Button(
                                    attrs = {
                                        attr("id", "web-schedule-event-create-button")
                                        onClick {
                                            val parsedStart =
                                                parseDateTimeInput(eventStart) ?: return@onClick
                                            onCreateEvent(
                                                eventProjectId,
                                                eventTitle,
                                                eventTitle,
                                                parsedStart,
                                                parseDateTimeInput(eventEnd),
                                                eventDescription,
                                                eventLocation,
                                            )
                                            eventTitle = ""
                                            eventEnd = ""
                                            eventDescription = ""
                                            eventLocation = ""
                                            eventComposerOpen = false
                                        }
                                        if (
                                            eventTitle.trim().isBlank() ||
                                                parseDateTimeInput(eventStart) == null
                                        ) {
                                            disabled()
                                        }
                                        style { primaryButton() }
                                    }
                                ) {
                                    Text("Create event")
                                }
                            }
                        }
                        if (scopedEvents.isEmpty()) {
                            ScheduleEmptyState("No events in this project view")
                        } else {
                            scopedEvents.forEach { event ->
                                EventEditorRow(
                                    event = event,
                                    allProjects = workspace.projects,
                                    onSave = onUpdateEvent,
                                )
                            }
                        }
                    }
                }
                if (selectedTab == WebScheduleManagerTab.Timeboxes) {
                    PanelCard("Timeboxes") {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "flex",
                                        "justify-content" to "space-between",
                                        "align-items" to "center",
                                        "gap" to "10px",
                                        "flex-wrap" to "wrap",
                                    )
                                }
                            }
                        ) {
                            NoticeBadge("${scopedTimeboxes.size} scheduled")
                            Button(
                                attrs = {
                                    attr("id", "web-schedule-new-timebox-button")
                                    onClick { timeboxComposerOpen = !timeboxComposerOpen }
                                    style { secondaryButton() }
                                }
                            ) {
                                Text(if (timeboxComposerOpen) "Hide form" else "New timebox")
                            }
                        }
                        if (timeboxComposerOpen) {
                            Div(attrs = { style { scheduleItemCard() } }) {
                                if (scopeProjects.size > 1) {
                                    ProjectSelector(
                                        projects = scopeProjects,
                                        currentProjectId = timeboxProjectId,
                                        allProjects = workspace.projects,
                                        onProjectSelected = { timeboxProjectId = it },
                                        id = "web-schedule-timebox-project-select",
                                    )
                                }
                                ScheduleInput(
                                    value = timeboxDescription,
                                    placeholder = "Timebox description",
                                    onChange = { timeboxDescription = it },
                                    id = "web-schedule-timebox-create-description-input",
                                )
                                DatePickerField(
                                    value = timeboxStart,
                                    label = "Start",
                                    placeholder = "Start date and time",
                                    kind = WebDateControlKind.DateTime,
                                    onChange = { timeboxStart = it },
                                    id = "web-schedule-timebox-create-start-input",
                                )
                                ScheduleInput(
                                    value = timeboxHours,
                                    placeholder = "Duration hours",
                                    onChange = { timeboxHours = it },
                                    id = "web-schedule-timebox-create-hours-input",
                                )
                                selectedTimeboxLinkTitle?.let { linkedTitle ->
                                    MutedText("Will link to selected ticket: $linkedTitle")
                                }
                                Button(
                                    attrs = {
                                        attr("id", "web-schedule-timebox-create-button")
                                        onClick {
                                            val parsedStart =
                                                parseDateTimeInput(timeboxStart) ?: return@onClick
                                            val linkedTicketIds =
                                                selectedTask
                                                    ?.takeIf { task ->
                                                        task.ticket.projectId == timeboxProjectId
                                                    }
                                                    ?.let { listOf(it.ticket.id) }
                                                    .orEmpty()
                                            onCreateTimebox(
                                                timeboxProjectId,
                                                linkedTicketIds,
                                                timeboxDescription,
                                                parsedStart,
                                                timeboxHours.toIntOrNull(),
                                            )
                                            timeboxDescription = ""
                                            timeboxComposerOpen = false
                                        }
                                        if (
                                            timeboxDescription.trim().isBlank() ||
                                                parseDateTimeInput(timeboxStart) == null
                                        ) {
                                            disabled()
                                        }
                                        style { primaryButton() }
                                    }
                                ) {
                                    Text("Create timebox")
                                }
                            }
                        }
                        if (scopedTimeboxes.isEmpty()) {
                            ScheduleEmptyState("No timeboxes in this project view")
                        } else {
                            scopedTimeboxes.forEach { timebox ->
                                TimeboxEditorRow(
                                    timebox = timebox,
                                    allProjects = workspace.projects,
                                    onSave = onUpdateTimebox,
                                )
                            }
                        }
                    }
                }
                if (selectedTab == WebScheduleManagerTab.Routines) {
                    PanelCard("Routines") {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "flex",
                                        "justify-content" to "space-between",
                                        "align-items" to "center",
                                        "gap" to "10px",
                                        "flex-wrap" to "wrap",
                                    )
                                }
                            }
                        ) {
                            NoticeBadge("${scopedRoutines.size} active")
                            Button(
                                attrs = {
                                    attr("id", "web-schedule-new-routine-button")
                                    onClick { routineComposerOpen = !routineComposerOpen }
                                    style { secondaryButton() }
                                }
                            ) {
                                Text(if (routineComposerOpen) "Hide form" else "New routine")
                            }
                        }
                        if (routineComposerOpen) {
                            Div(attrs = { style { scheduleItemCard() } }) {
                                if (scopeProjects.size > 1) {
                                    ProjectSelector(
                                        projects = scopeProjects,
                                        currentProjectId = routineProjectId,
                                        allProjects = workspace.projects,
                                        onProjectSelected = { routineProjectId = it },
                                        id = "web-schedule-routine-project-select",
                                    )
                                }
                                ScheduleInput(
                                    value = routineDescription,
                                    placeholder = "Routine description",
                                    onChange = { routineDescription = it },
                                    id = "web-schedule-routine-create-description-input",
                                )
                                Button(
                                    attrs = {
                                        attr("id", "web-schedule-routine-create-button")
                                        onClick {
                                            onCreateRoutine(routineProjectId, routineDescription)
                                            routineDescription = ""
                                            routineComposerOpen = false
                                        }
                                        if (routineDescription.trim().isBlank()) disabled()
                                        style { primaryButton() }
                                    }
                                ) {
                                    Text("Create routine")
                                }
                            }
                        }
                        if (scopedRoutines.isEmpty()) {
                            ScheduleEmptyState("No routines in this project view")
                        } else {
                            scopedRoutines.forEach { routine ->
                                RoutineEditorRow(
                                    routine = routine,
                                    allProjects = workspace.projects,
                                    onSave = onUpdateRoutine,
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ScheduleOverviewMetric(label: String, value: String) {
    Div(attrs = { style { props("display" to "grid", "gap" to "2px", "min-width" to "86px") } }) {
        Span(
            attrs = {
                style { props("font-size" to "12px", "font-weight" to "700", "color" to Muted) }
            }
        ) {
            Text(label)
        }
        Span(
            attrs = {
                style { props("font-size" to "20px", "font-weight" to "700", "color" to Ink) }
            }
        ) {
            Text(value)
        }
    }
}

@Composable
private fun ScheduleEmptyState(label: String) {
    Div(attrs = { style { scheduleItemCard() } }) { MutedText(label) }
}

@Composable
private fun EventEditorRow(
    event: Kairos.Event,
    allProjects: List<Kairos.Project>,
    onSave: (Kairos.EventId, String, String, ZonedDateTime, ZonedDateTime?) -> Unit,
) {
    var expanded by remember(event.id.value) { mutableStateOf(false) }
    var title by remember(event.id.value, event.title) { mutableStateOf(event.title) }
    var start by
        remember(event.id.value, event.dtStart) {
            mutableStateOf(event.dtStart.dateTimeInputValue())
        }
    var end by
        remember(event.id.value, event.dtEnd) { mutableStateOf(event.dtEnd.dateTimeInputValue()) }

    Div(
        attrs = {
            attr("data-schedule-event-row", event.id.value)
            style { scheduleItemCard() }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "justify-content" to "space-between",
                        "align-items" to "start",
                        "gap" to "12px",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "gap" to "4px",
                            "min-width" to "0",
                            "flex" to "1",
                        )
                    }
                }
            ) {
                event.projectId?.let { projectId -> workspaceProjectLabel(projectId, allProjects) }
                Span(attrs = { style { props("font-size" to "15px", "font-weight" to "700") } }) {
                    Text(event.title.ifBlank { "Untitled event" })
                }
                MutedText(formatEventTimeRange(event))
            }
            Button(
                attrs = {
                    attr("data-schedule-event-edit", event.id.value)
                    onClick { expanded = !expanded }
                    style { secondaryButton(selected = expanded) }
                }
            ) {
                Text(if (expanded) "Close" else "Edit")
            }
        }
        if (expanded) {
            ScheduleInput(
                value = title,
                placeholder = "Title",
                onChange = { title = it },
                id = "web-schedule-event-title-input-${event.id.value}",
            )
            DatePickerField(
                value = start,
                label = "Start",
                placeholder = "Start date and time",
                kind = WebDateControlKind.DateTime,
                onChange = { start = it },
                id = "web-schedule-event-start-input-${event.id.value}",
            )
            DatePickerField(
                value = end,
                label = "End",
                placeholder = "End date and time",
                kind = WebDateControlKind.DateTime,
                onChange = { end = it },
                id = "web-schedule-event-end-input-${event.id.value}",
            )
            Button(
                attrs = {
                    attr("data-schedule-event-save", event.id.value)
                    attr("id", "web-schedule-event-save-${event.id.value}")
                    onClick {
                        val parsedStart = parseDateTimeInput(start) ?: return@onClick
                        onSave(event.id, title, title, parsedStart, parseDateTimeInput(end))
                        expanded = false
                    }
                    if (title.trim().isBlank() || parseDateTimeInput(start) == null) {
                        disabled()
                    }
                    style { secondaryButton() }
                }
            ) {
                Text("Save")
            }
        }
    }
}

@Composable
private fun TimeboxEditorRow(
    timebox: Kairos.Timebox,
    allProjects: List<Kairos.Project>,
    onSave: (Kairos.TimeboxId, String, ZonedDateTime, Int?) -> Unit,
) {
    var expanded by remember(timebox.id.value) { mutableStateOf(false) }
    var description by
        remember(timebox.id.value, timebox.description) { mutableStateOf(timebox.description) }
    var start by
        remember(timebox.id.value, timebox.startTime) { mutableStateOf(timebox.startInputValue()) }
    var hours by
        remember(timebox.id.value, timebox.recordedDuration) {
            mutableStateOf(timebox.recordedDuration.totalWholeHoursOrNull()?.toString().orEmpty())
        }

    Div(
        attrs = {
            attr("data-schedule-timebox-row", timebox.id.value)
            style { scheduleItemCard() }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "justify-content" to "space-between",
                        "align-items" to "start",
                        "gap" to "12px",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "gap" to "4px",
                            "min-width" to "0",
                            "flex" to "1",
                        )
                    }
                }
            ) {
                timebox.projectId?.let { projectId ->
                    workspaceProjectLabel(projectId, allProjects)
                }
                Span(attrs = { style { props("font-size" to "15px", "font-weight" to "700") } }) {
                    Text(timebox.description.ifBlank { "Untitled timebox" })
                }
                MutedText(
                    listOfNotNull(
                            formatInstantTime(timebox.startTime),
                            timebox.recordedDuration.totalWholeHoursOrNull()?.let { "$it h" },
                            timebox.tickets.takeIf { it.isNotEmpty() }?.let { "${it.size} linked" },
                        )
                        .joinToString(" • ")
                )
            }
            Button(
                attrs = {
                    attr("data-schedule-timebox-edit", timebox.id.value)
                    onClick { expanded = !expanded }
                    style { secondaryButton(selected = expanded) }
                }
            ) {
                Text(if (expanded) "Close" else "Edit")
            }
        }
        if (expanded) {
            ScheduleInput(
                value = description,
                placeholder = "Description",
                onChange = { description = it },
                id = "web-schedule-timebox-description-input-${timebox.id.value}",
            )
            DatePickerField(
                value = start,
                label = "Start",
                placeholder = "Start date and time",
                kind = WebDateControlKind.DateTime,
                onChange = { start = it },
                id = "web-schedule-timebox-start-input-${timebox.id.value}",
            )
            ScheduleInput(
                value = hours,
                placeholder = "Duration hours",
                onChange = { hours = it },
                id = "web-schedule-timebox-hours-input-${timebox.id.value}",
            )
            Button(
                attrs = {
                    attr("data-schedule-timebox-save", timebox.id.value)
                    attr("id", "web-schedule-timebox-save-${timebox.id.value}")
                    onClick {
                        val parsedStart = parseDateTimeInput(start) ?: return@onClick
                        onSave(timebox.id, description, parsedStart, hours.toIntOrNull())
                        expanded = false
                    }
                    if (description.trim().isBlank() || parseDateTimeInput(start) == null) {
                        disabled()
                    }
                    style { secondaryButton() }
                }
            ) {
                Text("Save")
            }
        }
    }
}

@Composable
private fun RoutineEditorRow(
    routine: Kairos.Routine,
    allProjects: List<Kairos.Project>,
    onSave: (Kairos.RoutineId, String) -> Unit,
) {
    var expanded by remember(routine.id.value) { mutableStateOf(false) }
    var description by
        remember(routine.id.value, routine.description) { mutableStateOf(routine.description) }

    Div(
        attrs = {
            attr("data-schedule-routine-row", routine.id.value)
            style { scheduleItemCard() }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "justify-content" to "space-between",
                        "align-items" to "start",
                        "gap" to "12px",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "gap" to "4px",
                            "min-width" to "0",
                            "flex" to "1",
                        )
                    }
                }
            ) {
                routine.projectId?.let { projectId ->
                    workspaceProjectLabel(projectId, allProjects)
                }
                Span(attrs = { style { props("font-size" to "15px", "font-weight" to "700") } }) {
                    Text(routine.description.ifBlank { "Untitled routine" })
                }
                MutedText("Recurring project expectation")
            }
            Button(
                attrs = {
                    attr("data-schedule-routine-edit", routine.id.value)
                    onClick { expanded = !expanded }
                    style { secondaryButton(selected = expanded) }
                }
            ) {
                Text(if (expanded) "Close" else "Edit")
            }
        }
        if (expanded) {
            ScheduleInput(
                value = description,
                placeholder = "Description",
                onChange = { description = it },
                id = "web-schedule-routine-description-input-${routine.id.value}",
            )
            Button(
                attrs = {
                    attr("data-schedule-routine-save", routine.id.value)
                    attr("id", "web-schedule-routine-save-${routine.id.value}")
                    onClick {
                        onSave(routine.id, description)
                        expanded = false
                    }
                    if (description.trim().isBlank()) disabled()
                    style { secondaryButton() }
                }
            ) {
                Text("Save")
            }
        }
    }
}

@Composable
private fun DatePickerField(
    value: String,
    onChange: (String) -> Unit,
    onCommit: ((String) -> Unit)? = null,
    kind: WebDateControlKind,
    id: String? = null,
    label: String? = null,
    placeholder: String = "",
    width: String = "100%",
    height: String = "44px",
    attrs: InputAttrsScope<String>.() -> Unit = {},
) {
    var pickerOpen by remember(id, kind) { mutableStateOf(false) }
    val parsedDateTime = remember(value, kind) { parseDateControlInput(value, kind) }
    var visibleMonth by
        remember(id, kind, parsedDateTime?.year, parsedDateTime?.month?.ordinal) {
            mutableStateOf(parsedDateTime?.monthStart() ?: nowUiZoned().dateTime.monthStart())
        }

    Div(
        attrs = {
            style {
                props(
                    "position" to "relative",
                    "display" to "block",
                    "width" to width,
                    "max-width" to "100%",
                    "flex" to if (width == "100%") "1 1 0" else "0 1 $width",
                    "min-width" to "0",
                )
            }
        }
    ) {
        label?.let { labelText ->
            Span(
                attrs = {
                    style {
                        props(
                            "position" to "absolute",
                            "top" to "-7px",
                            "left" to "12px",
                            "z-index" to "2",
                            "padding" to "0 5px",
                            "background" to Paper,
                            "font-size" to "11px",
                            "font-weight" to "700",
                            "line-height" to "1",
                            "color" to Blue,
                        )
                    }
                }
            ) {
                Text(labelText)
            }
        }
        Input(
            type = InputType.Text,
            attrs = {
                id?.let { attr("id", it) }
                attr("data-date-control-value", normalizeDateControlDraft(value, kind))
                attr("class", DatePickerCssClass)
                value(dateControlDisplayValue(value, kind))
                if (placeholder.isNotBlank()) {
                    attr("placeholder", placeholder)
                    attr("aria-label", placeholder)
                }
                onInput { onChange(normalizeDateControlDraft(it.value, kind)) }
                onBlur { onCommit?.invoke(committableDateControlDraft(id, value, kind)) }
                attrs()
                style {
                    props(
                        "width" to "100%",
                        "min-width" to "0",
                        "height" to height,
                        "border" to "1px solid #cbd2dc",
                        "border-radius" to "8px",
                        "padding" to if (label == null) "0 42px 0 12px" else "7px 42px 0 12px",
                        "font-size" to "14px",
                        "font-weight" to "500",
                        "line-height" to "1",
                        "font-family" to "Inter, Arial, Helvetica, sans-serif",
                        "box-sizing" to "border-box",
                        "background" to Paper,
                        "color" to Ink,
                        "outline" to "none",
                        "color-scheme" to "light",
                        "accent-color" to Blue,
                    )
                }
            },
        )
        Button(
            attrs = {
                attr("type", "button")
                id?.let { attr("id", "$it-picker-button") }
                attr("aria-label", "Open date picker")
                onClick { pickerOpen = !pickerOpen }
                style {
                    props(
                        "position" to "absolute",
                        "right" to "2px",
                        "top" to "2px",
                        "transform" to "none",
                        "width" to "38px",
                        "height" to "calc($height - 4px)",
                        "border" to "0",
                        "border-radius" to "6px",
                        "background" to "transparent",
                        "display" to "grid",
                        "place-items" to "center",
                        "cursor" to "pointer",
                        "padding" to "0",
                    )
                }
            }
        ) {
            CalendarFieldIcon()
        }
        if (pickerOpen) {
            DatePickerPopover(
                id = id?.let { "$it-picker-popover" },
                value = parsedDateTime,
                visibleMonth = visibleMonth,
                kind = kind,
                onVisibleMonthChanged = { visibleMonth = it },
                onValueSelected = { selected ->
                    onChange(selected)
                    onCommit?.invoke(selected)
                },
                onClose = { pickerOpen = false },
            )
        }
    }
}

@Composable
private fun CalendarFieldIcon() {
    Div(
        attrs = {
            attr("aria-hidden", "true")
            style {
                props(
                    "position" to "relative",
                    "width" to "20px",
                    "height" to "20px",
                    "border" to "2px solid $Muted",
                    "border-radius" to "4px",
                    "box-sizing" to "border-box",
                    "pointer-events" to "none",
                    "background" to "transparent",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "position" to "absolute",
                        "left" to "2px",
                        "right" to "2px",
                        "top" to "5px",
                        "border-top" to "2px solid $Muted",
                    )
                }
            }
        )
        Div(
            attrs = {
                style {
                    props(
                        "position" to "absolute",
                        "right" to "3px",
                        "bottom" to "3px",
                        "width" to "4px",
                        "height" to "4px",
                        "background" to Muted,
                    )
                }
            }
        )
    }
}

@Composable
private fun DatePickerPopover(
    id: String?,
    value: LocalDateTime?,
    visibleMonth: LocalDate,
    kind: WebDateControlKind,
    onVisibleMonthChanged: (LocalDate) -> Unit,
    onValueSelected: (String) -> Unit,
    onClose: () -> Unit,
) {
    val compactPopover = window.innerWidth < 700
    val popoverWidth = "min(300px, calc(100vw - 48px))"
    val selectedDate = value?.dateOnly()
    val selectedTime = value ?: nowUiZoned().dateTime
    Div(
        attrs = {
            id?.let { attr("id", it) }
            style {
                props(
                    "position" to if (compactPopover) "relative" else "absolute",
                    "top" to if (compactPopover) "8px" else "calc(100% + 8px)",
                    "right" to if (compactPopover) "auto" else "0",
                    "left" to if (compactPopover) "calc(100% - $popoverWidth)" else "auto",
                    "z-index" to "90",
                    "width" to popoverWidth,
                    "min-width" to if (compactPopover) "0" else "min(260px, calc(100vw - 32px))",
                    "max-width" to
                        if (compactPopover) "calc(100vw - 48px)" else "calc(100vw - 32px)",
                    "border" to "1px solid #d6d9df",
                    "border-radius" to "8px",
                    "background" to Paper,
                    "box-shadow" to "0 18px 40px rgba(20, 22, 28, 0.18)",
                    "box-sizing" to "border-box",
                    "padding" to "12px",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "justify-content" to "space-between",
                        "gap" to "8px",
                        "margin-bottom" to "10px",
                    )
                }
            }
        ) {
            Button(
                attrs = {
                    attr("type", "button")
                    attr("aria-label", "Previous month")
                    onClick { onVisibleMonthChanged(visibleMonth + DatePeriod(months = -1)) }
                    style { pickerIconButton() }
                }
            ) {
                Text("<")
            }
            Div(
                attrs = {
                    style {
                        props(
                            "font-size" to "14px",
                            "font-weight" to "700",
                            "color" to Ink,
                            "text-align" to "center",
                        )
                    }
                }
            ) {
                Text(monthYearLabel(visibleMonth))
            }
            Button(
                attrs = {
                    attr("type", "button")
                    attr("aria-label", "Next month")
                    onClick { onVisibleMonthChanged(visibleMonth + DatePeriod(months = 1)) }
                    style { pickerIconButton() }
                }
            ) {
                Text(">")
            }
        }
        Div(
            attrs = {
                style {
                    props(
                        "display" to "grid",
                        "grid-template-columns" to "repeat(7, minmax(0, 1fr))",
                        "gap" to "4px",
                        "margin-bottom" to "6px",
                    )
                }
            }
        ) {
            listOf("Su", "Mo", "Tu", "We", "Th", "Fr", "Sa").forEach { label ->
                Div(
                    attrs = {
                        style {
                            props(
                                "height" to "24px",
                                "display" to "grid",
                                "place-items" to "center",
                                "font-size" to "11px",
                                "font-weight" to "700",
                                "color" to Muted,
                            )
                        }
                    }
                ) {
                    Text(label)
                }
            }
            calendarMonthCells(visibleMonth).forEach { date ->
                val inMonth = date.month == visibleMonth.month
                val selected = selectedDate == date
                Button(
                    attrs = {
                        attr("type", "button")
                        onClick {
                            val next = selectedTime.withDate(date)
                            onValueSelected(next.dateControlValue(kind))
                            if (kind == WebDateControlKind.Date) {
                                onClose()
                            }
                        }
                        style { pickerDayButton(selected = selected, inMonth = inMonth) }
                    }
                ) {
                    Text(date.day.toString())
                }
            }
        }
        if (kind == WebDateControlKind.DateTime) {
            DateTimePickerFooter(
                value = selectedTime,
                onValueSelected = { onValueSelected(it.dateControlValue(kind)) },
                onClose = onClose,
            )
        }
    }
}

@Composable
private fun DateTimePickerFooter(
    value: LocalDateTime,
    onValueSelected: (LocalDateTime) -> Unit,
    onClose: () -> Unit,
) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "grid",
                    "grid-template-columns" to "1fr 1fr 1fr auto",
                    "gap" to "8px",
                    "border-top" to "1px solid $Border",
                    "padding-top" to "10px",
                    "margin-top" to "10px",
                    "align-items" to "center",
                )
            }
        }
    ) {
        PickerSelect(
            label = "Hour",
            value = value.hour12().toString().padStart(2, '0'),
            options = (1..12).map { it.toString().padStart(2, '0') },
            onChange = { nextHour ->
                onValueSelected(value.withHour(nextHour.toIntOrNull() ?: value.hour12()))
            },
        )
        PickerSelect(
            label = "Minute",
            value = value.minute.toString().padStart(2, '0'),
            options = (0..55 step 5).map { it.toString().padStart(2, '0') },
            onChange = { nextMinute ->
                onValueSelected(value.withMinute(nextMinute.toIntOrNull() ?: value.minute))
            },
        )
        PickerSelect(
            label = "Period",
            value = value.dayPeriod(),
            options = listOf("AM", "PM"),
            onChange = { period -> onValueSelected(value.withPeriod(period)) },
        )
        Button(
            attrs = {
                attr("type", "button")
                onClick { onClose() }
                style { secondaryButton() }
            }
        ) {
            Text("OK")
        }
    }
}

@Composable
private fun PickerSelect(
    label: String,
    value: String,
    options: List<String>,
    onChange: (String) -> Unit,
) {
    Select(
        attrs = {
            attr("aria-label", label)
            onInput { event -> event.value?.let(onChange) }
            style {
                props(
                    "height" to "34px",
                    "min-width" to "0",
                    "width" to "100%",
                    "border" to "1px solid #cbd2dc",
                    "border-radius" to "8px",
                    "background" to Paper,
                    "font-size" to "13px",
                    "font-weight" to "700",
                    "color" to Ink,
                    "padding" to "0 6px",
                )
            }
        }
    ) {
        options.forEach { option ->
            Option(
                value = option,
                attrs = {
                    if (option == value) {
                        attr("selected", "selected")
                    }
                },
            ) {
                Text(option)
            }
        }
    }
}

@Composable
private fun ScheduleInput(
    value: String,
    placeholder: String,
    onChange: (String) -> Unit,
    id: String? = null,
) {
    Input(
        type = InputType.Text,
        attrs = {
            id?.let { attr("id", it) }
            value(value)
            attr("placeholder", placeholder)
            onInput { onChange(it.value) }
            style { scheduleTextInput() }
        },
    )
}

private fun StyleScope.scheduleTextInput() {
    props(
        "height" to "38px",
        "border" to "1px solid $Border",
        "border-radius" to "8px",
        "padding" to "0 12px",
        "font-size" to "13px",
        "background" to Paper,
        "width" to "100%",
        "box-sizing" to "border-box",
    )
}

private fun StyleScope.scheduleItemCard() {
    props(
        "display" to "grid",
        "gap" to "8px",
        "padding" to "12px",
        "border" to "1px solid $Border",
        "border-radius" to "8px",
        "background" to "#fbfcfd",
    )
}

@Composable
private fun workspaceProjectLabel(projectId: Kairos.ProjectId, allProjects: List<Kairos.Project>) {
    allProjects
        .firstOrNull { it.id == projectId }
        ?.let { project ->
            Span(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                Text(project.projectDisplayName(allProjects))
            }
        }
}

private const val WebBoardColumnWidth = 272

@Composable
private fun BoardView(
    columns: List<WebStageColumn>,
    compact: Boolean,
    onMoveTask: (WebTask, Kairos.TicketStage) -> Unit,
    onTaskSelected: (WebTask) -> Unit,
    onRunQuickAction: (WebTask) -> Unit,
) {
    val requiredBoardWidth =
        (columns.size * WebBoardColumnWidth) + ((columns.size - 1).coerceAtLeast(0) * 20) + 48
    val fittedDesktopBoard =
        !compact &&
            columns.isNotEmpty() &&
            columns.size <= 4 &&
            window.innerWidth >= requiredBoardWidth + 320
    val taskById =
        remember(columns) {
            columns.flatMap { column -> column.tasks }.associateBy { task -> task.ticket.id.value }
        }
    var draggedTaskId by remember { mutableStateOf<String?>(null) }
    var dropTargetStage by remember { mutableStateOf<Kairos.TicketStage?>(null) }

    if (!compact) {
        DisposableEffect(columns, onMoveTask) {
            data class DomListener(
                val element: HTMLElement,
                val type: String,
                val listener: EventListener,
            )

            val listeners = mutableListOf<DomListener>()

            val taskNodes = document.querySelectorAll("[data-kanban-task-id]")
            for (index in 0 until taskNodes.length) {
                val element = taskNodes.item(index) as? HTMLElement ?: continue
                element.draggable = true

                val dragStartListener = EventListener { event ->
                    val taskId = element.getAttribute("data-kanban-task-id") ?: return@EventListener
                    draggedTaskId = taskId
                    dropTargetStage = taskById[taskId]?.ticket?.stage
                    event.asDynamic().dataTransfer?.effectAllowed = "move"
                    event.asDynamic().dataTransfer?.setData("text/plain", taskId)
                }
                val dragEndListener = EventListener {
                    draggedTaskId = null
                    dropTargetStage = null
                }

                element.addEventListener("dragstart", dragStartListener)
                listeners += DomListener(element, "dragstart", dragStartListener)
                element.addEventListener("dragend", dragEndListener)
                listeners += DomListener(element, "dragend", dragEndListener)
            }

            val columnNodes = document.querySelectorAll("[data-kanban-stage]")
            for (index in 0 until columnNodes.length) {
                val element = columnNodes.item(index) as? HTMLElement ?: continue
                val stage = element.getAttribute("data-kanban-stage").toTicketStage() ?: continue

                val dragOverListener = EventListener { event ->
                    if (draggedTaskId == null) return@EventListener
                    event.preventDefault()
                    dropTargetStage = stage
                    event.asDynamic().dataTransfer?.dropEffect = "move"
                }
                val dropListener = EventListener { event ->
                    event.preventDefault()
                    val taskId =
                        draggedTaskId
                            ?: (event.asDynamic().dataTransfer?.getData("text/plain") as? String)
                    val task = taskId?.let(taskById::get)
                    if (task != null && task.ticket.stage != stage) {
                        onMoveTask(task, stage)
                    }
                    draggedTaskId = null
                    dropTargetStage = null
                }

                element.addEventListener("dragover", dragOverListener)
                listeners += DomListener(element, "dragover", dragOverListener)
                element.addEventListener("drop", dropListener)
                listeners += DomListener(element, "drop", dropListener)
            }

            onDispose {
                listeners.forEach { binding ->
                    binding.element.removeEventListener(binding.type, binding.listener)
                }
            }
        }
    }

    Div(
        attrs = {
            style {
                props(
                    "flex" to "1",
                    "padding" to if (compact) "16px 16px 24px" else "24px",
                    "background" to Canvas,
                    "overflow-x" to "auto",
                    "overflow-y" to if (compact) "hidden" else "auto",
                    "min-height" to "0",
                    "position" to "relative",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    if (compact) {
                        props(
                            "display" to "grid",
                            "grid-auto-flow" to "column",
                            "grid-auto-columns" to "minmax(min(86vw, 336px), min(86vw, 336px))",
                            "gap" to "16px",
                            "width" to "max-content",
                            "min-width" to "100%",
                            "box-sizing" to "border-box",
                            "padding-right" to "20px",
                            "padding-bottom" to "24px",
                            "scroll-snap-type" to "x proximity",
                            "align-items" to "start",
                        )
                    } else {
                        val gridTemplateColumns =
                            when {
                                fittedDesktopBoard -> "repeat(${columns.size}, minmax(0, 1fr))"
                                else ->
                                    "repeat(${columns.size}, minmax(${WebBoardColumnWidth}px, ${WebBoardColumnWidth}px))"
                            }
                        props(
                            "display" to "grid",
                            "grid-template-columns" to gridTemplateColumns,
                            "align-items" to if (fittedDesktopBoard) "start" else "stretch",
                            "gap" to "20px",
                            "min-height" to "100%",
                            "width" to if (fittedDesktopBoard) "100%" else "max-content",
                            "padding-bottom" to "24px",
                            "position" to "relative",
                        )
                    }
                }
            }
        ) {
            columns.forEach { column ->
                val emptyColumn = column.tasks.isEmpty()
                Div(
                    attrs = {
                        attr("id", "web-board-column-${column.stage.stageToken()}")
                        style {
                            props(
                                "width" to
                                    when {
                                        compact -> "min(86vw, 336px)"
                                        fittedDesktopBoard -> "100%"
                                        else -> "${WebBoardColumnWidth}px"
                                    },
                                "min-width" to
                                    when {
                                        compact -> "min(86vw, 336px)"
                                        fittedDesktopBoard -> "0"
                                        else -> "${WebBoardColumnWidth}px"
                                    },
                                "display" to "flex",
                                "flex-direction" to "column",
                                "gap" to "14px",
                                "scroll-snap-align" to if (compact) "start" else "none",
                                "min-height" to
                                    when {
                                        compact -> "0"
                                        emptyColumn -> "0"
                                        else -> "100%"
                                    },
                            )
                        }
                    }
                ) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "align-items" to "center",
                                    "gap" to "10px",
                                    "min-height" to if (compact) "26px" else "30px",
                                    "opacity" to if (emptyColumn) "0.72" else "1",
                                )
                            }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "width" to "8px",
                                        "height" to "8px",
                                        "border-radius" to "999px",
                                        "background" to column.stage.stageColor(),
                                    )
                                }
                            }
                        )
                        Span(
                            attrs = {
                                style {
                                    props(
                                        "font-size" to if (compact) "18px" else "20px",
                                        "font-weight" to if (emptyColumn) "600" else "700",
                                        "color" to if (emptyColumn) Muted else Ink,
                                    )
                                }
                            }
                        ) {
                            Text(column.stage.uiLabel())
                        }
                        CountBadge(column.tasks.size.toString(), muted = emptyColumn)
                    }
                    Div(
                        attrs = {
                            attr("data-kanban-stage", column.stage.stageToken())
                            style {
                                props(
                                    "border" to
                                        if (
                                            !compact &&
                                                dropTargetStage == column.stage &&
                                                draggedTaskId != null
                                        ) {
                                            "1px solid ${column.stage.stageColor()}"
                                        } else if (emptyColumn) {
                                            "1px dashed #d7dce5"
                                        } else {
                                            "1px solid $Border"
                                        },
                                    "border-radius" to "8px",
                                    "background" to
                                        if (
                                            !compact &&
                                                dropTargetStage == column.stage &&
                                                draggedTaskId != null
                                        ) {
                                            "#eefaf6"
                                        } else if (emptyColumn) {
                                            "#fcfcfd"
                                        } else {
                                            "#f7f8fb"
                                        },
                                    "padding" to "12px",
                                    "min-height" to
                                        when {
                                            compact && emptyColumn -> "124px"
                                            compact -> "min(62vh, 540px)"
                                            emptyColumn -> "124px"
                                            else -> "420px"
                                        },
                                    "height" to
                                        when {
                                            compact && emptyColumn -> "124px"
                                            compact -> "min(62vh, 540px)"
                                            else -> "auto"
                                        },
                                    "display" to "flex",
                                    "flex-direction" to "column",
                                    "gap" to "14px",
                                    "flex" to
                                        when {
                                            compact -> "0 0 auto"
                                            emptyColumn -> "0 0 auto"
                                            else -> "1 1 auto"
                                        },
                                    "box-sizing" to "border-box",
                                    "overflow-y" to
                                        if (compact && !emptyColumn) "auto" else "visible",
                                    "transition" to "background 120ms ease, border-color 120ms ease",
                                )
                            }
                        }
                    ) {
                        if (column.tasks.isEmpty()) {
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "padding" to "10px 8px",
                                            "font-size" to "13px",
                                            "color" to Muted,
                                        )
                                    }
                                }
                            ) {
                                Text("Drop tickets here")
                            }
                        } else {
                            column.tasks.forEach { task ->
                                TaskCard(
                                    task = task,
                                    dragging = !compact && draggedTaskId == task.ticket.id.value,
                                    onTaskSelected = onTaskSelected,
                                    onRunQuickAction = onRunQuickAction,
                                )
                            }
                        }
                    }
                }
            }
        }
        if (!compact && !fittedDesktopBoard && columns.size > 1) {
            Div(
                attrs = {
                    style {
                        props(
                            "position" to "sticky",
                            "top" to "0",
                            "right" to "0",
                            "margin-left" to "auto",
                            "width" to "28px",
                            "height" to "100%",
                            "background" to
                                "linear-gradient(90deg, rgba(247,248,251,0), rgba(247,248,251,0.92) 78%)",
                            "pointer-events" to "none",
                        )
                    }
                }
            )
        }
    }
}

@Composable
private fun TaskCard(
    task: WebTask,
    dragging: Boolean = false,
    onTaskSelected: (WebTask) -> Unit,
    onRunQuickAction: ((WebTask) -> Unit)? = null,
) {
    val quickAction = task.ticket.quickAction()
    val hasDueDate = task.ticket.deadline != null
    val linkedObjectiveLabel =
        task.objectiveCount
            .takeIf { it > 0 }
            ?.let { count -> "$count objective${if (count == 1) "" else "s"}" }
    Div(
        attrs = {
            attr("data-kanban-task-id", task.ticket.id.value)
            attr("data-ticket-id", task.ticket.id.value)
            attr("draggable", "true")
            onClick { onTaskSelected(task) }
            style {
                props(
                    "background" to Paper,
                    "border" to "1px solid $Border",
                    "border-top" to "3px solid ${task.project.projectAccent()}",
                    "border-radius" to "8px",
                    "padding" to "12px 14px 14px",
                    "box-shadow" to "0 1px 4px rgba(30,32,36,0.08)",
                    "cursor" to "pointer",
                    "box-sizing" to "border-box",
                    "opacity" to
                        when {
                            dragging -> "0.48"
                            task.ticket.completedAt == null -> "1"
                            else -> "0.62"
                        },
                    "transition" to "opacity 120ms ease, box-shadow 120ms ease",
                )
            }
        }
    ) {
        Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
            Div(
                attrs = {
                    style { props("display" to "flex", "align-items" to "center", "gap" to "8px") }
                }
            ) {
                Span(
                    attrs = {
                        style {
                            props(
                                "display" to "inline-block",
                                "width" to "8px",
                                "height" to "8px",
                                "border-radius" to "999px",
                                "background" to task.project.projectAccent(),
                            )
                        }
                    }
                )
                Span(
                    attrs = {
                        style {
                            props("font-size" to "12px", "font-weight" to "700", "color" to Muted)
                        }
                    }
                ) {
                    Text(task.project.name)
                }
            }
            Div(
                attrs = {
                    attr("title", task.ticket.title)
                    style {
                        props(
                            "font-size" to "15px",
                            "font-weight" to "700",
                            "line-height" to "1.3",
                            "overflow" to "hidden",
                            "display" to "-webkit-box",
                            "-webkit-line-clamp" to "2",
                            "-webkit-box-orient" to "vertical",
                        )
                    }
                }
            ) {
                Text(task.ticket.title)
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "gap" to "6px",
                            "align-items" to "center",
                            "flex-wrap" to "wrap",
                            "margin" to "0",
                        )
                    }
                }
            ) {
                MetaChip("${task.ticket.effortLabel()} pt", "#f5f6f8", Muted)
                linkedObjectiveLabel?.let { MetaChip(it, "#eef8f6", Teal) }
            }
            task.blockerLabel?.let { blockerLabel ->
                Div(
                    attrs = {
                        style {
                            props(
                                "padding" to "8px 10px",
                                "border-radius" to "8px",
                                "background" to "#fff4f0",
                                "border" to "1px solid #ffd5cb",
                                "font-size" to "12px",
                                "font-weight" to "700",
                                "line-height" to "1.35",
                                "color" to Coral,
                            )
                        }
                    }
                ) {
                    Text(blockerLabel)
                }
            }
        }
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "gap" to "12px",
                        "margin-top" to "12px",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "align-items" to "center",
                            "gap" to "10px",
                            "min-width" to "0",
                            "flex" to "1",
                        )
                    }
                }
            ) {
                Avatar(task.owner, 34)
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "grid",
                                "gap" to "3px",
                                "min-width" to "0",
                                "flex" to "1",
                                "font-size" to "13px",
                                "color" to Muted,
                            )
                        }
                    }
                ) {
                    Span(
                        attrs = {
                            style {
                                props(
                                    "font-weight" to "600",
                                    "color" to Ink,
                                    "min-width" to "0",
                                    "overflow" to "hidden",
                                    "text-overflow" to "ellipsis",
                                    "white-space" to "nowrap",
                                )
                            }
                        }
                    ) {
                        Text(task.owner?.displayName ?: "Unassigned")
                    }
                    Span(
                        attrs = {
                            style {
                                props(
                                    "font-weight" to if (hasDueDate) "600" else "500",
                                    "color" to if (hasDueDate) Muted else "#b8bec8",
                                    "font-size" to "12px",
                                    "line-height" to "1.35",
                                    "flex-shrink" to "0",
                                )
                            }
                        }
                    ) {
                        Text(if (hasDueDate) task.ticket.dueLabel() else "No due date")
                    }
                }
            }
            if (quickAction != null && onRunQuickAction != null) {
                Button(
                    attrs = {
                        attr("data-ticket-quick-action", task.ticket.id.value)
                        onClick {
                            it.stopPropagation()
                            onRunQuickAction(task)
                        }
                        style { quickTaskButton(tone = quickAction.tone) }
                    }
                ) {
                    Text(quickAction.label)
                }
            }
        }
    }
}

@Composable
private fun ListView(
    workspace: WebWorkspace,
    columns: List<WebColumn>,
    selectedTask: WebTask?,
    compact: Boolean,
    onTaskSelected: (WebTask) -> Unit,
    onRunQuickAction: (WebTask) -> Unit,
) {
    Div(
        attrs = {
            style {
                props(
                    "flex" to "1",
                    "overflow" to "auto",
                    "background" to Paper,
                    "padding-bottom" to "24px",
                )
            }
        }
    ) {
        if (!compact) {
            TableHeader()
        }
        columns.forEach { column ->
            Div(
                attrs = {
                    style {
                        props(
                            "min-height" to if (compact) "48px" else "42px",
                            "display" to "flex",
                            "align-items" to "center",
                            "justify-content" to "space-between",
                            "gap" to "12px",
                            "padding" to if (compact) "10px 16px" else "0 28px",
                            "font-weight" to "700",
                            "border-bottom" to "1px solid $Border",
                            "background" to "#fbfbfc",
                        )
                    }
                }
            ) {
                Text(column.project.projectDisplayName(workspace.projects))
                Span(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                    Text("${column.tasks.size} ticket${if (column.tasks.size == 1) "" else "s"}")
                }
            }
            column.tasks.forEach { groupedTask ->
                ListTaskRow(
                    workspace = workspace,
                    task = groupedTask,
                    selected = groupedTask.ticket.id == selectedTask?.ticket?.id,
                    compact = compact,
                    onTaskSelected = onTaskSelected,
                    onRunQuickAction = onRunQuickAction,
                )
            }
        }
    }
}

@Composable
private fun ListTaskRow(
    workspace: WebWorkspace,
    task: WebTask,
    selected: Boolean,
    compact: Boolean,
    onTaskSelected: (WebTask) -> Unit,
    onRunQuickAction: (WebTask) -> Unit,
) {
    val linkedObjectives =
        remember(workspace, task.ticket.id) { workspace.objectivesFor(task.ticket.id) }
    val activityCount =
        remember(workspace, task.ticket.id) { workspace.activitiesFor(task.ticket.id).size }
    val quickAction = task.ticket.quickAction()
    val dueColor =
        when {
            task.ticket.isOverdue() -> Coral
            task.ticket.isDueToday() -> Blue
            else -> Muted
        }

    Div(
        attrs = {
            attr("data-ticket-id", task.ticket.id.value)
            onClick { onTaskSelected(task) }
            style {
                props(
                    "min-height" to if (compact) "84px" else "68px",
                    "display" to "grid",
                    "grid-template-columns" to
                        if (compact) {
                            "minmax(0, 1fr)"
                        } else {
                            "minmax(320px,1.7fr) minmax(150px,0.9fr) 110px minmax(170px,1fr) auto"
                        },
                    "align-items" to if (compact) "start" else "center",
                    "gap" to if (compact) "12px" else "16px",
                    "padding" to if (compact) "12px 16px" else "10px 28px",
                    "border-bottom" to "1px solid $Border",
                    "border-left" to if (selected) "3px solid $Blue" else "3px solid transparent",
                    "background" to if (selected) "#eef4ff" else Paper,
                    "cursor" to "pointer",
                    "opacity" to if (task.ticket.completedAt == null) "1" else "0.62",
                    "box-sizing" to "border-box",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "start",
                        "gap" to "12px",
                        "min-width" to "0",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "width" to "10px",
                            "height" to "10px",
                            "border-radius" to "999px",
                            "margin-top" to "6px",
                            "background" to task.ticket.stage.stageColor(),
                            "flex-shrink" to "0",
                        )
                    }
                }
            )
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "gap" to "4px",
                            "min-width" to "0",
                            "flex" to "1",
                        )
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            props(
                                "font-size" to "15px",
                                "font-weight" to "600",
                                "color" to Ink,
                                "overflow" to "hidden",
                                "text-overflow" to "ellipsis",
                                "white-space" to "nowrap",
                            )
                        }
                    }
                ) {
                    Text(task.ticket.title)
                }
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "align-items" to "center",
                                "gap" to "6px",
                                "flex-wrap" to "wrap",
                                "font-size" to "12px",
                                "color" to Muted,
                            )
                        }
                    }
                ) {
                    MetaChip(
                        "${activityCount} update${if (activityCount == 1) "" else "s"}",
                        "#f6f7f9",
                        Muted,
                    )
                    if (compact && linkedObjectives.isNotEmpty()) {
                        MetaChip(
                            "${linkedObjectives.size} objective${if (linkedObjectives.size == 1) "" else "s"}",
                            "#eef8f6",
                            Teal,
                        )
                    }
                }
            }
        }
        if (compact) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "align-items" to "center",
                            "justify-content" to "space-between",
                            "gap" to "10px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "align-items" to "center",
                                "gap" to "10px",
                                "min-width" to "0",
                            )
                        }
                    }
                ) {
                    Avatar(task.owner, 28)
                    Span(
                        attrs = {
                            style {
                                props(
                                    "font-size" to "14px",
                                    "color" to Ink,
                                    "overflow" to "hidden",
                                    "text-overflow" to "ellipsis",
                                    "white-space" to "nowrap",
                                )
                            }
                        }
                    ) {
                        Text(task.owner?.displayName ?: "Unassigned")
                    }
                    Span(
                        attrs = {
                            style {
                                props(
                                    "font-size" to "13px",
                                    "font-weight" to
                                        if (task.ticket.isOverdue() || task.ticket.isDueToday()) {
                                            "700"
                                        } else {
                                            "600"
                                        },
                                    "color" to dueColor,
                                )
                            }
                        }
                    ) {
                        Text(task.ticket.dueLabel())
                    }
                }
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "align-items" to "center",
                                "gap" to "8px",
                                "flex-wrap" to "wrap",
                                "margin-left" to "auto",
                                "justify-content" to "flex-end",
                            )
                        }
                    }
                ) {
                    if (quickAction != null) {
                        Button(
                            attrs = {
                                attr("data-ticket-quick-action", task.ticket.id.value)
                                onClick {
                                    it.stopPropagation()
                                    onRunQuickAction(task)
                                }
                                style { quickTaskButton(tone = quickAction.tone) }
                            }
                        ) {
                            Text(quickAction.label)
                        }
                    }
                    if (quickAction == null) {
                        TaskStatusInline(task.status, task.statusColor)
                    }
                }
            }
        } else {
            Div(
                attrs = {
                    style { props("display" to "flex", "align-items" to "center", "gap" to "10px") }
                }
            ) {
                Avatar(task.owner, 28)
                Span(
                    attrs = {
                        style {
                            props(
                                "font-size" to "14px",
                                "color" to Ink,
                                "overflow" to "hidden",
                                "text-overflow" to "ellipsis",
                                "white-space" to "nowrap",
                            )
                        }
                    }
                ) {
                    Text(task.owner?.displayName ?: "Unassigned")
                }
            }
            Span(
                attrs = {
                    style {
                        props(
                            "font-size" to "14px",
                            "font-weight" to
                                if (task.ticket.isOverdue() || task.ticket.isDueToday()) "700"
                                else "600",
                            "color" to dueColor,
                        )
                    }
                }
            ) {
                Text(task.ticket.dueLabel())
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "align-items" to "center",
                            "justify-content" to "flex-start",
                            "gap" to "8px",
                            "flex-wrap" to "wrap",
                            "min-width" to "0",
                        )
                    }
                }
            ) {
                TaskStatusInline(task.status, task.statusColor)
                task.ticket.priority?.let { priority ->
                    MetaChip(
                        priority.uiLabel(),
                        taskTint(priority.priorityColor()),
                        priority.priorityColor(),
                    )
                }
                if (linkedObjectives.isNotEmpty()) {
                    MetaChip(
                        "${linkedObjectives.size} objective${if (linkedObjectives.size == 1) "" else "s"}",
                        "#eef8f6",
                        Teal,
                    )
                }
                task.blockerLabel?.let { blockerLabel -> BlockerChip(blockerLabel) }
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "align-items" to "center",
                            "justify-content" to "flex-end",
                            "gap" to "8px",
                        )
                    }
                }
            ) {
                quickAction?.let {
                    Button(
                        attrs = {
                            attr("data-ticket-quick-action", task.ticket.id.value)
                            onClick {
                                it.stopPropagation()
                                onRunQuickAction(task)
                            }
                            style { quickTaskButton(tone = it.tone) }
                        }
                    ) {
                        Text(it.label)
                    }
                }
                    ?: Span(
                        attrs = { style { props("font-size" to "12px", "color" to "#c1c5cd") } }
                    ) {
                        Text("—")
                    }
            }
        }
    }
}

@Composable
private fun OverviewView(
    workspace: WebWorkspace,
    selectedProjectId: Kairos.ProjectId?,
    tasks: List<WebTask>,
    onTaskSelected: (WebTask) -> Unit,
    onRunQuickAction: (WebTask) -> Unit,
) {
    val scopeProjectIds =
        remember(workspace, selectedProjectId) {
            selectedProjectId
                ?.let { projectId -> workspace.projectAndChildIds(projectId) }
                .orEmpty()
        }
    val scopedTaskIds = remember(tasks) { tasks.map { task -> task.ticket.id }.toSet() }
    val recentActivity =
        remember(workspace, tasks) { workspace.activityEntriesFor(scopedTaskIds).take(6) }
    val dueSoon = remember(tasks) { tasks.upcomingTasks(limit = 6) }
    val blockedTasks =
        remember(tasks) {
            tasks
                .filter { task -> task.ticket.completedAt == null && task.blockerLabel != null }
                .sortedByTimeline()
                .take(6)
        }
    val scopedObjectives =
        remember(workspace, scopedTaskIds, scopeProjectIds) {
            workspace
                .objectiveSummaries()
                .filter { summary ->
                    summary.objective.projectId in scopeProjectIds ||
                        summary.linkedTasks.any { linkedTask ->
                            linkedTask.ticket.id in scopedTaskIds
                        }
                }
                .take(6)
        }

    Div(
        attrs = {
            style {
                props(
                    "flex" to "1",
                    "overflow" to "auto",
                    "background" to Canvas,
                    "padding" to "24px",
                    "box-sizing" to "border-box",
                )
            }
        }
    ) {
        Div(attrs = { style { props("display" to "grid", "gap" to "20px") } }) {
            TwoPanelGrid {
                TaskPanel(
                    title = "Due soon",
                    tasks = dueSoon,
                    emptyLabel = "No upcoming deadlines in this view",
                    onTaskSelected = onTaskSelected,
                    onRunQuickAction = onRunQuickAction,
                )
                TaskPanel(
                    title = "Blocked by another ticket",
                    tasks = blockedTasks,
                    emptyLabel = "No blocked tickets in this scope",
                    onTaskSelected = onTaskSelected,
                    onRunQuickAction = onRunQuickAction,
                )
            }
            TwoPanelGrid {
                PanelCard("Objectives in scope") {
                    if (scopedObjectives.isEmpty()) {
                        MutedText("No objectives linked into this scope")
                    } else {
                        scopedObjectives.forEach { objective -> ObjectiveSummaryRow(objective) }
                    }
                }
                PanelCard("Recent activity") {
                    ActivityFeed(
                        entries = recentActivity,
                        emptyLabel = "No recent activity in this scope",
                    )
                }
            }
        }
    }
}

@Composable
private fun SummaryCard(
    label: String,
    value: String,
    detail: String? = null,
    accent: String? = null,
    compact: Boolean = false,
) {
    Div(
        attrs = {
            style {
                props(
                    "background" to Paper,
                    "border" to "1px solid $Border",
                    "border-radius" to "8px",
                    "padding" to if (compact) "12px" else "16px",
                    "display" to "grid",
                    "gap" to if (compact) "2px" else "4px",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style { props("display" to "flex", "align-items" to "center", "gap" to "8px") }
            }
        ) {
            accent?.let { color ->
                Span(
                    attrs = {
                        style {
                            props(
                                "display" to "inline-block",
                                "width" to "10px",
                                "height" to "10px",
                                "border-radius" to "999px",
                                "background" to color,
                            )
                        }
                    }
                )
            }
            Span(
                attrs = {
                    style {
                        props(
                            "font-size" to if (compact) "11px" else "12px",
                            "font-weight" to "700",
                            "color" to Muted,
                        )
                    }
                }
            ) {
                Text(label)
            }
        }
        Span(
            attrs = {
                style {
                    props(
                        "font-size" to if (compact) "24px" else "30px",
                        "font-weight" to "700",
                        "color" to Ink,
                    )
                }
            }
        ) {
            Text(value)
        }
        detail?.let { description ->
            Span(
                attrs = {
                    style {
                        props(
                            "font-size" to if (compact) "11px" else "12px",
                            "color" to Muted,
                            "line-height" to if (compact) "1.3" else "1.4",
                        )
                    }
                }
            ) {
                Text(description)
            }
        }
    }
}

@Composable
private fun NoticeBadge(label: String) {
    Span(
        attrs = {
            style {
                props(
                    "display" to "inline-flex",
                    "align-items" to "center",
                    "height" to "28px",
                    "border-radius" to "999px",
                    "padding" to "0 10px",
                    "background" to "#f2f4f7",
                    "color" to Muted,
                    "font-size" to "12px",
                    "font-weight" to "700",
                )
            }
        }
    ) {
        Text(label)
    }
}

@Composable
private fun ScopeBadge(label: String) {
    Span(
        attrs = {
            style {
                props(
                    "display" to "inline-flex",
                    "align-items" to "center",
                    "height" to "22px",
                    "border-radius" to "999px",
                    "padding" to "0 8px",
                    "background" to "#f5f6f8",
                    "color" to "#98a2b3",
                    "font-size" to "11px",
                    "font-weight" to "600",
                    "white-space" to "nowrap",
                )
            }
        }
    ) {
        Text(label)
    }
}

@Composable
private fun CountBadge(label: String, muted: Boolean = false) {
    Span(
        attrs = {
            style {
                props(
                    "display" to "inline-flex",
                    "align-items" to "center",
                    "justify-content" to "center",
                    "min-width" to "22px",
                    "height" to "20px",
                    "padding" to "0 6px",
                    "border-radius" to "999px",
                    "background" to if (muted) "#f3f5f8" else "#eceef2",
                    "color" to if (muted) "#9ca4b2" else Muted,
                    "font-size" to "11px",
                    "font-weight" to "700",
                )
            }
        }
    ) {
        Text(label)
    }
}

@Composable
private fun ToolbarGroup(label: String? = null, content: @Composable () -> Unit) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "flex",
                    "align-items" to "center",
                    "gap" to "8px",
                    "flex-wrap" to "nowrap",
                    "flex-shrink" to "0",
                    "padding" to "6px 8px",
                    "border" to "1px solid #e4e7ec",
                    "border-radius" to "10px",
                    "background" to Paper,
                )
            }
        }
    ) {
        label?.let {
            Span(
                attrs = {
                    style {
                        props(
                            "padding" to "0 4px 0 2px",
                            "font-size" to "11px",
                            "font-weight" to "700",
                            "letter-spacing" to "0.04em",
                            "text-transform" to "uppercase",
                            "color" to Muted,
                            "white-space" to "nowrap",
                        )
                    }
                }
            ) {
                Text(it)
            }
        }
        content()
    }
}

@Composable
private fun PanelCard(
    title: String,
    tone: WebPanelTone = WebPanelTone.Primary,
    actionLabel: String? = null,
    onAction: (() -> Unit)? = null,
    minHeight: String = "0",
    content: @Composable () -> Unit,
) {
    Div(
        attrs = {
            style {
                props(
                    "background" to if (tone == WebPanelTone.Primary) Paper else "#fbfcfd",
                    "border" to
                        if (tone == WebPanelTone.Primary) {
                            "1px solid $Border"
                        } else {
                            "1px solid #e4e7ec"
                        },
                    "border-radius" to "8px",
                    "padding" to "20px",
                    "display" to "grid",
                    "gap" to "12px",
                    "align-content" to "start",
                    "min-height" to minHeight,
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "justify-content" to "space-between",
                        "gap" to "12px",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "font-size" to if (tone == WebPanelTone.Primary) "20px" else "17px",
                            "font-weight" to if (tone == WebPanelTone.Primary) "700" else "600",
                            "color" to Ink,
                        )
                    }
                }
            ) {
                Text(title)
            }
            if (actionLabel != null && onAction != null) {
                Button(
                    attrs = {
                        onClick { onAction() }
                        style {
                            props(
                                "border" to "0",
                                "background" to "transparent",
                                "color" to Blue,
                                "font-size" to "13px",
                                "font-weight" to "700",
                                "padding" to "0",
                                "cursor" to "pointer",
                            )
                        }
                    }
                ) {
                    Text(actionLabel)
                }
            }
        }
        content()
    }
}

@Composable
private fun CompactTaskRow(
    task: WebTask,
    showProject: Boolean,
    onTaskSelected: (WebTask) -> Unit,
    onRunQuickAction: ((WebTask) -> Unit)? = null,
) {
    val quickAction = task.ticket.quickAction()
    val dueColor =
        when {
            task.ticket.isOverdue() -> Coral
            task.ticket.isDueToday() -> Blue
            else -> Muted
        }
    Div(
        attrs = {
            attr("data-ticket-id", task.ticket.id.value)
            onClick { onTaskSelected(task) }
            style {
                props(
                    "display" to "flex",
                    "justify-content" to "space-between",
                    "align-items" to "start",
                    "gap" to "12px",
                    "padding" to "10px 12px",
                    "border" to "1px solid $Border",
                    "border-left" to "4px solid ${task.project.projectAccent()}",
                    "border-radius" to "8px",
                    "background" to "#fbfcfd",
                    "cursor" to "pointer",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props("display" to "grid", "gap" to "4px", "min-width" to "0", "flex" to "1")
                }
            }
        ) {
            if (showProject) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "align-items" to "center",
                                "gap" to "8px",
                                "font-size" to "11px",
                                "font-weight" to "700",
                                "letter-spacing" to "0.02em",
                                "text-transform" to "uppercase",
                                "color" to Muted,
                            )
                        }
                    }
                ) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "width" to "8px",
                                    "height" to "8px",
                                    "border-radius" to "999px",
                                    "background" to task.project.projectAccent(),
                                    "flex-shrink" to "0",
                                )
                            }
                        }
                    )
                    Text(task.project.name)
                }
            }
            Div(
                attrs = {
                    style {
                        props(
                            "font-size" to "14px",
                            "font-weight" to "600",
                            "line-height" to "1.35",
                            "overflow" to "hidden",
                            "display" to "-webkit-box",
                            "-webkit-line-clamp" to "2",
                            "-webkit-box-orient" to "vertical",
                        )
                    }
                }
            ) {
                Text(task.ticket.title)
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "align-items" to "center",
                            "gap" to "8px",
                            "flex-wrap" to "wrap",
                            "font-size" to "12px",
                            "color" to Muted,
                        )
                    }
                }
            ) {
                task.ticket.priority?.let { priority ->
                    MetaChip(
                        priority.uiLabel(),
                        taskTint(priority.priorityColor()),
                        priority.priorityColor(),
                    )
                }
                Span { Text(task.owner?.displayName ?: "Unassigned") }
                Span { Text("•") }
                Span(
                    attrs = {
                        style {
                            props(
                                "color" to dueColor,
                                "font-weight" to if (dueColor == Muted) "600" else "700",
                            )
                        }
                    }
                ) {
                    Text(task.ticket.dueLabel())
                }
                if (task.objectiveCount > 0) {
                    MetaChip(
                        "${task.objectiveCount} objective${if (task.objectiveCount == 1) "" else "s"}",
                        "#eef8f6",
                        Teal,
                    )
                }
                task.blockerLabel?.let { blockerLabel -> BlockerChip(blockerLabel) }
            }
        }
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "gap" to "8px",
                        "flex-shrink" to "0",
                        "margin-left" to "auto",
                    )
                }
            }
        ) {
            if (quickAction != null && onRunQuickAction != null) {
                Button(
                    attrs = {
                        attr("data-ticket-quick-action", task.ticket.id.value)
                        onClick {
                            it.stopPropagation()
                            onRunQuickAction(task)
                        }
                        style { quickTaskButton(tone = quickAction.tone) }
                    }
                ) {
                    Text(quickAction.label)
                }
            }
            if (quickAction == null) {
                TaskStatusInline(task.status, task.statusColor)
            }
        }
    }
}

private fun Kairos.Ticket.quickAction(): WebTaskQuickAction? =
    when {
        completedAt != null -> null
        stage == Kairos.TicketStage.Inbox ->
            WebTaskQuickAction(
                label = "Plan",
                stage = Kairos.TicketStage.Todo,
                tone = WebTaskQuickActionTone.Neutral,
            )
        stage == Kairos.TicketStage.Todo ->
            WebTaskQuickAction(
                label = "Start",
                stage = Kairos.TicketStage.InProgress,
                tone = WebTaskQuickActionTone.Primary,
            )
        stage == Kairos.TicketStage.InProgress ->
            WebTaskQuickAction(
                label = "Review",
                stage = Kairos.TicketStage.Review,
                tone = WebTaskQuickActionTone.Review,
            )
        stage == Kairos.TicketStage.Review ->
            WebTaskQuickAction(
                label = "Mark done",
                markCompleted = true,
                tone = WebTaskQuickActionTone.Success,
            )
        else -> null
    }

@Composable
private fun DailySchedulePanel(workspace: WebWorkspace, dailyPlan: KairosDailyPlan) {
    if (dailyPlan.timeboxesToday.isEmpty() && dailyPlan.eventsToday.isEmpty()) {
        val upcomingItems = workspace.upcomingScheduleItems(from = dailyPlan.date)
        if (upcomingItems.isEmpty()) {
            MutedText("No scheduled events or timeboxes today")
            return
        }
        MutedText("Nothing scheduled today. Next up:")
        upcomingItems.forEach { item ->
            SchedulePlanRow(
                kind = item.kind,
                title = item.title,
                detail = item.detail,
                accent = item.accent,
            )
        }
        return
    }
    if (dailyPlan.timeboxesToday.isNotEmpty()) {
        SchedulePlanGroup(label = "Timeboxes", accent = Teal) {
            dailyPlan.timeboxesToday.forEach { timebox ->
                SchedulePlanRow(
                    kind = "Timebox",
                    title = timebox.description,
                    detail =
                        listOfNotNull(
                                timebox.projectId?.let { projectId ->
                                    workspace.projectName(projectId)
                                },
                                formatInstantTime(timebox.startTime),
                                timebox.recordedDuration?.spans?.joinToString(" ") { span ->
                                    "${span.value}${span.unit.shortLabel()}"
                                },
                            )
                            .joinToString(" - "),
                    accent = Teal,
                )
            }
        }
    }
    if (dailyPlan.eventsToday.isNotEmpty()) {
        SchedulePlanGroup(label = "Events", accent = Blue) {
            dailyPlan.eventsToday.forEach { event ->
                SchedulePlanRow(
                    kind = "Event",
                    title = event.title,
                    detail =
                        listOfNotNull(
                                event.projectId?.let { projectId ->
                                    workspace.projectName(projectId)
                                },
                                formatEventTimeRange(event),
                            )
                            .joinToString(" - "),
                    accent = Blue,
                )
            }
        }
    }
}

@Composable
private fun SchedulePlanRow(kind: String, title: String, detail: String, accent: String) {
    Div(attrs = { style { props("display" to "grid", "gap" to "4px") } }) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "justify-content" to "space-between",
                        "gap" to "12px",
                    )
                }
            }
        ) {
            Span(
                attrs = {
                    style {
                        props(
                            "font-size" to "14px",
                            "font-weight" to "600",
                            "color" to Ink,
                            "min-width" to "0",
                            "overflow" to "hidden",
                            "text-overflow" to "ellipsis",
                            "white-space" to "nowrap",
                        )
                    }
                }
            ) {
                Text(title)
            }
            Chip(kind, accent, Ink)
        }
        Span(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) { Text(detail) }
    }
}

@Composable
private fun SchedulePlanGroup(label: String, accent: String, content: @Composable () -> Unit) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "grid",
                    "gap" to "10px",
                    "padding" to "12px",
                    "border" to "1px solid $Border",
                    "border-radius" to "8px",
                    "background" to "#fbfcfd",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style { props("display" to "flex", "align-items" to "center", "gap" to "8px") }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "width" to "8px",
                            "height" to "8px",
                            "border-radius" to "999px",
                            "background" to accent,
                            "flex-shrink" to "0",
                        )
                    }
                }
            )
            Span(
                attrs = {
                    style { props("font-size" to "12px", "font-weight" to "700", "color" to Muted) }
                }
            ) {
                Text(label)
            }
        }
        Div(attrs = { style { props("display" to "grid", "gap" to "10px") } }) { content() }
    }
}

@Composable
private fun RoutinePlanRow(
    workspace: WebWorkspace,
    routine: Kairos.Routine,
    onOpen: (() -> Unit)? = null,
) {
    Div(
        attrs = {
            if (onOpen != null) {
                onClick { onOpen() }
            }
            style {
                props(
                    "display" to "grid",
                    "gap" to "4px",
                    "cursor" to if (onOpen != null) "pointer" else "default",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "justify-content" to "space-between",
                        "gap" to "12px",
                    )
                }
            }
        ) {
            Span(
                attrs = {
                    style { props("font-size" to "14px", "font-weight" to "600", "color" to Ink) }
                }
            ) {
                Text(routine.description)
            }
            if (onOpen != null) {
                Span(attrs = { style { props("font-size" to "14px", "color" to Blue) } }) {
                    Text(">")
                }
            }
        }
        Span(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
            Text(
                routine.projectId?.let { projectId -> workspace.projectName(projectId) }
                    ?: "Workspace routine"
            )
        }
    }
}

@Composable
private fun ObjectivePressureRow(
    workspace: WebWorkspace,
    pressure: KairosObjectivePressure,
    onOpen: (() -> Unit)? = null,
) {
    val objective = pressure.objective
    Div(
        attrs = {
            if (onOpen != null) {
                onClick { onOpen() }
            }
            style {
                props(
                    "display" to "grid",
                    "gap" to "4px",
                    "cursor" to if (onOpen != null) "pointer" else "default",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "justify-content" to "space-between",
                        "gap" to "12px",
                    )
                }
            }
        ) {
            Span(
                attrs = {
                    style { props("font-size" to "14px", "font-weight" to "600", "color" to Ink) }
                }
            ) {
                Text(objective.description)
            }
            if (onOpen != null) {
                Span(attrs = { style { props("font-size" to "14px", "color" to Blue) } }) {
                    Text(">")
                }
            }
        }
        Span(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
            Text(
                listOfNotNull(
                        objective.projectId?.let { projectId -> workspace.projectName(projectId) },
                        objective.deadline?.let(::formatDeadline),
                        "${pressure.openTicketCount} open tickets",
                        pressure.offTargetKpiCount
                            .takeIf { count -> count > 0 }
                            ?.let { count -> "$count KPI off target" },
                        pressure.missingMeasurementCount
                            .takeIf { count -> count > 0 }
                            ?.let { count -> "$count KPI missing" },
                    )
                    .joinToString(" - ")
            )
        }
    }
}

@Composable
private fun ObjectiveSummaryRow(summary: WebObjectiveSummary, onOpen: (() -> Unit)? = null) {
    Div(
        attrs = {
            if (onOpen != null) {
                onClick { onOpen() }
            }
            style {
                props(
                    "display" to "grid",
                    "gap" to "4px",
                    "padding-left" to "10px",
                    "border-left" to "3px solid ${summary.statusColor}",
                    "cursor" to if (onOpen != null) "pointer" else "default",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "justify-content" to "space-between",
                        "align-items" to "center",
                        "gap" to "12px",
                    )
                }
            }
        ) {
            Span(
                attrs = {
                    style { props("font-size" to "14px", "font-weight" to "600", "color" to Ink) }
                }
            ) {
                Text(summary.description)
            }
            Div(
                attrs = {
                    style { props("display" to "flex", "align-items" to "center", "gap" to "8px") }
                }
            ) {
                StatusPill(summary.statusLabel, summary.statusColor, summary.statusBackground)
                if (onOpen != null) {
                    Span(attrs = { style { props("font-size" to "14px", "color" to Blue) } }) {
                        Text(">")
                    }
                }
            }
        }
        Span(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
            Text(
                listOfNotNull(
                        summary.projectName.takeIf { it.isNotBlank() && it != "No project" },
                        summary.deadlineLabel.takeIf { it != "No deadline" },
                        "${summary.linkedTasks.size} linked tickets",
                    )
                    .joinToString(" - ")
            )
        }
    }
}

@Composable
private fun TaskSection(
    label: String,
    tasks: List<WebTask>,
    emptyLabel: String,
    onTaskSelected: (WebTask) -> Unit,
    onRunQuickAction: (WebTask) -> Unit,
) {
    Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "justify-content" to "space-between",
                        "gap" to "8px",
                        "flex-wrap" to "wrap",
                    )
                }
            }
        ) {
            Span(
                attrs = {
                    style { props("font-size" to "12px", "font-weight" to "700", "color" to Muted) }
                }
            ) {
                Text(label)
            }
            if (tasks.isNotEmpty()) {
                ScopeBadge("${tasks.size} open")
            }
        }
        if (tasks.isEmpty()) {
            MutedText(emptyLabel)
        } else {
            val groupedTasks =
                tasks
                    .groupBy { task -> task.project }
                    .toList()
                    .sortedWith(
                        compareByDescending<Pair<Kairos.Project, List<WebTask>>> { (_, grouped) ->
                                grouped.size
                            }
                            .thenBy { (project, _) -> project.name.lowercase() }
                    )
            groupedTasks.forEach { (project, groupedTasksForProject) ->
                Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "align-items" to "center",
                                    "gap" to "8px",
                                    "padding-top" to "4px",
                                )
                            }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "width" to "10px",
                                        "height" to "10px",
                                        "border-radius" to "999px",
                                        "background" to project.projectAccent(),
                                        "flex-shrink" to "0",
                                    )
                                }
                            }
                        )
                        Span(
                            attrs = {
                                style {
                                    props(
                                        "font-size" to "12px",
                                        "font-weight" to "700",
                                        "color" to Ink,
                                    )
                                }
                            }
                        ) {
                            Text(project.name)
                        }
                        Span(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                            Text(groupedTasksForProject.size.toString())
                        }
                    }
                    groupedTasksForProject.forEach { task ->
                        CompactTaskRow(
                            task = task,
                            showProject = false,
                            onTaskSelected = onTaskSelected,
                            onRunQuickAction = onRunQuickAction,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ActivityFeed(entries: List<WebActivityEntry>, emptyLabel: String) {
    if (entries.isEmpty()) {
        PanelEmptyState(
            emptyLabel,
            "Routing changes, assignment moves, and status updates will appear here.",
        )
        return
    }
    entries.forEachIndexed { index, activity ->
        Div(
            attrs = {
                attr("data-activity-id", activity.activityId.value)
                attr("data-activity-ticket-id", activity.ticketId.value)
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "flex-start",
                        "gap" to "12px",
                        "padding-top" to if (index == 0) "0" else "12px",
                        "border-top" to if (index == 0) "0" else "1px solid $Border",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "width" to "28px",
                            "height" to "28px",
                            "border-radius" to "999px",
                            "background" to activityAvatarColor(activity.actorName),
                            "color" to Ink,
                            "font-size" to "11px",
                            "font-weight" to "700",
                            "display" to "grid",
                            "place-items" to "center",
                            "flex-shrink" to "0",
                        )
                    }
                }
            ) {
                Text(activityInitials(activity.actorName))
            }
            Div(
                attrs = { style { props("display" to "grid", "gap" to "4px", "min-width" to "0") } }
            ) {
                Span(
                    attrs = {
                        style {
                            props(
                                "font-size" to "14px",
                                "font-weight" to "700",
                                "color" to Ink,
                                "overflow" to "hidden",
                                "text-overflow" to "ellipsis",
                                "white-space" to "nowrap",
                            )
                        }
                    }
                ) {
                    Text(activity.ticketTitle)
                }
                Span(
                    attrs = {
                        style {
                            props(
                                "font-size" to "13px",
                                "font-weight" to "600",
                                "color" to Muted,
                                "overflow" to "hidden",
                                "text-overflow" to "ellipsis",
                                "white-space" to "nowrap",
                            )
                        }
                    }
                ) {
                    Text(activity.summary)
                }
                Span(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                    Text("${activity.actorName} • ${activity.createdAtLabel}")
                }
            }
        }
    }
}

@Composable
private fun MutedText(label: String) {
    Span(attrs = { style { props("font-size" to "14px", "color" to Muted) } }) { Text(label) }
}

@Composable
private fun PanelEmptyState(label: String, detail: String? = null) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "grid",
                    "align-content" to "center",
                    "gap" to "10px",
                    "min-height" to "156px",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "width" to "30px",
                        "height" to "30px",
                        "border-radius" to "999px",
                        "background" to "#eef1f5",
                        "display" to "grid",
                        "place-items" to "center",
                        "color" to "#667085",
                        "font-size" to "12px",
                        "font-weight" to "700",
                    )
                }
            }
        ) {
            Text("i")
        }
        Div(
            attrs = {
                style { props("font-size" to "15px", "font-weight" to "700", "color" to Ink) }
            }
        ) {
            Text(label)
        }
        detail?.let { message ->
            Div(
                attrs = {
                    style {
                        props("font-size" to "13px", "line-height" to "1.45", "color" to Muted)
                    }
                }
            ) {
                Text(message)
            }
        }
    }
}

@Composable
private fun TableHeader() {
    Div(
        attrs = {
            style {
                props(
                    "height" to "38px",
                    "display" to "grid",
                    "grid-template-columns" to
                        "minmax(320px,1.7fr) minmax(150px,0.9fr) 110px minmax(170px,1fr) auto",
                    "align-items" to "center",
                    "gap" to "16px",
                    "padding" to "0 28px",
                    "border-bottom" to "1px solid $Border",
                    "color" to Muted,
                    "font-size" to "12px",
                    "font-weight" to "700",
                    "text-transform" to "uppercase",
                )
            }
        }
    ) {
        listOf("Title", "Assignee", "Due", "State", "Next").forEach { Span { Text(it) } }
    }
}

@Composable
private fun TimelineView(
    workspace: WebWorkspace,
    columns: List<WebColumn>,
    compact: Boolean,
    onTaskSelected: (WebTask) -> Unit,
    onScheduleEntrySelected: (WebScheduleEntry) -> Unit,
) {
    val scheduledEntries = workspace.scheduleEntries(columns)
    val unscheduledTasks =
        columns.flatMap { column -> column.tasks }.filter { task -> task.ticket.deadline == null }
    val schedule = buildTimelineSchedule(scheduledEntries)
    val timelineRows =
        remember(columns, scheduledEntries, schedule) {
            schedule
                ?.let { currentSchedule ->
                    buildTimelineRows(columns, scheduledEntries, currentSchedule)
                }
                .orEmpty()
        }
    LaunchedEffect(compact, schedule?.focusDayIndex, schedule?.days?.size) {
        if (compact) return@LaunchedEffect
        val currentSchedule = schedule ?: return@LaunchedEffect
        window.setTimeout(
            {
                val container = document.getElementById("timeline-scroll-container") as? HTMLElement
                if (container != null) {
                    val focusDay = (currentSchedule.focusDayIndex - 2).coerceAtLeast(0)
                    val scrollLeft = 210 + (focusDay * 76)
                    container.asDynamic().scrollLeft = scrollLeft
                }
            },
            0,
        )
    }
    Div(
        attrs = {
            attr("id", "timeline-scroll-container")
            style {
                props(
                    "flex" to "1",
                    "overflow" to "auto",
                    "background" to "#fbfcfd",
                    "padding" to if (compact) "16px" else "24px",
                    "box-sizing" to "border-box",
                )
            }
        }
    ) {
        if (unscheduledTasks.isNotEmpty()) {
            UnscheduledTaskStrip(tasks = unscheduledTasks, onTaskSelected = onTaskSelected)
        }
        if (schedule == null) {
            Div(
                attrs = {
                    style {
                        props(
                            "border" to "1px solid $Border",
                            "border-radius" to "8px",
                            "background" to Paper,
                            "padding" to "18px",
                            "color" to Muted,
                            "font-size" to "14px",
                        )
                    }
                }
            ) {
                Text("No scheduled tickets in this view")
            }
            return@Div
        }
        val today = todayDate()
        if (compact) {
            val groupedEntries =
                remember(scheduledEntries) {
                    scheduledEntries
                        .sortedBySchedule()
                        .groupBy { entry -> entry.startDate }
                        .toList()
                        .sortedBy { (date, _) -> date.toEpochDays() }
                }
            Div(attrs = { style { props("display" to "grid", "gap" to "12px") } }) {
                groupedEntries.forEach { (date, entries) ->
                    Div(
                        attrs = {
                            style {
                                props(
                                    "border" to "1px solid $Border",
                                    "border-radius" to "8px",
                                    "background" to Paper,
                                    "padding" to "14px",
                                    "display" to "grid",
                                    "gap" to "10px",
                                )
                            }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "flex",
                                        "justify-content" to "space-between",
                                        "align-items" to "baseline",
                                        "gap" to "8px",
                                        "flex-wrap" to "wrap",
                                    )
                                }
                            }
                        ) {
                            Span(
                                attrs = {
                                    style {
                                        props(
                                            "font-size" to "15px",
                                            "font-weight" to "700",
                                            "color" to if (date == today) Blue else Ink,
                                        )
                                    }
                                }
                            ) {
                                Text(
                                    "${weekdayLabel(date)} ${monthLabel(date.month.ordinal + 1)} ${date.day}"
                                )
                            }
                            Span(
                                attrs = { style { props("font-size" to "12px", "color" to Muted) } }
                            ) {
                                Text("${entries.size} item${if (entries.size == 1) "" else "s"}")
                            }
                        }
                        entries.forEach { entry ->
                            TimelineAgendaEntryCard(
                                workspace = workspace,
                                entry = entry,
                                muted = entry.shouldMuteOnCalendar(today),
                                onTaskSelected = onTaskSelected,
                                onScheduleEntrySelected = onScheduleEntrySelected,
                            )
                        }
                    }
                }
            }
            return@Div
        }
        val dayColumnWidth = 76
        val projectColumnWidth = 220
        Div(
            attrs = {
                style {
                    props(
                        "min-width" to
                            "${projectColumnWidth + (schedule.days.size * dayColumnWidth)}px",
                        "display" to "grid",
                        "grid-template-columns" to
                            "${projectColumnWidth}px repeat(${schedule.days.size}, ${dayColumnWidth}px)",
                        "grid-auto-rows" to "minmax(56px, auto)",
                        "border-top" to "1px solid $Border",
                        "border-left" to "1px solid $Border",
                        "background" to Paper,
                    )
                }
            }
        ) {
            Div(attrs = { style { timelineCell(true) } })
            schedule.monthSpans.forEach { span ->
                Div(
                    attrs = {
                        style {
                            timelineCell(true)
                            props("grid-column" to "span ${span.dayCount}")
                        }
                    }
                ) {
                    Text(span.label)
                }
            }
            Div(attrs = { style { timelineCell(true) } }) { Text("Project") }
            schedule.days.forEach { date ->
                Div(attrs = { style { timelineCell(true) } }) {
                    Div { Text(date.day.toString()) }
                    Div(
                        attrs = {
                            style {
                                props(
                                    "font-size" to "11px",
                                    "color" to Muted,
                                    "margin-top" to "2px",
                                )
                            }
                        }
                    ) {
                        Text(weekdayLabel(date))
                    }
                }
            }
            timelineRows.forEach { row ->
                val rowHeight = (row.laneCount * 38) + 20
                Div(
                    attrs = {
                        style {
                            timelineCell(false)
                            props(
                                "min-height" to "${rowHeight}px",
                                "position" to "sticky",
                                "left" to "0",
                                "z-index" to "2",
                                "background" to "#fbfcfd",
                            )
                        }
                    }
                ) {
                    Div(attrs = { style { props("display" to "grid", "gap" to "4px") } }) {
                        Text(row.project.name)
                        Span(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                            Text(
                                "${row.placedEntries.size} scheduled item${if (row.placedEntries.size == 1) "" else "s"}"
                            )
                        }
                    }
                }
                Div(
                    attrs = {
                        style {
                            props(
                                "grid-column" to "span ${schedule.days.size}",
                                "position" to "relative",
                                "min-height" to "${rowHeight}px",
                                "border-right" to "1px solid $Border",
                                "border-bottom" to "1px solid $Border",
                                "background" to Paper,
                                "overflow" to "hidden",
                            )
                        }
                    }
                ) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "position" to "absolute",
                                    "inset" to "0",
                                    "display" to "grid",
                                    "grid-template-columns" to
                                        "repeat(${schedule.days.size}, ${dayColumnWidth}px)",
                                    "pointer-events" to "none",
                                )
                            }
                        }
                    ) {
                        schedule.days.forEach { date ->
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "border-right" to "1px solid $Border",
                                            "background" to
                                                when {
                                                    date == today -> "#f5f8ff"
                                                    date.dayOfWeek.ordinal >= 5 -> "#fafbfd"
                                                    else -> "transparent"
                                                },
                                        )
                                    }
                                }
                            )
                        }
                    }
                    row.placedEntries.forEach { placed ->
                        val muted = placed.entry.shouldMuteOnCalendar(today)
                        val blocked = placed.entry.blockerLabel != null
                        val left = (placed.startDayIndex * dayColumnWidth) + 6
                        val spanDays =
                            (placed.endDayIndex - placed.startDayIndex + 1).coerceAtLeast(1)
                        val width = ((spanDays * dayColumnWidth) - 12).coerceAtLeast(64)
                        val top = 10 + (placed.laneIndex * 38)
                        Div(
                            attrs = {
                                if (placed.entry.clickableTask != null) {
                                    onClick { onTaskSelected(placed.entry.clickableTask) }
                                } else {
                                    onClick { onScheduleEntrySelected(placed.entry) }
                                }
                                style {
                                    props(
                                        "position" to "absolute",
                                        "left" to "${left}px",
                                        "top" to "${top}px",
                                        "width" to "${width}px",
                                        "min-height" to "28px",
                                        "display" to "grid",
                                        "gap" to "2px",
                                        "align-content" to "center",
                                        "box-sizing" to "border-box",
                                        "background" to
                                            if (muted) "#f4f6f8" else placed.entry.calendarAccent(),
                                        "color" to if (muted) Muted else Paper,
                                        "border" to
                                            if (muted) {
                                                "1px solid #dde2e8"
                                            } else if (blocked) {
                                                "2px solid $Coral"
                                            } else {
                                                "1px solid ${placed.entry.calendarAccent()}"
                                            },
                                        "border-radius" to "7px",
                                        "padding" to "6px 8px",
                                        "font-size" to "11px",
                                        "font-weight" to "700",
                                        "line-height" to "1.25",
                                        "cursor" to
                                            if (
                                                placed.entry.clickableTask != null ||
                                                    placed.entry.kind != WebScheduleEntryKind.Ticket
                                            ) {
                                                "pointer"
                                            } else {
                                                "default"
                                            },
                                        "overflow" to "hidden",
                                        "box-shadow" to
                                            if (muted) "none" else "0 1px 3px rgba(26,28,32,0.10)",
                                    )
                                }
                            }
                        ) {
                            if (placed.entry.kind != WebScheduleEntryKind.Ticket) {
                                Span(
                                    attrs = {
                                        style {
                                            props(
                                                "font-size" to "9px",
                                                "font-weight" to "700",
                                                "letter-spacing" to "0",
                                                "opacity" to if (muted) "1" else "0.84",
                                            )
                                        }
                                    }
                                ) {
                                    Text(placed.entry.kind.label())
                                }
                            }
                            placed.entry.blockerLabel?.let { label ->
                                Span(
                                    attrs = {
                                        style {
                                            props(
                                                "font-size" to "9px",
                                                "font-weight" to "700",
                                                "letter-spacing" to "0",
                                                "opacity" to if (muted) "1" else "0.9",
                                            )
                                        }
                                    }
                                ) {
                                    Text(label)
                                }
                            }
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "overflow" to "hidden",
                                            "text-overflow" to "ellipsis",
                                            "white-space" to "nowrap",
                                        )
                                    }
                                }
                            ) {
                                Text(placed.entry.title)
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CalendarView(
    workspace: WebWorkspace,
    columns: List<WebColumn>,
    compact: Boolean,
    onTaskSelected: (WebTask) -> Unit,
    onScheduleEntrySelected: (WebScheduleEntry) -> Unit,
) {
    val tasks = columns.flatMap { it.tasks }
    val scheduledEntries = workspace.scheduleEntries(columns)
    val unscheduledTasks = tasks.filter { task -> task.ticket.deadline == null }
    val months = buildCalendarMonths(scheduledEntries)
    val weeks = buildCalendarWeeks(scheduledEntries)
    val today = todayDate()
    val todayMonthIndex = remember(months, today) { initialCalendarMonthIndex(months, today) }
    val todayWeekIndex = remember(weeks, today) { initialCalendarWeekIndex(weeks, today) }
    var mode by remember(compact) { mutableStateOf(WebCalendarMode.Week) }
    var showUnscheduled by
        remember(unscheduledTasks.map { it.ticket.id.value }) {
            mutableStateOf(unscheduledTasks.size <= 5)
        }
    var visibleMonthIndex by remember(months, todayMonthIndex) { mutableStateOf(todayMonthIndex) }
    var visibleWeekIndex by remember(weeks, todayWeekIndex) { mutableStateOf(todayWeekIndex) }
    val visibleMonth = months.getOrNull(visibleMonthIndex)
    val visibleWeek = weeks.getOrNull(visibleWeekIndex)
    val visibleEntries =
        remember(mode, visibleMonth, visibleWeek, scheduledEntries) {
            when (mode) {
                WebCalendarMode.Month ->
                    visibleMonth?.let { month ->
                        scheduledEntries.filter { entry ->
                            entry.endDate >= month.startDate && entry.startDate <= month.endDate
                        }
                    }
                WebCalendarMode.Week ->
                    visibleWeek?.let { week ->
                        scheduledEntries.filter { entry ->
                            entry.endDate >= week.startDate && entry.startDate <= week.endDate
                        }
                    }
            }.orEmpty()
        }
    val visibleCompletedCount =
        remember(visibleEntries) {
            visibleEntries.count { entry ->
                entry.kind == WebScheduleEntryKind.Ticket && entry.completed
            }
        }
    val visiblePastCount =
        remember(visibleEntries, today) {
            visibleEntries.count { entry ->
                entry.kind != WebScheduleEntryKind.Ticket && entry.endDate < today
            }
        }
    val scheduleSummary =
        buildList {
                add("${visibleEntries.size} scheduled")
                if (visibleCompletedCount > 0) add("$visibleCompletedCount completed")
                if (visiblePastCount > 0) add("$visiblePastCount past")
                if (unscheduledTasks.isNotEmpty()) {
                    add("${unscheduledTasks.size} unscheduled")
                }
            }
            .joinToString(" · ")
    val visibleLabel =
        when (mode) {
            WebCalendarMode.Month -> visibleMonth?.label.orEmpty()
            WebCalendarMode.Week -> visibleWeek?.label.orEmpty()
        }

    Div(
        attrs = {
            style {
                props(
                    "flex" to "1",
                    "overflow" to "auto",
                    "background" to Paper,
                    "padding" to if (compact) "16px" else "24px",
                    "box-sizing" to "border-box",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style { props("display" to "grid", "gap" to "24px", "align-content" to "start") }
            }
        ) {
            if (months.isEmpty() || weeks.isEmpty()) {
                Div(
                    attrs = {
                        style {
                            props(
                                "border" to "1px solid $Border",
                                "border-radius" to "8px",
                                "background" to "#fbfcfd",
                                "padding" to "18px",
                                "color" to Muted,
                                "font-size" to "14px",
                            )
                        }
                    }
                ) {
                    Text("No calendar dates in this view")
                }
                return@Div
            }
            if (compact) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "grid",
                                "gap" to "10px",
                                "position" to "sticky",
                                "top" to "0",
                                "z-index" to "2",
                                "padding" to "0 0 10px",
                                "background" to Paper,
                            )
                        }
                    }
                ) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "align-items" to "center",
                                    "justify-content" to "space-between",
                                    "gap" to "8px",
                                )
                            }
                        }
                    ) {
                        Button(
                            attrs = {
                                onClick {
                                    when (mode) {
                                        WebCalendarMode.Month ->
                                            visibleMonthIndex =
                                                (visibleMonthIndex - 1).coerceAtLeast(0)
                                        WebCalendarMode.Week ->
                                            visibleWeekIndex =
                                                (visibleWeekIndex - 1).coerceAtLeast(0)
                                    }
                                }
                                if (
                                    (mode == WebCalendarMode.Month && visibleMonthIndex <= 0) ||
                                        (mode == WebCalendarMode.Week && visibleWeekIndex <= 0)
                                ) {
                                    disabled()
                                }
                                attr("aria-label", "Previous")
                                style { toolbarButton() }
                            }
                        ) {
                            Text("←")
                        }
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "grid",
                                        "gap" to "2px",
                                        "justify-items" to "center",
                                        "min-width" to "0",
                                        "flex" to "1",
                                    )
                                }
                            }
                        ) {
                            Span(
                                attrs = {
                                    style {
                                        props(
                                            "font-size" to "18px",
                                            "font-weight" to "700",
                                            "color" to Ink,
                                        )
                                    }
                                }
                            ) {
                                Text(visibleLabel)
                            }
                            Span(
                                attrs = { style { props("font-size" to "11px", "color" to Muted) } }
                            ) {
                                Text(scheduleSummary)
                            }
                        }
                        Button(
                            attrs = {
                                onClick {
                                    when (mode) {
                                        WebCalendarMode.Month ->
                                            visibleMonthIndex =
                                                (visibleMonthIndex + 1).coerceAtMost(
                                                    months.lastIndex
                                                )
                                        WebCalendarMode.Week ->
                                            visibleWeekIndex =
                                                (visibleWeekIndex + 1).coerceAtMost(weeks.lastIndex)
                                    }
                                }
                                if (
                                    (mode == WebCalendarMode.Month &&
                                        visibleMonthIndex >= months.lastIndex) ||
                                        (mode == WebCalendarMode.Week &&
                                            visibleWeekIndex >= weeks.lastIndex)
                                ) {
                                    disabled()
                                }
                                attr("aria-label", "Next")
                                style { toolbarButton() }
                            }
                        ) {
                            Text("→")
                        }
                    }
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "align-items" to "center",
                                    "justify-content" to "space-between",
                                    "gap" to "8px",
                                    "flex-wrap" to "wrap",
                                )
                            }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "flex",
                                        "align-items" to "center",
                                        "gap" to "8px",
                                    )
                                }
                            }
                        ) {
                            Button(
                                attrs = {
                                    onClick { mode = WebCalendarMode.Month }
                                    style {
                                        calendarModeButton(selected = mode == WebCalendarMode.Month)
                                    }
                                }
                            ) {
                                Text("Month")
                            }
                            Button(
                                attrs = {
                                    onClick { mode = WebCalendarMode.Week }
                                    style {
                                        calendarModeButton(selected = mode == WebCalendarMode.Week)
                                    }
                                }
                            ) {
                                Text("Week")
                            }
                        }
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "flex",
                                        "align-items" to "center",
                                        "gap" to "8px",
                                        "margin-left" to "auto",
                                    )
                                }
                            }
                        ) {
                            if (unscheduledTasks.isNotEmpty()) {
                                Button(
                                    attrs = {
                                        attr("id", "web-calendar-toggle-unscheduled-button")
                                        onClick { showUnscheduled = !showUnscheduled }
                                        style { toolbarButton(selected = showUnscheduled) }
                                    }
                                ) {
                                    Text("Unscheduled ${unscheduledTasks.size}")
                                }
                            }
                            Button(
                                attrs = {
                                    onClick {
                                        visibleMonthIndex = todayMonthIndex
                                        visibleWeekIndex = todayWeekIndex
                                    }
                                    if (
                                        (mode == WebCalendarMode.Month &&
                                            visibleMonthIndex == todayMonthIndex) ||
                                            (mode == WebCalendarMode.Week &&
                                                visibleWeekIndex == todayWeekIndex)
                                    ) {
                                        disabled()
                                    }
                                    style { toolbarButton(selected = true) }
                                }
                            ) {
                                Text("Today")
                            }
                        }
                    }
                }
            } else {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "justify-content" to "space-between",
                                "align-items" to "center",
                                "gap" to "14px",
                                "flex-wrap" to "wrap",
                            )
                        }
                    }
                ) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "align-items" to "center",
                                    "gap" to "12px",
                                    "flex-wrap" to "wrap",
                                )
                            }
                        }
                    ) {
                        Div(attrs = { style { props("display" to "grid", "gap" to "4px") } }) {
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "font-size" to "20px",
                                            "font-weight" to "700",
                                            "color" to Ink,
                                        )
                                    }
                                }
                            ) {
                                Text(visibleLabel)
                            }
                            Span(
                                attrs = {
                                    style {
                                        props(
                                            "font-size" to "12px",
                                            "line-height" to "1.35",
                                            "color" to Muted,
                                        )
                                    }
                                }
                            ) {
                                Text(scheduleSummary)
                            }
                        }
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "flex",
                                        "align-items" to "center",
                                        "gap" to "8px",
                                        "flex-wrap" to "wrap",
                                    )
                                }
                            }
                        ) {
                            Button(
                                attrs = {
                                    onClick { mode = WebCalendarMode.Month }
                                    style {
                                        calendarModeButton(selected = mode == WebCalendarMode.Month)
                                    }
                                }
                            ) {
                                Text("Month")
                            }
                            Button(
                                attrs = {
                                    onClick { mode = WebCalendarMode.Week }
                                    style {
                                        calendarModeButton(selected = mode == WebCalendarMode.Week)
                                    }
                                }
                            ) {
                                Text("Week")
                            }
                        }
                    }
                    Div(
                        attrs = {
                            style {
                                props(
                                    "display" to "flex",
                                    "align-items" to "center",
                                    "gap" to "10px",
                                    "flex-wrap" to "wrap",
                                    "margin-left" to "auto",
                                )
                            }
                        }
                    ) {
                        if (unscheduledTasks.isNotEmpty()) {
                            Button(
                                attrs = {
                                    attr("id", "web-calendar-toggle-unscheduled-button")
                                    onClick { showUnscheduled = !showUnscheduled }
                                    style { toolbarButton(selected = showUnscheduled) }
                                }
                            ) {
                                Text("Unscheduled · ${unscheduledTasks.size}")
                            }
                        }
                        Button(
                            attrs = {
                                onClick {
                                    when (mode) {
                                        WebCalendarMode.Month ->
                                            visibleMonthIndex =
                                                (visibleMonthIndex - 1).coerceAtLeast(0)
                                        WebCalendarMode.Week ->
                                            visibleWeekIndex =
                                                (visibleWeekIndex - 1).coerceAtLeast(0)
                                    }
                                }
                                if (
                                    (mode == WebCalendarMode.Month && visibleMonthIndex <= 0) ||
                                        (mode == WebCalendarMode.Week && visibleWeekIndex <= 0)
                                ) {
                                    disabled()
                                }
                                attr("aria-label", "Previous")
                                style { toolbarButton() }
                            }
                        ) {
                            Text("←")
                        }
                        Button(
                            attrs = {
                                onClick {
                                    visibleMonthIndex = todayMonthIndex
                                    visibleWeekIndex = todayWeekIndex
                                }
                                if (
                                    (mode == WebCalendarMode.Month &&
                                        visibleMonthIndex == todayMonthIndex) ||
                                        (mode == WebCalendarMode.Week &&
                                            visibleWeekIndex == todayWeekIndex)
                                ) {
                                    disabled()
                                }
                                style { toolbarButton(selected = true) }
                            }
                        ) {
                            Text("Today")
                        }
                        Button(
                            attrs = {
                                onClick {
                                    when (mode) {
                                        WebCalendarMode.Month ->
                                            visibleMonthIndex =
                                                (visibleMonthIndex + 1).coerceAtMost(
                                                    months.lastIndex
                                                )
                                        WebCalendarMode.Week ->
                                            visibleWeekIndex =
                                                (visibleWeekIndex + 1).coerceAtMost(weeks.lastIndex)
                                    }
                                }
                                if (
                                    (mode == WebCalendarMode.Month &&
                                        visibleMonthIndex >= months.lastIndex) ||
                                        (mode == WebCalendarMode.Week &&
                                            visibleWeekIndex >= weeks.lastIndex)
                                ) {
                                    disabled()
                                }
                                attr("aria-label", "Next")
                                style { toolbarButton() }
                            }
                        ) {
                            Text("→")
                        }
                    }
                }
            }
            if (unscheduledTasks.isNotEmpty()) {
                if (showUnscheduled) {
                    UnscheduledTaskStrip(
                        tasks = unscheduledTasks,
                        onTaskSelected = onTaskSelected,
                        title = "Unscheduled",
                    )
                }
            }
            if (compact) {
                when (mode) {
                    WebCalendarMode.Month -> {
                        val month = visibleMonth ?: return@Div
                        CalendarAgenda(
                            days = generateDates(month.startDate, month.endDate),
                            entries = scheduledEntries,
                            today = today,
                            onTaskSelected = onTaskSelected,
                            onScheduleEntrySelected = onScheduleEntrySelected,
                        )
                    }
                    WebCalendarMode.Week -> {
                        val week = visibleWeek ?: return@Div
                        CalendarCompactWeekStrip(
                            week = week,
                            entries = scheduledEntries,
                            today = today,
                            onTaskSelected = onTaskSelected,
                            onScheduleEntrySelected = onScheduleEntrySelected,
                        )
                    }
                }
            } else if (mode == WebCalendarMode.Week) {
                CalendarWeekGrid(
                    week = visibleWeek ?: return@Div,
                    entries = scheduledEntries,
                    today = today,
                    onTaskSelected = onTaskSelected,
                    onScheduleEntrySelected = onScheduleEntrySelected,
                )
            } else {
                CalendarMonthGrid(
                    month = visibleMonth ?: return@Div,
                    entries = scheduledEntries,
                    today = today,
                    onTaskSelected = onTaskSelected,
                    onScheduleEntrySelected = onScheduleEntrySelected,
                )
            }
        }
    }
}

@Composable
private fun CalendarCompactWeekStrip(
    week: WebCalendarWeekRange,
    entries: List<WebScheduleEntry>,
    today: LocalDate,
    onTaskSelected: (WebTask) -> Unit,
    onScheduleEntrySelected: (WebScheduleEntry) -> Unit,
) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "grid",
                    "grid-auto-flow" to "column",
                    "grid-auto-columns" to "calc(100vw - 32px)",
                    "gap" to "12px",
                    "overflow-x" to "auto",
                    "padding-bottom" to "12px",
                    "padding-right" to "12px",
                    "scroll-snap-type" to "x proximity",
                )
            }
        }
    ) {
        week.days.forEach { day ->
            val dayEntries = entries.filter { entry -> entry.includes(day) }.sortedBySchedule()
            Div(attrs = { style { props("scroll-snap-align" to "start", "min-width" to "0") } }) {
                CalendarDesktopDayCell(
                    day = day,
                    dayEntries = dayEntries,
                    today = today,
                    onTaskSelected = onTaskSelected,
                    onScheduleEntrySelected = onScheduleEntrySelected,
                    minHeight = "420px",
                    showWeekdayInHeader = true,
                    previewLimit = 2,
                )
            }
        }
    }
}

@Composable
private fun CalendarEntryCard(
    entry: WebScheduleEntry,
    muted: Boolean,
    onTaskSelected: (WebTask) -> Unit,
    onScheduleEntrySelected: (WebScheduleEntry) -> Unit,
    dense: Boolean = false,
    showProject: Boolean = false,
) {
    val meta =
        buildList {
                if (showProject) {
                    add(entry.projectName)
                }
                entry.detail?.takeIf { detail -> detail.isNotBlank() }?.let(::add)
                entry.blockerLabel?.let(::add)
                entry.timelineRangeLabel()?.let(::add)
            }
            .joinToString(" · ")
    Div(
        attrs = {
            attr("data-schedule-entry-kind", entry.kind.name.lowercase())
            attr("data-schedule-entry-id", entry.key)
            attr("data-schedule-entry-title", entry.title)
            if (entry.clickableTask != null) {
                onClick { onTaskSelected(entry.clickableTask) }
            } else {
                onClick { onScheduleEntrySelected(entry) }
            }
            style {
                props(
                    "display" to "grid",
                    "gap" to if (dense) "4px" else "6px",
                    "background" to entry.calendarSurfaceBackground(muted),
                    "color" to if (muted) Muted else Ink,
                    "border" to "1px solid ${if (muted) "#e5e8ee" else Border}",
                    "border-left" to
                        "4px solid ${if (muted) "#d4d9e2" else entry.blockerLabel?.let { Coral } ?: entry.calendarAccent()}",
                    "border-radius" to "6px",
                    "padding" to if (dense) "7px 8px" else "9px 10px",
                    "font-size" to if (dense) "12px" else "13px",
                    "font-weight" to if (muted) "600" else "700",
                    "cursor" to "pointer",
                    "opacity" to if (muted) "0.74" else "1",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "justify-content" to "space-between",
                        "gap" to "8px",
                        "flex-wrap" to "wrap",
                    )
                }
            }
        ) {
            Span(
                attrs = {
                    style {
                        props(
                            "display" to "inline-flex",
                            "align-items" to "center",
                            "height" to "18px",
                            "padding" to "0 6px",
                            "border-radius" to "999px",
                            "background" to entry.calendarBadgeBackground(muted),
                            "font-size" to "10px",
                            "font-weight" to "700",
                            "color" to entry.calendarBadgeTextColor(muted),
                        )
                    }
                }
            ) {
                Text(if (entry.kind == WebScheduleEntryKind.Ticket) "Due" else entry.kind.label())
            }
        }
        Span(
            attrs = {
                style {
                    props(
                        "color" to if (muted) Muted else Ink,
                        "line-height" to "1.35",
                        "overflow" to "hidden",
                        "display" to "-webkit-box",
                        "-webkit-line-clamp" to if (dense) "2" else "3",
                        "-webkit-box-orient" to "vertical",
                    )
                }
            }
        ) {
            Text(entry.title)
        }
        if (meta.isNotBlank()) {
            Span(
                attrs = {
                    style {
                        props(
                            "font-size" to if (dense) "11px" else "12px",
                            "color" to Muted,
                            "line-height" to "1.4",
                        )
                    }
                }
            ) {
                Text(meta)
            }
        }
    }
}

@Composable
private fun CalendarAgenda(
    days: List<LocalDate>,
    entries: List<WebScheduleEntry>,
    today: LocalDate,
    onTaskSelected: (WebTask) -> Unit,
    onScheduleEntrySelected: (WebScheduleEntry) -> Unit,
) {
    val visibleDays =
        remember(days, entries, today) {
            days.filter { day -> day == today || entries.any { entry -> entry.includes(day) } }
        }
    if (visibleDays.isEmpty()) {
        Div(
            attrs = {
                style {
                    props(
                        "border" to "1px dashed $Border",
                        "border-radius" to "8px",
                        "background" to "#fbfcfd",
                        "padding" to "18px",
                        "font-size" to "14px",
                        "color" to Muted,
                    )
                }
            }
        ) {
            Text("No scheduled work in this range")
        }
        return
    }
    Div(attrs = { style { props("display" to "grid", "gap" to "12px") } }) {
        visibleDays.forEach { day ->
            val dayEntries = entries.filter { entry -> entry.includes(day) }.sortedBySchedule()
            val isToday = day == today
            Div(
                attrs = {
                    style {
                        props(
                            "border" to "1px solid $Border",
                            "border-radius" to "8px",
                            "background" to Paper,
                            "padding" to "12px",
                            "display" to "grid",
                            "gap" to "10px",
                            "outline" to if (isToday) "2px solid $Blue" else "none",
                            "outline-offset" to if (isToday) "-2px" else "0",
                        )
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "flex",
                                "justify-content" to "space-between",
                                "align-items" to "baseline",
                                "gap" to "8px",
                                "flex-wrap" to "wrap",
                            )
                        }
                    }
                ) {
                    Span(
                        attrs = {
                            style {
                                props(
                                    "display" to "inline-flex",
                                    "align-items" to "center",
                                    "gap" to "8px",
                                )
                            }
                        }
                    ) {
                        Span(
                            attrs = {
                                style {
                                    props(
                                        "font-size" to "15px",
                                        "font-weight" to "700",
                                        "color" to Ink,
                                    )
                                }
                            }
                        ) {
                            Text("${weekdayLabel(day)} ${monthLabel(day.month.ordinal + 1)}")
                        }
                        Span(
                            attrs = {
                                style {
                                    props(
                                        "display" to "inline-flex",
                                        "align-items" to "center",
                                        "justify-content" to "center",
                                        "min-width" to "28px",
                                        "height" to "28px",
                                        "border-radius" to "999px",
                                        "background" to if (isToday) Blue else "#eef1f5",
                                        "color" to if (isToday) Paper else Ink,
                                        "font-size" to "13px",
                                        "font-weight" to "800",
                                    )
                                }
                            }
                        ) {
                            Text(day.day.toString())
                        }
                    }
                    Span(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                        Text("${dayEntries.size} item${if (dayEntries.size == 1) "" else "s"}")
                    }
                }
                if (dayEntries.isEmpty()) {
                    if (isToday) {
                        MutedText("Open day")
                    }
                } else {
                    dayEntries.forEach { entry ->
                        CalendarEntryCard(
                            entry = entry,
                            muted = entry.shouldMuteOnCalendar(today),
                            onTaskSelected = onTaskSelected,
                            onScheduleEntrySelected = onScheduleEntrySelected,
                            showProject = true,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun CalendarMonthGrid(
    month: WebCalendarMonthGrid,
    entries: List<WebScheduleEntry>,
    today: LocalDate,
    onTaskSelected: (WebTask) -> Unit,
    onScheduleEntrySelected: (WebScheduleEntry) -> Unit,
) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "grid",
                    "grid-template-columns" to "repeat(7, minmax(0, 1fr))",
                    "border" to "1px solid $Border",
                    "border-radius" to "8px",
                    "overflow" to "hidden",
                )
            }
        }
    ) {
        weekdayLabels().forEach { weekday ->
            Div(
                attrs = {
                    style {
                        props(
                            "padding" to "10px 12px",
                            "background" to "#fbfcfd",
                            "border-bottom" to "1px solid $Border",
                            "font-size" to "12px",
                            "font-weight" to "700",
                            "color" to Muted,
                        )
                    }
                }
            ) {
                Text(weekday)
            }
        }
        month.weeks.forEach { week ->
            week.forEach { day ->
                val dayEntries =
                    day?.let { date ->
                        entries.filter { entry -> entry.includes(date) }.sortedBySchedule()
                    } ?: emptyList()
                CalendarDesktopDayCell(
                    day = day,
                    dayEntries = dayEntries,
                    today = today,
                    onTaskSelected = onTaskSelected,
                    onScheduleEntrySelected = onScheduleEntrySelected,
                    minHeight = if (dayEntries.isEmpty()) "128px" else "156px",
                    previewLimit = 3,
                )
            }
        }
    }
}

@Composable
private fun CalendarWeekGrid(
    week: WebCalendarWeekRange,
    entries: List<WebScheduleEntry>,
    today: LocalDate,
    onTaskSelected: (WebTask) -> Unit,
    onScheduleEntrySelected: (WebScheduleEntry) -> Unit,
) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "grid",
                    "grid-template-columns" to "repeat(7, minmax(0, 1fr))",
                    "border" to "1px solid $Border",
                    "border-radius" to "8px",
                    "overflow" to "hidden",
                )
            }
        }
    ) {
        week.days.forEach { day ->
            val dayEntries = entries.filter { entry -> entry.includes(day) }.sortedBySchedule()
            CalendarDesktopDayCell(
                day = day,
                dayEntries = dayEntries,
                today = today,
                onTaskSelected = onTaskSelected,
                onScheduleEntrySelected = onScheduleEntrySelected,
                minHeight = "420px",
                showWeekdayInHeader = true,
            )
        }
    }
}

@Composable
private fun CalendarDesktopDayCell(
    day: LocalDate?,
    dayEntries: List<WebScheduleEntry>,
    today: LocalDate,
    onTaskSelected: (WebTask) -> Unit,
    onScheduleEntrySelected: (WebScheduleEntry) -> Unit,
    minHeight: String,
    showWeekdayInHeader: Boolean = false,
    previewLimit: Int? = null,
) {
    val isToday = day == today
    val isWeekend = day?.dayOfWeek?.ordinal?.let { it == 0 || it == 6 } == true
    val visibleEntries =
        previewLimit?.let { limit -> dayEntries.previewCalendarEntries(limit) } ?: dayEntries
    Div(
        attrs = {
            style {
                props(
                    "display" to "grid",
                    "grid-template-rows" to "auto minmax(0, 1fr)",
                    "gap" to "10px",
                    "padding" to "10px",
                    "min-height" to minHeight,
                    "box-sizing" to "border-box",
                    "background" to
                        when {
                            day == null -> "#fbfcfd"
                            isToday -> "#f5f8ff"
                            isWeekend -> "#fafbfd"
                            else -> Paper
                        },
                    "border-right" to "1px solid $Border",
                    "border-bottom" to "1px solid $Border",
                    "outline" to if (isToday) "2px solid $Blue" else "none",
                    "outline-offset" to "-2px",
                )
            }
        }
    ) {
        if (day == null) {
            Span(attrs = { style { props("font-size" to "12px", "color" to "#c1c5cd") } }) {
                Text("")
            }
        } else {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "justify-content" to "space-between",
                            "align-items" to "baseline",
                            "gap" to "8px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                Div(attrs = { style { props("display" to "grid", "gap" to "2px") } }) {
                    if (showWeekdayInHeader) {
                        Span(
                            attrs = {
                                style {
                                    props(
                                        "font-size" to "11px",
                                        "font-weight" to "700",
                                        "color" to Muted,
                                        "text-transform" to "uppercase",
                                    )
                                }
                            }
                        ) {
                            Text(weekdayLabel(day))
                        }
                    }
                    Span(
                        attrs = {
                            style {
                                props(
                                    "font-size" to if (showWeekdayInHeader) "24px" else "18px",
                                    "font-weight" to "700",
                                    "color" to if (isToday) Blue else Ink,
                                    "display" to "inline-flex",
                                    "align-items" to "center",
                                    "justify-content" to "center",
                                    "min-width" to if (showWeekdayInHeader) "34px" else "30px",
                                    "height" to if (showWeekdayInHeader) "34px" else "30px",
                                    "border-radius" to "999px",
                                    "background" to if (isToday) "#dceaff" else "transparent",
                                )
                            }
                        }
                    ) {
                        Text(day.day.toString())
                    }
                }
                if (dayEntries.isNotEmpty()) {
                    CountBadge(dayEntries.size.toString())
                }
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "gap" to "6px",
                            "align-content" to "start",
                            "min-height" to "0",
                            "overflow-y" to if (previewLimit == null) "auto" else "visible",
                            "padding-right" to "2px",
                        )
                    }
                }
            ) {
                if (visibleEntries.isNotEmpty()) {
                    visibleEntries.forEach { entry ->
                        CalendarEntryCard(
                            entry = entry,
                            muted = entry.shouldMuteOnCalendar(today),
                            onTaskSelected = onTaskSelected,
                            onScheduleEntrySelected = onScheduleEntrySelected,
                            dense = true,
                            showProject = true,
                        )
                    }
                    if (previewLimit != null && dayEntries.size > visibleEntries.size) {
                        Span(
                            attrs = {
                                style {
                                    props(
                                        "font-size" to "11px",
                                        "font-weight" to "700",
                                        "color" to Muted,
                                    )
                                }
                            }
                        ) {
                            Text("+${dayEntries.size - visibleEntries.size} more")
                        }
                    }
                } else {
                    Span(
                        attrs = {
                            style {
                                props(
                                    "font-size" to "11px",
                                    "font-weight" to "600",
                                    "color" to "#b4bbc6",
                                    "min-height" to if (showWeekdayInHeader) "24px" else "12px",
                                )
                            }
                        }
                    ) {
                        Text(if (day != null && !showWeekdayInHeader) "Open day" else "")
                    }
                }
            }
        }
    }
}

@Composable
private fun TimelineAgendaEntryCard(
    workspace: WebWorkspace,
    entry: WebScheduleEntry,
    muted: Boolean,
    onTaskSelected: (WebTask) -> Unit,
    onScheduleEntrySelected: (WebScheduleEntry) -> Unit,
) {
    val detailParts = buildList {
        add(workspace.projectName(entry.projectId))
        entry.blockerLabel?.let(::add)
        entry.timelineRangeLabel()?.let(::add)
    }
    Div(
        attrs = {
            attr("data-schedule-entry-kind", entry.kind.name.lowercase())
            attr("data-schedule-entry-id", entry.key)
            attr("data-schedule-entry-title", entry.title)
            if (entry.clickableTask != null) {
                onClick { onTaskSelected(entry.clickableTask) }
            } else {
                onClick { onScheduleEntrySelected(entry) }
            }
            style {
                props(
                    "display" to "grid",
                    "gap" to "6px",
                    "background" to if (muted) "#f6f7f9" else Paper,
                    "color" to if (muted) Muted else Ink,
                    "border" to "1px solid ${if (muted) "#e2e5ea" else Border}",
                    "border-left" to
                        "4px solid ${if (muted) "#d5dae2" else entry.blockerLabel?.let { Coral } ?: entry.calendarAccent()}",
                    "border-radius" to "8px",
                    "padding" to "10px 12px",
                    "cursor" to "pointer",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "justify-content" to "space-between",
                        "align-items" to "start",
                        "gap" to "10px",
                    )
                }
            }
        ) {
            Span(
                attrs = {
                    style {
                        props(
                            "font-size" to "14px",
                            "font-weight" to "700",
                            "line-height" to "1.35",
                            "min-width" to "0",
                        )
                    }
                }
            ) {
                Text(entry.title)
            }
            Chip(entry.kind.label(), if (muted) "#e7e8ea" else entry.calendarAccent(), Ink)
        }
        if (detailParts.isNotEmpty()) {
            Span(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                Text(detailParts.joinToString(" - "))
            }
        }
    }
}

@Composable
private fun UnscheduledTaskStrip(
    tasks: List<WebTask>,
    onTaskSelected: (WebTask) -> Unit,
    title: String = "Unscheduled tickets",
) {
    var expanded by
        remember(tasks.map { task -> task.ticket.id.value }) { mutableStateOf(tasks.size <= 5) }
    Div(
        attrs = {
            style {
                props(
                    "border" to "1px solid $Border",
                    "border-radius" to "8px",
                    "background" to Paper,
                    "padding" to "16px",
                    "margin-bottom" to "18px",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "start",
                        "justify-content" to "space-between",
                        "gap" to "12px",
                        "margin-bottom" to "12px",
                        "flex-wrap" to "wrap",
                    )
                }
            }
        ) {
            Div(attrs = { style { props("display" to "grid", "gap" to "4px") } }) {
                Div(
                    attrs = {
                        style {
                            props("font-size" to "15px", "font-weight" to "700", "color" to Ink)
                        }
                    }
                ) {
                    Text(title)
                }
                Span(attrs = { style { props("font-size" to "13px", "color" to Muted) } }) {
                    Text("Open a ticket to assign a due date and pull it into the calendar.")
                }
            }
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "flex",
                            "align-items" to "center",
                            "gap" to "8px",
                            "flex-wrap" to "wrap",
                        )
                    }
                }
            ) {
                NoticeBadge("${tasks.size} unscheduled")
                if (tasks.size > 5) {
                    Button(
                        attrs = {
                            onClick { expanded = !expanded }
                            style { secondaryButton() }
                        }
                    ) {
                        Text(if (expanded) "Hide unscheduled" else "Show unscheduled")
                    }
                }
            }
        }
        if (expanded) {
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "grid-template-columns" to "repeat(auto-fit, minmax(220px, 1fr))",
                            "gap" to "10px",
                            "padding-top" to "16px",
                            "border-top" to "1px solid $Border",
                        )
                    }
                }
            ) {
                tasks.take(10).forEach { task ->
                    Div(
                        attrs = {
                            onClick { onTaskSelected(task) }
                            style {
                                props(
                                    "display" to "grid",
                                    "gap" to "6px",
                                    "background" to "#fbfcfd",
                                    "border" to "1px solid $Border",
                                    "border-radius" to "8px",
                                    "padding" to "10px 12px",
                                    "cursor" to "pointer",
                                )
                            }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "flex",
                                        "align-items" to "center",
                                        "gap" to "8px",
                                    )
                                }
                            }
                        ) {
                            Span(
                                attrs = {
                                    style {
                                        props(
                                            "display" to "inline-block",
                                            "width" to "8px",
                                            "height" to "8px",
                                            "background" to task.project.projectAccent(),
                                            "border-radius" to "999px",
                                            "flex-shrink" to "0",
                                        )
                                    }
                                }
                            )
                            Span(
                                attrs = { style { props("font-size" to "12px", "color" to Muted) } }
                            ) {
                                Text(task.project.name)
                            }
                        }
                        Span(
                            attrs = {
                                style {
                                    props(
                                        "font-size" to "13px",
                                        "font-weight" to "700",
                                        "color" to Ink,
                                    )
                                }
                            }
                        ) {
                            Text(task.ticket.title)
                        }
                        Span(attrs = { style { props("font-size" to "12px", "color" to Muted) } }) {
                            Text(task.owner?.displayName ?: "Unassigned")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun TaskDetails(
    workspace: WebWorkspace,
    compact: Boolean,
    task: WebTask?,
    activities: List<Kairos.TicketActivity>,
    onSetCompleted: (WebTask, Boolean) -> Unit,
    onUpdateTitle: (WebTask, String) -> Unit,
    onUpdateDescription: (WebTask, String) -> Unit,
    onUpdateAssignee: (WebTask, Kairos.UserId?) -> Unit,
    onUpdateProject: (WebTask, Kairos.ProjectId) -> Unit,
    onUpdatePriority: (WebTask, Kairos.TicketPriority) -> Unit,
    onUpdateUrgency: (WebTask, Kairos.TicketUrgency) -> Unit,
    onUpdateDeadline: (WebTask, ZonedDateTime?) -> Unit,
    onUpdateStage: (WebTask, Kairos.TicketStage) -> Unit,
    onSetTicketBlocker: (WebTask, Kairos.TicketId, Boolean) -> Unit,
    onCreateObjective: (String, Kairos.ProjectId?, ZonedDateTime?, List<Kairos.TicketId>) -> Unit,
    onSetObjectiveLink: (WebTask, Kairos.Objective, Boolean) -> Unit,
    onClose: () -> Unit,
) {
    var titleDraft by
        remember(task?.ticket?.id, task?.ticket?.title) {
            mutableStateOf(task?.ticket?.title.orEmpty())
        }
    var descriptionDraft by
        remember(task?.ticket?.id, task?.ticket?.description) {
            mutableStateOf(task?.ticket?.description.orEmpty())
        }
    var dueDateDraft by
        remember(task?.ticket?.id, task?.ticket?.deadline) {
            mutableStateOf(task?.ticket?.deadline.inputValue())
        }
    val dueDateDraftRef =
        remember(task?.ticket?.id, task?.ticket?.deadline) {
            MutableTextDraft(task?.ticket?.deadline.inputValue())
        }
    var lastCommittedDueDateDraft by
        remember(task?.ticket?.id, task?.ticket?.deadline) {
            mutableStateOf(task?.ticket?.deadline.inputValue())
        }
    var titleEditing by remember(task?.ticket?.id) { mutableStateOf(false) }
    var descriptionEditing by remember(task?.ticket?.id) { mutableStateOf(false) }
    var activityExpanded by remember(task?.ticket?.id) { mutableStateOf(false) }
    val linkedObjectives =
        remember(workspace, task?.ticket?.id) {
            task?.let { workspace.objectivesFor(it.ticket.id) }.orEmpty()
        }
    val availableObjectives =
        remember(workspace, task?.ticket?.projectId) {
            task?.let { workspace.objectiveCandidates(it.ticket.projectId) }.orEmpty()
        }
    val selectableObjectives =
        remember(linkedObjectives, availableObjectives) {
            (linkedObjectives + availableObjectives).distinctBy { objective -> objective.id }
        }
    val linkedObjectiveIds = linkedObjectives.map { it.id }.toSet()
    val allTasks =
        remember(workspace) {
            workspace.filteredTasks("", includeCompleted = true, WebSort.Created)
        }
    val blockingEdges =
        remember(workspace.ticketBlockers, task?.ticket?.id) {
            task
                ?.let { current ->
                    workspace.ticketBlockers.filter { blocker ->
                        blocker.blockedTicketId == current.ticket.id
                    }
                }
                .orEmpty()
        }
    val blockingTicketIds = blockingEdges.map { it.blockingTicketId }.toSet()
    val blockingTasks =
        remember(allTasks, blockingTicketIds) {
            allTasks.filter { candidate -> candidate.ticket.id in blockingTicketIds }
        }
    val blockerCandidates =
        remember(allTasks, task?.ticket?.id, blockingTicketIds, workspace.ticketBlockers) {
            val currentTicketId = task?.ticket?.id
            allTasks.filter { candidate ->
                currentTicketId != null &&
                    candidate.ticket.id != currentTicketId &&
                    candidate.ticket.id !in blockingTicketIds &&
                    candidate.ticket.completedAt == null &&
                    workspace.ticketBlockers.none { blocker ->
                        blocker.blockedTicketId == candidate.ticket.id &&
                            blocker.blockingTicketId == currentTicketId
                    }
            }
        }
    val stackDetailRows = compact || window.innerWidth < 720
    fun commitDueDateDraft(rawDraftOverride: String? = null) {
        val currentTask = task ?: return
        val normalizedStateDraft =
            normalizeDateControlDraft(dueDateDraftRef.value, WebDateControlKind.Date)
        val normalizedOverride =
            rawDraftOverride?.let { normalizeDateControlDraft(it, WebDateControlKind.Date) }
        val normalizedDraft =
            normalizedOverride?.takeIf { it.isBlank() || parseDeadlineInput(it) != null }
                ?: normalizedStateDraft
        val parsedDeadline = parseDeadlineInput(normalizedDraft)
        val persistedDraft = currentTask.ticket.deadline.inputValue()
        dueDateDraft = normalizedDraft
        dueDateDraftRef.value = normalizedDraft
        when {
            normalizedDraft == lastCommittedDueDateDraft -> return
            normalizedDraft.isBlank() -> {
                lastCommittedDueDateDraft = normalizedDraft
                onUpdateDeadline(currentTask, null)
            }
            parsedDeadline != null -> {
                lastCommittedDueDateDraft = normalizedDraft
                onUpdateDeadline(currentTask, parsedDeadline)
            }
            else -> {
                dueDateDraft = persistedDraft
                dueDateDraftRef.value = persistedDraft
            }
        }
    }

    Div(
        attrs = {
            attr("id", "web-task-details")
            style {
                props(
                    "width" to if (compact) "100%" else "496px",
                    "min-width" to if (compact) "0" else "360px",
                    "max-width" to "100%",
                    "height" to "100%",
                    "background" to Paper,
                    "border-left" to if (compact) "0" else "1px solid $Border",
                    "box-shadow" to if (compact) "none" else "-4px 0 18px rgba(20,22,28,0.10)",
                    "display" to "flex",
                    "flex-direction" to "column",
                    "overflow" to "hidden",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "min-height" to "54px",
                        "display" to "flex",
                        "align-items" to "center",
                        "gap" to "12px",
                        "padding" to "0 24px",
                        "border-bottom" to "1px solid $Border",
                        "flex-shrink" to "0",
                        "background" to "#f8f9fb",
                        "position" to "sticky",
                        "top" to "0",
                        "z-index" to "2",
                    )
                }
            }
        ) {
            Span(
                attrs = {
                    style { props("font-size" to "14px", "font-weight" to "700", "color" to Ink) }
                }
            ) {
                Text("Ticket details")
            }
            Div(attrs = { style { props("flex" to "1") } })
            if (task != null) {
                Button(
                    attrs = {
                        attr("id", "web-ticket-toggle-complete-button")
                        onClick { onSetCompleted(task, task.ticket.completedAt == null) }
                        style { primaryButton() }
                    }
                ) {
                    Text(if (task.ticket.completedAt == null) "Mark complete" else "Reopen ticket")
                }
            }
            Span(
                attrs = {
                    style {
                        props(
                            "color" to Muted,
                            "font-size" to "12px",
                            "font-weight" to "700",
                            "white-space" to "nowrap",
                        )
                    }
                }
            ) {
                Text("${activities.size} update${if (activities.size == 1) "" else "s"}")
            }
            Button(
                attrs = {
                    attr("id", "web-ticket-close-button")
                    onMouseDown {
                        commitDueDateDraft(inputElementValue("web-ticket-deadline-input"))
                    }
                    onClick { onClose() }
                    style { tertiaryButton() }
                }
            ) {
                Text("Close")
            }
        }
        if (task == null) {
            Div(
                attrs = {
                    style {
                        props(
                            "padding" to "24px",
                            "color" to Muted,
                            "flex" to "1",
                            "overflow" to "auto",
                        )
                    }
                }
            ) {
                Text("Select a ticket")
            }
        } else {
            Div(
                attrs = {
                    style {
                        props(
                            "padding" to "24px",
                            "box-sizing" to "border-box",
                            "flex" to "1",
                            "overflow" to "auto",
                            "padding-bottom" to "calc(env(safe-area-inset-bottom, 0px) + 24px)",
                        )
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            props(
                                "font-size" to "13px",
                                "line-height" to "1.45",
                                "color" to Muted,
                                "margin-bottom" to "16px",
                                "word-break" to "break-word",
                            )
                        }
                    }
                ) {
                    Text(task.workspaceLabel)
                }
                if (titleEditing) {
                    Input(
                        type = InputType.Text,
                        attrs = {
                            attr("id", "web-ticket-title-input")
                            value(titleDraft)
                            onInput { titleDraft = it.value }
                            onBlur {
                                onUpdateTitle(task, titleDraft)
                                titleEditing = false
                            }
                            onKeyDown { event ->
                                if (event.key == "Enter") {
                                    event.preventDefault()
                                    onUpdateTitle(task, titleDraft)
                                    titleEditing = false
                                } else if (event.key == "Escape") {
                                    event.preventDefault()
                                    titleDraft = task.ticket.title
                                    titleEditing = false
                                }
                            }
                            attr("placeholder", "Ticket title")
                            style {
                                props(
                                    "border" to "1px solid $Border",
                                    "border-radius" to "10px",
                                    "padding" to "12px 14px",
                                    "width" to "100%",
                                    "box-sizing" to "border-box",
                                    "font-size" to "26px",
                                    "font-weight" to "700",
                                    "line-height" to "1.25",
                                    "color" to Ink,
                                    "background" to Paper,
                                    "font-family" to "inherit",
                                )
                            }
                        },
                    )
                } else {
                    Div(
                        attrs = {
                            attr("id", "web-ticket-title-display")
                            attr("title", "Click to rename")
                            onClick { titleEditing = true }
                            style { props("padding" to "4px 0 10px", "cursor" to "text") }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "font-size" to "30px",
                                        "font-weight" to "700",
                                        "line-height" to "1.18",
                                        "color" to Ink,
                                        "word-break" to "break-word",
                                    )
                                }
                            }
                        ) {
                            Text(task.ticket.title)
                        }
                    }
                }
                SectionLabel("Who and when")
                DetailRow("Assignee", stacked = stackDetailRows) {
                    AssigneeSelector(
                        users = workspace.assignableUsers(task.project),
                        selectedAssigneeUserId = task.ticket.assigneeUserId,
                        onUserSelected = { userId -> onUpdateAssignee(task, userId) },
                    )
                }
                DetailRow("Due date", stacked = stackDetailRows) {
                    DatePickerField(
                        id = "web-ticket-deadline-input",
                        value = dueDateDraft,
                        placeholder = "Due date",
                        onCommit = { nextDraft ->
                            val normalizedDraft =
                                normalizeDateControlDraft(nextDraft, WebDateControlKind.Date)
                            dueDateDraft = normalizedDraft
                            dueDateDraftRef.value = normalizedDraft
                            commitDueDateDraft()
                        },
                        kind = WebDateControlKind.Date,
                        width = "100%",
                        height = "36px",
                        onChange = {
                            dueDateDraft = it
                            dueDateDraftRef.value = it
                        },
                    )
                }
                SectionLabel("Classification")
                DetailRow("Project", stacked = stackDetailRows) {
                    ProjectSelector(
                        projects = workspace.movableProjects(task.ticket.projectId),
                        currentProjectId = task.ticket.projectId,
                        allProjects = workspace.projects,
                        onProjectSelected = { projectId -> onUpdateProject(task, projectId) },
                    )
                }
                DetailRow("Priority", stacked = stackDetailRows) {
                    PrioritySelector(
                        currentPriority = task.ticket.priority,
                        onPrioritySelected = { priority -> onUpdatePriority(task, priority) },
                    )
                }
                DetailRow("Timing", stacked = stackDetailRows) {
                    UrgencySelector(
                        currentUrgency = task.ticket.urgency,
                        onUrgencySelected = { urgency -> onUpdateUrgency(task, urgency) },
                    )
                }
                DetailRow("Stage", stacked = stackDetailRows) {
                    StageSelector(
                        currentStage = task.ticket.stage,
                        onStageSelected = { stage -> onUpdateStage(task, stage) },
                    )
                }
                SectionLabel("Dependencies")
                DetailRow("Blocked by", stacked = stackDetailRows) {
                    Div(attrs = { style { props("display" to "grid", "gap" to "10px") } }) {
                        if (blockingTasks.isEmpty()) {
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "border" to "1px dashed $Border",
                                            "border-radius" to "8px",
                                            "padding" to "10px 12px",
                                            "background" to "#fbfcfd",
                                        )
                                    }
                                }
                            ) {
                                MutedText("No blockers linked yet")
                            }
                        } else {
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "display" to "flex",
                                            "gap" to "8px",
                                            "flex-wrap" to "wrap",
                                        )
                                    }
                                }
                            ) {
                                blockingTasks.forEach { blocker ->
                                    Button(
                                        attrs = {
                                            attr(
                                                "data-ticket-blocker-remove",
                                                blocker.ticket.id.value,
                                            )
                                            onClick {
                                                onSetTicketBlocker(task, blocker.ticket.id, false)
                                            }
                                            style { tertiaryButton() }
                                        }
                                    ) {
                                        Text("${blocker.ticket.title} ×")
                                    }
                                }
                            }
                        }
                        if (blockerCandidates.isNotEmpty()) {
                            BlockerSelector(
                                blockers = blockerCandidates,
                                onBlockerSelected = { blocker ->
                                    onSetTicketBlocker(task, blocker.ticket.id, true)
                                },
                            )
                        }
                    }
                }
                DetailRow("Objectives", stacked = stackDetailRows) {
                    Div(attrs = { style { props("display" to "grid", "gap" to "10px") } }) {
                        ObjectiveSelector(
                            linkedObjectives = linkedObjectives,
                            availableObjectives =
                                selectableObjectives.filter { objective ->
                                    objective.id !in linkedObjectiveIds
                                },
                            allProjects = workspace.projects,
                            onObjectiveLinked = { objective ->
                                onSetObjectiveLink(task, objective, true)
                            },
                            onObjectiveRemoved = { objective ->
                                onSetObjectiveLink(task, objective, false)
                            },
                        )
                        Button(
                            attrs = {
                                attr("id", "web-ticket-create-objective-button")
                                onClick {
                                    onCreateObjective(
                                        task.ticket.title,
                                        task.ticket.projectId,
                                        task.ticket.deadline,
                                        listOf(task.ticket.id),
                                    )
                                }
                                style { tertiaryButton() }
                            }
                        ) {
                            Text("Create from ticket")
                        }
                    }
                }
                SectionLabel("Description")
                MarkdownDescriptionEditor(
                    value = descriptionDraft,
                    savedValue = task.ticket.description,
                    editing = descriptionEditing,
                    onStartEditing = { descriptionEditing = true },
                    onChange = { descriptionDraft = it },
                    onCancel = {
                        descriptionDraft = task.ticket.description
                        descriptionEditing = false
                    },
                    onSave = {
                        onUpdateDescription(task, descriptionDraft)
                        descriptionEditing = false
                    },
                )
                SectionLabel("Activity")
                Div(attrs = { style { props("display" to "grid", "gap" to "10px") } }) {
                    Button(
                        attrs = {
                            attr("id", "web-ticket-activity-toggle")
                            onClick { activityExpanded = !activityExpanded }
                            style { tertiaryButton() }
                        }
                    ) {
                        Text(
                            if (activityExpanded) {
                                "Hide ${activities.size} updates"
                            } else {
                                "Show ${activities.size} updates"
                            }
                        )
                    }
                    if (activityExpanded) {
                        Div(attrs = { attr("id", "web-ticket-history") }) {
                            activities.asReversed().forEach { activity ->
                                val author =
                                    activity.actorUserId?.let { userId ->
                                        workspace.users.firstOrNull { it.id == userId }
                                    } ?: task.owner
                                Div(
                                    attrs = {
                                        attr("data-ticket-activity-id", activity.id.value)
                                        style {
                                            props(
                                                "display" to "flex",
                                                "gap" to "10px",
                                                "align-items" to "center",
                                                "background" to "#fbfcfd",
                                                "border-radius" to "8px",
                                                "padding" to "10px",
                                                "margin-bottom" to "8px",
                                            )
                                        }
                                    }
                                ) {
                                    Avatar(author, 28)
                                    Div {
                                        Div(
                                            attrs = {
                                                style {
                                                    props(
                                                        "font-size" to "12px",
                                                        "font-weight" to "700",
                                                        "color" to Muted,
                                                        "margin-bottom" to "4px",
                                                    )
                                                }
                                            }
                                        ) {
                                            Text(
                                                "${author?.displayName ?: "Kairos"} - " +
                                                    relativeLabel(
                                                        activity.createdAt.toEpochMilliseconds()
                                                    )
                                            )
                                        }
                                        Text(activity.summary)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ScheduleEntryDetails(
    workspace: WebWorkspace,
    compact: Boolean,
    entry: WebScheduleEntry,
    onUpdateEvent: (Kairos.EventId, String, String, ZonedDateTime, ZonedDateTime?) -> Unit,
    onUpdateTimebox: (Kairos.TimeboxId, String, ZonedDateTime, Int?) -> Unit,
    onClose: () -> Unit,
) {
    Div(
        attrs = {
            attr("id", "web-schedule-details")
            attr("data-schedule-entry-kind", entry.kind.name.lowercase())
            attr("data-schedule-entry-id", entry.key)
            style {
                props(
                    "width" to if (compact) "100%" else "420px",
                    "min-width" to if (compact) "0" else "320px",
                    "max-width" to "100%",
                    "height" to "100%",
                    "background" to Paper,
                    "border-left" to if (compact) "0" else "1px solid $Border",
                    "box-shadow" to if (compact) "none" else "-4px 0 24px rgba(0,0,0,0.15)",
                    "display" to "flex",
                    "flex-direction" to "column",
                    "overflow" to "auto",
                )
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    props(
                        "height" to "54px",
                        "display" to "flex",
                        "align-items" to "center",
                        "gap" to "12px",
                        "padding" to "0 24px",
                        "border-bottom" to "1px solid $Border",
                        "flex-shrink" to "0",
                        "background" to "#f8f9fb",
                    )
                }
            }
        ) {
            Chip(entry.kind.label(), entry.calendarAccent(), Paper)
            Div(attrs = { style { props("flex" to "1") } })
            Button(
                attrs = {
                    attr("id", "web-schedule-close-button")
                    onClick { onClose() }
                    style { secondaryButton() }
                }
            ) {
                Text("Close")
            }
        }
        Div(attrs = { style { props("padding" to "24px", "box-sizing" to "border-box") } }) {
            Div(
                attrs = {
                    style {
                        props(
                            "background" to "#f2f3f5",
                            "padding" to "12px 16px",
                            "border-radius" to "6px",
                            "font-size" to "14px",
                            "font-weight" to "700",
                            "margin-bottom" to "18px",
                        )
                    }
                }
            ) {
                Text("Project path: ${entry.projectName}")
            }
            Div(
                attrs = {
                    style {
                        props(
                            "font-size" to "26px",
                            "font-weight" to "700",
                            "line-height" to "1.25",
                        )
                    }
                }
            ) {
                Text(entry.title)
            }
            when (entry.kind) {
                WebScheduleEntryKind.Event -> {
                    val event = workspace.events.firstOrNull { it.id.value == entry.key }
                    if (event == null) {
                        MutedText("This event is no longer available.")
                    } else {
                        EventScheduleDetails(event = event, onUpdateEvent = onUpdateEvent)
                    }
                }
                WebScheduleEntryKind.Timebox -> {
                    val timebox = workspace.timeboxes.firstOrNull { it.id.value == entry.key }
                    if (timebox == null) {
                        MutedText("This timebox is no longer available.")
                    } else {
                        TimeboxScheduleDetails(
                            workspace = workspace,
                            timebox = timebox,
                            onUpdateTimebox = onUpdateTimebox,
                        )
                    }
                }
                WebScheduleEntryKind.Ticket -> {
                    MutedText("Ticket schedule entries open the ticket detail panel.")
                }
            }
        }
    }
}

@Composable
private fun EventScheduleDetails(
    event: Kairos.Event,
    onUpdateEvent: (Kairos.EventId, String, String, ZonedDateTime, ZonedDateTime?) -> Unit,
) {
    var title by remember(event.id.value, event.title) { mutableStateOf(event.title) }
    var start by
        remember(event.id.value, event.dtStart) {
            mutableStateOf(event.dtStart.dateTimeInputValue())
        }
    var end by
        remember(event.id.value, event.dtEnd) { mutableStateOf(event.dtEnd.dateTimeInputValue()) }

    SectionLabel("Event")
    DetailRow("Title") {
        ScheduleInput(
            value = title,
            placeholder = "Event title",
            onChange = { title = it },
            id = "web-schedule-event-title-input",
        )
    }
    DetailRow("Start") {
        DatePickerField(
            value = start,
            placeholder = "Start date and time",
            kind = WebDateControlKind.DateTime,
            onChange = { start = it },
            id = "web-schedule-event-start-input",
        )
    }
    DetailRow("End") {
        DatePickerField(
            value = end,
            placeholder = "End date and time",
            kind = WebDateControlKind.DateTime,
            onChange = { end = it },
            id = "web-schedule-event-end-input",
        )
    }
    event.location
        ?.takeIf { it.isNotBlank() }
        ?.let { location -> DetailRow("Location") { Text(location) } }
    event.description
        ?.takeIf { it.isNotBlank() }
        ?.let { description ->
            SectionLabel("Description")
            MutedText(description)
        }
    Div(attrs = { style { props("margin-top" to "18px") } }) {
        Button(
            attrs = {
                attr("id", "web-schedule-event-save-button")
                onClick {
                    val parsedStart = parseDateTimeInput(start) ?: return@onClick
                    onUpdateEvent(event.id, title, title, parsedStart, parseDateTimeInput(end))
                }
                if (title.trim().isBlank() || parseDateTimeInput(start) == null) {
                    disabled()
                }
                style { secondaryButton() }
            }
        ) {
            Text("Save event")
        }
    }
}

@Composable
private fun TimeboxScheduleDetails(
    workspace: WebWorkspace,
    timebox: Kairos.Timebox,
    onUpdateTimebox: (Kairos.TimeboxId, String, ZonedDateTime, Int?) -> Unit,
) {
    var description by
        remember(timebox.id.value, timebox.description) { mutableStateOf(timebox.description) }
    var start by
        remember(timebox.id.value, timebox.startTime) { mutableStateOf(timebox.startInputValue()) }
    var hours by
        remember(timebox.id.value, timebox.recordedDuration) {
            mutableStateOf(timebox.recordedDuration.totalWholeHoursOrNull()?.toString().orEmpty())
        }
    val linkedTickets =
        timebox.tickets.mapNotNull { ticketId ->
            workspace.tickets.firstOrNull { ticket -> ticket.id == ticketId }
        }

    SectionLabel("Timebox")
    DetailRow("Description") {
        ScheduleInput(
            value = description,
            placeholder = "Description",
            onChange = { description = it },
            id = "web-schedule-timebox-description-input",
        )
    }
    DetailRow("Start") {
        DatePickerField(
            value = start,
            placeholder = "Start date and time",
            kind = WebDateControlKind.DateTime,
            onChange = { start = it },
            id = "web-schedule-timebox-start-input",
        )
    }
    DetailRow("Hours") {
        ScheduleInput(
            value = hours,
            placeholder = "Duration hours",
            onChange = { hours = it },
            id = "web-schedule-timebox-hours-input",
        )
    }
    if (linkedTickets.isNotEmpty()) {
        DetailRow("Linked tickets") {
            Div(
                attrs = {
                    style { props("display" to "flex", "gap" to "8px", "flex-wrap" to "wrap") }
                }
            ) {
                linkedTickets.forEach { ticket -> Chip(ticket.title, "#e7e8ea", Ink) }
            }
        }
    }
    Div(attrs = { style { props("margin-top" to "18px") } }) {
        Button(
            attrs = {
                attr("id", "web-schedule-timebox-save-button")
                onClick {
                    val parsedStart = parseDateTimeInput(start) ?: return@onClick
                    onUpdateTimebox(timebox.id, description, parsedStart, hours.toIntOrNull())
                }
                if (description.trim().isBlank() || parseDateTimeInput(start) == null) {
                    disabled()
                }
                style { secondaryButton() }
            }
        ) {
            Text("Save timebox")
        }
    }
}

@Composable
private fun SectionLabel(label: String) {
    Div(
        attrs = {
            style { props("font-size" to "13px", "font-weight" to "700", "margin" to "26px 0 8px") }
        }
    ) {
        Text(label)
    }
}

@Composable
private fun DetailRow(label: String, stacked: Boolean = false, content: @Composable () -> Unit) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "grid",
                    "grid-template-columns" to if (stacked) "1fr" else "92px minmax(0, 1fr)",
                    "align-items" to "start",
                    "gap" to if (stacked) "8px" else "12px",
                    "margin-top" to "18px",
                    "font-size" to "14px",
                )
            }
        }
    ) {
        Span(attrs = { style { props("color" to Muted) } }) { Text(label) }
        Div(
            attrs = {
                style {
                    props(
                        "display" to "flex",
                        "align-items" to "center",
                        "gap" to "8px",
                        "flex-wrap" to "wrap",
                        "min-width" to "0",
                        "width" to "100%",
                    )
                }
            }
        ) {
            content()
        }
    }
}

@Composable
private fun StageSelector(
    currentStage: Kairos.TicketStage,
    onStageSelected: (Kairos.TicketStage) -> Unit,
) {
    var expanded by remember(currentStage) { mutableStateOf(false) }

    Div(attrs = { style { props("position" to "relative", "width" to "100%") } }) {
        Button(
            attrs = {
                attr("id", "web-ticket-stage-select")
                attr("data-ticket-stage", currentStage.stageToken())
                attr("aria-haspopup", "listbox")
                attr("aria-expanded", expanded.toString())
                onClick { expanded = !expanded }
                style {
                    props(
                        "min-height" to "36px",
                        "width" to "100%",
                        "border" to if (expanded) "1px solid $Blue" else "1px solid $Border",
                        "border-radius" to "8px",
                        "padding" to "7px 12px",
                        "font-size" to "13px",
                        "font-weight" to "600",
                        "background" to if (expanded) "#f8fbff" else Paper,
                        "color" to Ink,
                        "box-sizing" to "border-box",
                        "display" to "flex",
                        "align-items" to "center",
                        "justify-content" to "space-between",
                        "gap" to "10px",
                        "cursor" to "pointer",
                        "text-align" to "left",
                        "box-shadow" to if (expanded) "0 0 0 3px rgba(47,111,219,0.10)" else "none",
                    )
                }
            }
        ) {
            Span(
                attrs = {
                    style {
                        props(
                            "display" to "inline-flex",
                            "align-items" to "center",
                            "gap" to "8px",
                            "min-width" to "0",
                            "flex" to "1",
                        )
                    }
                }
            ) {
                Span(
                    attrs = {
                        style {
                            props(
                                "width" to "8px",
                                "height" to "8px",
                                "border-radius" to "999px",
                                "background" to currentStage.stageColor(),
                                "flex-shrink" to "0",
                            )
                        }
                    }
                )
                Text(currentStage.uiLabel())
            }
            Span(attrs = { style { props("color" to Muted, "font-size" to "12px") } }) { Text("▾") }
        }
        if (expanded) {
            Div(
                attrs = {
                    style {
                        props(
                            "position" to "absolute",
                            "top" to "calc(100% + 8px)",
                            "left" to "0",
                            "right" to "0",
                            "z-index" to "26",
                            "display" to "grid",
                            "gap" to "4px",
                            "padding" to "6px",
                            "border" to "1px solid $Border",
                            "border-radius" to "8px",
                            "background" to Paper,
                            "box-shadow" to "0 18px 38px rgba(37,39,45,0.18)",
                            "max-height" to "260px",
                            "overflow-y" to "auto",
                        )
                    }
                }
            ) {
                Kairos.TicketStage.entries.forEach { stage ->
                    Button(
                        attrs = {
                            attr("data-ticket-stage-option", stage.stageToken())
                            onClick {
                                expanded = false
                                if (stage != currentStage) {
                                    onStageSelected(stage)
                                }
                            }
                            style {
                                props(
                                    "min-height" to "36px",
                                    "width" to "100%",
                                    "border" to
                                        if (stage == currentStage) "1px solid ${stage.stageColor()}"
                                        else "1px solid transparent",
                                    "border-radius" to "8px",
                                    "background" to
                                        if (stage == currentStage) taskTint(stage.stageColor())
                                        else Paper,
                                    "color" to Ink,
                                    "padding" to "8px 10px",
                                    "display" to "flex",
                                    "align-items" to "center",
                                    "justify-content" to "space-between",
                                    "cursor" to "pointer",
                                    "text-align" to "left",
                                    "font-size" to "13px",
                                    "font-weight" to "600",
                                    "gap" to "10px",
                                )
                            }
                        }
                    ) {
                        Span(
                            attrs = {
                                style {
                                    props(
                                        "display" to "inline-flex",
                                        "align-items" to "center",
                                        "gap" to "8px",
                                    )
                                }
                            }
                        ) {
                            Span(
                                attrs = {
                                    style {
                                        props(
                                            "width" to "8px",
                                            "height" to "8px",
                                            "border-radius" to "999px",
                                            "background" to stage.stageColor(),
                                            "flex-shrink" to "0",
                                        )
                                    }
                                }
                            )
                            Text(stage.uiLabel())
                        }
                        if (stage == currentStage) {
                            Span(
                                attrs = {
                                    style {
                                        props(
                                            "font-size" to "12px",
                                            "font-weight" to "700",
                                            "color" to stage.stageColor(),
                                        )
                                    }
                                }
                            ) {
                                Text("Active")
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun BlockerSelector(
    blockers: List<WebTask>,
    onBlockerSelected: (WebTask) -> Unit,
    id: String = "web-ticket-blocker-select",
) {
    var expanded by remember(blockers) { mutableStateOf(false) }

    Div(attrs = { style { props("position" to "relative", "width" to "100%") } }) {
        Button(
            attrs = {
                attr("id", id)
                attr("aria-haspopup", "listbox")
                attr("aria-expanded", expanded.toString())
                onClick { expanded = !expanded }
                style {
                    props(
                        "min-height" to "36px",
                        "width" to "100%",
                        "border" to "1px solid $Border",
                        "border-radius" to "8px",
                        "padding" to "7px 12px",
                        "font-size" to "13px",
                        "font-weight" to "600",
                        "background" to Paper,
                        "color" to Ink,
                        "box-sizing" to "border-box",
                        "display" to "flex",
                        "align-items" to "center",
                        "justify-content" to "space-between",
                        "gap" to "10px",
                        "cursor" to "pointer",
                        "text-align" to "left",
                    )
                }
            }
        ) {
            Span(
                attrs = {
                    style {
                        props(
                            "overflow" to "hidden",
                            "text-overflow" to "ellipsis",
                            "white-space" to "nowrap",
                        )
                    }
                }
            ) {
                Text("Link blocker")
            }
            Span(attrs = { style { props("color" to Muted, "font-size" to "12px") } }) { Text("▾") }
        }
        if (expanded) {
            Div(
                attrs = {
                    style {
                        props(
                            "position" to "absolute",
                            "top" to "calc(100% + 6px)",
                            "left" to "0",
                            "right" to "0",
                            "z-index" to "20",
                            "display" to "grid",
                            "gap" to "4px",
                            "padding" to "6px",
                            "border" to "1px solid $Border",
                            "border-radius" to "8px",
                            "background" to Paper,
                            "box-shadow" to "0 10px 24px rgba(37,39,45,0.12)",
                        )
                    }
                }
            ) {
                blockers.forEach { blocker ->
                    Button(
                        attrs = {
                            attr("data-ticket-blocker-option", blocker.ticket.id.value)
                            onClick {
                                expanded = false
                                onBlockerSelected(blocker)
                            }
                            style {
                                props(
                                    "min-height" to "36px",
                                    "width" to "100%",
                                    "border" to "1px solid transparent",
                                    "border-radius" to "8px",
                                    "background" to Paper,
                                    "color" to Ink,
                                    "padding" to "8px 10px",
                                    "display" to "grid",
                                    "gap" to "3px",
                                    "cursor" to "pointer",
                                    "text-align" to "left",
                                )
                            }
                        }
                    ) {
                        Span(
                            attrs = {
                                style {
                                    props(
                                        "font-size" to "13px",
                                        "font-weight" to "700",
                                        "overflow" to "hidden",
                                        "text-overflow" to "ellipsis",
                                        "white-space" to "nowrap",
                                    )
                                }
                            }
                        ) {
                            Text(blocker.ticket.title)
                        }
                        Span(
                            attrs = {
                                style {
                                    props(
                                        "font-size" to "11px",
                                        "color" to Muted,
                                        "overflow" to "hidden",
                                        "text-overflow" to "ellipsis",
                                        "white-space" to "nowrap",
                                    )
                                }
                            }
                        ) {
                            Text(blocker.workspaceLabel)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun PrioritySelector(
    currentPriority: Kairos.TicketPriority?,
    onPrioritySelected: (Kairos.TicketPriority) -> Unit,
) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "flex",
                    "align-items" to "center",
                    "gap" to "6px",
                    "flex-wrap" to "nowrap",
                    "min-width" to "0",
                    "width" to "100%",
                )
            }
        }
    ) {
        Kairos.TicketPriority.entries.forEach { priority ->
            val color = priority.priorityColor()
            Button(
                attrs = {
                    attr("data-ticket-priority", priority.name.lowercase())
                    onClick { onPrioritySelected(priority) }
                    style { selectorPill(selected = priority == currentPriority, color = color) }
                }
            ) {
                Text(priority.uiLabel())
            }
        }
    }
}

private fun WebScheduleEntry.calendarSurfaceBackground(muted: Boolean): String =
    when {
        muted -> "#f7f8fa"
        kind == WebScheduleEntryKind.Event -> "#eef4ff"
        kind == WebScheduleEntryKind.Timebox -> "#e9f8f4"
        else -> "#fff6e8"
    }

private fun WebScheduleEntry.calendarBadgeBackground(muted: Boolean): String =
    when {
        muted -> "#eceff4"
        kind == WebScheduleEntryKind.Event -> "#dfe9ff"
        kind == WebScheduleEntryKind.Timebox -> "#d8f2ea"
        else -> "#fde7c6"
    }

private fun WebScheduleEntry.calendarBadgeTextColor(muted: Boolean): String =
    when {
        muted -> Muted
        kind == WebScheduleEntryKind.Event -> Blue
        kind == WebScheduleEntryKind.Timebox -> Teal
        else -> "#a65b00"
    }

@Composable
private fun UrgencySelector(
    currentUrgency: Kairos.TicketUrgency?,
    onUrgencySelected: (Kairos.TicketUrgency) -> Unit,
) {
    var expanded by remember(currentUrgency) { mutableStateOf(false) }
    val selectedUrgency = currentUrgency ?: Kairos.TicketUrgency.Medium

    Div(attrs = { style { props("position" to "relative", "width" to "100%") } }) {
        Button(
            attrs = {
                attr("id", "web-ticket-urgency-select")
                attr("data-ticket-urgency", selectedUrgency.name.lowercase())
                attr("aria-haspopup", "listbox")
                attr("aria-expanded", expanded.toString())
                onClick { expanded = !expanded }
                style {
                    props(
                        "min-height" to "36px",
                        "width" to "100%",
                        "border" to "1px solid $Border",
                        "border-radius" to "8px",
                        "padding" to "7px 12px",
                        "font-size" to "13px",
                        "font-weight" to "600",
                        "background" to Paper,
                        "color" to Ink,
                        "box-sizing" to "border-box",
                        "display" to "flex",
                        "align-items" to "center",
                        "justify-content" to "space-between",
                        "gap" to "10px",
                        "cursor" to "pointer",
                        "text-align" to "left",
                    )
                }
            }
        ) {
            Span(
                attrs = {
                    style {
                        props(
                            "display" to "inline-flex",
                            "align-items" to "center",
                            "gap" to "8px",
                            "min-width" to "0",
                            "flex" to "1",
                        )
                    }
                }
            ) {
                Span(
                    attrs = {
                        style {
                            props(
                                "width" to "8px",
                                "height" to "8px",
                                "border-radius" to "999px",
                                "background" to selectedUrgency.urgencyColor(),
                                "flex-shrink" to "0",
                            )
                        }
                    }
                )
                Text(selectedUrgency.uiLabel())
            }
            Span(attrs = { style { props("color" to Muted, "font-size" to "12px") } }) { Text("▾") }
        }
        if (expanded) {
            Div(
                attrs = {
                    style {
                        props(
                            "position" to "absolute",
                            "top" to "calc(100% + 6px)",
                            "left" to "0",
                            "right" to "0",
                            "z-index" to "20",
                            "display" to "grid",
                            "gap" to "4px",
                            "padding" to "6px",
                            "border" to "1px solid $Border",
                            "border-radius" to "8px",
                            "background" to Paper,
                            "box-shadow" to "0 10px 24px rgba(37,39,45,0.12)",
                        )
                    }
                }
            ) {
                Kairos.TicketUrgency.entries.forEach { urgency ->
                    val selected = urgency == currentUrgency
                    Button(
                        attrs = {
                            attr("data-ticket-urgency-option", urgency.name.lowercase())
                            onClick {
                                expanded = false
                                if (!selected) {
                                    onUrgencySelected(urgency)
                                }
                            }
                            style {
                                props(
                                    "min-height" to "38px",
                                    "width" to "100%",
                                    "border" to
                                        if (selected) "1px solid ${urgency.urgencyColor()}"
                                        else "1px solid transparent",
                                    "border-radius" to "8px",
                                    "background" to
                                        if (selected) hexToRgba(urgency.urgencyColor(), 0.12)
                                        else Paper,
                                    "padding" to "8px 10px",
                                    "display" to "flex",
                                    "align-items" to "center",
                                    "justify-content" to "space-between",
                                    "gap" to "10px",
                                    "cursor" to "pointer",
                                    "text-align" to "left",
                                    "font-size" to "13px",
                                    "font-weight" to if (selected) "700" else "600",
                                    "color" to Ink,
                                )
                            }
                        }
                    ) {
                        Span(
                            attrs = {
                                style {
                                    props(
                                        "display" to "inline-flex",
                                        "align-items" to "center",
                                        "gap" to "8px",
                                    )
                                }
                            }
                        ) {
                            Span(
                                attrs = {
                                    style {
                                        props(
                                            "width" to "8px",
                                            "height" to "8px",
                                            "border-radius" to "999px",
                                            "background" to urgency.urgencyColor(),
                                        )
                                    }
                                }
                            )
                            Text(urgency.uiLabel())
                        }
                        if (selected) {
                            Span(
                                attrs = {
                                    style {
                                        props(
                                            "font-size" to "12px",
                                            "color" to urgency.urgencyColor(),
                                        )
                                    }
                                }
                            ) {
                                Text("Active")
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AssigneeSelector(
    users: List<Kairos.User>,
    selectedAssigneeUserId: Kairos.UserId?,
    onUserSelected: (Kairos.UserId?) -> Unit,
    id: String = "web-ticket-assignee-select",
) {
    var expanded by remember(selectedAssigneeUserId?.value, users) { mutableStateOf(false) }
    val selectedUser = users.firstOrNull { user -> user.id == selectedAssigneeUserId }

    Div(attrs = { style { props("position" to "relative", "width" to "100%") } }) {
        Button(
            attrs = {
                attr("id", id)
                attr("data-ticket-assignee-select", selectedAssigneeUserId?.value ?: "")
                attr("aria-haspopup", "listbox")
                attr("aria-expanded", expanded.toString())
                onClick { expanded = !expanded }
                onKeyDown { event ->
                    if (event.key == "Escape") {
                        expanded = false
                    }
                }
                style {
                    props(
                        "min-height" to "36px",
                        "width" to "100%",
                        "border" to "1px solid $Border",
                        "border-radius" to "8px",
                        "padding" to "7px 12px",
                        "font-size" to "13px",
                        "font-weight" to "600",
                        "background" to Paper,
                        "color" to Ink,
                        "box-sizing" to "border-box",
                        "display" to "flex",
                        "align-items" to "center",
                        "justify-content" to "space-between",
                        "gap" to "10px",
                        "cursor" to "pointer",
                        "text-align" to "left",
                    )
                }
            }
        ) {
            Span(
                attrs = {
                    style {
                        props(
                            "display" to "inline-flex",
                            "align-items" to "center",
                            "gap" to "8px",
                            "min-width" to "0",
                            "flex" to "1",
                        )
                    }
                }
            ) {
                selectedUser?.let { user ->
                    Avatar(user, 22)
                    Span(
                        attrs = {
                            style {
                                props(
                                    "overflow" to "hidden",
                                    "text-overflow" to "ellipsis",
                                    "white-space" to "nowrap",
                                )
                            }
                        }
                    ) {
                        Text(user.displayName)
                    }
                } ?: Text("Unassigned")
            }
            Span(attrs = { style { props("color" to Muted, "font-size" to "12px") } }) { Text("▾") }
        }
        if (expanded) {
            Div(
                attrs = {
                    style {
                        props(
                            "position" to "absolute",
                            "top" to "calc(100% + 6px)",
                            "left" to "0",
                            "right" to "0",
                            "z-index" to "20",
                            "display" to "grid",
                            "gap" to "4px",
                            "padding" to "6px",
                            "border" to "1px solid $Border",
                            "border-radius" to "8px",
                            "background" to Paper,
                            "box-shadow" to "0 10px 24px rgba(37,39,45,0.12)",
                        )
                    }
                }
            ) {
                Button(
                    attrs = {
                        attr("data-ticket-assignee-option", "")
                        onClick {
                            expanded = false
                            if (selectedAssigneeUserId != null) {
                                onUserSelected(null)
                            }
                        }
                        style {
                            props(
                                "min-height" to "36px",
                                "width" to "100%",
                                "border" to
                                    if (selectedAssigneeUserId == null) {
                                        "1px solid $Blue"
                                    } else {
                                        "1px solid transparent"
                                    },
                                "border-radius" to "8px",
                                "background" to
                                    if (selectedAssigneeUserId == null) {
                                        "#eef4ff"
                                    } else {
                                        Paper
                                    },
                                "color" to Ink,
                                "padding" to "8px 10px",
                                "display" to "flex",
                                "align-items" to "center",
                                "justify-content" to "space-between",
                                "gap" to "10px",
                                "cursor" to "pointer",
                                "text-align" to "left",
                                "font-size" to "13px",
                                "font-weight" to
                                    if (selectedAssigneeUserId == null) {
                                        "700"
                                    } else {
                                        "600"
                                    },
                            )
                        }
                    }
                ) {
                    Span(
                        attrs = {
                            style {
                                props(
                                    "display" to "inline-flex",
                                    "align-items" to "center",
                                    "gap" to "8px",
                                )
                            }
                        }
                    ) {
                        Text("Unassigned")
                    }
                    if (selectedAssigneeUserId == null) {
                        Text("✓")
                    }
                }
                users.forEach { user ->
                    val selected = user.id == selectedAssigneeUserId
                    Button(
                        attrs = {
                            attr("data-ticket-assignee-option", user.id.value)
                            onClick {
                                expanded = false
                                if (!selected) {
                                    onUserSelected(user.id)
                                }
                            }
                            style {
                                props(
                                    "min-height" to "36px",
                                    "width" to "100%",
                                    "border" to
                                        if (selected) {
                                            "1px solid $Blue"
                                        } else {
                                            "1px solid transparent"
                                        },
                                    "border-radius" to "8px",
                                    "background" to if (selected) "#eef4ff" else Paper,
                                    "color" to Ink,
                                    "padding" to "8px 10px",
                                    "display" to "flex",
                                    "align-items" to "center",
                                    "justify-content" to "space-between",
                                    "gap" to "10px",
                                    "cursor" to "pointer",
                                    "text-align" to "left",
                                    "font-size" to "13px",
                                    "font-weight" to if (selected) "700" else "600",
                                )
                            }
                        }
                    ) {
                        Span(
                            attrs = {
                                style {
                                    props(
                                        "display" to "inline-flex",
                                        "align-items" to "center",
                                        "gap" to "8px",
                                        "min-width" to "0",
                                        "flex" to "1",
                                    )
                                }
                            }
                        ) {
                            Avatar(user, 22)
                            Span(
                                attrs = {
                                    style {
                                        props(
                                            "overflow" to "hidden",
                                            "text-overflow" to "ellipsis",
                                            "white-space" to "nowrap",
                                        )
                                    }
                                }
                            ) {
                                Text(user.displayName)
                            }
                        }
                        if (selected) {
                            Text("✓")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ProjectSelector(
    projects: List<Kairos.Project>,
    currentProjectId: Kairos.ProjectId,
    allProjects: List<Kairos.Project>,
    onProjectSelected: (Kairos.ProjectId) -> Unit,
    id: String = "web-ticket-project-select",
) {
    var expanded by remember(currentProjectId) { mutableStateOf(false) }
    var query by remember(currentProjectId) { mutableStateOf("") }
    val currentProject = projects.firstOrNull { project -> project.id == currentProjectId }
    val filteredProjects =
        remember(projects, query) {
            val needle = query.trim().lowercase()
            if (needle.isBlank()) {
                projects
            } else {
                projects.filter { project ->
                    project.projectDisplayName(allProjects).lowercase().contains(needle) ||
                        project.name.lowercase().contains(needle)
                }
            }
        }
    Div(attrs = { style { props("position" to "relative", "width" to "100%") } }) {
        Button(
            attrs = {
                attr("id", id)
                attr("data-ticket-project-select", currentProjectId.value)
                attr("aria-haspopup", "listbox")
                attr("aria-expanded", expanded.toString())
                onClick { expanded = !expanded }
                onKeyDown { event ->
                    if (event.key == "Escape") {
                        expanded = false
                    }
                }
                style {
                    props(
                        "min-height" to "36px",
                        "width" to "100%",
                        "border" to "1px solid $Border",
                        "border-radius" to "8px",
                        "padding" to "9px 12px",
                        "font-size" to "13px",
                        "font-weight" to "600",
                        "background" to Paper,
                        "color" to Ink,
                        "box-sizing" to "border-box",
                        "display" to "flex",
                        "align-items" to "center",
                        "justify-content" to "space-between",
                        "gap" to "10px",
                        "cursor" to "pointer",
                        "text-align" to "left",
                    )
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        props(
                            "flex" to "1",
                            "display" to "grid",
                            "gap" to "2px",
                            "min-width" to "0",
                        )
                    }
                }
            ) {
                Span(
                    attrs = {
                        style {
                            props(
                                "line-height" to "1.25",
                                "white-space" to "normal",
                                "overflow" to "hidden",
                                "display" to "-webkit-box",
                                "-webkit-line-clamp" to "2",
                                "-webkit-box-orient" to "vertical",
                            )
                        }
                    }
                ) {
                    Text(currentProject?.name ?: currentProjectId.value)
                }
                currentProject?.projectContextLabel(allProjects)?.let { context ->
                    Span(attrs = { style { props("font-size" to "11px", "color" to Muted) } }) {
                        Text(context)
                    }
                }
            }
            Span(attrs = { style { props("color" to Muted, "font-size" to "12px") } }) { Text("▾") }
        }
        if (expanded) {
            Div(
                attrs = {
                    style {
                        props(
                            "position" to "absolute",
                            "top" to "calc(100% + 6px)",
                            "left" to "0",
                            "right" to "0",
                            "z-index" to "20",
                            "display" to "grid",
                            "gap" to "4px",
                            "padding" to "6px",
                            "border" to "1px solid $Border",
                            "border-radius" to "8px",
                            "background" to Paper,
                            "box-shadow" to "0 10px 24px rgba(37,39,45,0.12)",
                            "max-height" to "264px",
                            "overflow" to "hidden",
                        )
                    }
                }
            ) {
                Input(
                    type = InputType.Text,
                    attrs = {
                        attr("data-ticket-project-search", id)
                        value(query)
                        onInput { query = it.value }
                        attr("placeholder", "Search projects")
                        style { modalTextInput() }
                    },
                )
                Div(
                    attrs = {
                        style {
                            props(
                                "display" to "grid",
                                "gap" to "4px",
                                "overflow-y" to "auto",
                                "padding-right" to "2px",
                            )
                        }
                    }
                ) {
                    filteredProjects.forEach { project ->
                        val selected = project.id == currentProjectId
                        val depth = project.projectDepth(allProjects)
                        Button(
                            attrs = {
                                attr("data-ticket-project-option", project.id.value)
                                onClick {
                                    expanded = false
                                    if (!selected) {
                                        onProjectSelected(project.id)
                                    }
                                }
                                style {
                                    props(
                                        "min-height" to "36px",
                                        "width" to "100%",
                                        "border" to
                                            if (selected) {
                                                "1px solid $Blue"
                                            } else {
                                                "1px solid transparent"
                                            },
                                        "border-radius" to "8px",
                                        "background" to if (selected) "#eef4ff" else Paper,
                                        "color" to Ink,
                                        "padding" to "8px 10px 8px ${10 + (depth * 14)}px",
                                        "display" to "flex",
                                        "align-items" to "center",
                                        "justify-content" to "space-between",
                                        "gap" to "10px",
                                        "cursor" to "pointer",
                                        "text-align" to "left",
                                        "font-size" to "13px",
                                        "font-weight" to if (selected) "700" else "600",
                                    )
                                }
                            }
                        ) {
                            Div(
                                attrs = {
                                    style {
                                        props(
                                            "flex" to "1",
                                            "display" to "grid",
                                            "gap" to "2px",
                                            "min-width" to "0",
                                        )
                                    }
                                }
                            ) {
                                Span(
                                    attrs = {
                                        style {
                                            props(
                                                "line-height" to "1.25",
                                                "white-space" to "normal",
                                                "font-weight" to if (depth == 0) "700" else "600",
                                            )
                                        }
                                    }
                                ) {
                                    Text(project.name)
                                }
                                if (depth > 0) {
                                    Span(
                                        attrs = {
                                            style { props("font-size" to "11px", "color" to Muted) }
                                        }
                                    ) {
                                        Text(project.projectContextLabel(allProjects).orEmpty())
                                    }
                                }
                            }
                            if (selected) {
                                Span(
                                    attrs = {
                                        style { props("color" to Blue, "font-size" to "12px") }
                                    }
                                ) {
                                    Text("✓")
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ObjectiveSelector(
    linkedObjectives: List<Kairos.Objective>,
    availableObjectives: List<Kairos.Objective>,
    allProjects: List<Kairos.Project>,
    onObjectiveLinked: (Kairos.Objective) -> Unit,
    onObjectiveRemoved: (Kairos.Objective) -> Unit,
    id: String = "web-ticket-objective-select",
) {
    var expanded by remember(linkedObjectives, availableObjectives) { mutableStateOf(false) }
    var query by remember(linkedObjectives, availableObjectives) { mutableStateOf("") }
    val filteredObjectives =
        remember(availableObjectives, query) {
            val needle = query.trim().lowercase()
            if (needle.isBlank()) {
                availableObjectives
            } else {
                availableObjectives.filter { objective ->
                    objective.description.lowercase().contains(needle) ||
                        objective.projectLabel(allProjects)?.lowercase()?.contains(needle) == true
                }
            }
        }

    Div(attrs = { style { props("display" to "grid", "gap" to "8px", "width" to "100%") } }) {
        if (linkedObjectives.isEmpty()) {
            MutedText("No objectives linked")
        } else {
            Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
                linkedObjectives.forEach { objective ->
                    Button(
                        attrs = {
                            attr("data-objective-unlink-id", objective.id.value)
                            attr("title", objective.description)
                            onClick { onObjectiveRemoved(objective) }
                            style {
                                props(
                                    "min-height" to "34px",
                                    "width" to "100%",
                                    "border" to "1px solid $Blue",
                                    "border-radius" to "8px",
                                    "background" to "#eef4ff",
                                    "color" to Ink,
                                    "padding" to "8px 10px",
                                    "font-size" to "13px",
                                    "font-weight" to "700",
                                    "cursor" to "pointer",
                                    "display" to "inline-flex",
                                    "align-items" to "center",
                                    "justify-content" to "space-between",
                                    "gap" to "10px",
                                    "flex" to "0 1 auto",
                                )
                            }
                        }
                    ) {
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "grid",
                                        "gap" to "2px",
                                        "min-width" to "0",
                                        "flex" to "1",
                                    )
                                }
                            }
                        ) {
                            Span(
                                attrs = {
                                    style {
                                        props(
                                            "display" to "block",
                                            "overflow" to "hidden",
                                            "text-overflow" to "ellipsis",
                                            "white-space" to "nowrap",
                                            "min-width" to "0",
                                        )
                                    }
                                }
                            ) {
                                Text(objective.description)
                            }
                            objective.projectLabel(allProjects)?.let { projectLabel ->
                                Span(
                                    attrs = {
                                        style { props("font-size" to "11px", "color" to Muted) }
                                    }
                                ) {
                                    Text(projectLabel)
                                }
                            }
                        }
                        Text("×")
                    }
                }
            }
        }
        if (availableObjectives.isNotEmpty()) {
            Div(attrs = { style { props("position" to "relative", "width" to "100%") } }) {
                Button(
                    attrs = {
                        attr("id", id)
                        attr("aria-haspopup", "listbox")
                        attr("aria-expanded", expanded.toString())
                        onClick { expanded = !expanded }
                        style {
                            props("width" to "100%")
                            secondaryButton(selected = expanded)
                        }
                    }
                ) {
                    Text(
                        if (linkedObjectives.isEmpty()) "Link objective"
                        else "Link another objective"
                    )
                }
                if (expanded) {
                    Div(
                        attrs = {
                            style {
                                props(
                                    "position" to "absolute",
                                    "top" to "calc(100% + 6px)",
                                    "left" to "0",
                                    "right" to "0",
                                    "z-index" to "20",
                                    "display" to "grid",
                                    "gap" to "4px",
                                    "padding" to "6px",
                                    "border" to "1px solid $Border",
                                    "border-radius" to "8px",
                                    "background" to Paper,
                                    "box-shadow" to "0 10px 24px rgba(37,39,45,0.12)",
                                    "max-height" to "264px",
                                    "overflow" to "hidden",
                                )
                            }
                        }
                    ) {
                        Input(
                            type = InputType.Text,
                            attrs = {
                                attr("data-objective-link-search", id)
                                value(query)
                                onInput { query = it.value }
                                attr("placeholder", "Search objectives")
                                style { modalTextInput() }
                            },
                        )
                        Div(
                            attrs = {
                                style {
                                    props(
                                        "display" to "grid",
                                        "gap" to "4px",
                                        "overflow-y" to "auto",
                                        "padding-right" to "2px",
                                    )
                                }
                            }
                        ) {
                            filteredObjectives.forEach { objective ->
                                Button(
                                    attrs = {
                                        attr("data-objective-link-option", objective.id.value)
                                        onClick {
                                            expanded = false
                                            onObjectiveLinked(objective)
                                        }
                                        style { dropdownOptionButton() }
                                    }
                                ) {
                                    Div(
                                        attrs = {
                                            style {
                                                props(
                                                    "display" to "grid",
                                                    "gap" to "2px",
                                                    "min-width" to "0",
                                                    "width" to "100%",
                                                )
                                            }
                                        }
                                    ) {
                                        Span(
                                            attrs = {
                                                style {
                                                    props(
                                                        "display" to "block",
                                                        "overflow" to "hidden",
                                                        "text-overflow" to "ellipsis",
                                                        "white-space" to "nowrap",
                                                        "min-width" to "0",
                                                        "width" to "100%",
                                                    )
                                                }
                                            }
                                        ) {
                                            Text(objective.description)
                                        }
                                        objective.projectLabel(allProjects)?.let { projectLabel ->
                                            Span(
                                                attrs = {
                                                    style {
                                                        props(
                                                            "font-size" to "11px",
                                                            "color" to Muted,
                                                        )
                                                    }
                                                }
                                            ) {
                                                Text(projectLabel)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun MarkdownDescriptionEditor(
    value: String,
    savedValue: String,
    editing: Boolean,
    onStartEditing: () -> Unit,
    onChange: (String) -> Unit,
    onCancel: () -> Unit,
    onSave: () -> Unit,
) {
    if (!editing) {
        Div(
            attrs = {
                attr("id", "web-ticket-description-preview")
                attr("tabindex", "0")
                onClick { onStartEditing() }
                onKeyDown { event ->
                    if (event.key == "Enter" || event.key == " ") {
                        event.preventDefault()
                        onStartEditing()
                    }
                }
                style {
                    props(
                        "border" to "1px solid $Border",
                        "border-radius" to "8px",
                        "padding" to "14px",
                        "min-height" to "124px",
                        "background" to Paper,
                        "cursor" to "text",
                    )
                }
            }
        ) {
            MarkdownPreviewContent(
                source = savedValue,
                emptyLabel = "Add details, links, and handoff notes",
            )
        }
        return
    }

    Div(attrs = { attr("id", "web-ticket-description-editor") }) {
        TextArea(
            attrs = {
                attr("id", "web-ticket-description-input")
                value(value)
                onInput { onChange(it.value) }
                onKeyDown { event ->
                    if ((event.metaKey || event.ctrlKey) && event.key == "Enter") {
                        event.preventDefault()
                        onSave()
                    } else if (event.key == "Escape") {
                        event.preventDefault()
                        onCancel()
                    }
                }
                style {
                    props(
                        "border" to "1px solid $Border",
                        "border-radius" to "8px",
                        "padding" to "14px",
                        "min-height" to "144px",
                        "width" to "100%",
                        "box-sizing" to "border-box",
                        "font-size" to "14px",
                        "line-height" to "1.6",
                        "font-family" to "ui-monospace, SFMono-Regular, Menlo, monospace",
                        "resize" to "vertical",
                        "background" to "#fbfcfd",
                        "color" to Ink,
                    )
                }
            }
        )
        Div(attrs = { style { props("margin-top" to "10px") } }) {
            InlineGuidance("Markdown is supported. Press Ctrl/Cmd+Enter to save or Esc to cancel.")
        }
        Div(
            attrs = {
                style {
                    props(
                        "margin-top" to "10px",
                        "display" to "flex",
                        "gap" to "10px",
                        "flex-wrap" to "wrap",
                    )
                }
            }
        ) {
            Button(
                attrs = {
                    attr("id", "web-ticket-save-description-button")
                    if (value == savedValue) disabled()
                    onClick { onSave() }
                    style { primaryButton(enabled = value != savedValue) }
                }
            ) {
                Text("Save description")
            }
            Button(
                attrs = {
                    attr("id", "web-ticket-cancel-description-button")
                    onClick { onCancel() }
                    style { secondaryButton() }
                }
            ) {
                Text("Cancel")
            }
        }
    }
}

@Composable
private fun MarkdownPreviewContent(source: String, emptyLabel: String) {
    val blocks = parseKairosMarkdown(source)
    if (blocks.isEmpty()) {
        Span(attrs = { style { props("color" to Muted) } }) { Text(emptyLabel) }
        return
    }
    blocks.forEachIndexed { index, block ->
        when (block) {
            is KairosMarkdownBlock.Heading -> MarkdownHeading(block)
            is KairosMarkdownBlock.Paragraph -> MarkdownParagraph(block.content)
            is KairosMarkdownBlock.BulletList -> MarkdownBulletList(block.items)
            is KairosMarkdownBlock.OrderedList -> MarkdownOrderedList(block.items)
            is KairosMarkdownBlock.Quote -> MarkdownQuote(block.content)
            is KairosMarkdownBlock.CodeFence -> MarkdownCodeFence(block.code)
        }
        if (index != blocks.lastIndex) {
            Div(attrs = { style { props("height" to "10px") } })
        }
    }
}

@Composable
private fun MarkdownHeading(block: KairosMarkdownBlock.Heading) {
    val size =
        when (block.level) {
            1 -> "24px"
            2 -> "20px"
            3 -> "17px"
            else -> "15px"
        }
    Div(
        attrs = {
            style {
                props(
                    "font-size" to size,
                    "font-weight" to "700",
                    "line-height" to "1.35",
                    "color" to Ink,
                )
            }
        }
    ) {
        MarkdownInlineContent(block.content)
    }
}

@Composable
private fun MarkdownParagraph(content: String) {
    Div(attrs = { style { props("line-height" to "1.65", "color" to Ink) } }) {
        MarkdownInlineContent(content)
    }
}

@Composable
private fun MarkdownBulletList(items: List<String>) {
    Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
        items.forEach { item ->
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "grid-template-columns" to "14px minmax(0, 1fr)",
                            "gap" to "10px",
                            "align-items" to "start",
                            "line-height" to "1.6",
                        )
                    }
                }
            ) {
                Span(attrs = { style { props("color" to Teal, "font-weight" to "700") } }) {
                    Text("•")
                }
                Div(attrs = { style { props("min-width" to "0", "color" to Ink) } }) {
                    MarkdownInlineContent(item)
                }
            }
        }
    }
}

@Composable
private fun MarkdownOrderedList(items: List<String>) {
    Div(attrs = { style { props("display" to "grid", "gap" to "8px") } }) {
        items.forEachIndexed { index, item ->
            Div(
                attrs = {
                    style {
                        props(
                            "display" to "grid",
                            "grid-template-columns" to "24px minmax(0, 1fr)",
                            "gap" to "10px",
                            "align-items" to "start",
                            "line-height" to "1.6",
                        )
                    }
                }
            ) {
                Span(
                    attrs = {
                        style {
                            props("color" to Muted, "font-weight" to "700", "text-align" to "right")
                        }
                    }
                ) {
                    Text("${index + 1}.")
                }
                Div(attrs = { style { props("min-width" to "0", "color" to Ink) } }) {
                    MarkdownInlineContent(item)
                }
            }
        }
    }
}

@Composable
private fun MarkdownQuote(content: String) {
    Div(
        attrs = {
            style {
                props(
                    "border-left" to "3px solid $Teal",
                    "padding-left" to "12px",
                    "color" to Muted,
                    "display" to "grid",
                    "gap" to "6px",
                )
            }
        }
    ) {
        content.split('\n').forEach { line -> MarkdownParagraph(line) }
    }
}

@Composable
private fun MarkdownCodeFence(code: String) {
    Div(
        attrs = {
            style {
                props(
                    "border" to "1px solid $Border",
                    "border-radius" to "8px",
                    "padding" to "12px 14px",
                    "background" to "#fbfcfd",
                    "font-family" to "ui-monospace, SFMono-Regular, Menlo, monospace",
                    "font-size" to "13px",
                    "line-height" to "1.6",
                    "white-space" to "pre-wrap",
                    "overflow-x" to "auto",
                )
            }
        }
    ) {
        Text(code)
    }
}

@Composable
private fun MarkdownInlineContent(source: String) {
    MarkdownInlineFragments(parseKairosMarkdownInline(source))
}

@Composable
private fun MarkdownInlineFragments(fragments: List<KairosMarkdownInline>) {
    fragments.forEach { fragment ->
        when (fragment) {
            is KairosMarkdownInline.Text -> Text(fragment.value)
            is KairosMarkdownInline.Bold -> {
                Span(attrs = { style { props("font-weight" to "700") } }) {
                    MarkdownInlineFragments(fragment.children)
                }
            }
            is KairosMarkdownInline.Italic -> {
                Span(attrs = { style { props("font-style" to "italic") } }) {
                    MarkdownInlineFragments(fragment.children)
                }
            }
            is KairosMarkdownInline.Code -> {
                Span(
                    attrs = {
                        style {
                            props(
                                "font-family" to "ui-monospace, SFMono-Regular, Menlo, monospace",
                                "font-size" to "0.95em",
                                "background" to "#f2f3f5",
                                "border-radius" to "6px",
                                "padding" to "1px 5px",
                            )
                        }
                    }
                ) {
                    Text(fragment.value)
                }
            }
            is KairosMarkdownInline.Link -> {
                Span(
                    attrs = {
                        style {
                            props(
                                "color" to Blue,
                                "text-decoration" to "underline",
                                "text-underline-offset" to "2px",
                            )
                        }
                    }
                ) {
                    MarkdownInlineFragments(fragment.label)
                }
            }
        }
    }
}

@Composable
private fun AvatarStack(users: List<Kairos.User>, compact: Boolean) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "flex",
                    "align-items" to "center",
                    "margin-left" to if (compact) "0" else "8px",
                    "flex-shrink" to "0",
                )
            }
        }
    ) {
        users.forEachIndexed { index, user ->
            Avatar(user, if (compact) 28 else 30, stacked = index > 0)
        }
    }
}

@Composable
private fun Avatar(user: Kairos.User?, size: Int, stacked: Boolean = false) {
    Div(
        attrs = {
            style {
                props(
                    "position" to "relative",
                    "width" to "${size}px",
                    "height" to "${size}px",
                    "margin-left" to if (stacked) "-8px" else "0",
                    "flex-shrink" to "0",
                )
            }
        }
    ) {
        Img(
            src = user.avatarUrl(),
            alt = user?.displayName ?: "Unassigned",
            attrs = {
                style {
                    props(
                        "width" to "${size}px",
                        "height" to "${size}px",
                        "border-radius" to "50%",
                        "object-fit" to "cover",
                        "border" to "2px solid $Paper",
                        "box-sizing" to "border-box",
                        "background" to "#d9dde4",
                        "display" to "block",
                    )
                }
            },
        )
        if (user?.kind == Kairos.UserKind.Agent) {
            Div(
                attrs = {
                    style {
                        props(
                            "position" to "absolute",
                            "right" to "-2px",
                            "bottom" to "-2px",
                            "width" to "${(size * 0.42).toInt().coerceAtLeast(12)}px",
                            "height" to "${(size * 0.42).toInt().coerceAtLeast(12)}px",
                            "border-radius" to "999px",
                            "background" to Blue,
                            "border" to "2px solid $Paper",
                            "display" to "flex",
                            "align-items" to "center",
                            "justify-content" to "center",
                            "color" to Paper,
                            "font-size" to "${(size * 0.18).toInt().coerceAtLeast(8)}px",
                            "font-weight" to "900",
                            "line-height" to "1",
                        )
                    }
                }
            ) {
                Text("A")
            }
        }
    }
}

@Composable
private fun Chip(label: String, color: String, textColor: String) {
    Span(
        attrs = {
            style {
                props(
                    "display" to "inline-flex",
                    "align-items" to "center",
                    "height" to "26px",
                    "border-radius" to "6px",
                    "padding" to "0 9px",
                    "background" to color,
                    "color" to textColor,
                    "font-size" to "13px",
                    "font-weight" to "700",
                    "white-space" to "nowrap",
                )
            }
        }
    ) {
        Text(label)
    }
}

@Composable
private fun MetaChip(label: String, background: String = "#f3f4f6", textColor: String = Ink) {
    Span(
        attrs = {
            style {
                props(
                    "display" to "inline-flex",
                    "align-items" to "center",
                    "height" to "22px",
                    "border-radius" to "999px",
                    "padding" to "0 8px",
                    "background" to background,
                    "color" to textColor,
                    "font-size" to "11px",
                    "font-weight" to "700",
                    "white-space" to "nowrap",
                )
            }
        }
    ) {
        Text(label)
    }
}

@Composable
private fun BlockerChip(label: String) {
    Span(
        attrs = {
            style {
                props(
                    "display" to "inline-flex",
                    "align-items" to "start",
                    "max-width" to "100%",
                    "border-radius" to "6px",
                    "padding" to "5px 8px",
                    "border" to "1px solid #f5c4ba",
                    "background" to "#ffeeea",
                    "color" to Coral,
                    "font-size" to "12px",
                    "font-weight" to "700",
                    "line-height" to "1.4",
                    "box-sizing" to "border-box",
                    "white-space" to "normal",
                    "overflow-wrap" to "anywhere",
                )
            }
        }
    ) {
        Text("⛔ $label")
    }
}

@Composable
private fun TaskStatusBadge(label: String, color: String) {
    Span(
        attrs = {
            style {
                props(
                    "display" to "inline-flex",
                    "align-items" to "center",
                    "gap" to "6px",
                    "height" to "24px",
                    "border-radius" to "999px",
                    "padding" to "0 9px",
                    "border" to "1px solid ${taskTint(color)}",
                    "background" to Paper,
                    "color" to Muted,
                    "font-size" to "12px",
                    "font-weight" to "600",
                    "white-space" to "nowrap",
                )
            }
        }
    ) {
        Span(
            attrs = {
                style {
                    props(
                        "width" to "6px",
                        "height" to "6px",
                        "border-radius" to "999px",
                        "background" to color,
                    )
                }
            }
        )
        Text(label)
    }
}

@Composable
private fun TaskStatusInline(label: String, color: String) {
    Span(
        attrs = {
            style {
                props(
                    "display" to "inline-flex",
                    "align-items" to "center",
                    "gap" to "6px",
                    "height" to "24px",
                    "padding" to "0 9px",
                    "border-radius" to "999px",
                    "background" to hexToRgba(color, 0.12),
                    "border" to "1px solid ${hexToRgba(color, 0.18)}",
                    "font-size" to "12px",
                    "font-weight" to "700",
                    "color" to color,
                    "white-space" to "nowrap",
                )
            }
        }
    ) {
        Span(
            attrs = {
                style {
                    props(
                        "width" to "7px",
                        "height" to "7px",
                        "border-radius" to "999px",
                        "background" to color,
                        "flex-shrink" to "0",
                    )
                }
            }
        )
        Text(label)
    }
}

@Composable
private fun InlineGuidance(text: String) {
    Div(
        attrs = {
            style {
                props(
                    "display" to "flex",
                    "align-items" to "center",
                    "gap" to "8px",
                    "min-width" to "0",
                    "max-width" to "760px",
                    "font-size" to "14px",
                    "color" to Muted,
                    "line-height" to "1.45",
                )
            }
        }
    ) {
        Span(
            attrs = {
                style {
                    props(
                        "display" to "inline-flex",
                        "align-items" to "center",
                        "justify-content" to "center",
                        "width" to "18px",
                        "height" to "18px",
                        "border-radius" to "999px",
                        "background" to "#eef1f5",
                        "color" to "#667085",
                        "font-size" to "11px",
                        "font-weight" to "700",
                        "flex-shrink" to "0",
                    )
                }
            }
        ) {
            Text("i")
        }
        Text(text)
    }
}

@Composable
private fun StatusPill(label: String, dotColor: String, background: String) {
    Span(
        attrs = {
            style {
                props(
                    "display" to "inline-flex",
                    "align-items" to "center",
                    "gap" to "8px",
                    "height" to "28px",
                    "border-radius" to "8px",
                    "padding" to "0 12px",
                    "background" to background,
                    "color" to dotColor,
                    "font-size" to "14px",
                    "font-weight" to "700",
                    "white-space" to "nowrap",
                )
            }
        }
    ) {
        Span(
            attrs = {
                style {
                    props(
                        "width" to "8px",
                        "height" to "8px",
                        "border-radius" to "50%",
                        "background" to dotColor,
                    )
                }
            }
        )
        Text(label)
    }
}

private fun createEmptyWorkspace(): WebWorkspace {
    val now = Clock.System.now()
    return WebWorkspace(
        workspace =
            Kairos.Workspace(
                id = Kairos.WorkspaceId("web-empty-workspace"),
                createdAt = now,
                name = "Kairos",
            ),
        sourceName = "Kairos",
        users = emptyList(),
        projects = emptyList(),
        tickets = emptyList(),
    )
}

private fun createWorkspace(): WebWorkspace {
    val now = Clock.System.now()
    val jamie = user("web-user-jamie", "Jamie Staples", "jamie@phoenix.example", now)
    val alan = user("web-user-alan", "Alan Wang", "alan@phoenix.example", now)
    val mina = user("web-user-mina", "Mina Frost", "mina@phoenix.example", now)
    val noor = user("web-user-noor", "Noor Lin", "noor@phoenix.example", now)
    val ravi = user("web-user-ravi", "Ravi Singh", "ravi@phoenix.example", now)

    val root = project("web-project-root", "Requests for Creative Production", null, now)
    val ads = project("web-project-ads", "Ads", root.id, now)
    val web = project("web-project-web", "Web", root.id, now)
    val content = project("web-project-content", "Content", root.id, now)
    val email = project("web-project-email", "Email", root.id, now)
    val campaignLeadsKpi =
        kpi(
            id = "web-kpi-campaign-leads",
            projectId = root.id,
            name = "Community campaign leads",
            createdAt = now,
            targetValue = Kairos.KpiTarget.AtLeast(500.0),
        )
    val qaScoreKpi =
        kpi(
            id = "web-kpi-launch-qa-score",
            projectId = web.id,
            name = "Launch QA score",
            createdAt = now,
            targetValue = Kairos.KpiTarget.AtLeast(95.0),
        )
    val launchObjective =
        objective(
            id = "web-objective-launch",
            projectId = root.id,
            description = "Launch the community mobile app campaign",
            kpiTargets =
                listOf(
                    Kairos.KpiWithTarget(
                        kpi = campaignLeadsKpi.id,
                        target = Kairos.KpiTarget.AtLeast(500.0),
                    )
                ),
            ticketIds =
                listOf(
                    Kairos.TicketId("web-ticket-1"),
                    Kairos.TicketId("web-ticket-2"),
                    Kairos.TicketId("web-ticket-3"),
                ),
            deadline = ZonedDateTime(LocalDateTime(2026, 4, 30, 17, 0), TimeZone.UTC),
            createdAt = now,
        )
    val reviewObjective =
        objective(
            id = "web-objective-review",
            projectId = root.id,
            description = "Launch review checklist",
            kpiTargets =
                listOf(
                    Kairos.KpiWithTarget(
                        kpi = qaScoreKpi.id,
                        target = Kairos.KpiTarget.AtLeast(95.0),
                    )
                ),
            ticketIds = listOf(Kairos.TicketId("web-ticket-1")),
            deadline = ZonedDateTime(LocalDateTime(2026, 5, 2, 23, 59), TimeZone.UTC),
            createdAt = now,
        )
    val timeboxes =
        listOf(
            timebox(
                id = "web-timebox-1",
                projectId = ads.id,
                ticketIds = listOf(Kairos.TicketId("web-ticket-1")),
                description = "Budget planning block",
                startTime =
                    ZonedDateTime(LocalDateTime(2026, 4, 16, 9, 0), TimeZone.UTC).toInstant(),
                recordedDuration = TimeSpan(listOf(PrimitiveTimeSpan(2, TimeUnit.Hours))),
                createdAt = now,
            ),
            timebox(
                id = "web-timebox-2",
                projectId = web.id,
                ticketIds = listOf(Kairos.TicketId("web-ticket-3")),
                description = "Web update work block",
                startTime =
                    ZonedDateTime(LocalDateTime(2026, 4, 18, 13, 0), TimeZone.UTC).toInstant(),
                recordedDuration = TimeSpan(listOf(PrimitiveTimeSpan(3, TimeUnit.Hours))),
                createdAt = now,
            ),
            timebox(
                id = "web-timebox-3",
                projectId = content.id,
                ticketIds = listOf(Kairos.TicketId("web-ticket-6")),
                description = "Help content drafting",
                startTime =
                    ZonedDateTime(LocalDateTime(2026, 4, 17, 10, 0), TimeZone.UTC).toInstant(),
                recordedDuration = TimeSpan(listOf(PrimitiveTimeSpan(4, TimeUnit.Hours))),
                createdAt = now,
            ),
        )
    val routines =
        listOf(
            routine("web-routine-1", ads.id, "Review ad spend intake", now),
            routine("web-routine-2", web.id, "Sweep outstanding design requests", now),
            routine("web-routine-3", content.id, "Check launch copy dependencies", now),
        )
    val events =
        listOf(
            event(
                id = "web-event-1",
                projectId = ads.id,
                title = "Campaign budget review",
                summary = "Campaign budget review",
                dtStart = ZonedDateTime(LocalDateTime(2026, 4, 16, 14, 0), TimeZone.UTC),
                dtEnd = ZonedDateTime(LocalDateTime(2026, 4, 16, 15, 0), TimeZone.UTC),
                createdAt = now,
                description = "Review the monthly community ads budget and approvals.",
                location = "Phoenix / Ops room",
            ),
            event(
                id = "web-event-2",
                projectId = web.id,
                title = "Website design review",
                summary = "Website design review",
                dtStart = ZonedDateTime(LocalDateTime(2026, 4, 28, 15, 0), TimeZone.UTC),
                dtEnd = ZonedDateTime(LocalDateTime(2026, 4, 28, 16, 30), TimeZone.UTC),
                createdAt = now,
                description = "Review the request and final QA pass together.",
                location = "Phoenix / Design",
            ),
            event(
                id = "web-event-3",
                projectId = content.id,
                title = "Launch content workshop",
                summary = "Launch content workshop",
                dtStart = ZonedDateTime(LocalDateTime(2026, 4, 24, 10, 0), TimeZone.UTC),
                dtEnd = ZonedDateTime(LocalDateTime(2026, 4, 24, 12, 0), TimeZone.UTC),
                createdAt = now,
                description = "Align launch copy, support content, and localization.",
                location = "Phoenix / Content",
            ),
        )
    val tickets =
        listOf(
            ticket("web-ticket-1", ads.id, jamie.id, "Monthly budget for community ads", 16),
            ticket("web-ticket-2", ads.id, alan.id, "Launch community retargeting ads", 20),
            ticket("web-ticket-3", web.id, mina.id, "Identify web updates", 18),
            ticket("web-ticket-4", web.id, noor.id, "Website design request", 28),
            ticket("web-ticket-5", web.id, ravi.id, "Website design QA", 30),
            ticket("web-ticket-6", content.id, jamie.id, "How to use the community mobile app", 17),
            ticket(
                "web-ticket-7",
                content.id,
                alan.id,
                "Announcing the community mobile app",
                2026,
                5,
                4,
            ),
            ticket("web-ticket-8", content.id, mina.id, "Localized ad creative", 2026, 5, 6),
            ticket("web-ticket-9", email.id, noor.id, "Community mobile app announcement", 20),
            ticket("web-ticket-10", email.id, ravi.id, "Monthly newsletter inclusion", 2026, 5, 4),
        )
    val activities =
        tickets.mapIndexed { index, ticket ->
            Kairos.TicketActivity(
                id = Kairos.TicketActivityId("web-activity-${index + 1}"),
                createdAt = now,
                ticketId = ticket.id,
                actorUserId = ticket.assigneeUserId,
                kind = Kairos.TicketActivityKind.Created,
                summary = "Ticket created",
            )
        }

    return WebWorkspace(
            workspace =
                Kairos.Workspace(
                    id = DemoWebWorkspaceId,
                    createdAt = DemoWebWorkspaceCreatedAt,
                    name = "Phoenix",
                ),
            memberships =
                listOf(jamie, alan, mina, noor, ravi).map { user ->
                    Kairos.WorkspaceMembership(
                        workspaceId = DemoWebWorkspaceId,
                        userId = user.id,
                        createdAt = now,
                    )
                },
            sourceName = "Browser local workspace",
            users = listOf(jamie, alan, mina, noor, ravi),
            projects = listOf(root, ads, web, content, email),
            tickets = tickets,
            ticketBlockers =
                listOf(
                    Kairos.TicketBlocker(
                        blockedTicketId = Kairos.TicketId("web-ticket-5"),
                        blockingTicketId = Kairos.TicketId("web-ticket-3"),
                        createdAt = now,
                    ),
                    Kairos.TicketBlocker(
                        blockedTicketId = Kairos.TicketId("web-ticket-8"),
                        blockingTicketId = Kairos.TicketId("web-ticket-6"),
                        createdAt = now,
                    ),
                ),
            activities = activities,
            objectives = listOf(launchObjective, reviewObjective),
            kpis = listOf(campaignLeadsKpi, qaScoreKpi),
            measurements =
                listOf(
                    kpiMeasurement("web-kpi-measurement-1", campaignLeadsKpi.id.value, 312.0, now),
                    kpiMeasurement("web-kpi-measurement-2", qaScoreKpi.id.value, 91.0, now),
                ),
            timeboxes = timeboxes,
            routines = routines,
            events = events,
        )
        .rebuildKpiEvaluations()
}

private fun WebWorkspace.toColumns(
    selectedProjectId: Kairos.ProjectId?,
    searchQuery: String,
    includeCompleted: Boolean,
    hideEmptyColumns: Boolean,
    sort: WebSort,
): List<WebColumn> {
    val selectedProject = selectedProjectId?.let { id -> projects.firstOrNull { it.id == id } }
    val root = primaryProject()
    val visibleProjects =
        when {
            selectedProject == null ->
                root
                    ?.let { rootProject ->
                        projects.filter { project -> project.parentProjectId == rootProject.id }
                    }
                    .orEmpty()
            selectedProject.parentProjectId == null ->
                projects.filter { project -> project.parentProjectId == selectedProject.id }
            else -> listOf(selectedProject)
        }.ifEmpty { projects.filter { it.parentProjectId != null }.ifEmpty { projects } }

    val normalizedQuery = searchQuery.trim().lowercase()
    return visibleProjects
        .map { project ->
            val tasks =
                tickets
                    .filter { ticket ->
                        ticket.projectId == project.id &&
                            (includeCompleted || ticket.completedAt == null) &&
                            (normalizedQuery.isBlank() ||
                                ticket.matches(normalizedQuery, project, this))
                    }
                    .sortedFor(sort)
                    .map { ticket ->
                        val owner =
                            ticket.assigneeUserId?.let { userId ->
                                users.firstOrNull { it.id == userId }
                            }
                        val status = ticket.statusLabel()
                        WebTask(
                            ticket = ticket,
                            project = project,
                            workspaceLabel = projectLabel(project.id),
                            owner = owner,
                            status = status.first,
                            statusColor = status.second,
                            accent = project.projectAccent(),
                            objectiveCount = objectiveCountFor(ticket.id),
                            blockerLabel = ticketBlockerLabel(ticket.id),
                        )
                    }
            WebColumn(project = project, tasks = tasks)
        }
        .filter { column -> !hideEmptyColumns || column.tasks.isNotEmpty() }
}

private fun WebWorkspace.filteredTasks(
    searchQuery: String,
    includeCompleted: Boolean,
    sort: WebSort,
): List<WebTask> {
    val normalizedQuery = searchQuery.trim().lowercase()
    return tickets
        .filter { ticket ->
            val project = projects.firstOrNull { it.id == ticket.projectId }
            (includeCompleted || ticket.completedAt == null) &&
                (normalizedQuery.isBlank() ||
                    (project != null && ticket.matches(normalizedQuery, project, this)))
        }
        .sortedFor(sort)
        .map { ticket ->
            val project = projects.firstOrNull { it.id == ticket.projectId } ?: return@map null
            val owner =
                ticket.assigneeUserId?.let { userId -> users.firstOrNull { it.id == userId } }
            val status = ticket.statusLabel()
            WebTask(
                ticket = ticket,
                project = project,
                workspaceLabel = projectLabel(project.id),
                owner = owner,
                status = status.first,
                statusColor = status.second,
                accent = project.projectAccent(),
                objectiveCount = objectiveCountFor(ticket.id),
                blockerLabel = ticketBlockerLabel(ticket.id),
            )
        }
        .filterNotNull()
}

private fun Kairos.Ticket.matches(
    normalizedQuery: String,
    project: Kairos.Project,
    workspace: WebWorkspace,
): Boolean {
    val user = assigneeUserId?.let { userId -> workspace.users.firstOrNull { it.id == userId } }
    return listOf(
            title,
            description,
            project.name,
            stage.uiLabel(),
            user?.displayName.orEmpty(),
            user?.email.orEmpty(),
        )
        .any { it.lowercase().contains(normalizedQuery) }
}

private fun List<Kairos.Ticket>.sortedFor(sort: WebSort): List<Kairos.Ticket> =
    when (sort) {
        WebSort.Created -> sortedBy { it.createdAt.toEpochMilliseconds() }
        WebSort.Due ->
            sortedBy { it.deadline?.toInstant()?.toEpochMilliseconds() ?: Long.MAX_VALUE }
        WebSort.Title -> sortedBy { it.title.lowercase() }
    }

private data class WebTaskSummary(
    val openCount: Int,
    val completedCount: Int,
    val inProgressCount: Int,
    val reviewCount: Int,
    val blockedCount: Int,
    val overdueCount: Int,
    val dueSoonCount: Int,
    val unassignedCount: Int,
    val activityCount: Int,
    val completionPercent: Int,
)

private data class WebObjectiveSummary(
    val objective: Kairos.Objective,
    val description: String,
    val projectName: String,
    val deadlineLabel: String,
    val progressPercent: Int,
    val statusLabel: String,
    val statusColor: String,
    val statusBackground: String,
    val linkedTasks: List<WebTask>,
    val kpis: List<WebObjectiveKpiSummary>,
)

private data class WebObjectiveKpiSummary(
    val kpi: Kairos.Kpi,
    val target: Kairos.KpiTarget,
    val evaluation: KairosKpiEvaluation?,
    val currentValue: Double?,
    val recentPoints: List<KairosKpiValuePoint>,
    val latestValueLabel: String,
    val targetLabel: String,
    val progressPercent: Int,
    val statusLabel: String,
    val statusColor: String,
    val statusBackground: String,
)

private data class WebWorkspaceKpiSummary(
    val kpi: Kairos.Kpi,
    val projectName: String,
    val linkedObjectiveCount: Int,
    val evaluation: KairosKpiEvaluation?,
    val currentValue: Double?,
    val recentPoints: List<KairosKpiValuePoint>,
    val latestValueLabel: String,
    val targetLabel: String,
    val progressLabel: String,
    val statusLabel: String,
    val statusColor: String,
    val statusBackground: String,
)

private fun WebWorkspace.summaryFor(tasks: List<WebTask>): WebTaskSummary {
    val taskIds = tasks.map { task -> task.ticket.id }.toSet()
    val openCount = tasks.count { task -> task.ticket.completedAt == null }
    val completedCount = tasks.size - openCount
    val inProgressCount = tasks.count { task -> task.ticket.stage == Kairos.TicketStage.InProgress }
    val reviewCount = tasks.count { task -> task.ticket.stage == Kairos.TicketStage.Review }
    val overdueCount =
        tasks.count { task ->
            task.ticket.completedAt == null &&
                task.ticket.deadline?.let { it.toInstant() < Clock.System.now() } == true
        }
    val dueSoonCount =
        tasks.count { task ->
            task.ticket.completedAt == null &&
                task.ticket.deadline?.let { deadline ->
                    val now = Clock.System.now().toEpochMilliseconds()
                    val cutoff = now + 7 * 24 * 60 * 60 * 1000
                    deadline.toInstant().toEpochMilliseconds() in now..cutoff
                } == true
        }
    val completionPercent =
        if (tasks.isEmpty()) 0
        else ((completedCount.toDouble() / tasks.size.toDouble()) * 100).toInt()
    return WebTaskSummary(
        openCount = openCount,
        completedCount = completedCount,
        inProgressCount = inProgressCount,
        reviewCount = reviewCount,
        blockedCount =
            tasks.count { task -> task.ticket.completedAt == null && task.blockerLabel != null },
        overdueCount = overdueCount,
        dueSoonCount = dueSoonCount,
        unassignedCount = tasks.count { task -> task.ticket.assigneeUserId == null },
        activityCount = activities.count { activity -> activity.ticketId in taskIds },
        completionPercent = completionPercent,
    )
}

private fun WebWorkspace.objectiveSummaries(): List<WebObjectiveSummary> {
    val taskIndex =
        filteredTasks(searchQuery = "", includeCompleted = true, sort = WebSort.Created)
            .associateBy { task -> task.ticket.id }
    return objectives
        .sortedBy { objective -> objective.createdAt.toEpochMilliseconds() }
        .map { objective ->
            val linkedTasks = objective.tickets.mapNotNull { ticketId -> taskIndex[ticketId] }
            val linkedKpis = objective.kpiTargets.mapNotNull { target -> kpiSummary(target) }
            val completedCount = linkedTasks.count { task -> task.ticket.completedAt != null }
            val totalCount = objective.tickets.size
            val taskProgressPercent =
                when {
                    totalCount == 0 -> null
                    completedCount >= totalCount -> 100
                    else -> ((completedCount.toDouble() / totalCount.toDouble()) * 100).toInt()
                }
            val kpiProgressPercent =
                linkedKpis
                    .takeIf { it.isNotEmpty() }
                    ?.let { kpis -> kpis.map { it.progressPercent }.average().toInt() }
            val progressInputs = listOfNotNull(taskProgressPercent, kpiProgressPercent)
            val progressPercent =
                if (progressInputs.isEmpty()) {
                    0
                } else {
                    progressInputs.average().toInt()
                }
            val signal =
                when {
                    progressPercent >= 100 && progressInputs.isNotEmpty() ->
                        Triple("Complete", Green, "#e8f6ec")
                    objective.deadline?.let { it.toInstant() < Clock.System.now() } == true ->
                        Triple("Overdue", Coral, "#ffeee9")
                    linkedKpis.isNotEmpty() && linkedKpis.any { it.currentValue == null } ->
                        Triple("Awaiting measurement", Yellow, "#fff7dd")
                    progressInputs.isEmpty() -> Triple("No rollup yet", Muted, "#f1f2f4")
                    else -> Triple("In flight", Green, "#e8f6ec")
                }
            WebObjectiveSummary(
                objective = objective,
                description = objective.description,
                projectName =
                    objective.projectId?.let(::projectName).orEmpty().ifBlank { "No project" },
                deadlineLabel =
                    objective.deadline?.let(::formatDeadline).orEmpty().ifBlank { "No deadline" },
                progressPercent = progressPercent,
                statusLabel = signal.first,
                statusColor = signal.second,
                statusBackground = signal.third,
                linkedTasks = linkedTasks,
                kpis = linkedKpis,
            )
        }
}

private fun WebWorkspace.kpiSummary(kpiTarget: Kairos.KpiWithTarget): WebObjectiveKpiSummary? {
    val kpi = kpis.firstOrNull { it.id == kpiTarget.kpi } ?: return null
    val evaluation = kpiEvaluation(kpi.id)
    val currentValue = evaluation?.currentValue
    val progressPercent = kpiTarget.target.progressPercent(currentValue)
    val met = kpiTarget.target.isMetBy(currentValue)
    val status =
        when {
            currentValue == null -> Triple("Awaiting measurement", Muted, "#f1f2f4")
            met -> Triple("On track", Green, "#e8f6ec")
            else -> Triple("Off track", Coral, "#ffeee9")
        }
    return WebObjectiveKpiSummary(
        kpi = kpi,
        target = kpiTarget.target,
        evaluation = evaluation,
        currentValue = currentValue,
        recentPoints = evaluation?.recentPoints.orEmpty(),
        latestValueLabel = currentValue?.formatObjectiveMetric() ?: "No value yet",
        targetLabel = kpiTarget.target.uiLabel(),
        progressPercent = progressPercent,
        statusLabel = status.first,
        statusColor = status.second,
        statusBackground = status.third,
    )
}

private fun WebWorkspace.workspaceKpiSummaries(): List<WebWorkspaceKpiSummary> =
    kpis
        .sortedWith(
            compareBy<Kairos.Kpi>(
                { it.projectId?.let(::projectName).orEmpty() },
                { it.name.lowercase() },
            )
        )
        .map { kpi ->
            val evaluation = kpiEvaluation(kpi.id)
            val currentValue = evaluation?.currentValue
            val target = kpi.targetValue
            val progressLabel =
                target?.progressPercent(currentValue)?.let { percent -> "$percent%" }
                    ?: currentValue?.formatObjectiveMetric()
                    ?: "-"
            val status =
                when {
                    evaluation?.error != null -> Triple("Error", Coral, "#ffeee9")
                    target == null && currentValue == null -> Triple("No target", Muted, "#f1f2f4")
                    target == null -> Triple("Tracked", Blue, "#eaf1ff")
                    currentValue == null -> Triple("Awaiting measurement", Muted, "#f1f2f4")
                    target.isMetBy(currentValue) -> Triple("On track", Green, "#e8f6ec")
                    else -> Triple("Off track", Coral, "#ffeee9")
                }
            WebWorkspaceKpiSummary(
                kpi = kpi,
                projectName = kpi.projectId?.let(::projectName).orEmpty().ifBlank { "No project" },
                linkedObjectiveCount =
                    objectives.count { objective ->
                        objective.kpiTargets.any { targetRef -> targetRef.kpi == kpi.id }
                    },
                evaluation = evaluation,
                currentValue = currentValue,
                recentPoints = evaluation?.recentPoints.orEmpty(),
                latestValueLabel = currentValue?.formatObjectiveMetric() ?: "No value yet",
                targetLabel = target?.uiLabel() ?: "No target",
                progressLabel = progressLabel,
                statusLabel = status.first,
                statusColor = status.second,
                statusBackground = status.third,
            )
        }

private fun WebWorkspace.availableKpisFor(objective: Kairos.Objective): List<Kairos.Kpi> {
    val linkedIds = objective.kpiTargets.map { it.kpi }.toSet()
    return kpis.filter { kpi ->
        kpi.id !in linkedIds &&
            (objective.projectId == null || kpi.projectId == objective.projectId)
    }
}

private fun Kairos.Ticket.statusLabel(): Pair<String, String> =
    when {
        completedAt != null -> "Done" to Green
        else -> stage.uiLabel() to stage.stageColor()
    }

private fun Kairos.Ticket.effortLabel(): String =
    when (priority) {
        Kairos.TicketPriority.High -> "8"
        Kairos.TicketPriority.Medium -> "4"
        Kairos.TicketPriority.Low -> "2"
        null -> "1"
    }

private fun Kairos.Ticket.dueLabel(): String {
    val local = deadline?.dateTime ?: return "No due date"
    return "${monthLabel(local.month.ordinal + 1)} ${local.day}"
}

private fun Kairos.Ticket.isOverdue(today: LocalDate = todayDate()): Boolean =
    completedAt == null && deadline?.dateTime?.date?.let { date -> date < today } == true

private fun Kairos.Ticket.isDueToday(today: LocalDate = todayDate()): Boolean =
    completedAt == null && deadline?.dateTime?.date == today

private fun WebWorkspace.primaryProject(): Kairos.Project? =
    rootProjects().firstOrNull() ?: projects.firstOrNull()

private fun WebWorkspace.rootProjects(): List<Kairos.Project> {
    val byId = projects.associateBy { project -> project.id }
    return projects
        .filter { project -> project.isTopLevelVisibleProject(byId) }
        .sortedBy { project -> project.name.lowercase() }
}

private fun WebWorkspace.projectAndChildIds(projectId: Kairos.ProjectId): Set<Kairos.ProjectId> =
    buildSet {
        fun collect(currentProjectId: Kairos.ProjectId) {
            if (!add(currentProjectId)) return
            projects
                .filter { project -> project.parentProjectId == currentProjectId }
                .forEach { child -> collect(child.id) }
        }
        collect(projectId)
    }

private fun WebWorkspace.childProjects(parentProjectId: Kairos.ProjectId?): List<Kairos.Project> =
    projects
        .filter { project -> project.parentProjectId == parentProjectId }
        .sortedBy { project -> project.name.lowercase() }

private fun WebWorkspace.projectRootId(projectId: Kairos.ProjectId): Kairos.ProjectId {
    val byId = projects.associateBy { project -> project.id }
    var current = byId[projectId] ?: return projectId
    while (true) {
        val parentId = current.parentProjectId ?: return current.id
        current = byId[parentId] ?: return current.id
    }
}

private fun Kairos.Project.isTopLevelVisibleProject(
    byId: Map<Kairos.ProjectId, Kairos.Project>
): Boolean = parentProjectId == null || byId[parentProjectId] == null

private fun WebWorkspace.projectHasVisibleWork(projectId: Kairos.ProjectId): Boolean {
    val scopeIds = projectAndChildIds(projectId)
    return tickets.any { ticket -> ticket.projectId in scopeIds } ||
        objectives.any { objective -> objective.projectId in scopeIds } ||
        kpis.any { kpi -> kpi.projectId in scopeIds } ||
        events.any { event -> event.projectId in scopeIds } ||
        timeboxes.any { timebox -> timebox.projectId in scopeIds } ||
        routines.any { routine -> routine.projectId in scopeIds }
}

private fun WebWorkspace.projectOpenTicketCount(projectId: Kairos.ProjectId): Int {
    val scopeIds = projectAndChildIds(projectId)
    return tickets.count { ticket -> ticket.projectId in scopeIds && ticket.completedAt == null }
}

private fun WebWorkspace.projectScopeSummary(projectId: Kairos.ProjectId): String {
    val scopeIds = projectAndChildIds(projectId)
    val ticketCount =
        tickets.count { ticket -> ticket.projectId in scopeIds && ticket.completedAt == null }
    val objectiveCount = objectives.count { objective -> objective.projectId in scopeIds }
    return buildList {
            if (ticketCount > 0) {
                add("$ticketCount open")
            }
            if (objectiveCount > 0) {
                add("$objectiveCount objective${if (objectiveCount == 1) "" else "s"}")
            }
        }
        .joinToString(" • ")
        .ifBlank { "No active work" }
}

private fun WebWorkspace.defaultTaskProjectId(
    selectedProjectId: Kairos.ProjectId?
): Kairos.ProjectId? {
    val selectedProject = selectedProjectId?.let { id -> projects.firstOrNull { it.id == id } }
    if (selectedProject != null && selectedProject.parentProjectId != null)
        return selectedProject.id
    val parentId = selectedProject?.id ?: primaryProject()?.id
    return projects.firstOrNull { it.parentProjectId == parentId }?.id
        ?: selectedProject?.id
        ?: primaryProject()?.id
}

private fun WebWorkspace.assignableUsers(project: Kairos.Project): List<Kairos.User> = users

private fun WebWorkspace.movableProjects(currentProjectId: Kairos.ProjectId): List<Kairos.Project> {
    val scopedProjects = projects.filter { it.parentProjectId != null }
    val candidates = if (scopedProjects.isEmpty()) projects else scopedProjects
    return candidates
        .distinctBy { it.id }
        .sortedBy { project -> project.projectDisplayName(projects) }
        .ifEmpty { projects.firstOrNull { it.id == currentProjectId }?.let(::listOf).orEmpty() }
}

private fun WebWorkspace.objectiveProjectOptions(): List<Kairos.Project> {
    val scopedProjects = projects.filter { it.parentProjectId != null }
    return (if (scopedProjects.isEmpty()) projects else scopedProjects)
        .distinctBy { it.id }
        .sortedBy { project -> project.projectDisplayName(projects) }
}

private fun WebWorkspace.objectiveCandidates(projectId: Kairos.ProjectId): List<Kairos.Objective> =
    objectives
        .filter { objective -> objective.projectId == null || objective.projectId == projectId }
        .sortedBy { objective -> objective.createdAt.toEpochMilliseconds() }

private fun WebWorkspace.objectivesFor(ticketId: Kairos.TicketId): List<Kairos.Objective> =
    objectives
        .filter { objective -> ticketId in objective.tickets }
        .sortedBy { objective -> objective.createdAt.toEpochMilliseconds() }

private fun WebWorkspace.objectiveCountFor(ticketId: Kairos.TicketId): Int =
    objectives.count { objective -> ticketId in objective.tickets }

private fun WebWorkspace.activitiesFor(ticketId: Kairos.TicketId): List<Kairos.TicketActivity> =
    activities.filter { it.ticketId == ticketId }.sortedBy { it.createdAt.toEpochMilliseconds() }

private fun WebWorkspace.activityEntriesFor(
    ticketIds: Set<Kairos.TicketId>
): List<WebActivityEntry> =
    activities
        .filter { activity -> activity.ticketId in ticketIds }
        .sortedByDescending { activity -> activity.createdAt.toEpochMilliseconds() }
        .map { activity ->
            WebActivityEntry(
                activityId = activity.id,
                ticketId = activity.ticketId,
                ticketTitle =
                    tickets.firstOrNull { ticket -> ticket.id == activity.ticketId }?.title
                        ?: "Unknown ticket",
                summary = activity.summary,
                actorName = userDisplayName(activity.actorUserId),
                createdAtLabel = relativeLabel(activity.createdAt.toEpochMilliseconds()),
            )
        }

private fun WebWorkspace.projectLabel(selectedProjectId: Kairos.ProjectId?): String =
    selectedProjectId?.let { id -> projects.firstOrNull { project -> project.id == id }?.name }
        ?: primaryProject()?.name
        ?: "Workspace"

private fun WebWorkspace.projectName(projectId: Kairos.ProjectId): String =
    projects.firstOrNull { project -> project.id == projectId }?.name ?: "Unknown project"

private fun WebWorkspace.projectAccent(projectId: Kairos.ProjectId): String =
    projects.firstOrNull { project -> project.id == projectId }?.projectAccent() ?: Blue

private fun WebWorkspace.ticketBlockerLabel(ticketId: Kairos.TicketId): String? {
    val blockers =
        ticketBlockers
            .filter { blocker -> blocker.blockedTicketId == ticketId }
            .mapNotNull { blocker ->
                tickets.firstOrNull { ticket -> ticket.id == blocker.blockingTicketId }
            }
            .filter { ticket -> ticket.completedAt == null }
    return when (blockers.size) {
        0 -> null
        1 -> "Blocked by ${blockers.first().title}"
        else -> "Blocked by ${blockers.size} tickets"
    }
}

private fun WebWorkspace.kpiEvaluation(kpiId: Kairos.KpiId): KairosKpiEvaluation? =
    kpiEvaluations.firstOrNull { evaluation -> evaluation.kpiId == kpiId }

private fun WebWorkspace.projectPathLabel(project: Kairos.Project): String {
    val byId = projects.associateBy { candidate -> candidate.id }
    val names = mutableListOf<String>()
    var current: Kairos.Project? = project
    while (current != null) {
        names += current.name
        current = current.parentProjectId?.let { parentId -> byId[parentId] }
    }
    return names.asReversed().joinToString(" / ")
}

private fun WebWorkspace.userDisplayName(userId: Kairos.UserId?): String =
    userId?.let { id -> users.firstOrNull { user -> user.id == id }?.displayName } ?: "Kairos"

private fun WebWorkspace.inferredWorkspaceMembers(): List<KairosWorkspaceMember> =
    memberships
        .mapNotNull { membership ->
            val user =
                users.firstOrNull { candidate -> candidate.id == membership.userId }
                    ?: return@mapNotNull null
            KairosWorkspaceMember(
                user = user,
                username = user.displayName.demoUsername(),
                role = membership.role,
                createdAt = membership.createdAt,
            )
        }
        .sortedWith(
            compareBy<KairosWorkspaceMember>(
                { member -> if (member.role == Kairos.WorkspaceMembershipRole.Owner) 0 else 1 },
                { member -> member.user.displayName.lowercase() },
            )
        )

private fun WebWorkspace.toSnapshot(): KairosWorkspaceSnapshot =
    KairosWorkspaceSnapshot(
        workspace = workspace,
        memberships = memberships,
        sourceName = sourceName,
        users = users,
        projects = projects,
        tickets = tickets,
        ticketBlockers = ticketBlockers,
        activities = activities,
        objectives = objectives,
        kpis = kpis,
        measurements = measurements,
        kpiEvaluations = kpiEvaluations,
        timeboxes = timeboxes,
        routines = routines,
        events = events,
    )

private fun WebWorkspace.rebuildKpiEvaluations(): WebWorkspace =
    copy(kpiEvaluations = buildKairosKpiEvaluations(toSnapshot()))

private fun String.demoUsername(): String =
    lowercase().replace(Regex("[^a-z0-9]+"), "_").trim('_').ifEmpty { "member" }

private fun defaultMeasuredKpiOrigin(): Kairos.KpiOrigin.Measured =
    Kairos.KpiOrigin.Measured(
        frequency = Kairos.KpiFrequency.Weekly,
        unit = Kairos.KpiUnit.Count,
        accumulationType = Kairos.KpiAccumulationType.Summation,
    )

private fun parseWebKpiTargetSpec(rawValue: String): Kairos.KpiTarget {
    val value = rawValue.trim()
    if (value.isEmpty()) {
        throw IllegalArgumentException("KPI target must not be blank.")
    }
    if (value.startsWith(">=")) {
        return Kairos.KpiTarget.AtLeast(value.removePrefix(">=").trim().toDoubleOrIllegal("target"))
    }
    if (value.startsWith("<=")) {
        return Kairos.KpiTarget.AtMost(value.removePrefix("<=").trim().toDoubleOrIllegal("target"))
    }
    val rangeSeparator = value.indexOf("..")
    if (rangeSeparator >= 0) {
        return Kairos.KpiTarget.Range(
            min = value.substring(0, rangeSeparator).trim().toDoubleOrIllegal("target minimum"),
            max = value.substring(rangeSeparator + 2).trim().toDoubleOrIllegal("target maximum"),
        )
    }
    return Kairos.KpiTarget.AtLeast(value.toDoubleOrIllegal("target"))
}

private fun parseWebKpiFormula(workspace: WebWorkspace, rawFormula: String): Kairos.KpiExpr =
    parseKairosKpiFormula(rawFormula) { selector -> workspace.resolveKpiReference(selector)?.id }

private fun WebWorkspace.resolveKpiReference(selector: String): Kairos.Kpi? {
    val trimmed = selector.trim()
    val exactId = kpis.firstOrNull { kpi -> kpi.id.value == trimmed }
    if (exactId != null) {
        return exactId
    }
    val exactNameMatches = kpis.filter { kpi -> kpi.name.equals(trimmed, ignoreCase = true) }
    return when {
        exactNameMatches.size == 1 -> exactNameMatches.single()
        exactNameMatches.isEmpty() -> null
        else ->
            throw IllegalArgumentException(
                "KPI reference '$selector' is ambiguous. Use an exact id or a unique name."
            )
    }
}

private fun String.toDoubleOrIllegal(fieldName: String): Double =
    toDoubleOrNull() ?: throw IllegalArgumentException("Invalid $fieldName '$this'.")

private fun Kairos.KpiTarget.toUiDraft(): String =
    when (this) {
        is Kairos.KpiTarget.AtLeast -> ">=${value.formatObjectiveMetric()}"
        is Kairos.KpiTarget.AtMost -> "<=${value.formatObjectiveMetric()}"
        is Kairos.KpiTarget.Range ->
            "${min.formatObjectiveMetric()}..${max.formatObjectiveMetric()}"
    }

private fun List<WebTask>.toStageColumns(showEmptyColumns: Boolean): List<WebStageColumn> =
    listOf(
            Kairos.TicketStage.Inbox,
            Kairos.TicketStage.Todo,
            Kairos.TicketStage.InProgress,
            Kairos.TicketStage.Review,
            Kairos.TicketStage.Blocked,
        )
        .map { stage ->
            WebStageColumn(stage = stage, tasks = filter { task -> task.ticket.stage == stage })
        }
        .filter { column -> showEmptyColumns || column.tasks.isNotEmpty() }

private fun List<WebTask>.upcomingTasks(limit: Int): List<WebTask> =
    filter { task -> task.ticket.completedAt == null && task.ticket.deadline != null }
        .sortedBy { task ->
            task.ticket.deadline?.toInstant()?.toEpochMilliseconds() ?: Long.MAX_VALUE
        }
        .take(limit)

private fun List<WebTask>.overdueTasks(limit: Int): List<WebTask> =
    filter { task ->
            task.ticket.completedAt == null &&
                task.ticket.deadline?.let { it.toInstant() < Clock.System.now() } == true
        }
        .sortedBy { task ->
            task.ticket.deadline?.toInstant()?.toEpochMilliseconds() ?: Long.MAX_VALUE
        }
        .take(limit)

private fun currentUiLocaleTag(): String =
    loadStoredCurrentLocaleTag()?.trim()?.takeIf { it.isNotEmpty() }
        ?: window.navigator.language.trim().takeIf { it.isNotEmpty() }
        ?: "en-US"

private fun browserTimeZoneId(): String? =
    try {
        js("Intl.DateTimeFormat().resolvedOptions().timeZone") as? String
    } catch (error: Throwable) {
        propagateUiThrowable(error)
        null
    }

private fun currentUiTimeZone(): TimeZone {
    val timeZoneId =
        loadStoredCurrentTimeZoneId()?.trim()?.takeIf { it.isNotEmpty() }
            ?: browserTimeZoneId()?.trim()?.takeIf { it.isNotEmpty() }
    return try {
        TimeZone.of(timeZoneId ?: "UTC")
    } catch (_: IllegalArgumentException) {
        TimeZone.UTC
    }
}

private fun jsIntlOptions(vararg values: Pair<String, Any?>): dynamic {
    val options = js("{}")
    values.forEach { (key, value) -> options[key] = value }
    return options
}

private fun kotlin.time.Instant.toUiDate(): Date = Date(toEpochMilliseconds().toDouble())

private fun formatUiDate(instant: kotlin.time.Instant, includeYear: Boolean = true): String =
    instant
        .toUiDate()
        .toLocaleDateString(
            currentUiLocaleTag(),
            jsIntlOptions(
                "timeZone" to currentUiTimeZone().id,
                "month" to "short",
                "day" to "numeric",
                "year" to if (includeYear) "numeric" else null,
            ),
        )

private fun formatUiTime(instant: kotlin.time.Instant): String {
    val local = instant.toLocalDateTime(currentUiTimeZone())
    return formatClockTime(local.hour, local.minute)
}

private fun ZonedDateTime.toUiZoned(): ZonedDateTime {
    val timeZone = currentUiTimeZone()
    return ZonedDateTime(toInstant().toLocalDateTime(timeZone), timeZone)
}

private fun formatDeadline(deadline: ZonedDateTime): String {
    return formatUiDate(deadline.toInstant())
}

private fun formatMeasurementRecordedAt(recordedAt: kotlin.time.Instant): String {
    val date = recordedAt.toLocalDateTime(currentUiTimeZone()).date
    return "${monthLabel(date.month.ordinal + 1)} ${date.day}, ${date.year}"
}

private fun formatInstantTime(instant: kotlin.time.Instant): String = formatUiTime(instant)

private fun formatEventTimeRange(event: Kairos.Event): String {
    val start = formatUiTime(event.dtStart.toInstant())
    val end = formatUiTime((event.dtEnd ?: event.dtStart).toInstant())
    return "$start - $end"
}

private fun ZonedDateTime.calendarDetail(): String? =
    dateTime.timeOfDayLabel()?.let { time -> "Due $time" }

private fun Kairos.Event.calendarDetail(): String =
    listOfNotNull(
            formatEventTimeRange(this),
            location?.trim().takeIf { value -> !value.isNullOrEmpty() },
        )
        .joinToString(" · ")

private fun Kairos.Timebox.calendarDetail(): String =
    listOfNotNull(formatInstantTime(startTime), recordedDuration?.calendarDurationLabel())
        .joinToString(" · ")

private fun TimeSpan.calendarDurationLabel(): String =
    spans.joinToString(" ") { span -> "${span.value}${span.unit.shortLabel()}" }

private fun LocalDateTime.timeOfDayLabel(): String? =
    if (hour == 0 && minute == 0 && second == 0) {
        null
    } else {
        formatClockTime(hour, minute)
    }

private fun formatClockTime(hour: Int, minute: Int): String {
    val normalizedHour =
        when {
            hour == 0 -> 12
            hour > 12 -> hour - 12
            else -> hour
        }
    val suffix = if (hour < 12) "AM" else "PM"
    return "$normalizedHour:${minute.twoDigits()} $suffix"
}

private fun Kairos.KpiTarget.uiLabel(): String =
    when (this) {
        is Kairos.KpiTarget.AtLeast -> ">= ${value.formatObjectiveMetric()}"
        is Kairos.KpiTarget.AtMost -> "<= ${value.formatObjectiveMetric()}"
        is Kairos.KpiTarget.Range -> "${min.formatObjectiveMetric()}-${max.formatObjectiveMetric()}"
    }

private fun TimeUnit.shortLabel(): String =
    when (this) {
        TimeUnit.Seconds -> "s"
        TimeUnit.Minutes -> "m"
        TimeUnit.Hours -> "h"
        TimeUnit.Days -> "d"
        TimeUnit.Weeks -> "w"
        TimeUnit.Months -> "mo"
        TimeUnit.Years -> "y"
    }

private fun Int.twoDigits(): String = toString().padStart(2, '0')

private fun Kairos.KpiTarget.isMetBy(value: Double?): Boolean =
    when {
        value == null -> false
        this is Kairos.KpiTarget.AtLeast -> value >= this.value
        this is Kairos.KpiTarget.AtMost -> value <= this.value
        this is Kairos.KpiTarget.Range -> value in this.min..this.max
        else -> false
    }

private fun Kairos.KpiTarget.progressPercent(value: Double?): Int {
    val currentValue = value ?: return 0
    val ratio =
        when (this) {
            is Kairos.KpiTarget.AtLeast -> if (this.value <= 0.0) 1.0 else currentValue / this.value
            is Kairos.KpiTarget.AtMost ->
                if (currentValue <= this.value) {
                    1.0
                } else if (currentValue == 0.0) {
                    0.0
                } else {
                    this.value / currentValue
                }
            is Kairos.KpiTarget.Range ->
                when {
                    currentValue in this.min..this.max -> 1.0
                    currentValue < this.min -> if (this.min <= 0.0) 1.0 else currentValue / this.min
                    currentValue == 0.0 -> 0.0
                    else -> this.max / currentValue
                }
        }
    return (ratio.coerceIn(0.0, 1.0) * 100.0).toInt()
}

private fun Double.formatObjectiveMetric(): String =
    if (this % 1.0 == 0.0) {
        toInt().toString()
    } else {
        toString()
    }

private fun WebWorkspace.scheduleEntries(columns: List<WebColumn>): List<WebScheduleEntry> =
    buildList {
        val visibleProjects = columns.map { it.project.id }.toSet()
        val projectNames = columns.associate { column -> column.project.id to column.project.name }
        val tasksById = columns.flatMap { column -> column.tasks }.associateBy { it.ticket.id }
        columns.forEach { column ->
            column.tasks.filterScheduled().forEach { task ->
                val deadline = task.ticket.deadline ?: return@forEach
                val deadlineDate = deadline.dateTime.date
                add(
                    WebScheduleEntry(
                        key = task.ticket.id.value,
                        projectId = column.project.id,
                        projectName = column.project.name,
                        title = task.ticket.title,
                        startDate = deadlineDate,
                        endDate = deadlineDate,
                        accent = task.accent,
                        kind = WebScheduleEntryKind.Ticket,
                        detail = deadline.calendarDetail(),
                        blockerLabel = ticketBlockerLabel(task.ticket.id),
                        sortEpochMs = deadline.toInstant().toEpochMilliseconds(),
                        clickableTask = task,
                        completed = task.ticket.completedAt != null,
                    )
                )
            }
        }
        timeboxes
            .filter { timebox -> timebox.projectId != null && timebox.projectId in visibleProjects }
            .forEach { timebox ->
                val projectId = timebox.projectId ?: return@forEach
                add(
                    WebScheduleEntry(
                        key = timebox.id.value,
                        projectId = projectId,
                        projectName = projectNames[projectId] ?: projectName(projectId),
                        title = timebox.description,
                        startDate = timebox.startDate(),
                        endDate = timebox.endDate(),
                        accent = projectAccent(projectId),
                        kind = WebScheduleEntryKind.Timebox,
                        detail = timebox.calendarDetail(),
                        sortEpochMs = timebox.startTime.toEpochMilliseconds(),
                        clickableTask = timebox.tickets.firstNotNullOfOrNull(tasksById::get),
                    )
                )
            }
        events
            .filter { event -> event.projectId != null && event.projectId in visibleProjects }
            .forEach { event ->
                val projectId = event.projectId ?: return@forEach
                add(
                    WebScheduleEntry(
                        key = event.id.value,
                        projectId = projectId,
                        projectName = projectNames[projectId] ?: projectName(projectId),
                        title = event.summary.ifBlank { event.title },
                        startDate = event.startDate(),
                        endDate = event.endDate(),
                        accent = projectAccent(projectId),
                        kind = WebScheduleEntryKind.Event,
                        detail = event.calendarDetail(),
                        sortEpochMs = event.dtStart.toInstant().toEpochMilliseconds(),
                    )
                )
            }
    }

private fun WebWorkspace.upcomingScheduleItems(
    from: LocalDate,
    limit: Int = 4,
): List<WebUpcomingScheduleItem> =
    buildList {
            events
                .filter { event ->
                    val startDate = event.startDate()
                    startDate >= from
                }
                .sortedBy { it.dtStart.toInstant() }
                .forEach { event ->
                    add(
                        WebUpcomingScheduleItem(
                            kind = "Event",
                            title = event.title,
                            detail =
                                listOfNotNull(
                                        event.projectId?.let { projectId ->
                                            projectName(projectId)
                                        },
                                        formatEventTimeRange(event),
                                        formatDeadline(event.dtStart),
                                    )
                                    .joinToString(" - "),
                            accent = Blue,
                            sortEpochMs = event.dtStart.toInstant().toEpochMilliseconds(),
                        )
                    )
                }
            timeboxes
                .filter { timebox ->
                    val startDate = timebox.startDate()
                    startDate >= from
                }
                .sortedBy { it.startTime }
                .forEach { timebox ->
                    val start =
                        ZonedDateTime(
                            timebox.startTime.toLocalDateTime(currentUiTimeZone()),
                            currentUiTimeZone(),
                        )
                    add(
                        WebUpcomingScheduleItem(
                            kind = "Timebox",
                            title = timebox.description,
                            detail =
                                listOfNotNull(
                                        timebox.projectId?.let { projectId ->
                                            projectName(projectId)
                                        },
                                        formatInstantTime(timebox.startTime),
                                        formatDeadline(start),
                                    )
                                    .joinToString(" - "),
                            accent = Teal,
                            sortEpochMs = timebox.startTime.toEpochMilliseconds(),
                        )
                    )
                }
        }
        .sortedBy { item -> item.sortEpochMs }
        .take(limit)

private fun List<WebTask>.filterScheduled(): List<WebTask> = filter { task ->
    task.ticket.deadline != null
}

private fun WebTask.deadlineDate(): LocalDate? = ticket.deadline?.toUiZoned()?.dateTime?.date

private fun Kairos.Timebox.startDate(): LocalDate =
    startTime.toLocalDateTime(currentUiTimeZone()).date

private fun Kairos.Timebox.endDate(): LocalDate {
    val start = startTime.toLocalDateTime(currentUiTimeZone())
    var date = start.date
    var seconds = (start.hour * 3600L) + (start.minute * 60L) + start.second
    recordedDuration?.spans.orEmpty().forEach { span ->
        when (span.unit) {
            TimeUnit.Seconds -> seconds += span.value
            TimeUnit.Minutes -> seconds += span.value * 60L
            TimeUnit.Hours -> seconds += span.value * 3600L
            TimeUnit.Days -> date += DatePeriod(days = span.value.toInt())
            TimeUnit.Weeks -> date += DatePeriod(days = (span.value * 7L).toInt())
            TimeUnit.Months -> date += DatePeriod(months = span.value.toInt())
            TimeUnit.Years -> date += DatePeriod(years = span.value.toInt())
        }
    }
    val extraDays = (seconds / 86_400L).toInt()
    if (extraDays > 0) {
        date += DatePeriod(days = extraDays)
    }
    return date
}

private fun Kairos.Event.startDate(): LocalDate = dtStart.toUiZoned().dateTime.date

private fun Kairos.Event.endDate(): LocalDate = (dtEnd ?: dtStart).toUiZoned().dateTime.date

private fun List<WebTask>.sortedByTimeline(): List<WebTask> =
    sortedWith(
        compareBy<WebTask> { task -> task.ticket.completedAt != null }
            .thenBy { task -> task.ticket.stage.ordinal }
            .thenBy { task -> task.ticket.createdAt.toEpochMilliseconds() }
    )

private fun List<WebScheduleEntry>.sortedBySchedule(): List<WebScheduleEntry> =
    sortedWith(
        compareBy<WebScheduleEntry> { entry -> entry.completed }
            .thenBy { entry -> entry.startDate.toEpochDays() }
            .thenBy { entry -> entry.kind.rank() }
            .thenBy { entry -> entry.sortEpochMs }
            .thenBy { entry -> entry.title.lowercase() }
    )

private fun List<WebScheduleEntry>.previewCalendarEntries(limit: Int): List<WebScheduleEntry> {
    if (size <= limit) return this
    val distinctKinds =
        buildList<WebScheduleEntry> {
            webCalendarKindOrder().forEach { kind ->
                firstOrNull { entry -> entry.kind == kind }?.let(::add)
            }
        }
    if (distinctKinds.size >= limit) {
        return distinctKinds.take(limit)
    }
    val selectedKeys = distinctKinds.mapTo(mutableSetOf()) { entry -> entry.key }
    val remaining =
        filterNot { entry -> entry.key in selectedKeys }.take(limit - distinctKinds.size)
    return distinctKinds + remaining
}

private fun WebScheduleEntry.includes(date: LocalDate): Boolean =
    date >= startDate && date <= endDate

private fun WebScheduleEntry.shouldMuteOnCalendar(today: LocalDate): Boolean =
    completed || (kind != WebScheduleEntryKind.Ticket && endDate < today)

private fun WebScheduleEntryKind.rank(): Int =
    when (this) {
        WebScheduleEntryKind.Event -> 0
        WebScheduleEntryKind.Timebox -> 1
        WebScheduleEntryKind.Ticket -> 2
    }

private fun webCalendarKindOrder(): List<WebScheduleEntryKind> =
    listOf(WebScheduleEntryKind.Event, WebScheduleEntryKind.Timebox, WebScheduleEntryKind.Ticket)

private fun WebScheduleEntryKind.label(): String =
    when (this) {
        WebScheduleEntryKind.Ticket -> "Ticket"
        WebScheduleEntryKind.Event -> "Event"
        WebScheduleEntryKind.Timebox -> "Timebox"
    }

private fun buildTimelineSchedule(entries: List<WebScheduleEntry>): WebTimelineSchedule? {
    val dates = entries.flatMap { entry -> listOf(entry.startDate, entry.endDate) }
    if (dates.isEmpty()) return null
    val start = startOfMonth(dates.minOrNull() ?: return null)
    val end = endOfMonth(dates.maxOrNull() ?: return null)
    val days = generateDates(start, end)
    val today = todayDate()
    val focusDate =
        entries
            .filter { entry -> !entry.completed && entry.endDate >= today }
            .minByOrNull { entry -> entry.startDate.toEpochDays() }
            ?.startDate
            ?: entries.minByOrNull { entry -> entry.startDate.toEpochDays() }?.startDate
            ?: start
    val monthSpans =
        days
            .groupBy { date -> date.year to (date.month.ordinal + 1) }
            .entries
            .map { (yearMonth, monthDays) ->
                WebTimelineMonthSpan(
                    label = "${monthLabel(yearMonth.second)} ${yearMonth.first}",
                    dayCount = monthDays.size,
                )
            }
    val focusDayIndex = days.indexOf(focusDate).coerceAtLeast(0)
    return WebTimelineSchedule(days = days, monthSpans = monthSpans, focusDayIndex = focusDayIndex)
}

private fun buildTimelineRows(
    columns: List<WebColumn>,
    entries: List<WebScheduleEntry>,
    schedule: WebTimelineSchedule,
): List<WebTimelineRow> {
    val dayIndexByDate =
        schedule.days.withIndex().associate { indexed -> indexed.value to indexed.index }
    return columns.mapNotNull { column ->
        val projectEntries =
            entries.filter { entry -> entry.projectId == column.project.id }.sortedBySchedule()
        if (projectEntries.isEmpty()) {
            null
        } else {
            val laneEndIndices = mutableListOf<Int>()
            val placedEntries =
                projectEntries.map { entry ->
                    val startIndex = dayIndexByDate[entry.startDate] ?: 0
                    val endIndex = dayIndexByDate[entry.endDate] ?: startIndex
                    val laneIndex =
                        laneEndIndices
                            .indexOfFirst { lastDayIndex -> lastDayIndex < startIndex }
                            .takeIf { it >= 0 } ?: laneEndIndices.size.also { laneEndIndices += -1 }
                    laneEndIndices[laneIndex] = endIndex
                    WebTimelinePlacedEntry(
                        entry = entry,
                        laneIndex = laneIndex,
                        startDayIndex = startIndex,
                        endDayIndex = endIndex,
                    )
                }
            WebTimelineRow(
                project = column.project,
                placedEntries = placedEntries,
                laneCount = laneEndIndices.size.coerceAtLeast(1),
            )
        }
    }
}

private fun buildCalendarMonths(entries: List<WebScheduleEntry>): List<WebCalendarMonthGrid> {
    val dates = entries.flatMap { entry -> listOf(entry.startDate, entry.endDate) }
    if (dates.isEmpty()) return emptyList()
    val firstMonth = startOfMonth(dates.minOrNull() ?: return emptyList())
    val lastMonth = startOfMonth(dates.maxOrNull() ?: return emptyList())
    val months = mutableListOf<WebCalendarMonthGrid>()
    var current = firstMonth
    while (current <= lastMonth) {
        val dayCount = dayDistance(current, nextMonth(current))
        val monthDays = (0 until dayCount).map { offset -> current + DatePeriod(days = offset) }
        val leading = current.dayOfWeek.ordinal
        val cells =
            buildList<LocalDate?> {
                repeat(leading) { add(null) }
                addAll(monthDays)
                while (size % 7 != 0) add(null)
            }
        months +=
            WebCalendarMonthGrid(
                label = "${monthLabel(current.month.ordinal + 1)} ${current.year}",
                startDate = current,
                endDate = endOfMonth(current),
                weeks = cells.chunked(7),
            )
        current = nextMonth(current)
    }
    return months
}

private fun buildCalendarWeeks(entries: List<WebScheduleEntry>): List<WebCalendarWeekRange> {
    val dates = entries.flatMap { entry -> listOf(entry.startDate, entry.endDate) }
    if (dates.isEmpty()) return emptyList()
    val firstWeek = startOfWeek(dates.minOrNull() ?: return emptyList())
    val lastWeek = startOfWeek(dates.maxOrNull() ?: return emptyList())
    val weeks = mutableListOf<WebCalendarWeekRange>()
    var current = firstWeek
    while (current <= lastWeek) {
        val days = generateDates(current, current + DatePeriod(days = 6))
        val end = days.last()
        weeks +=
            WebCalendarWeekRange(
                label =
                    if (current.month == end.month) {
                        "${monthLabel(current.month.ordinal + 1)} ${current.day}-${end.day}, ${current.year}"
                    } else {
                        "${monthLabel(current.month.ordinal + 1)} ${current.day} - ${monthLabel(end.month.ordinal + 1)} ${end.day}, ${end.year}"
                    },
                startDate = current,
                endDate = end,
                days = days,
            )
        current += DatePeriod(days = 7)
    }
    return weeks
}

private fun initialCalendarMonthIndex(months: List<WebCalendarMonthGrid>, today: LocalDate): Int {
    if (months.isEmpty()) return 0
    months
        .indexOfFirst { month -> today in month.startDate..month.endDate }
        .takeIf { it >= 0 }
        ?.let {
            return it
        }
    months
        .indexOfFirst { month -> month.startDate > today }
        .takeIf { it >= 0 }
        ?.let {
            return it
        }
    return months.lastIndex
}

private fun initialCalendarWeekIndex(weeks: List<WebCalendarWeekRange>, today: LocalDate): Int {
    if (weeks.isEmpty()) return 0
    weeks
        .indexOfFirst { week -> today in week.startDate..week.endDate }
        .takeIf { it >= 0 }
        ?.let {
            return it
        }
    weeks
        .indexOfFirst { week -> week.startDate > today }
        .takeIf { it >= 0 }
        ?.let {
            return it
        }
    return weeks.lastIndex
}

private fun generateDates(start: LocalDate, end: LocalDate): List<LocalDate> =
    (0..dayDistance(start, end)).map { offset -> start + DatePeriod(days = offset) }

private fun startOfWeek(date: LocalDate): LocalDate =
    date + DatePeriod(days = -date.dayOfWeek.ordinal)

private fun startOfMonth(date: LocalDate): LocalDate =
    LocalDate(date.year, date.month.ordinal + 1, 1)

private fun dayDistance(start: LocalDate, end: LocalDate): Int =
    (end.toEpochDays() - start.toEpochDays()).toInt()

private fun endOfMonth(date: LocalDate): LocalDate {
    val first = startOfMonth(date)
    val next = nextMonth(first)
    return next + DatePeriod(days = -1)
}

private fun nextMonth(date: LocalDate): LocalDate =
    if (date.month == kotlinx.datetime.Month.DECEMBER) {
        LocalDate(date.year + 1, 1, 1)
    } else {
        LocalDate(date.year, date.month.ordinal + 2, 1)
    }

private fun weekdayLabels(): List<String> = listOf("Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun")

private fun weekdayLabel(date: LocalDate): String = weekdayLabels()[date.dayOfWeek.ordinal]

private fun WebScheduleEntry.timelineRangeLabel(): String? =
    if (startDate == endDate) {
        null
    } else {
        "${monthLabel(startDate.month.ordinal + 1)} ${startDate.day} - ${monthLabel(endDate.month.ordinal + 1)} ${endDate.day}"
    }

private fun todayDate(): LocalDate = Clock.System.now().toLocalDateTime(currentUiTimeZone()).date

private fun user(
    id: String,
    displayName: String,
    email: String,
    createdAt: kotlin.time.Instant,
): Kairos.User =
    Kairos.User(
        id = Kairos.UserId(id),
        createdAt = createdAt,
        displayName = displayName,
        email = email,
    )

private val DemoWebWorkspaceCreatedAt = Clock.System.now()
private val DemoWebWorkspaceId = Kairos.WorkspaceId("workspace-phoenix")

private fun project(
    id: String,
    name: String,
    parentProjectId: Kairos.ProjectId?,
    createdAt: kotlin.time.Instant,
    workspaceId: Kairos.WorkspaceId = DemoWebWorkspaceId,
): Kairos.Project =
    Kairos.Project(
        id = Kairos.ProjectId(id),
        createdAt = createdAt,
        workspaceId = workspaceId,
        name = name,
        parentProjectId = parentProjectId,
    )

private fun objective(
    id: String,
    projectId: Kairos.ProjectId?,
    description: String,
    kpiTargets: List<Kairos.KpiWithTarget> = emptyList(),
    ticketIds: List<Kairos.TicketId>,
    deadline: ZonedDateTime?,
    createdAt: kotlin.time.Instant,
    workspaceId: Kairos.WorkspaceId = DemoWebWorkspaceId,
): Kairos.Objective =
    Kairos.Objective(
        id = Kairos.ObjectiveId(id),
        createdAt = createdAt,
        workspaceId = workspaceId,
        projectId = projectId,
        description = description,
        kpiTargets = kpiTargets,
        tickets = ticketIds,
        deadline = deadline,
    )

private fun kpi(
    id: String,
    projectId: Kairos.ProjectId?,
    name: String,
    createdAt: kotlin.time.Instant,
    targetValue: Kairos.KpiTarget?,
    workspaceId: Kairos.WorkspaceId = DemoWebWorkspaceId,
): Kairos.Kpi =
    Kairos.Kpi(
        id = Kairos.KpiId(id),
        createdAt = createdAt,
        workspaceId = workspaceId,
        projectId = projectId,
        name = name,
        origin =
            Kairos.KpiOrigin.Measured(
                frequency = Kairos.KpiFrequency.Weekly,
                unit = Kairos.KpiUnit.Count,
                accumulationType = Kairos.KpiAccumulationType.Summation,
            ),
        targetValue = targetValue,
    )

private fun kpiMeasurement(
    id: String,
    kpiId: String,
    value: Double,
    recordedAt: kotlin.time.Instant,
): Kairos.KpiMeasurement =
    Kairos.KpiMeasurement(
        id = Kairos.KpiMeasurementId(id),
        kpiId = Kairos.KpiId(kpiId),
        value = value,
        recordedAt = recordedAt,
    )

private fun timebox(
    id: String,
    projectId: Kairos.ProjectId?,
    ticketIds: List<Kairos.TicketId>,
    description: String,
    startTime: kotlin.time.Instant,
    recordedDuration: TimeSpan?,
    createdAt: kotlin.time.Instant,
    workspaceId: Kairos.WorkspaceId = DemoWebWorkspaceId,
): Kairos.Timebox =
    Kairos.Timebox(
        id = Kairos.TimeboxId(id),
        createdAt = createdAt,
        workspaceId = workspaceId,
        projectId = projectId,
        tickets = ticketIds,
        description = description,
        startTime = startTime,
        recordedDuration = recordedDuration,
    )

private fun routine(
    id: String,
    projectId: Kairos.ProjectId?,
    description: String,
    createdAt: kotlin.time.Instant,
    workspaceId: Kairos.WorkspaceId = DemoWebWorkspaceId,
): Kairos.Routine =
    Kairos.Routine(
        id = Kairos.RoutineId(id),
        createdAt = createdAt,
        workspaceId = workspaceId,
        projectId = projectId,
        description = description,
    )

private fun event(
    id: String,
    projectId: Kairos.ProjectId?,
    title: String,
    summary: String,
    dtStart: ZonedDateTime,
    dtEnd: ZonedDateTime?,
    createdAt: kotlin.time.Instant,
    description: String?,
    location: String?,
    workspaceId: Kairos.WorkspaceId = DemoWebWorkspaceId,
): Kairos.Event =
    Kairos.Event(
        id = Kairos.EventId(id),
        createdAt = createdAt,
        workspaceId = workspaceId,
        projectId = projectId,
        title = title,
        uniqueId = "$id@kairos.web.demo",
        dtStamp = ZonedDateTime(LocalDateTime(2026, 4, 11, 9, 0), TimeZone.UTC),
        dtStart = dtStart,
        dtEnd = dtEnd,
        summary = summary,
        description = description,
        location = location,
    )

private fun ticket(
    id: String,
    projectId: Kairos.ProjectId,
    assigneeUserId: Kairos.UserId,
    title: String,
    day: Int,
): Kairos.Ticket = ticket(id, projectId, assigneeUserId, title, 2026, 4, day)

private fun ticket(
    id: String,
    projectId: Kairos.ProjectId,
    assigneeUserId: Kairos.UserId,
    title: String,
    year: Int,
    month: Int,
    day: Int,
): Kairos.Ticket =
    Kairos.Ticket(
        id = Kairos.TicketId(id),
        createdAt = Clock.System.now(),
        projectId = projectId,
        title = title,
        stage = Kairos.TicketStage.Todo,
        description = "Coordinate ${title.lowercase()} and keep the next handoff clear.",
        deadline = ZonedDateTime(LocalDateTime(year, month, day, 17, 0), TimeZone.UTC),
        priority = Kairos.TicketPriority.Medium,
        urgency = Kairos.TicketUrgency.Medium,
        completedAt = null,
        assigneeUserId = assigneeUserId,
    )

private fun Kairos.TicketStage.uiLabel(): String =
    when (this) {
        Kairos.TicketStage.Inbox -> "Inbox"
        Kairos.TicketStage.Todo -> "To do"
        Kairos.TicketStage.InProgress -> "In progress"
        Kairos.TicketStage.Review -> "In review"
        Kairos.TicketStage.Blocked -> "Blocked"
    }

private fun Kairos.TicketStage.stageToken(): String =
    when (this) {
        Kairos.TicketStage.Inbox -> "inbox"
        Kairos.TicketStage.Todo -> "todo"
        Kairos.TicketStage.InProgress -> "in-progress"
        Kairos.TicketStage.Review -> "review"
        Kairos.TicketStage.Blocked -> "blocked"
    }

private fun String?.toTicketStage(): Kairos.TicketStage? =
    when (this?.lowercase()) {
        "inbox" -> Kairos.TicketStage.Inbox
        "todo" -> Kairos.TicketStage.Todo
        "in-progress" -> Kairos.TicketStage.InProgress
        "review" -> Kairos.TicketStage.Review
        "blocked" -> Kairos.TicketStage.Blocked
        else -> null
    }

private fun Kairos.TicketStage.stageColor(): String =
    when (this) {
        Kairos.TicketStage.Inbox -> "#e7e8ea"
        Kairos.TicketStage.Todo -> Teal
        Kairos.TicketStage.InProgress -> Yellow
        Kairos.TicketStage.Review -> Blue
        Kairos.TicketStage.Blocked -> Coral
    }

private fun Kairos.TicketPriority.uiLabel(): String =
    when (this) {
        Kairos.TicketPriority.Low -> "Low"
        Kairos.TicketPriority.Medium -> "Medium"
        Kairos.TicketPriority.High -> "High"
    }

private fun Kairos.TicketPriority.priorityColor(): String =
    when (this) {
        Kairos.TicketPriority.Low -> "#f6d171"
        Kairos.TicketPriority.Medium -> "#f4a67f"
        Kairos.TicketPriority.High -> "#ef7c73"
    }

private fun Kairos.TicketUrgency.uiLabel(): String =
    when (this) {
        Kairos.TicketUrgency.LongTerm -> "Backlog"
        Kairos.TicketUrgency.Low -> "Later"
        Kairos.TicketUrgency.Medium -> "Soon"
        Kairos.TicketUrgency.High -> "Immediate"
    }

private fun Kairos.TicketUrgency.urgencyColor(): String =
    when (this) {
        Kairos.TicketUrgency.LongTerm -> "#dde7f8"
        Kairos.TicketUrgency.Low -> "#e7e8ea"
        Kairos.TicketUrgency.Medium -> "#f6d171"
        Kairos.TicketUrgency.High -> "#ef7c73"
    }

private fun Kairos.User?.avatarUrl(): String =
    this?.avatarUrl?.trim()?.takeIf { it.isNotEmpty() }
        ?: when (this?.displayName?.substringBefore(" ")?.lowercase()) {
            "jamie" -> "/avatars/jamie.svg"
            "alan" -> "/avatars/alan.svg"
            "mina" -> "/avatars/mina.svg"
            "noor" -> "/avatars/noor.svg"
            "ravi" -> "/avatars/ravi.svg"
            else -> "/avatars/jamie.svg"
        }

private fun monthLabel(month: Int): String =
    Date("2026-${month.toString().padStart(2, '0')}-01T12:00:00Z")
        .toLocaleDateString("en-US", jsIntlOptions("timeZone" to "UTC", "month" to "short"))

private fun monthYearLabel(date: LocalDate): String =
    Date("${date.year}-${date.monthNumber().toString().padStart(2, '0')}-01T12:00:00Z")
        .toLocaleDateString(
            "en-US",
            jsIntlOptions("timeZone" to "UTC", "month" to "long", "year" to "numeric"),
        )

private fun relativeLabel(epochMs: Long): String {
    val elapsedMs = Clock.System.now().toEpochMilliseconds() - epochMs
    val elapsedMinutes = elapsedMs / 60_000
    return when {
        elapsedMinutes < 1 -> "just now"
        elapsedMinutes < 60 -> "${elapsedMinutes}m ago"
        elapsedMinutes < 1_440 -> "${elapsedMinutes / 60}h ago"
        else -> "${elapsedMinutes / 1_440}d ago"
    }
}

private fun ZonedDateTime?.inputValue(): String {
    val local = this?.toUiZoned()?.dateTime ?: return ""
    return buildString {
        append(local.year)
        append("-")
        append(local.month.ordinal.plus(1).toString().padStart(2, '0'))
        append("-")
        append(local.day.toString().padStart(2, '0'))
    }
}

private fun ZonedDateTime?.dateTimeInputValue(): String {
    val local = this?.toUiZoned()?.dateTime ?: return ""
    return local.dateTimeInputValue()
}

private fun LocalDate.dateInputValue(): String =
    "${year}-${monthNumber().toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"

private fun LocalDateTime.dateTimeInputValue(): String {
    return buildString {
        append(year)
        append("-")
        append(monthNumber().toString().padStart(2, '0'))
        append("-")
        append(day.toString().padStart(2, '0'))
        append("T")
        append(hour.toString().padStart(2, '0'))
        append(":")
        append(minute.toString().padStart(2, '0'))
    }
}

private fun LocalDateTime.dateControlValue(kind: WebDateControlKind): String =
    when (kind) {
        WebDateControlKind.Date -> dateOnly().dateInputValue()
        WebDateControlKind.DateTime -> dateTimeInputValue()
    }

private fun dateControlDisplayValue(value: String, kind: WebDateControlKind): String {
    val parsed = parseDateControlInput(value, kind) ?: return value
    return when (kind) {
        WebDateControlKind.Date -> formatLocalDateDisplay(parsed.dateOnly())
        WebDateControlKind.DateTime ->
            "${formatLocalDateDisplay(parsed.dateOnly())}, ${parsed.hour12().toString().padStart(2, '0')}:${
                parsed.minute.toString().padStart(2, '0')
            } ${parsed.dayPeriod()}"
    }
}

private fun formatLocalDateDisplay(date: LocalDate): String =
    Date(
            "${date.year}-${date.monthNumber().toString().padStart(2, '0')}-${date.day.toString().padStart(2, '0')}T12:00:00Z"
        )
        .toLocaleDateString(
            currentUiLocaleTag(),
            jsIntlOptions(
                "timeZone" to "UTC",
                "month" to "short",
                "day" to "numeric",
                "year" to "numeric",
            ),
        )

private fun normalizeDateControlDraft(value: String, kind: WebDateControlKind): String =
    parseDateControlInput(value, kind)?.dateControlValue(kind) ?: value

private fun committableDateControlDraft(
    id: String?,
    value: String,
    kind: WebDateControlKind,
): String {
    val normalizedStateDraft = normalizeDateControlDraft(value, kind)
    val normalizedInputDraft = inputElementValue(id)?.let { normalizeDateControlDraft(it, kind) }
    return normalizedInputDraft?.takeIf { it.isBlank() || parseDateControlInput(it, kind) != null }
        ?: normalizedStateDraft
}

private fun parseDateControlInput(value: String, kind: WebDateControlKind): LocalDateTime? =
    when (kind) {
        WebDateControlKind.Date -> parseDeadlineInput(value)?.toUiZoned()?.dateTime
        WebDateControlKind.DateTime -> parseDateTimeInput(value)?.toUiZoned()?.dateTime
    }

private fun parseDeadlineInput(value: String): ZonedDateTime? {
    val trimmed = value.trim()
    if (trimmed.isBlank()) return null
    val date = parseIsoDatePrefix(trimmed) ?: return null
    return ZonedDateTime(
        LocalDateTime(date.year, date.monthNumber(), date.day, 17, 0),
        currentUiTimeZone(),
    )
}

private fun parseDateTimeInput(value: String): ZonedDateTime? {
    val trimmed = value.trim()
    if (trimmed.isBlank()) {
        return null
    }
    val parsed =
        try {
            LocalDateTime.parse(trimmed)
        } catch (_: IllegalArgumentException) {
            null
        }
    if (parsed != null) {
        return ZonedDateTime(parsed, currentUiTimeZone())
    }
    val displayMatch =
        Regex(
                """^\s*(\d{4})-(\d{1,2})-(\d{1,2})(?:[T,\s]+)(\d{1,2}):(\d{2})(?:\s*([AaPp][Mm]))?\s*$"""
            )
            .matchEntire(trimmed)
    if (displayMatch != null) {
        val year = displayMatch.groupValues[1].toIntOrNull() ?: return null
        val month = displayMatch.groupValues[2].toIntOrNull() ?: return null
        val day = displayMatch.groupValues[3].toIntOrNull() ?: return null
        val rawHour = displayMatch.groupValues[4].toIntOrNull() ?: return null
        val minute = displayMatch.groupValues[5].toIntOrNull() ?: return null
        val period = displayMatch.groupValues[6].uppercase().takeIf { it.isNotBlank() }
        val hour =
            when (period) {
                "AM" -> if (rawHour == 12) 0 else rawHour
                "PM" -> if (rawHour == 12) 12 else rawHour + 12
                else -> rawHour
            }
        return localDateTimeOrNull(year, month, day, hour, minute)?.let {
            ZonedDateTime(it, currentUiTimeZone())
        }
    }
    val date = parseIsoDatePrefix(trimmed) ?: return null
    return ZonedDateTime(
        LocalDateTime(date.year, date.monthNumber(), date.day, 9, 0),
        currentUiTimeZone(),
    )
}

private fun nowUiZoned(): ZonedDateTime =
    ZonedDateTime(Clock.System.now().toLocalDateTime(currentUiTimeZone()), currentUiTimeZone())

private fun parseIsoDatePrefix(value: String): LocalDate? {
    val match = Regex("""^\s*(\d{4})-(\d{1,2})-(\d{1,2})""").find(value) ?: return null
    val year = match.groupValues[1].toIntOrNull() ?: return null
    val month = match.groupValues[2].toIntOrNull() ?: return null
    val day = match.groupValues[3].toIntOrNull() ?: return null
    return localDateOrNull(year, month, day)
}

private fun inputElementValue(id: String?): String? =
    id?.let { document.getElementById(it) as? HTMLInputElement }?.value

private class MutableTextDraft(var value: String)

private fun localDateOrNull(year: Int, month: Int, day: Int): LocalDate? =
    try {
        LocalDate(year, month, day)
    } catch (_: IllegalArgumentException) {
        null
    }

private fun localDateTimeOrNull(
    year: Int,
    month: Int,
    day: Int,
    hour: Int,
    minute: Int,
): LocalDateTime? =
    try {
        LocalDateTime(year, month, day, hour, minute)
    } catch (_: IllegalArgumentException) {
        null
    }

private fun LocalDate.monthNumber(): Int = month.ordinal + 1

private fun LocalDateTime.monthNumber(): Int = month.ordinal + 1

private fun LocalDateTime.dateOnly(): LocalDate = LocalDate(year, monthNumber(), day)

private fun LocalDateTime.monthStart(): LocalDate = LocalDate(year, monthNumber(), 1)

private fun LocalDateTime.withDate(date: LocalDate): LocalDateTime =
    LocalDateTime(date.year, date.monthNumber(), date.day, hour, minute)

private fun LocalDateTime.hour12(): Int {
    val value = hour % 12
    return if (value == 0) 12 else value
}

private fun LocalDateTime.dayPeriod(): String = if (hour < 12) "AM" else "PM"

private fun LocalDateTime.withHour(hour12: Int): LocalDateTime {
    if (hour12 !in 1..12) return this
    val nextHour =
        when (dayPeriod()) {
            "AM" -> if (hour12 == 12) 0 else hour12
            else -> if (hour12 == 12) 12 else hour12 + 12
        }
    return LocalDateTime(year, monthNumber(), day, nextHour, minute)
}

private fun LocalDateTime.withMinute(nextMinute: Int): LocalDateTime {
    if (nextMinute !in 0..59) return this
    return LocalDateTime(year, monthNumber(), day, hour, nextMinute)
}

private fun LocalDateTime.withPeriod(period: String): LocalDateTime {
    val nextHour =
        when (period.uppercase()) {
            "AM" -> if (hour >= 12) hour - 12 else hour
            "PM" -> if (hour < 12) hour + 12 else hour
            else -> hour
        }
    return LocalDateTime(year, monthNumber(), day, nextHour, minute)
}

private fun calendarMonthCells(monthStart: LocalDate): List<LocalDate> {
    val leadingDays = (monthStart.dayOfWeek.ordinal + 1) % 7
    val firstVisibleDay = monthStart + DatePeriod(days = -leadingDays)
    return (0 until 42).map { offset -> firstVisibleDay + DatePeriod(days = offset) }
}

private fun Int?.toTimeSpanOrNull(): TimeSpan? {
    val hours = this ?: return null
    if (hours <= 0) {
        return null
    }
    return TimeSpan(listOf(PrimitiveTimeSpan(hours.toLong(), TimeUnit.Hours)))
}

private fun Kairos.Timebox.startInputValue(): String =
    ZonedDateTime(startTime.toLocalDateTime(currentUiTimeZone()), currentUiTimeZone())
        .dateTimeInputValue()

private fun TimeSpan?.totalWholeHoursOrNull(): Int? {
    val spans = this?.spans ?: return null
    var totalHours = 0L
    for (span in spans) {
        totalHours +=
            when (span.unit) {
                TimeUnit.Seconds -> return null
                TimeUnit.Minutes -> return null
                TimeUnit.Hours -> span.value
                TimeUnit.Days -> span.value * 24L
                TimeUnit.Weeks -> span.value * 24L * 7L
                TimeUnit.Months -> return null
                TimeUnit.Years -> return null
            }
    }
    return totalHours.toInt()
}

private fun Kairos.Project.projectDisplayName(allProjects: List<Kairos.Project>): String {
    val byId = allProjects.associateBy { it.id }
    val names = mutableListOf<String>()
    var current: Kairos.Project? = this
    while (current != null) {
        names += current.name
        current = current.parentProjectId?.let(byId::get)
    }
    return names.asReversed().joinToString(" / ")
}

private fun Kairos.Project.projectDepth(allProjects: List<Kairos.Project>): Int {
    val byId = allProjects.associateBy { it.id }
    var depth = 0
    var current = parentProjectId?.let(byId::get)
    while (current != null) {
        depth += 1
        current = current.parentProjectId?.let(byId::get)
    }
    return depth
}

private fun Kairos.Project.projectContextLabel(allProjects: List<Kairos.Project>): String? {
    val byId = allProjects.associateBy { it.id }
    val names = mutableListOf<String>()
    var current = parentProjectId?.let(byId::get)
    while (current != null) {
        names += current.name
        current = current.parentProjectId?.let(byId::get)
    }
    return names.asReversed().joinToString(" / ").ifBlank { null }
}

private fun Kairos.Objective.projectLabel(allProjects: List<Kairos.Project>): String? =
    projectId?.let { id ->
        allProjects.firstOrNull { project -> project.id == id }?.projectDisplayName(allProjects)
    }

private fun propagateUiThrowable(error: Throwable) {
    when (error) {
        is kotlin.coroutines.cancellation.CancellationException,
        is Error,
        is IllegalStateException,
        is IllegalArgumentException,
        is UnsupportedOperationException,
        is NoSuchElementException,
        is IndexOutOfBoundsException,
        is NullPointerException,
        is ClassCastException,
        is ArithmeticException,
        is ConcurrentModificationException -> throw error
    }
}

private fun stableIndex(value: String, size: Int): Int {
    val hash = value.hashCode()
    return abs(if (hash == Int.MIN_VALUE) 0 else hash) % size
}

private fun activityInitials(name: String): String =
    name
        .split(" ")
        .filter { it.isNotBlank() }
        .take(2)
        .mapNotNull { it.firstOrNull()?.uppercaseChar()?.toString() }
        .joinToString("")
        .ifBlank { "K" }

private fun activityAvatarColor(name: String): String =
    AccentColors[stableIndex(name, AccentColors.size)]

private fun taskTint(color: String): String =
    when (color) {
        Coral -> "#ffeee9"
        Blue -> "#eaf1ff"
        Teal -> "#e8faf6"
        Green -> "#e8f6ec"
        Yellow -> "#fff7dd"
        Pink -> "#fdeaf4"
        else -> "#f1f2f4"
    }

private fun Kairos.Project.projectAccent(): String =
    AccentColors[stableIndex(name, AccentColors.size)]

private fun WebScheduleEntry.calendarAccent(): String =
    when (kind) {
        WebScheduleEntryKind.Ticket -> accent
        WebScheduleEntryKind.Event -> Blue
        WebScheduleEntryKind.Timebox -> Teal
    }

private fun hexToRgba(hex: String, alpha: Double): String {
    val normalized = hex.removePrefix("#")
    if (normalized.length != 6) return hex
    val red = normalized.substring(0, 2).toIntOrNull(16) ?: return hex
    val green = normalized.substring(2, 4).toIntOrNull(16) ?: return hex
    val blue = normalized.substring(4, 6).toIntOrNull(16) ?: return hex
    return "rgba($red, $green, $blue, ${alpha.coerceIn(0.0, 1.0)})"
}

private fun StyleScope.primaryButton(enabled: Boolean = true) {
    props(
        "height" to "36px",
        "border" to if (enabled) "0" else "1px solid #d7dee8",
        "border-radius" to "8px",
        "background" to if (enabled) "#2f6fdb" else Paper,
        "color" to if (enabled) Paper else "#667085",
        "padding" to "0 16px",
        "font-size" to "14px",
        "font-weight" to "700",
        "cursor" to if (enabled) "pointer" else "not-allowed",
        "box-shadow" to if (enabled) "0 6px 16px rgba(47,111,219,0.22)" else "none",
        "opacity" to if (enabled) "1" else "0.46",
        "transition" to "background 120ms ease, color 120ms ease, border-color 120ms ease",
    )
}

private fun StyleScope.secondaryButton(selected: Boolean = false) {
    props(
        "height" to "36px",
        "border" to if (selected) "1px solid #c4d4ef" else "1px solid #d7dce5",
        "border-radius" to "8px",
        "background" to if (selected) "#f3f7ff" else Paper,
        "box-shadow" to if (selected) "inset 0 0 0 1px #d9e5fb" else "none",
        "color" to if (selected) Blue else Ink,
        "padding" to "0 14px",
        "font-size" to "14px",
        "font-weight" to "700",
        "cursor" to "pointer",
        "transition" to "background 120ms ease, border-color 120ms ease, color 120ms ease",
    )
}

private fun StyleScope.ghostButton() {
    props(
        "height" to "36px",
        "border" to "0",
        "border-radius" to "8px",
        "background" to "transparent",
        "color" to Muted,
        "padding" to "0 8px",
        "font-size" to "13px",
        "font-weight" to "700",
        "cursor" to "pointer",
    )
}

private fun StyleScope.inlineLinkButton() {
    props(
        "height" to "auto",
        "border" to "0",
        "border-radius" to "0",
        "background" to "transparent",
        "color" to Blue,
        "padding" to "0",
        "font-size" to "13px",
        "font-weight" to "700",
        "line-height" to "1.5",
        "cursor" to "pointer",
        "text-decoration" to "none",
    )
}

private fun StyleScope.destructiveButton() {
    props(
        "height" to "36px",
        "border" to "1px solid #c94c3b",
        "border-radius" to "8px",
        "background" to "#c94c3b",
        "color" to Paper,
        "padding" to "0 14px",
        "font-size" to "14px",
        "font-weight" to "700",
        "cursor" to "pointer",
        "box-shadow" to "0 8px 18px rgba(201,76,59,0.22)",
    )
}

private fun StyleScope.overflowButton(selected: Boolean = false) {
    props(
        "height" to "40px",
        "width" to "44px",
        "min-width" to "44px",
        "border" to if (selected) "1px solid #c4d4ef" else "1px solid #d7dce5",
        "border-radius" to "8px",
        "background" to if (selected) "#f3f7ff" else Paper,
        "color" to if (selected) Blue else Muted,
        "padding" to "0",
        "font-size" to "18px",
        "font-weight" to "700",
        "cursor" to "pointer",
        "transition" to "background 120ms ease, border-color 120ms ease, color 120ms ease",
    )
}

private fun StyleScope.toolbarButton(selected: Boolean = false) {
    props(
        "height" to "32px",
        "border" to if (selected) "1px solid #c8d5eb" else "1px solid #d7dce5",
        "border-radius" to "8px",
        "background" to if (selected) "#f3f7ff" else Paper,
        "color" to if (selected) Blue else Ink,
        "padding" to "0 12px",
        "font-size" to "12px",
        "font-weight" to "700",
        "cursor" to "pointer",
        "white-space" to "nowrap",
        "transition" to "background 120ms ease, border-color 120ms ease, color 120ms ease",
    )
}

private fun StyleScope.tertiaryButton() {
    props(
        "height" to "36px",
        "border" to "1px solid #e3e6eb",
        "border-radius" to "8px",
        "background" to "#f7f8fa",
        "color" to Muted,
        "padding" to "0 14px",
        "font-size" to "13px",
        "font-weight" to "700",
        "cursor" to "pointer",
    )
}

private fun StyleScope.dropdownOptionButton() {
    props(
        "min-height" to "36px",
        "width" to "100%",
        "border" to "1px solid transparent",
        "border-radius" to "8px",
        "background" to Paper,
        "color" to Ink,
        "padding" to "8px 10px",
        "display" to "flex",
        "align-items" to "flex-start",
        "justify-content" to "flex-start",
        "cursor" to "pointer",
        "text-align" to "left",
        "font-size" to "13px",
        "font-weight" to "600",
    )
}

private fun StyleScope.tabButton(selected: Boolean) {
    props(
        "height" to "36px",
        "border" to "0",
        "border-bottom" to if (selected) "3px solid $Blue" else "3px solid transparent",
        "border-radius" to "0",
        "background" to "transparent",
        "color" to if (selected) Ink else "#8b93a1",
        "padding" to "0 4px",
        "font-size" to "14px",
        "font-weight" to if (selected) "800" else "600",
        "opacity" to if (selected) "1" else "0.78",
        "cursor" to "pointer",
        "white-space" to "nowrap",
        "transition" to "color 120ms ease, border-color 120ms ease",
    )
}

private fun StyleScope.pickerIconButton() {
    props(
        "width" to "32px",
        "height" to "32px",
        "border" to "0",
        "border-radius" to "8px",
        "background" to "#f2f4f7",
        "color" to Ink,
        "font-size" to "16px",
        "font-weight" to "800",
        "cursor" to "pointer",
        "padding" to "0",
    )
}

private fun StyleScope.pickerDayButton(selected: Boolean, inMonth: Boolean) {
    props(
        "height" to "32px",
        "min-width" to "0",
        "border" to if (selected) "1px solid $Blue" else "1px solid transparent",
        "border-radius" to "8px",
        "background" to if (selected) "#dceaff" else "transparent",
        "color" to
            when {
                selected -> Ink
                inMonth -> Ink
                else -> "#a2a7b0"
            },
        "font-size" to "13px",
        "font-weight" to if (selected) "800" else "600",
        "cursor" to "pointer",
        "padding" to "0",
    )
}

private fun StyleScope.cardSurface() {
    props(
        "display" to "grid",
        "gap" to "12px",
        "padding" to "16px",
        "border" to "1px solid $Border",
        "border-radius" to "8px",
        "background" to "#fafafb",
        "box-sizing" to "border-box",
    )
}

private fun StyleScope.formInputStyle() {
    modalTextInput()
}

private fun StyleScope.modalTextInput() {
    props(
        "width" to "100%",
        "height" to "40px",
        "border" to "1px solid $Border",
        "border-radius" to "8px",
        "padding" to "0 12px",
        "font-size" to "14px",
        "box-sizing" to "border-box",
        "background" to Paper,
        "color" to Ink,
    )
}

private fun StyleScope.modalReadonlyBox() {
    props(
        "width" to "100%",
        "min-height" to "40px",
        "display" to "flex",
        "align-items" to "center",
        "padding" to "0 12px",
        "border" to "1px solid $Border",
        "border-radius" to "8px",
        "box-sizing" to "border-box",
        "background" to "#fafafb",
        "font-size" to "14px",
        "color" to Ink,
    )
}

private fun StyleScope.calendarModeButton(selected: Boolean) {
    props(
        "height" to "32px",
        "border" to if (selected) "1px solid #c4d4ef" else "1px solid #d7dce5",
        "border-radius" to "999px",
        "background" to if (selected) "#f3f7ff" else Paper,
        "color" to if (selected) Blue else Ink,
        "padding" to "0 12px",
        "font-size" to "13px",
        "font-weight" to "700",
        "cursor" to "pointer",
    )
}

private fun StyleScope.quickTaskButton(tone: WebTaskQuickActionTone) {
    val background =
        when (tone) {
            WebTaskQuickActionTone.Neutral -> "#eef4ff"
            WebTaskQuickActionTone.Primary -> Blue
            WebTaskQuickActionTone.Review -> "#fff1d8"
            WebTaskQuickActionTone.Success -> Green
        }
    val border =
        when (tone) {
            WebTaskQuickActionTone.Neutral -> "1px solid #d6e4fb"
            WebTaskQuickActionTone.Primary -> "0"
            WebTaskQuickActionTone.Review -> "1px solid #f1cb87"
            WebTaskQuickActionTone.Success -> "0"
        }
    val color =
        when (tone) {
            WebTaskQuickActionTone.Neutral -> Blue
            WebTaskQuickActionTone.Primary -> Paper
            WebTaskQuickActionTone.Review -> "#8a5600"
            WebTaskQuickActionTone.Success -> Paper
        }
    props(
        "height" to "30px",
        "border" to border,
        "border-radius" to "6px",
        "background" to background,
        "color" to color,
        "padding" to "0 12px",
        "font-size" to "12px",
        "font-weight" to "700",
        "cursor" to "pointer",
        "white-space" to "nowrap",
        "box-shadow" to
            when (tone) {
                WebTaskQuickActionTone.Primary -> "0 6px 14px rgba(47,111,219,0.18)"
                WebTaskQuickActionTone.Success -> "0 6px 14px rgba(46,142,78,0.18)"
                else -> "none"
            },
    )
}

private fun StyleScope.selectorPill(selected: Boolean, color: String, minWidth: String? = null) {
    props(
        "height" to "30px",
        "border" to if (selected) "1px solid $color" else "1px solid $Border",
        "border-radius" to "8px",
        "background" to if (selected) color else Paper,
        "color" to Ink,
        "padding" to "0 12px",
        "font-size" to "12px",
        "font-weight" to if (selected) "700" else "600",
        "cursor" to "pointer",
        "white-space" to "nowrap",
        "flex-shrink" to "0",
        "box-shadow" to if (selected) "0 8px 18px rgba(28,31,38,0.08)" else "none",
    )
    minWidth?.let { value -> props("min-width" to value, "justify-content" to "center") }
}

private fun StyleScope.timelineCell(header: Boolean) {
    props(
        "border-right" to "1px solid $Border",
        "border-bottom" to "1px solid $Border",
        "padding" to "12px",
        "box-sizing" to "border-box",
        "font-size" to if (header) "14px" else "13px",
        "font-weight" to if (header) "700" else "600",
        "color" to if (header) Muted else Ink,
        "background" to if (header) Paper else "#fbfcfd",
        "overflow" to "hidden",
    )
}

private fun StyleScope.props(vararg declarations: Pair<String, String>) {
    declarations.forEach { (name, value) -> property(name, value) }
}
